// 작성 폼 트리 렌더러 (web ↔ admin 공유 — WriteFormRenderer에서 이전, 2026-07-08).
//
// 순수 프레젠테이션 렌더: InputWidget 단일 입력 위젯 렌더(renderWriteNode)와
// ComposerWidget 트리 walk(ComposerWidgetTreeView)를 담는다. dataSources(RowsCollectionEditor 등)
// 도메인 위젯 렌더는 web 전용이라 이 패키지에 두지 않고 `footer` slot으로 주입받는다.
// 동작 불변이 최우선(web 운영 위임 페이지가 이 렌더러를 소비).
import {
  Fragment,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  COMPOSER_BASE_VIEW_KEY,
  COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS,
  COMPOSER_IMPROVEMENT_CANDIDATE_PART_META,
  COMPOSER_VIEWS,
  comboboxSelectedIdKey,
  effectiveModalPurpose,
  effectiveOptionsSources,
  effectiveRowAddRemove,
  formatBusinessNumberInput,
  formatPhoneInput,
  ftTextCellPrefillKey,
  REGISTERED_FORM_WIDGETS_META,
  renderDisplayTemplate,
  ROW_ADD_REMOVE_NONE,
  ROWS_SEED_SUFFIX,
  suggestionDictKey,
  type BranchWidgetSlot,
  type ComposerSuggestSearch,
  type ComposerSuggestSelect,
  type ComposerSuggestionRow,
  type ComposerTextStyle,
  type ComposerWidget,
  type DataDef,
  type DataDefField,
  type DataSourceDef,
  type DataSourceFieldMap,
  type FormTableColumn,
  type FormTableFixedCell,
  type FormTableRow,
  type FormTableWidget,
  type GroupWidget,
  type ImprovementCandidatePart,
  type InputWidget,
  type MissingRequiredField,
  type MissingRequirement,
  type ModalCommitMap,
  type ModalTriggerWidget,
  type RepeatWidget,
  type SortableSourceFieldMap,
  withObjectParticle,
} from '@safetyhub/shared';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Modal, buttonClassName } from '@safetyhub/ui';
import { createPortal } from 'react-dom';
import { computeCellCompute, evaluateCondition, readBindingValue } from './RenderTemplate';
import { applyRectDecoration, boxSpacingToCss, cellStyleToCss, resolveCellBorder } from './layout';
import { cellDiagonalOverlay } from './tableCellDiagonal';
import {
  applyDynamicSelectSelection,
  collectWidgetDefaultValues,
  resolveDynamicSelectOptions,
} from './composerWidgets';
import {
  AnnotatedImageView,
  IMAGE_PLACEHOLDER_STYLE,
  READONLY_IMAGE_STYLE,
} from './AnnotatedImageView';
// 사진 업로드 한 벌(정본 photoUploadWidget.tsx — 2026-09-07 분리, 행 그리드 photo 필드와 공유).
import { ImageWithFallback } from './ImageWithFallback';
import { PhotoUploadWidget, PHOTO_CELL_THUMB_STYLE } from './photoUploadWidget';
import type { ComposerPhotoUpload } from './photoUploadWidget';
// 개선 이미지 AI 호스트 콜백 계약(등록형 행 위젯 improvement-image) — 컨텍스트 채널 타입만.
import type { ComposerImprovementImageHost } from './widgets/improvementImageHost';
// 날짜/기간 컨트롤 + 값 표시 규약 — 행 그리드(RowsCollectionEditor)와 공유하는 단일 정본.
import { DateFieldInput, DateRangeFieldInput } from './DateFieldInput';
import {
  dateInputValue,
  dateRangeDisplayLabel,
  dateWidgetDisplayText,
  isDateRangeValue,
} from './dateValue';
import {
  SignatureSlotView,
  rowSignerName,
  signatureSlotViewProps,
  type ComposerSignatureSlotsContext,
  type SignatureSlotViewProps,
} from './SignatureSlotView';
import { normalizeFormTable } from './formTableNormalize';
import { resolveTableFields } from './resolveTableFields';
import { applyRegisteredWrites, collectScalarRegisteredWrites } from './registeredWidgetWrites';
// 범용 검색 제안 축(suggestionsFrom) — 드롭다운 한 벌 + 필터/전파 헬퍼(콜백 실행부는 호스트).
import {
  SUGGEST_REQUIRE_SELECTION_HINT,
  SUGGEST_REQUIRE_SELECTION_PLACEHOLDER,
  SUGGEST_REQUIRE_SELECTION_UNAVAILABLE,
  SUGGEST_UNAVAILABLE_CONTROL_STYLE,
  SuggestNote,
  SuggestionDropdown,
  applySuggestionPick,
  buildSuggestFilter,
  isTypedCommitBlocked,
  type SuggestionControlBindings,
} from './suggestionDropdown';
// 순환 참조(requiredValidation ↔ 이 파일)지만 양쪽 다 함수 호출 시점 사용이라 안전 —
// 검증 순회(제외 규칙)는 requiredValidation이 단일 정본이므로 여기 중복 구현하지 않는다.
import { collectInvalidInputFormat, collectMissingRequired } from './requiredValidation';
// 긴 텍스트 접기(InputWidget.collapseLines) — 표시 상태 전용 축(본문·print 미소비, 규약은 그쪽 doc).
import { CollapsibleText } from './collapsibleText';
import { applyFormat, getByPath, isEmptyConditionValue } from './resolver';
// 구간 배지(rangeMap display:'badge')·병기(annotationKey) — 본문 렌더와 **같은 구현**(단일 정본).
// 작성폼용 배지를 새로 그리면 두 화면이 갈라진다.
import { rangeDisplayNode } from './rangeBadge';
import { runAssembler } from './assemblers';
// 반복 위젯(repeat) 골격 — 행 그리드 자산 승계(중복 구현 금지, 결정 2026-07-20-composer-repeat-
// widget-row-scope §골격 승계): 행 카드/헤더/배지 스타일 + 되돌리기 버튼 + textarea-list 편집기.
// RowsCollectionEditor는 dataSourceCollection·suggestionDropdown·shared만 import하므로 순환 없음.
import {
  RevertButton,
  TextareaListField,
  addItemButtonDisabledStyle,
  addItemButtonStyle,
  rowBadgeStyle,
  rowCardStyle,
  rowHeaderBaseStyle,
  summaryPrimaryStyle,
  summarySecondaryStyle,
} from './RowsCollectionEditor';
import {
  addRow as addCollectionRow,
  blankFieldValue,
  collapsedAfterRemove,
  collapsedForToggleAll,
  dataSourceFieldsToRowFields,
  findSeedRow,
  isAllRowsCollapsed,
  isRowAddBlockedByLimit,
  isRowModified,
  makeRowScopedOnChange,
  normalizeList,
  normalizeRows,
  reconcileRowScopedPending,
  removeRowAtIndex,
  replaceRowAtIndex,
  rowAddLimitTitle,
  rowCollapseKey,
  rowsAfterAddedRowConfirm,
  rowsAfterRequiredSelectionEditEnd,
  rowsBeforeAddRow,
  summaryFieldsForRow,
  summaryText,
  toggleCollapseKey,
  updateRowAtIndex,
  type RowScopedPending,
} from './dataSourceCollection';
// 행 추가 직후 "방금 만든 행"으로 커서 — 판정(순수)과 DOM 포커스 부속을 목록형 focusOnMount와
// 같은 규약으로 모아 둔 모듈(addedRowFocus doc 참조).
import {
  FT_REPEAT_ROW_ATTR,
  REPEAT_ROW_ID_ATTR,
  TABLE_WIDGET_ATTR,
  firstTypeableCellKey,
  focusAddedRowContainer,
  focusAddedTableCell,
} from './addedRowFocus';

/**
 * 등록형 폼 위젯 구현 계약 — host(web)가 REGISTERED_FORM_WIDGETS_META 키별로 이 타입 컴포넌트를 등록.
 * 위젯은 자기 값(bindingKey)과 전파 대상 쓰기를 모두 `onChange`로 낸다(전파는 applyRegisteredWrites 사용).
 */
export interface ComposerRegisteredFormWidgetProps {
  /** widgetKey/writes/bindingKey를 포함한 위젯 정의. */
  widget: InputWidget;
  /** 전체 content 값(전파 대상 배열 행 접근 등). */
  values: Record<string, unknown>;
  /** 자기 값 + 전파 대상 쓰기 모두 이걸로(bindingKey 또는 배열 defKey 대상). */
  onChange: (bindingKey: string, value: unknown) => void;
  /**
   * host가 주입하는 렌더 컨텍스트(무도메인 — id 문자열만). 위젯이 현장/회사 범위 조회(SiteContact 등)에 사용.
   * 미주입(admin 미리보기 등)이면 위젯은 안내/비활성으로 degrade.
   * resolvedRows는 트리 렌더 스코프의 후보 rows(기준표 등)를 브리지 위젯(행 위젯→폼 위젯 어댑터)에
   * 흘려주기 위한 합성 채널 — 렌더 호출부가 registeredWidgetContext에 얹어 넘긴다(rowWidgetAsFormWidget).
   * values는 본문 스코프 값 — 반복(repeat) 행 스코프의 브리지 위젯이 기준표 등 본문 채널을 폴백
   * 조회하도록 RepeatRowsBlock이 registeredWidgetContext에 주입한다(행 스코프 값엔 본문 키 부재).
   * improvementImage는 개선 이미지 AI 호스트 콜백(등록형 행 위젯 `improvement-image` 소비) —
   * 호스트가 렌더러 prop `onImprovementImage`로 주면 렌더러가 여기 싣는다(등록형 위젯의 유일한 채널).
   */
  context?: {
    companyId?: string;
    siteId?: string;
    resolvedRows?: Record<string, Record<string, unknown>[]>;
    values?: Record<string, unknown>;
    improvementImage?: ComposerImprovementImageHost;
  };
}
export type ComposerRegisteredFormWidget = ComponentType<ComposerRegisteredFormWidgetProps>;

/** Tailwind `md` 브레이크포인트 — 이 폭 미만 뷰는 그룹 단 수를 자동 1단 degrade(구 md: 동작 유지). */
const MD_BREAKPOINT_PX = 768;
import { orderSiblingsForView } from './composerWidgetTree';

// 작성 폼 컨트롤 결 = 시안 `remote-files/safetyhub_안전관리상태보고서.html`의 .field-input/.field-select/
// .field-date·.field-textarea(2026-08-02 규약 교체 — 구 SMS 작성 페이지 패리티(2026-07-17)는 폐기).
// 모양 값(높이·패딩·radius·테두리·색·폰트)은 전부 인라인 style(CONTROL_VISUAL_STYLE /
// TEXTAREA_VISUAL_STYLE)이 소유한다 — rc에서만 처음 쓰는 Tailwind 유틸은 앱 CSS에 미생성될 수 있어
// (purge 함정) 조용히 무스타일이 되기 때문. 클래스에는 web·admin 양쪽에서 이미 쓰는 상태 유틸
// (focus:*)과 placeholder 색만 남긴다(placeholder 색은 인라인 style로 지정 불가).
const inputClass =
  'w-full placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500';

// textarea 전용 — 세로 리사이즈만 허용(resize-y)+ 연한 placeholder. 나머지 모양은 아래 인라인.
const textareaClass =
  'w-full resize-y placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500';

// 시안 .field-input/.field-select/.field-date — 높이 42 · 패딩 0 14 · radius 10 · 14px/600 ·
// 글자 ink-800 · 테두리 line(#e8ecf2) · 흰 바탕. select/date 래퍼도 같은 값을 공유한다.
const CONTROL_VISUAL_STYLE: CSSProperties = {
  height: 42,
  padding: '0 14px',
  borderRadius: 10,
  border: '1px solid #e8ecf2',
  backgroundColor: '#ffffff',
  fontSize: 14,
  fontWeight: 600,
  color: '#1e293b',
  letterSpacing: '-0.005em',
};

// 시안 .field-textarea — 패딩 13/14 · min-height 96 · 행간 1.55 · 14px/500(본문형이라 input보다 얇다).
const TEXTAREA_VISUAL_STYLE: CSSProperties = {
  padding: '13px 14px',
  minHeight: 96,
  borderRadius: 10,
  border: '1px solid #e8ecf2',
  backgroundColor: '#ffffff',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.55,
  color: '#1e293b',
  letterSpacing: '-0.005em',
};

// 체크박스/라디오 — 시안 .check-item input(18px · accent-color brand-600). 브라우저 기본(13px)은
// 시안 대비 확연히 작다. formTable 셀 안 체크/라디오는 셀 밀도 규약이라 대상이 아니다.
const CHOICE_CONTROL_STYLE: CSSProperties = {
  width: 18,
  height: 18,
  accentColor: '#4f46e5',
  cursor: 'pointer',
  flex: 'none',
};

// 체크박스/라디오 라벨(컨트롤과 한 몸) — 시안 .check-item .lbl(13.5px · ink-700). 굵기 550은
// 표준 웨이트가 아니라 500으로 근사한다(시안 750→700 근사와 같은 처리).
const CHOICE_LABEL_STYLE: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 500,
  color: '#334155',
};

// 입력 컨트롤(input/textarea/select)은 UA 규약상 text-align을 상속하지 않아(Chromium=start)
// formTable 셀 정렬(td textAlign)이 editable 위젯 값에 반영되지 않았다(2026-07-13 사용자 제보,
// readonly 값 div는 일반 요소라 상속 정상). 명시 inherit로 셀/컨테이너 정렬을 따르게 한다.
const inputInheritAlignStyle: CSSProperties = { textAlign: 'inherit' };

// formTable 셀 안 컨트롤 전용 — 폰트·색도 UA 규약상 미상속 + 컨트롤 폰트가 고정이라, 셀
// 스타일(표 cellStyle/셀 style)을 바꿔도 위젯 값 표시가 불변이었다(2026-07-13 사용자 제보 2건:
// 크기·글자색). 셀 경로에서만 텍스트 축 전면 inherit — font shorthand(스타일/굵기/크기/줄간격/
// 폰트) + 자간 + 밑줄 + 색(일반 폼의 input은 부모 폰트가 표 기본과 달라 전역 적용 시 회귀).
const inputInheritCellFontStyle: CSSProperties = {
  ...inputInheritAlignStyle,
  font: 'inherit',
  letterSpacing: 'inherit',
  textDecoration: 'inherit',
  color: 'inherit',
  // 구 SMS 셀 컨트롤 모양 — 테두리 + 라운드 4 + 패딩 6/8. 셀 경로는 별도 밀도 규약이라 시안 컨트롤
  // 결(CONTROL_VISUAL_STYLE)을 통째로 얹지 않는다(높이 42가 표 행 높이를 밀어버림 —
  // composeControlStyle 참조). 테두리만은 시안 line 토큰(#e8ecf2)을 공유한다: 옛 inputClass의
  // `border border-gray-300`이 시안 정렬(36ce9325e)에서 제거되며 셀 안 컨트롤이 무테두리가 됐고
  // (preflight border 0), radius 4는 테두리를 전제한 값이라 홀로 남으면 보이지 않는다.
  border: '1px solid #e8ecf2',
  borderRadius: 4,
  padding: '6px 8px',
};
// formTable 셀 안 number 컨트롤 — 다른 컨트롤과 완전 동일(셀 채움 + 셀 textAlign 상속).
// "숫자=우측" 고정 규약도 B 모델(셀 전체=컨트롤, 정렬·간격은 셀 속성으로 저작 제어)에 따라
// 제거 — 우측 정렬을 원하면 셀 정렬로 지정한다(2026-07-14 제보: 셀 가운데 정렬이 input에
// 반영되지 않던 원인이 이 고정 right였음).
const numberCellControlStyle: CSSProperties = inputInheritCellFontStyle;
// readonly 값 표시도 동일 — 셀 색 미지정 시 표 기본(gray-700)을 상속하는데, 구 SMS의 readonly
// 계(명) 박스도 gray-700(본문색)이라 원본과 일치(기존 gray-500 고정이 오히려 이탈이었음).
// + 셀 안 readonly는 **박스 크롬(배경/테두리/패딩)을 벗고 셀이 바탕을 소유**(2026-08-04 사용자
// 확정) — readonlyValueClass의 gray-50 박스가 셀 배경(값 셀 흰색 등)과 어긋나는 이중 크롬을
// 해소한다. 컨트롤이 아니라 값 표시라 plain text가 legacy 카드(값=민짜 텍스트) 질감과 일치.
// 인라인 style이 클래스(readonlyValueClass)를 덮는다.
const readonlyInheritCellFontStyle: CSSProperties = {
  font: 'inherit',
  letterSpacing: 'inherit',
  textDecoration: 'inherit',
  color: 'inherit',
  background: 'transparent',
  border: 'none',
  borderRadius: 0,
  padding: 0,
  minHeight: 0,
};

// enabledWhen 비활성 컨트롤 시각 — 구 SMS disabled 입력 관례(bg-gray-100/text-gray-500/not-allowed,
// web AddressSearch 등)의 인라인 등가(rc 신규 Tailwind 유틸 금지 — purge 규약, disabled: variant 포함).
// pointerEvents:none — disabled 컨트롤은 click 이벤트 자체가 미발생이라 캔버스 selectable 래퍼가
// 클릭을 못 받는데, none이면 클릭 타깃이 래퍼로 통과해 위젯 선택이 유지된다(실폼에선 disabled와
// 중복이라 무해). 캔버스=실폼 동일 스타일(WYSIWYG — interactive 특례 없음).
const DISABLED_CONTROL_STYLE: CSSProperties = {
  backgroundColor: '#f3f4f6',
  color: '#6b7280',
  cursor: 'not-allowed',
  pointerEvents: 'none',
};
// disabled 시 라벨 텍스트(radio 옵션/checkbox 라벨 등 컨트롤과 한 몸인 라벨만) 회색 강조.
const DISABLED_LABEL_STYLE: CSSProperties = { color: '#9ca3af' };

// radio displayVariant:'buttons' — 구 TBM 작성 폼 「위험성평가 실시여부」의 예/아니오 토글
// (apps/web/src/pages/tbm/new.tsx `.yn-toggle` = `grid grid-cols-2 gap-2.5` + Chip `w-full py-3`)
// 전사. 값은 전부 인라인이다 — 등분 그리드(grid-cols-N)는 옵션 수에 따라 달라져 Tailwind 정적
// 스캔 대상이 될 수 없고, 선택 강조색·테두리·패딩은 rc에서만 처음 쓰는 유틸이라 purge 함정
// (조용한 무스타일)에 걸린다.
const RADIO_BUTTONS_GROUP_STYLE: CSSProperties = { display: 'grid', gap: 10 };
// Chip 껍데기 규약(packages/ui Chip) — radius 10(rounded-control) · 굵은 글씨 · 자간 -0.005em ·
// 가로 꽉 참. 높이는 구 호출부의 `py-3`(12px)이 정본이라 패딩으로 낸다(고정 height 아님 — 라벨이
// 길어 줄바꿈되면 늘어나야 한다).
const RADIO_BUTTON_BASE_STYLE: CSSProperties = {
  width: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 14px',
  borderRadius: 10,
  borderWidth: 1,
  borderStyle: 'solid',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '-0.005em',
  cursor: 'pointer',
  transition: 'all 150ms',
};
// 선택 상태 — Chip STATE_CLASS.on의 인라인 등가(brand-500 테두리 · brand-50→#f5f7ff 그라데이션 ·
// brand-700 글자). 미선택은 off(line 테두리 · 흰 면 · ink-700). hover 변화는 클래스 없이 낼 수
// 없어 생략한다 — 선택 여부가 색으로 이미 갈려 있어 결이 무너지지 않는다.
const RADIO_BUTTON_ON_STYLE: CSSProperties = {
  borderColor: '#6366f1',
  backgroundImage: 'linear-gradient(to bottom right, #eef2ff, #f5f7ff)',
  backgroundColor: '#eef2ff',
  color: '#4338ca',
};
const RADIO_BUTTON_OFF_STYLE: CSSProperties = {
  borderColor: '#e8ecf2',
  backgroundColor: '#ffffff',
  color: '#334155',
};
// registered/dataSourceRows(host 소유 렌더)·sortableSelect(dnd 복합 위젯) — 개별 컨트롤에 disabled
// 속성을 꽂을 수 없어 서브트리 통째 비활성(반투명 + 포인터 차단). 레이아웃 중립(일반 block 래퍼).
const DISABLED_SUBTREE_STYLE: CSSProperties = { opacity: 0.6, pointerEvents: 'none' };

// 섹션(group) 카드 = 시안 .acc-section — radius 16 · 테두리 line(#e8ecf2) · 그림자 shadow-sm.
// 저작 boxStyle이 지정되면 그 위에 덮인다(사용자 지정 우선).
// ⚠️ 배경은 여기(전역)에 넣지 않는다 — 2026-08-04 흰색 전역 적용을 시도했으나 SMS 등 기존
// 템플릿은 본문 투명(페이지 바탕 비침) 결이 확정 룩이라 사용자 정정. 배경은 섹션 저작 축
// (GroupWidget.titleFill/contentFill)으로 템플릿별 지정한다.
const SECTION_BOX_STYLE: CSSProperties = {
  borderRadius: 16,
  border: '1px solid #e8ecf2',
  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
};
// 섹션 헤더 = 시안 .acc-h — 패딩 17/22. 접힘 상태는 옅은 상단 그라데이션, 펼침 상태는 흰 바탕 +
// 본문과의 구분선(.acc-section.expanded .acc-h). 접기 없는 섹션은 항상 펼침으로 본다.
const SECTION_HEADER_BASE_STYLE: CSSProperties = { padding: '17px 22px' };
const SECTION_HEADER_COLLAPSED_STYLE: CSSProperties = {
  background: 'linear-gradient(180deg,#fff,#fbfcfe)',
};
const SECTION_HEADER_EXPANDED_STYLE: CSSProperties = {
  background: '#ffffff',
  borderBottom: '1px solid #eef1f6',
};
// 섹션 본문 = 시안 .acc-body — 패딩 22/24/24. boxStyle 지정 group은 boxDecorStyle이 패딩을 소유하므로
// (이중 적용 금지, 위 boxDecorStyle 주석) 그 경로엔 얹지 않고 종전 p-5를 유지한다.
const SECTION_BODY_STYLE: CSSProperties = { padding: '22px 24px 24px' };

const LABEL_ALIGN_CLASS: Record<'start' | 'center' | 'end', string> = {
  start: '',
  center: 'text-center',
  end: 'text-right',
};

/** 텍스트 위젯의 위젯별 기본 폰트 스타일(숫자 모델). admin placeholder 표시용으로도 export. */
export interface ComposerTextStyleDefaults {
  fontSize: number;
  fontWeight: number;
  /** 미지정=상속(색 미부여). */
  color?: string;
}
/** input 라벨 기본 = 시안 .field-label(13px/700, ink-700). */
export const COMPOSER_INPUT_LABEL_DEFAULT_STYLE: ComposerTextStyleDefaults = {
  fontSize: 13,
  fontWeight: 700,
  color: '#334155',
};
/** heading 기본 = 시안 .sub-h(14.5px, ink-900). 굵기 750은 표준이 아니라 700으로 근사. */
export const COMPOSER_HEADING_DEFAULT_STYLE: ComposerTextStyleDefaults = {
  fontSize: 14.5,
  fontWeight: 700,
  color: '#0f172a',
};
/** 섹션명(group title) 기본 = 시안 .acc-h-title(16.5px/800, 색은 헤더에서 상속). */
export const COMPOSER_SECTION_TITLE_DEFAULT_STYLE: ComposerTextStyleDefaults = {
  fontSize: 16.5,
  fontWeight: 800,
};
/** 라벨 보조 설명(labelHint) 기본 = 시안 .field-label .hint(11.5px/500, ink-400). */
export const COMPOSER_LABEL_HINT_DEFAULT_STYLE: ComposerTextStyleDefaults = {
  fontSize: 11.5,
  fontWeight: 500,
  color: '#94a3b8',
};
/** 작성폼 표 본문 셀 기본 = 구 SMS 대화면 작성 테이블 본문(report-xl-text 기준, normal/gray-700). */
export const COMPOSER_FORM_TABLE_CELL_DEFAULT_STYLE: ComposerTextStyleDefaults = {
  fontSize: 16,
  fontWeight: 400,
  color: '#374151',
};

/**
 * 텍스트 스타일(숫자 모델)을 인라인 CSS로 합성. 미지정 필드는 위젯별 기본값.
 * 기본값에 color가 없고 지정도 없으면 color를 부여하지 않는다(부모 색 상속).
 * (export: 자체 라벨을 소유하는 등록형 위젯 구현이 라벨 규약을 재사용 — RiskCriteriaDetail 등.)
 */
export function textStyleCss(
  style: ComposerTextStyle | undefined,
  defaults: ComposerTextStyleDefaults,
): CSSProperties {
  const s = style ?? {};
  const css: CSSProperties = {
    fontSize: `${s.fontSize ?? defaults.fontSize}px`,
    fontWeight: s.fontWeight ?? defaults.fontWeight,
  };
  const color = s.color ?? defaults.color;
  if (color) css.color = color;
  // 정렬 축(2026-08-04) — 지정 시에만 부여(미지정=기존 좌측, 회귀 0). CSS textAlign은
  // start/center/end를 그대로 받는다(RTL 중립).
  if (s.align) css.textAlign = s.align;
  return css;
}

/** 단순 컨트롤(labelEl + 단일 컨트롤)만 side 배치 허용 — radio/checkbox/table 등은 top 고정. */
const SIDE_LAYOUT_WIDGETS = new Set<string>([
  'text',
  'number',
  'date',
  'time',
  'select',
  'textarea',
  'combobox',
]);

/**
 * readonly여도 공통 "readonly 텍스트 표현"으로 접지 않고 **자기 case 렌더로 내려가는** 위젯.
 *  - table/sortableSelect: 편집과 같은 레이아웃으로 값만 표시하는 자체 readonly 분기 보유
 *    (과거 별도 구식 렌더가 편집 모드와 달라 보이던 문제 — 2026-07-15 사용자 제보).
 *  - image/annotated-image: 애초에 표시 전용(입력 아님) — 텍스트 표현이 불가하고 객체 값은
 *    [object Object]로 샌다.
 */
const READONLY_SELF_RENDER_WIDGETS = new Set<string>([
  'table',
  'sortableSelect',
  'image',
  'annotated-image',
  // photo: readonly는 텍스트 표현(URL 문자열이 그대로 새는 것) 대신 image 동형 <img> 표시.
  'photo',
  // signature: 애초에 표시 전용(값을 이 칸에 타이핑하지 않는다) — 텍스트로 접으면 서명 data URL이
  // 통째로 새 나온다. readonly 여부와 무관하게 같은 서명 칸을 보여야 한다.
  'signature',
]);

/**
 * 긴 텍스트 접기(collapseLines)를 소비하는 위젯 — 값이 여러 줄 텍스트로 표시되는 것만.
 * 그 외 위젯(select/date/checkbox/table…)은 선언이 있어도 무시한다(editEntry 규약 동형).
 */
const COLLAPSE_TEXT_WIDGETS = new Set<string>(['text', 'textarea', 'textarea-list', 'text-list']);

/** 이 노드에 유효한 접기 줄 수 — 비대상 위젯이면 undefined(접기 없음). */
function collapseLinesFor(node: InputWidget): number | undefined {
  return COLLAPSE_TEXT_WIDGETS.has(node.widget) ? node.collapseLines : undefined;
}

/**
 * 표 칼럼(DataDefField)의 접기 줄 수 — 위젯 축(collapseLinesFor)의 셀 판. 텍스트로 표시되는
 * 칼럼(text/textarea, 미지정=text)만 대상이고, 값이 라벨/기호로 치환되는 칼럼(checkbox/select/
 * radio/number)은 접을 것이 없다.
 */
function collapseLinesForField(field: DataDefField): number | undefined {
  const w = field.widget ?? 'text';
  return w === 'text' || w === 'textarea' ? field.collapseLines : undefined;
}

/**
 * 표 칼럼(DataDefField.align) → CSS textAlign. 미지정=undefined — 스타일을 만들지 않아
 * 기존 렌더와 픽셀 동일(회귀 0, 기본값 주입 금지). 'start'도 명시 'left'로 낸다 —
 * checkbox 칼럼처럼 클래스 기본이 가운데인 셀을 왼쪽으로 되돌리는 지정이 유효해야 한다.
 */
function cellTextAlignForField(field: DataDefField): 'left' | 'center' | 'right' | undefined {
  switch (field.align) {
    case 'start':
      return 'left';
    case 'center':
      return 'center';
    case 'end':
      return 'right';
    default:
      return undefined;
  }
}

/**
 * 라벨 미표시(labelPlacement:'none') 여부 — 라벨 요소와 그 부속(병기·필수 *·보조 설명 labelHint)을
 * 통째로 미렌더한다는 뜻이다. 라벨이 없으면 labelAlign·labelStyle도 소비처가 사라진다.
 * 접근명은 controlAriaLabelFor가 컨트롤 aria-label로 보전한다(shared labelPlacement 규약).
 */
export function isLabelHidden(node: Pick<InputWidget, 'labelPlacement'>): boolean {
  return node.labelPlacement === 'none';
}

/**
 * 라벨 미표시일 때 컨트롤에 붙일 접근명(라벨 텍스트) — `<label htmlFor>`로 얻던 이름을 대신한다.
 * 그 외(top/side/미지정)엔 undefined라 aria-label 속성이 아예 출력되지 않는다(회귀 0 — 기존
 * 마크업 불변). 라벨 텍스트가 비어 있으면 붙일 이름 자체가 없으므로 undefined.
 */
export function controlAriaLabelFor(
  node: Pick<InputWidget, 'labelPlacement' | 'label'>,
): string | undefined {
  return isLabelHidden(node) && node.label ? node.label : undefined;
}

/** node별 라벨 표시(레이아웃 클래스 + 폰트 인라인 style) 산출 — 미지정=시안 .field-label 기본. */
export function labelPresentationFor(node: InputWidget): { className: string; style: CSSProperties } {
  const style = textStyleCss(node.labelStyle, COMPOSER_INPUT_LABEL_DEFAULT_STYLE);
  // side 인라인 라벨은 실제 side 레이아웃과 동일 조건일 때만(readonly·비단순 위젯은 top 고정).
  const side =
    node.labelPlacement === 'side' &&
    node.mode !== 'readonly' &&
    SIDE_LAYOUT_WIDGETS.has(node.widget);
  // side=인라인(shrink-0, 하단여백 없음) / top=block + 하단여백 + 정렬.
  const layout = side ? 'shrink-0' : 'block mb-1';
  const align = side ? '' : LABEL_ALIGN_CLASS[node.labelAlign ?? 'start'];
  return { className: [layout, align].filter(Boolean).join(' '), style };
}

/** 라벨 보조 설명(labelHint) 렌더 — 라벨 뒤 작은 회색(기본). labelHintStyle로 파트별 폰트 지정 가능. */
function labelHintEl(node: InputWidget): ReactNode {
  if (!node.labelHint) return null;
  return <span style={textStyleCss(node.labelHintStyle, COMPOSER_LABEL_HINT_DEFAULT_STYLE)}> {node.labelHint}</span>;
}

/**
 * 라벨 병기(labelSuffixFrom) 렌더 — 참조 위젯 값이 비어 있지 않은 string/number면 라벨 텍스트
 * 바로 뒤에 ` (값)` 병기, 비거나 다른 타입이면 괄호째 생략(규칙은 렌더러 소유 — shared
 * InputWidget.labelSuffixFrom 규약). 실폼·캔버스 동일 렌더(WYSIWYG — interactive 특례 없음).
 * 폰트는 라벨 본문을 그대로 상속(추가 스타일 없음). 순서: 라벨 → 병기 → required * → labelHint.
 */
function labelSuffixEl(
  node: InputWidget,
  values: Record<string, unknown> | undefined,
): ReactNode {
  if (!node.labelSuffixFrom || !values) return null;
  const v = values[node.labelSuffixFrom];
  const text =
    typeof v === 'number' && Number.isFinite(v)
      ? String(v)
      : typeof v === 'string'
        ? v.trim()
        : '';
  if (text === '') return null;
  return <> ({text})</>;
}

/** 필수 표식 `*` — 라벨·표형 열 헤더 공용 마크업 단일 정본(빨강 + 앞 공백 한 칸). */
const REQUIRED_STAR_EL = <span className="text-red-500"> *</span>;

/**
 * 라벨 요소를 렌더할지 — 내용이 전무한 라벨(빈 <label>도 mb-1을 차지해 셀/행 높이를 키움,
 * 2026-07-14)과 라벨 미표시('none' — 부속까지 함께 사라짐)를 한 판정으로. values는 라벨 병기
 * (labelSuffixFrom) 판정용(미전달=병기 축 무시).
 */
function shouldRenderLabel(node: InputWidget, values?: Record<string, unknown>): boolean {
  return (
    !isLabelHidden(node) &&
    (Boolean(node.label) ||
      node.required === true ||
      Boolean(node.labelHint) ||
      labelSuffixEl(node, values) != null)
  );
}

/**
 * 라벨 **내용** 조립 단일 정본 — `{label}{병기}{required *}{labelHint}` 순서(labelSuffixEl doc).
 * 감싸는 요소(<label>/<legend>/<div>)와 그 게이트는 호출부 소유다 — 조립 순서·표식 마크업이
 * 호출부마다 복제돼 어긋나는 것만 막는다(2026-08-24 6벌 수렴: renderWriteNodeControl 기본/radio
 * legend/checkbox + 등록형 placeholder + rowFormWidgetBridge + RiskCriteriaDetailWidget).
 * - values 미전달 = 병기(labelSuffixFrom) 미평가(그 호출부에 값 스코프가 없던 종전 동작 유지).
 * - showRequired:false = 표시 전용 위젯(RiskCriteriaDetailWidget — `*` 없음 규약)용.
 */
export function WidgetLabelContent({
  node,
  values,
  showRequired = true,
}: {
  node: InputWidget;
  values?: Record<string, unknown>;
  showRequired?: boolean;
}): ReactElement {
  return (
    <>
      {node.label}
      {labelSuffixEl(node, values)}
      {showRequired && node.required === true ? REQUIRED_STAR_EL : null}
      {labelHintEl(node)}
    </>
  );
}
// readonly 값 표시 = **민짜 텍스트**(2026-08-04 사용자 확정 — 배경 투명화 후에도 테두리가
// input처럼 보여 제거). 셀 안 readonly 민짜화와 같은 철학: readonly는 컨트롤이 아니라 값
// 표시라 박스 크롬 없이 섹션 바탕 위 텍스트로. 상하 패딩·최소 높이는 유지해 editable
// input과 혼재된 폼에서도 세로 리듬이 어긋나지 않는다. 값 색은 본문색(gray-700) —
// 박스가 사라진 민짜 텍스트에서 500은 placeholder처럼 흐릿하다.
const readonlyValueClass =
  'w-full py-2 text-sm text-gray-700 min-h-[2.375rem] whitespace-pre-wrap break-words';

/**
 * readonly 모드에서 위젯 종류별로 값을 사람이 읽을 텍스트로 변환.
 * values/resolvedRows(옵션) — select 옵션 동적 소스(optionsFrom) 라벨 해소용(미전달=정적 options만).
 * outerValues — 반복 행 스코프의 본문 값 폴백(편집 select와 같은 rows를 봐야 라벨이 같다).
 */
function readonlyDisplayValue(
  node: InputWidget,
  value: unknown,
  values?: Record<string, unknown>,
  resolvedRows?: Record<string, Record<string, unknown>[]>,
  outerValues?: Record<string, unknown>,
): string {
  if (value === undefined || value === null || value === '') return '—';
  // select/radio: 선택된 옵션의 라벨로 표시(동적 옵션 우선 — 미해소면 정적 options 폴백).
  if (node.widget === 'select' || node.widget === 'radio') {
    const options =
      resolveDynamicSelectOptions(node.optionsFrom, values, resolvedRows, outerValues) ??
      node.options;
    // 옵션 value는 String 규격 — number 값(기준표 level 등)도 String으로 라벨 매칭.
    const opt = options?.find((o) => o.value === String(value));
    if (opt) return opt.label;
  }
  if (node.widget === 'checkbox') return value ? '예' : '아니오';
  // textarea-list/text-list(문자열 배열): 항목을 줄바꿈으로 나열(행 그리드 readonlyText 동형). 빈 목록='—'.
  if (node.widget === 'textarea-list' || node.widget === 'text-list') {
    const items = normalizeList(value).filter((s) => s.trim() !== '');
    return items.length > 0 ? items.join('\n') : '—';
  }
  // date: prefill 등이 ISO datetime(2026-06-02T01:17:17.788Z)을 넣으면 그대로 표시되므로
  // 로컬 yyyy-MM-dd로 정규화(dateInputValue 재사용, 편집 date input 표시와 동일 규약). 파싱 불가면 원값.
  if (node.widget === 'date') {
    const normalized = dateInputValue(value);
    return normalized === '' ? String(value) : normalized;
  }
  // date-range: 값이 **객체**라 아래 String(value) 폴백을 타면 '[object Object]'가 샌다.
  // 표기는 한글 라벨로 — 기간은 두 날짜를 잇는 문장이라 'yyyy-MM-dd ~ yyyy-MM-dd'보다
  // 읽는 자리(작성 페이지 읽기 전용)에서 한글이 낫다는 판단(셀 raw 표기는 dateRangeRawText 별도).
  if (node.widget === 'date-range') {
    const label = dateRangeDisplayLabel(value);
    return label === '' ? '—' : label;
  }
  // 그 외 객체 값이 [object Object]로 새는 것을 막는 최후 방어 — 기간 형태면 그 표기로 접는다
  // (선언 없이 값만 기간인 경로: 표 셀·등록형 위젯이 넘긴 값 등).
  if (isDateRangeValue(value) && (value.start !== undefined || value.end !== undefined)) {
    return dateRangeDisplayLabel(value);
  }
  return String(value);
}

/**
 * 작성폼 표시 형식(InputWidget.format)의 **보조 경로 조회 스코프**를 합성한다.
 *
 * format 중 보조 경로를 읽는 kind가 있다 — rangeMap의 `rangesFrom`/`rangesKey`(운영자 기준표 배열),
 * unit의 `unitPath`, template/switch의 scope 참조. 본문 렌더는 RenderData flat 하나가 그 스코프지만,
 * 작성폼은 값 채널이 셋으로 갈라져 있어 합성이 필요하다:
 *
 *   { ...본문 값(outerValues), ...현재 스코프 값(values), ...카탈로그 rows(resolvedRows) }
 *
 * - **outerValues(본문 값)를 가장 아래** — 반복(repeat) 행 안에서는 `values`가 행 객체라 본문 키가
 *   없다. 기준표(`risk-assessment-config`)는 서버 prefill이 **본문 값**에 싣는 단수 entry이므로
 *   (report-composer.service `resolved['risk-assessment-config']`), 행 안 위젯이 그것을 읽으려면
 *   본문 값을 깔아야 한다. branch condition 스코프(`branchScope`)가 이미 쓰는 것과 **같은 규약**이고,
 *   등록형 위젯이 기준표를 찾는 경로(rowWidgetAsFormWidget의 `context.values` 폴백)와도 같은 채널이다.
 * - **현재 스코프 값이 그 위** — 행 안에서는 행 필드가 동명 본문 키를 이긴다(branchScope 동형).
 * - **resolvedRows가 맨 위** — host가 런타임 resolve한 카탈로그 rows가 최우선(resolveDynamicSelectRows
 *   규약 동형: resolvedRows 우선 → values 폴백). 키 공간이 대개 겹치지 않아 실무상 합집합이다.
 *
 * 덕분에 저작자는 본문과 **같은 형식 하나**를 두 화면에 쓸 수 있다 — 권장 축인
 * `rangesFrom: { catalogKey: 'risk-assessment-config', select: 'riskLevelCriteria' }`는 이 합성
 * 스코프에서 카탈로그 키로 조회되고(단수 entry는 배열 감싸짐도 방어), 본문 RenderData도 같은 이름의
 * 중첩 객체를 싣는다. 레거시 축인 `rangesKey`('risk-assessment-config.riskLevelCriteria') 문자열도
 * 같은 스코프에서 그대로 동작한다.
 * 기준표가 어느 채널에도 없으면 matchRangeCriterion이 미매칭을 돌려 **배지 없이 텍스트로 degrade**한다
 * (가짜 색 금지 — 그쪽 doc 규약).
 */
export function writeFormatScope(
  values?: Record<string, unknown>,
  resolvedRows?: Record<string, Record<string, unknown>[]>,
  outerValues?: Record<string, unknown>,
): Record<string, unknown> {
  return { ...outerValues, ...values, ...resolvedRows };
}

/**
 * readonly 값 표시에 저작 표시 형식(node.format)을 적용한 문자열 — format 미지정이면 undefined
 * (호출부가 위젯별 기본 표시 readonlyDisplayValue로 내려간다 = 회귀 0).
 *
 * **우선순위: 저작 format이 위젯별 기본 표시를 대체한다(겹쳐 적용하지 않는다).**
 * 근거 — 두 변환을 겹치면 결과가 kind마다 달라져 예측 불가다(date 위젯의 yyyy-MM-dd에 date
 * format을 또 먹이거나, text-list의 '\n' 결합 문자열에 join을 다시 먹이는 꼴). format은 저작자가
 * **명시 지정**한 표시 규칙이므로 그것이 곧 표시다. 라벨 해소(select optionsFrom)처럼 format으로
 * 대체하기 아까운 처리가 필요하면 format을 지정하지 않으면 된다(기본 표시가 그대로 산다).
 *
 * 빈값: format의 empty/fallback 규약을 그대로 살리되(unit.empty·default.fallback 등), 결과가 빈
 * 문자열이면 기본 표기('—')로 되돌린다.
 */
function readonlyFormattedText(
  node: InputWidget,
  value: unknown,
  scope: Record<string, unknown>,
): string | undefined {
  if (!node.format) return undefined;
  const out = applyFormat(value, node.format, scope);
  const text = out === null || out === undefined ? '' : String(out);
  return text === '' ? '—' : text;
}

const cellInputClass =
  'w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500';

// date/date-range 컨트롤과 그 값 규약은 전용 모듈이 소유한다(행 그리드와 공유 — 재구현 금지).
// 이 파일에 있던 dateInputValue/dateDisplayLabel/DATE_PLACEHOLDER_DEFAULT/DateFieldInput은
// dateValue.ts(순수) + DateFieldInput.tsx(컨트롤)로 이전했다. 컨트롤의 결(className)만 여기서 준다.

/**
 * 작성폼 table 셀의 편집 위젯을 칼럼 정의(field.widget)별로 렌더.
 * 미지정/'text'=text input(기존 동작 유지=회귀0), 'number'/'checkbox'/'select'는 해당 입력.
 */
function renderEditableCell(
  value: unknown,
  field: DataDefField,
  onCellChange: (next: unknown) => void,
  /**
   * 입력 확정(포커스 이탈) 콜백 — 읽기전용 칼럼의 "최초 1회 입력" 확정 판정 + 선택 전용 칸의
   * "고르지 않고 끝낸 추가 행 취소" 판정에 사용(옵션). blur 이벤트를 그대로 넘겨 호출부가
   * relatedTarget(같은 표의 버튼으로 넘어간 blur)을 볼 수 있게 한다.
   */
  onBlur?: (e: ReactFocusEvent<HTMLElement>) => void,
  /** enabledWhen 비활성(표 위젯 단위) — 셀 컨트롤 disabled(값 유지·표시). */
  disabled?: boolean,
  /**
   * 셀 검색 제안(DataDefField.suggestionsFrom) 접합부 — 호스트 검색 콜백이 배선됐을 때만 호출부가
   * 만들어 준다(미배선=일반 입력 degrade). 소비 위젯은 text/textarea뿐이다.
   * requireSelection이면 타이핑은 값이 되지 않는다(검색어 초안은 드롭다운이 소유).
   */
  cellSuggest?: {
    search: (query: string) => Promise<ComposerSuggestionRow[]>;
    onPick: (row: ComposerSuggestionRow) => void;
    requireSelection?: boolean;
  },
  /**
   * 저작 캔버스 렌더 — 검색이 **일부러** 꺼져 있는 상태라(샘플 값 오염 방지) 미배선 degrade 대상이
   * 아니다. 캔버스는 실폼과 같은 모양이어야 하고(WYSIWYG) 그 값은 저작 샘플이라 자유 타이핑을
   * 그대로 둔다. 실폼(web)에선 false/미전달.
   */
  authoringCanvas?: boolean,
  /**
   * photo 셀 업로드 호스트 콜백 — InputWidget 'photo'와 같은 채널(renderWriteNode uploadPhoto를
   * 표 경로가 그대로 내려준다). widget:'photo' 칼럼만 소비, 미배선이면 안내 한 줄로 degrade
   * (캔버스는 실폼과 같은 버튼 모양 유지 — inert).
   */
  uploadPhoto?: ComposerPhotoUpload,
) {
  const disabledStyle = disabled ? DISABLED_CONTROL_STYLE : undefined;
  // 칼럼 정렬(field.align) — 컨트롤은 w-full이라 td textAlign이 안 닿는다. 컨트롤 자체의
  // textAlign으로 내용을 정렬한다(미지정=스타일 자체를 안 만들어 기존과 동일 — 회귀 0).
  const cellAlign = cellTextAlignForField(field);
  const withAlign = (base?: CSSProperties): CSSProperties | undefined =>
    cellAlign ? { ...base, textAlign: cellAlign } : base;
  // 날짜 컨트롤은 표시 span + 투명 input을 겹친 **flex 래퍼**라 textAlign이 닿지 않는다 —
  // 같은 정렬을 justifyContent로 준다(아래 radio 케이스가 인라인으로 쓰는 것과 같은 매핑).
  const withJustify = (base?: CSSProperties): CSSProperties | undefined =>
    cellAlign
      ? {
          ...base,
          justifyContent:
            cellAlign === 'center' ? 'center' : cellAlign === 'right' ? 'flex-end' : 'flex-start',
        }
      : base;
  // 선택 전용 칸의 커밋 게이트는 **선언(field.suggestionsFrom.requireSelection)** 에 건다.
  // ⚠️ 드롭다운 배선(sctl) 유무에 걸면 안 된다 — 호스트가 onSuggestSearch를 안 넘긴 화면(외부 위임
  // 작성 페이지: 검색 API가 인증 뒤라 배선 자체가 불가)에서 sctl이 undefined가 되어 `!sctl?.x`가
  // true → 타이핑이 그대로 커밋된다. 그 칸은 값이 "골라진 원문"이어야 하는 것이므로(오타·창작 조문이
  // 결재 완료본=법적 증빙에 남는 것을 막는 보장) 배선 없이 자유 입력으로 열 수 없다.
  const requireSelection = isTypedCommitBlocked(field.suggestionsFrom) && !authoringCanvas;
  // 검색 미배선 + 선택 전용 = 값을 만들 방법이 없는 칸 → 읽기 전용으로 degrade(은닉 금지 — 칸은
  // 그대로 두고 값도 그대로 보이며, 왜 못 쓰는지 한 줄로 말한다).
  const suggestUnavailable = requireSelection && !cellSuggest;
  /** 제안 활성 시 컨트롤을 드롭다운으로 감싼다(text/textarea 공용 — InputWidget text 케이스 동형). */
  const withSuggest = (
    control: (sctl?: SuggestionControlBindings) => ReactNode,
  ): ReactNode =>
    cellSuggest ? (
      <SuggestionDropdown
        search={cellSuggest.search}
        onPick={cellSuggest.onPick}
        requireSelection={cellSuggest.requireSelection}
      >
        {(sctl) => control(sctl)}
      </SuggestionDropdown>
    ) : (
      control()
    );
  /** 셀 표시값 — 선택 전용 칸은 검색어 초안이 있으면 그것을(확정값은 선택 시에만 바뀐다). */
  const cellText = (sctl?: SuggestionControlBindings): string =>
    sctl?.draftValue ?? (value === undefined || value === null ? '' : String(value));
  /** 선택 전용 칸은 빈 칸에서도 "검색해 고르는 칸"임을 드러낸다(은닉 금지). */
  const suggestPlaceholder = (sctl?: SuggestionControlBindings): string | undefined =>
    sctl?.requireSelection ? SUGGEST_REQUIRE_SELECTION_PLACEHOLDER : undefined;
  /** 검색 미배선 degrade 렌더 — 값은 그대로 읽히되(readOnly) 입력은 막고, 이유를 한 줄로 붙인다. */
  const unavailableCell = (multiline: boolean): ReactNode => {
    const style = withAlign({ ...disabledStyle, ...SUGGEST_UNAVAILABLE_CONTROL_STYLE });
    return (
      <div>
        {multiline ? (
          <textarea className={cellInputClass} style={style} rows={3} readOnly value={cellText()} />
        ) : (
          <input type="text" className={cellInputClass} style={style} readOnly value={cellText()} />
        )}
        <SuggestNote text={SUGGEST_REQUIRE_SELECTION_UNAVAILABLE} />
      </div>
    );
  };
  switch (field.widget) {
    // 날짜/기간 셀 — 동적 데이터셋 행 축(renderFormTableRepeatCell)·작성폼 트리 위젯과 **같은
    // 컨트롤**을 쓴다(달력 진입점이 surface마다 달라지지 않게 — 재구현 금지). 값 채널도 같다:
    // date=yyyy-MM-dd 문자열 / date-range={start?,end?} 객체(반대쪽 보존 병합은 컨트롤 소유).
    case 'date':
      return (
        <DateFieldInput
          value={value}
          disabled={disabled}
          className={cellInputClass}
          controlStyle={withJustify(disabledStyle)}
          onChange={(next) => onCellChange(next)}
          onBlur={onBlur}
        />
      );

    case 'date-range':
      return (
        <DateRangeFieldInput
          value={value}
          disabled={disabled}
          className={cellInputClass}
          controlStyle={withJustify(disabledStyle)}
          onChange={(next) => onCellChange(next)}
          onBlur={onBlur}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          className={cellInputClass}
          style={withAlign(disabledStyle)}
          value={value === undefined || value === null || value === '' ? '' : (value as number)}
          disabled={disabled}
          onChange={(e) => onCellChange(e.target.value === '' ? undefined : Number(e.target.value))}
          onBlur={onBlur}
        />
      );

    case 'checkbox':
      return (
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={disabled}
          // 셀 경로 제외 규약(밀도·타이포)은 컨트롤 박스가 대상 — 체크박스 크기는 셀 저작
          // 속성이 없고 시안 체크리스트(.check-item input 18px)가 바로 이 표라 시안 결을 따른다.
          style={CHOICE_CONTROL_STYLE}
          onChange={(e) => onCellChange(e.target.checked)}
          onBlur={onBlur}
        />
      );

    case 'select':
      return (
        <select
          className={cellInputClass}
          style={withAlign(disabledStyle)}
          value={(value as string) ?? ''}
          disabled={disabled}
          onChange={(e) => onCellChange(e.target.value)}
          onBlur={onBlur}
        >
          <option value="">선택</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case 'radio':
      // controlled 라디오(양호/불량 등) — 셀별 name 불필요(checked를 value로 제어, 행 간 독립).
      // 정렬은 flex 컨테이너라 textAlign이 아니라 justifyContent로(미지정=스타일 없음, 기존 그대로).
      return (
        <div
          className="flex flex-wrap items-center gap-2"
          style={
            cellAlign
              ? {
                  justifyContent:
                    cellAlign === 'center'
                      ? 'center'
                      : cellAlign === 'right'
                        ? 'flex-end'
                        : 'flex-start',
                }
              : undefined
          }
        >
          {field.options?.map((opt) => (
            <label
              key={opt.value}
              className="inline-flex items-center gap-1 text-xs"
              style={disabled ? DISABLED_LABEL_STYLE : undefined}
            >
              <input
                type="radio"
                checked={value === opt.value}
                disabled={disabled}
                onChange={() => onCellChange(opt.value)}
                onBlur={onBlur}
              />
              {opt.label}
            </label>
          ))}
        </div>
      );

    case 'photo': {
      // 행별 증빙 사진 셀 — 값=URL string(빈 값 ''). 업로드 채널은 독립 배치 photo 위젯과 동일
      // (uploadPhoto 호스트 콜백). 미배선(external 공유 등)은 저장된 값(URL)이 있으면 readonly
      // 썸네일로 표시(증빙이 가려지면 안 된다 — W3), 빈 값일 때만 안내 한 줄로 degrade. 캔버스는
      // 실폼과 같은 버튼 모양을 유지한다(inert — renderWriteNode photo case와 같은 판정).
      if (!uploadPhoto && !authoringCanvas) {
        const savedUrl = typeof value === 'string' ? value.trim() : '';
        return savedUrl !== '' ? (
          photoReadonlyCellNode(value, field.label || field.key)
        ) : (
          <p className="text-xs text-gray-400">사진 업로드는 내부 작성 화면에서 할 수 있습니다.</p>
        );
      }
      return (
        <PhotoUploadWidget
          value={value}
          alt={field.label || field.key}
          onValueChange={(next) => onCellChange(next)}
          uploadPhoto={uploadPhoto}
          disabled={disabled}
          authoringCanvas={authoringCanvas}
          compact
        />
      );
    }

    case 'textarea':
      // 여러 줄 셀(법령 조문 본문 등) — 편집 진입은 곧 전문이라 접기(collapseLines)는 미적용.
      if (suggestUnavailable) return unavailableCell(true);
      return withSuggest((sctl) => (
        <textarea
          className={cellInputClass}
          style={withAlign(disabledStyle)}
          rows={3}
          aria-autocomplete={sctl ? 'list' : undefined}
          placeholder={suggestPlaceholder(sctl)}
          value={cellText(sctl)}
          disabled={disabled}
          onChange={(e) => {
            // 선택 전용 칸: 친 글자는 검색어일 뿐이라 값에 기록하지 않는다(제안 선택에서만 커밋).
            // 게이트는 선언(requireSelection)이지 sctl 유무가 아니다 — 위 상수 주석 참조.
            if (!requireSelection) onCellChange(e.target.value);
            sctl?.onQueryChange(e.target.value);
          }}
          onKeyDown={(e) => {
            sctl?.onKeyDown(e);
          }}
          onBlur={(e) => {
            sctl?.onBlur();
            onBlur?.(e);
          }}
        />
      ));

    case 'text':
    default:
      if (suggestUnavailable) return unavailableCell(false);
      return withSuggest((sctl) => (
        <input
          type="text"
          className={cellInputClass}
          style={withAlign(disabledStyle)}
          aria-autocomplete={sctl ? 'list' : undefined}
          autoComplete={sctl ? 'off' : undefined}
          placeholder={suggestPlaceholder(sctl)}
          value={cellText(sctl)}
          disabled={disabled}
          onChange={(e) => {
            // 게이트는 선언(requireSelection) — sctl 유무가 아니다(위 상수 주석 참조).
            if (!requireSelection) onCellChange(e.target.value);
            sctl?.onQueryChange(e.target.value);
          }}
          onKeyDown={(e) => {
            sctl?.onKeyDown(e);
          }}
          onBlur={(e) => {
            sctl?.onBlur();
            onBlur?.(e);
          }}
        />
      ));
  }
}

/**
 * readonly table 셀의 값 표시 — checkbox 칼럼만 ✓/—, 그 외는 문자열.
 *
 * 저장 값이 화면에서 어떤 글자가 되는지의 **정본**이다. 값을 만들어 넣는 쪽(admin 엑셀
 * 가져오기 미리보기 등)이 표시 규칙을 따로 구현하면, 여기 규칙이 바뀔 때 미리보기가 조용히
 * 거짓말을 한다. 그래서 rc 공개 API로 내보낸다.
 */
export function readonlyCellDisplay(value: unknown, field: DataDefField): string {
  if (field.widget === 'checkbox') return value ? '✓' : '—';
  // 날짜/기간은 String(value) 폴백보다 **먼저** 갈라야 한다 — 기간은 객체라 [object Object]가 새고,
  // 단일 날짜도 ISO datetime prefill이 그대로 보인다. 표기는 행 그리드·반복 셀 readonly와 같은
  // 정본(dateWidgetDisplayText — 한글 라벨)이다: 같은 값이 작성 화면에서 자리마다 다르게 보이면
  // 안 된다(양식 본문 셀의 raw "s ~ e" 표기는 바인딩 경로가 따로 소유 — dateRangeRawText).
  // 빈값('')은 이 함수의 빈값 표기 규약('—')으로 접는다.
  const dateText = dateWidgetDisplayText(field.widget, value);
  if (dateText !== undefined) return dateText === '' ? '—' : dateText;
  if (value === undefined || value === null) return '—';
  if ((field.widget === 'select' || field.widget === 'radio') && field.options) {
    const opt = field.options.find((o) => o.value === value);
    if (opt) return opt.label;
  }
  // photo 칼럼도 문자열 정본은 URL 원문(String 폴백)이다 — 문자열 소비처(admin 가져오기
  // 미리보기 등)는 URL 텍스트로 보이고, rc 표 readonly 셀은 photoReadonlyCellNode(<img>)로
  // 먼저 분기한다(표시가 텍스트가 아닌 유일한 칼럼 — 정본 함수의 반환 타입은 넓히지 않는다).
  return String(value);
}

// 읽기 전용 이미지 카드 결(READONLY_IMAGE_STYLE)은 placeholder 짝과 함께 AnnotatedImageView가
// 소유한다(위 import) — 행 그리드 사진 칸도 같은 한 벌을 쓴다.
// 사진 업로드 위젯 한 벌(PhotoUploadWidget·runPhotoUpload·썸네일 결)은 photoUploadWidget.tsx로
// 분리(2026-09-07 — 행 그리드 photo 필드 공유, RowsCollectionEditor와의 순환 회피). 기존 공개
// 이름은 아래 re-export가 유지한다.

/**
 * 읽기 전용 이미지 값 표시 — URL 있으면 카드 결 이미지(READONLY_IMAGE_STYLE), 없으면 placeholder
 * 박스. widget:'image'와 widget:'photo' readonly가 축자 복사로 갈라져 있던 것을 한 벌로 수렴(W8).
 */
function readonlyImageNode(url: string, alt: string, placeholder?: string): ReactNode {
  return url !== '' ? (
    <ImageWithFallback src={url} alt={alt} style={READONLY_IMAGE_STYLE} />
  ) : (
    <div style={IMAGE_PLACEHOLDER_STYLE}>{placeholder ?? '사진 없음'}</div>
  );
}

/**
 * readonly 표 셀의 photo 칼럼 표시 — 값(URL)이 있으면 컴팩트 썸네일 <img>(편집 셀 compact와
 * 같은 96×64 결), 없으면 '—'(다른 칼럼의 빈 값 표기와 동일).
 */
function photoReadonlyCellNode(value: unknown, alt: string): ReactNode {
  const url = typeof value === 'string' ? value.trim() : '';
  if (url === '') return '—';
  return <ImageWithFallback src={url} alt={alt} style={PHOTO_CELL_THUMB_STYLE} />;
}

/**
 * 캔버스 텍스트 위젯(input 라벨·heading 문구·group 섹션명) 더블클릭 인라인 편집 input.
 * seed 값으로 시작 + autofocus + 전체선택. Enter/blur = 커밋, Esc = 취소(원복).
 * 한글 IME 안전(조합 중 Enter는 조합 확정용이라 무시). 부모가 pointer-events-none이어도 스스로 재활성.
 * commit/cancel은 doneRef로 1회만 — Esc가 유발한 blur가 취소를 커밋으로 뒤집는 것을 차단.
 * rc 전용 Tailwind 유틸 금지 규약에 따라 스타일은 인라인 style로만 부여한다.
 */
function InlineTextEditor({
  initial,
  textStyle,
  onCommit,
  onCancel,
}: {
  initial: string;
  /** 원 텍스트 폰트 근사(급격한 layout shift 방지) — textStyleCss 산출값을 그대로 전달. */
  textStyle?: CSSProperties;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const doneRef = useRef(false);
  const finish = (commit: boolean) => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (commit) onCommit(value);
    else onCancel();
  };
  return (
    <input
      type="text"
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      // 선택/해제·드래그 등 상위 캔버스 로직과 충돌 방지(편집 input 내부 상호작용은 자체 소비).
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          // 한글 조합 확정용 Enter는 커밋으로 소비하지 않는다.
          if (e.nativeEvent.isComposing) return;
          e.preventDefault();
          e.stopPropagation();
          finish(true);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          finish(false);
        }
      }}
      onBlur={() => finish(true)}
      style={{
        // 부모 pointer-events-none(단축키 보호용) 하위여도 입력 재활성.
        pointerEvents: 'auto',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        padding: '1px 4px',
        border: '1px solid #6366f1', // brand-500
        borderRadius: 4,
        background: '#ffffff',
        outline: 'none',
        font: 'inherit',
        ...textStyle,
      }}
    />
  );
}

/** ClickToEditField가 renderControl에 넘기는 컨트롤 접합부(포커스·종료 배선). */
interface ClickToEditControlProps {
  /** mount 시 1회 포커스 + 커서 끝 이동 — 제어 컴포넌트라 매 키입력 재렌더마다 ref 콜백이 다시
   * 불려도 포커스를 강탈하지 않게 1회 게이트. */
  focusRef: (el: HTMLInputElement | HTMLTextAreaElement | null) => void;
  /** blur = 표시 상태 복귀(값은 이미 onChange로 전파 — 저장 개념 없음). */
  onBlur: () => void;
  /** Esc = 표시 상태 복귀. 값 롤백은 없다 — 폼 전체가 제출 전 draft라 구 페이지의 '취소'와 달리
   * 순수 표시 축만 닫는다(롤백을 붙이면 draft 자동 반영 채널과 이중 상태가 된다). */
  onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

/**
 * 편집 진입 'click'(InputWidget.editEntry — text/textarea 전용) 표시 래퍼: 평소 민짜 텍스트
 * (readonly 값 표시와 같은 결 + 편집 어포던스), 클릭/Enter/Space로 입력 컨트롤 전환, blur/Esc로
 * 표시 복귀. 구 SI 작성 페이지 EditableField 동형 — 빈 값은 '클릭하여 편집' 안내(이탤릭 회색),
 * hover 배경·cursor·title로 편집 가능함을 알린다. hover는 인라인 state로(rc 신규 Tailwind 유틸
 * 금지 — hover: variant 포함, purge 규약). disabled(enabledWhen 미충족)면 진입 자체를 막고
 * 민짜 표시만 유지한다(legacy disabled=cursor-default 동형).
 */
function ClickToEditField({
  displayValue,
  placeholder,
  inheritCellFont,
  disabled,
  collapseLines,
  controlBoxStyle,
  renderControl,
}: {
  displayValue: string;
  placeholder?: string;
  /** formTable 셀 안 — 표시 상태가 셀 폰트/밀도를 상속(readonly 셀 민짜와 동일 결). */
  inheritCellFont?: boolean;
  disabled?: boolean;
  /**
   * 표시↔편집 전환 시 **텍스트가 튀지 않게** 하는 박스 메트릭(clickDisplayBoxStyle 산출값).
   * 표시 상태는 원래 padding 0(셀 경로는 readonlyInheritCellFontStyle이 클래스 py-2까지 덮는다)이라,
   * 편집 컨트롤의 패딩·테두리·높이만큼 같은 텍스트가 가로·세로로 이동했다. 컨트롤과 같은 박스를
   * (테두리는 투명으로 자리만) 표시 상태에도 주어 이동을 0으로 만든다.
   */
  controlBoxStyle?: CSSProperties;
  /**
   * 긴 텍스트 접기(collapseLines) — **표시 상태에만** 적용한다. 편집으로 전환되면 컨트롤이
   * 전문을 들고 있으므로 자동 펼침과 같다(접힌 채 편집하는 상태가 존재하지 않음).
   */
  collapseLines?: number;
  renderControl: (ctl: ClickToEditControlProps) => ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const didFocusRef = useRef(false);

  if (editing) {
    return renderControl({
      focusRef: (el) => {
        if (!el || didFocusRef.current) return;
        didFocusRef.current = true;
        el.focus();
        const len = el.value.length;
        try {
          el.setSelectionRange(len, len);
        } catch {
          /* 일부 input type은 selection 미지원 — 포커스만으로 충분 */
        }
      },
      onBlur: () => setEditing(false),
      onKeyDown: (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          setEditing(false);
        }
      },
    });
  }

  const isEmpty = displayValue.trim() === '';
  const enter = () => {
    if (disabled) return;
    // ⚠️ hover를 끄고 들어간다 — 이 div는 곧 언마운트되므로 onMouseLeave가 영영 오지 않는다.
    // 그대로 두면 편집을 끝내고 표시로 돌아왔을 때 마우스가 딴 데 있어도 hover 배경이 켜진 채 남는다.
    setHover(false);
    didFocusRef.current = false;
    setEditing(true);
  };
  return (
    <div
      role="button"
      tabIndex={disabled ? undefined : 0}
      title={disabled ? undefined : '클릭하여 편집'}
      className={readonlyValueClass}
      style={{
        ...(inheritCellFont ? readonlyInheritCellFontStyle : undefined),
        // 컨트롤과 같은 박스(테두리는 투명) — 셀 상속 스타일(padding 0·border none)보다 뒤에 둬야
        // 그것을 덮는다. borderRadius도 여기서 오므로 아래 hover 분기의 4는 미지정일 때만 쓰인다.
        ...controlBoxStyle,
        ...(disabled
          ? { cursor: 'default' }
          : {
              cursor: 'pointer',
              borderRadius: controlBoxStyle?.borderRadius ?? 4,
              // hover 배경은 legacy hover:bg-surface-hover 등가 — 인라인 state 관리(위 purge 규약).
              backgroundColor: hover ? 'rgba(15,23,42,0.045)' : undefined,
              transition: 'background-color 120ms',
            }),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={enter}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          enter();
        }
      }}
    >
      {/* 고정 높이 컨트롤(input)과 세로 정렬을 맞추려면 표시 박스가 flex여야 한다 —
          내용을 전폭 블록으로 감싸 flex item 축소·정렬 규칙이 텍스트 정렬을 바꾸지 않게 한다.
          flex가 아닌 경우(textarea·셀 경로)엔 그냥 블록이라 레이아웃 변화 없음. */}
      <div style={{ width: '100%', minWidth: 0 }}>
        {isEmpty ? (
          <span className="text-gray-400 italic">{placeholder ?? '클릭하여 편집'}</span>
        ) : (
          <CollapsibleText text={displayValue} collapseLines={collapseLines} />
        )}
      </div>
    </div>
  );
}

/**
 * 편집 진입('click') **표시 상태**에 줄 박스 메트릭 — 편집 컨트롤과 같은 자리를 차지하게 한다.
 * 컨트롤 style에서 박스 축(패딩·radius·테두리·높이)만 뽑아 오되:
 *  - 테두리는 **투명**으로(두께는 그대로) — 자리만 차지하고 보이지는 않는다.
 *  - 고정 `height`는 `minHeight`로 옮기고 flex 세로 가운데 — 컨트롤(input)이 텍스트를 가운데
 *    두므로 표시도 같아야 전환 시 세로 이동이 0이 된다. 높이가 없는 컨트롤(textarea·셀 경로)은
 *    패딩만으로 위에서부터 흐르므로 flex를 걸지 않는다.
 */
function clickDisplayBoxStyle(control: CSSProperties): CSSProperties {
  const box: CSSProperties = {
    boxSizing: 'border-box',
    padding: control.padding,
    borderRadius: control.borderRadius,
  };
  if (control.border !== undefined) {
    box.border = control.border;
    box.borderColor = 'transparent';
  }
  if (typeof control.height === 'number') {
    box.minHeight = control.height;
    box.display = 'flex';
    box.alignItems = 'center';
  } else if (control.minHeight !== undefined) {
    box.minHeight = control.minHeight;
  }
  return box;
}

// 사진 업로드 한 벌은 photoUploadWidget.tsx가 정본(2026-09-07 분리) — 기존 import 경로 호환 re-export.
export {
  PHOTO_UPLOAD_ERROR_TEXT,
  runPhotoUpload,
  takeSelectedFile,
} from './photoUploadWidget';
export type { ComposerPhotoUpload } from './photoUploadWidget';

/**
 * 캔버스 더블클릭 인라인 편집 대상 위젯인지 — heading(문구)/group(섹션명)/input(라벨).
 * registered·dataSourceRows도 placeholder 라벨 자리에 인라인 편집 지원(labelSlot 스왑).
 */
function isTextEditableWidget(w: ComposerWidget): boolean {
  if (w.kind === 'heading') return true;
  if (w.kind === 'group') return true;
  if (w.kind === 'input') return true;
  return false;
}

/**
 * 행 인스턴스 스코프 DOM id 접미(domIdSuffix) 채널 — 같은 위젯 서브트리가 **여러 번** 렌더되는
 * 중첩 인스턴스(반복 행·그 안의 모달)에서 `id`/`htmlFor`/(radio) `name`이 충돌하는 것을 막는다.
 *
 * 왜 필요한가: `wf-{node.id}`는 위젯 축 고정이라 repeat가 행마다 같은 위젯을 렌더하면 문서에
 * 같은 id가 N개 생긴다 → 어느 행의 `<label htmlFor>`를 눌러도 브라우저가 **문서 첫 번째** input을
 * 활성화한다(2026-08-24 제보: 표형 점검 표 '사진 제외' 체크박스가 어느 행을 눌러도 1행만 토글).
 * radio는 같은 `name`이 행 간 **단일 선택 그룹**을 만들어 키보드 이동·그룹 시맨틱이 통째로 깨진다.
 *
 * 규약: 최상위(비행) 렌더는 suffix 없음 = `wf-{id}` **그대로**(기존 소비처 회귀 0 — web
 * new-composed/external의 missing 포커스 `getElementById('wf-'+id)`, admin SimulationPage).
 *
 * ⚠️ **이 구분자는 「nodeId·bindingKey에 `--`가 없다」에 의존한다** — `findWriteFieldElement`가
 * 행 인스턴스를 **접두 일치**(`[id^="wf-{id}--"]`)로 찾기 때문이다. 저작 id가 `items--extra`면
 * `focusMissingRequiredField('items')`가 **다른 위젯**을 잡는다. 그것을 지키는 곳을 전수로 적는다
 * (2026-10-05 조사 — 묻기 전엔 「아무도 안 지킨다」로 보였고 실제 답은 「한 입구는 구조가 지키고
 * 둘은 안 지킨다」였다):
 *   ① **admin 에디터 신설 위젯 — 구조적으로 안전.** id가 전부 `crypto.randomUUID()`다
 *      (`EditorPage.tsx` 20여 자리 · 복제는 `nodeClipboard.ts` `newWidgetId`). UUID는 8-4-4-4-12
 *      hex라 **연속 하이픈이 나올 수 없고**, 저작자가 id를 직접 타이핑하는 입력도 없다.
 *   ② **번들 템플릿 파일의 손작성 id**(`tml-wf-…`·`si-wf-…`·`ra-…`) — 사람이 쓰므로 `--`가 가능하다.
 *      2026-10-05 실측 **0건**이고, 그 0을 지키는 검사를 `seed-report-templates.spec.ts`
 *      (번들 4개 템플릿을 모두 들고 있는 유일한 자리 중 하나)에 두었다.
 *   ③ **파생 노드 id `wf-${binding.bindingKey}`**(`writeForm.ts` scaffoldFromBinding) —
 *      **지키는 곳이 없다.** bindingKey가 `--`를 가지면 그대로 전파된다. 오늘 도달 불가에 가까운
 *      이유는 bindingKey가 카탈로그 키·양식 저작 문자열이고 실데이터에 그 형태가 없기 때문이지
 *      막는 코드가 있어서가 **아니다**. 막으려면 ②와 같은 축을 bindingKey 수집 지점에 두면 된다.
 * ⇒ 구분자를 바꾸거나 접두 일치를 손볼 때 ①만 보고 「안전하다」고 읽지 말 것.
 */
const DOM_ID_SUFFIX_SEP = '--';

/** `wf-{id}`(최상위) / `wf-{id}--{suffix}`(행 인스턴스) — 위젯 DOM id 합성 단일 지점. */
export function writeFieldDomId(nodeId: string, domIdSuffix?: string): string {
  return domIdSuffix ? `wf-${nodeId}${DOM_ID_SUFFIX_SEP}${domIdSuffix}` : `wf-${nodeId}`;
}

/**
 * 행 하나의 suffix 조각 — rowId(안정 축, 삭제/재정렬 불변) 우선, 없으면 index 폴백
 * (`rowCollapseKey`와 같은 원천이라 접힘·포커스 축과 어긋나지 않는다).
 * DOM id에 안전한 문자만 남기고, 정규화가 문자를 **바꿨다면** index를 덧붙여 인스턴스 안 유일성을
 * 회복한다(서로 다른 rowId가 같은 조각으로 뭉개지는 것 방지 — UUID rowId는 이 경로를 안 탄다).
 */
export function rowDomIdSegment(rowKey: string, rowIdx: number): string {
  const safe = rowKey.replace(/[^A-Za-z0-9_-]/g, '_');
  return safe === rowKey ? safe : `${safe}_${rowIdx}`;
}

/** 중첩 repeat 누적 — 바깥 행 suffix 뒤에 이 행 조각을 잇는다(`r1--r2`). */
export function appendRowDomIdSuffix(
  parentSuffix: string | undefined,
  rowKey: string,
  rowIdx: number,
): string {
  const seg = rowDomIdSegment(rowKey, rowIdx);
  return parentSuffix ? `${parentSuffix}${DOM_ID_SUFFIX_SEP}${seg}` : seg;
}

/**
 * 위젯 **래퍼** 앵커 속성 이름 — required 미입력 「그 칸으로 데려가기」의 2차 채널(2026-09-29).
 *
 * 왜 `id`가 아닌 별도 속성인가: `id={inputId}`를 쥐는 것은 19갈래 중 9종(text·number·date·
 * date-range·time·textarea·select·checkbox·combobox)뿐이고, 나머지는 컨트롤이 하나로 특정되지 않아
 * (radio=옵션 N개, table=셀 M×N, text/textarea-list=항목 N개) 앵커를 얹을 **단일 컨트롤이 없다**.
 * 그 래퍼(div/fieldset)에 `id`를 또 얹으면 이미 컨트롤이 쥔 종류와 **중복 id**가 생겨
 * `getElementById`가 엉뚱한 것을 잡고(행 스코프 유일화 축 — `rowInstanceDomId.test.tsx`) 어떤 종류가
 * 이미 쥐고 있는지를 **목록으로 판정**해야 하는데 그 손나열은 새 위젯마다 낡는다. 그래서 래퍼에는
 * 중복이 무해한 data 속성을 **무조건** 얹고, 조회 쪽(findWriteFieldElement)이 순서를 정한다.
 *
 * ⚠️ 이미 있는 `data-composer-widget-id`(:8700 부근)를 재사용하지 **않는다** — 그것은 **interactive
 * (저작 캔버스) 래퍼 전용**이고 실폼 렌더는 그 앞에서 조기 반환하므로 host 3곳(web new-composed·
 * external·admin Simulation)이 보는 DOM에는 아예 없다. 「이미 앵커가 있다」로 읽지 말 것.
 *
 * 값은 `writeFieldDomId`와 **같은 문자열**(`wf-{id}` / `wf-{id}--{행}`)이다 — 어휘를 하나로 두어
 * id 조회와 접두 폴백 규약(행 인스턴스)이 그대로 평행 이동한다.
 */
export const WRITE_FIELD_ANCHOR_ATTR = 'data-write-field';

/** 래퍼 앵커 속성 한 벌 — 자기 DOM 루트를 소유한 위젯 컴포넌트가 루트에 스프레드한다. */
export function writeFieldAnchorProps(
  nodeId: string,
  domIdSuffix?: string,
): Record<string, string> {
  return { [WRITE_FIELD_ANCHOR_ATTR]: writeFieldDomId(nodeId, domIdSuffix) };
}

/**
 * `el.focus()`가 실제로 동작하는 요소 — 래퍼 앵커에서 **하강**할 대상 선택자.
 * disabled 컨트롤은 포커스가 no-op이라 제외한다(제외하지 않으면 「포커스했다」고 믿는 자리로
 * 조용히 빠진다). `a`는 href 없으면 포커스 대상이 아니다.
 */
const WRITE_FIELD_FOCUSABLE_SELECTOR =
  'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href]';

/** 앵커 요소 자신이 포커스 대상인가 — 태그 축(census FOCUSABLE_TAGS와 같은 집합). */
function isWriteFieldFocusable(el: HTMLElement): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag === 'a') return el.hasAttribute('href');
  if (!['input', 'select', 'textarea', 'button'].includes(tag)) return false;
  return !el.hasAttribute('disabled');
}

/**
 * host의 "그 위젯의 입력 요소 찾기" 단일 진입점 — required 미입력 스크롤/포커스가 쓴다.
 *
 * ⚠️ `collectMissingRequired`는 **반복 행 스코프로 하강**하면서도 `id`는 위젯 id(행 무관)를
 * 돌려준다. 접미 도입 전에는 `getElementById('wf-'+id)`가 (중복 id 중) 첫 행 input을 잡아 우연히
 * 동작했으므로, 접미 도입 후에도 같은 대상을 잡도록 **접두 매칭 폴백**을 둔다(행 특정은 검증
 * 반환 계약에 행 경로가 없어 불가 — 라벨의 "N번째 행 · " 안내가 그 몫을 한다).
 *
 * 탐색 순서 넷(앞이 이기므로 컨트롤 앵커를 쥔 9종은 1단계에서 끝나 **회귀 0**):
 *   ① `id="wf-x"`(컨트롤 직접 앵커, 최상위) → ② `[id^="wf-x--"]`(그 행 인스턴스)
 *   → ③ `[data-write-field="wf-x"]`(래퍼 앵커) → ④ `[data-write-field^="wf-x--"]`(그 행 래퍼)
 * ③④는 컨트롤이 하나로 특정되지 않는 종류(radio·table·목록형·미디어)의 래퍼를 잡는다 —
 * 스크롤은 래퍼로 충분하고 포커스는 `focusMissingRequiredField`가 그 안 첫 컨트롤로 하강한다.
 */
export function findWriteFieldElement(nodeId: string, doc?: Document): HTMLElement | null {
  const d = doc ?? (typeof document === 'undefined' ? undefined : document);
  if (!d) return null;
  const exact = d.getElementById(writeFieldDomId(nodeId));
  if (exact) return exact;
  // 속성 선택자 값 안의 `"`/`\`만 이스케이프(위젯 id는 템플릿 저작 문자열이라 방어적으로).
  const esc = (s: string): string => s.replace(/["\\]/g, '\\$&');
  const domId = esc(writeFieldDomId(nodeId));
  const prefix = esc(`wf-${nodeId}${DOM_ID_SUFFIX_SEP}`);
  try {
    return (
      d.querySelector<HTMLElement>(`[id^="${prefix}"]`) ??
      d.querySelector<HTMLElement>(`[${WRITE_FIELD_ANCHOR_ATTR}="${domId}"]`) ??
      d.querySelector<HTMLElement>(`[${WRITE_FIELD_ANCHOR_ATTR}^="${prefix}"]`)
    );
  } catch {
    // 저작 문자열 id가 selector 파싱을 깨도(이론상 잔여 케이스) 포커스 안내는 토스트로 degrade.
    return null;
  }
}

/**
 * required 미입력 안내 토스트 문구 — host 3곳(web new-composed·external·admin Simulation)이
 * 같은 문구를 복제하던 것을 단일 정본으로(2026-08-24). 토스트 **호출**은 host 소유(각자 토스트
 * 시스템) — 여기선 문구만 만든다. missing은 collectMissingRequired 반환(첫 항목 기준 + 잔여 건수).
 *
 * **문구는 `requirement` 축으로 갈린다**(2026-09-29): 값 칸은 「입력해주세요」, required repeat
 * 컨테이너(행 0개)는 **「행을 추가해주세요」**. 전자를 컨테이너에 쓰면 채울 칸이 없는 상태를
 * 가리켜 사용자를 막다른 곳으로 보낸다 — 그 칸의 할 일은 「+ 행 추가」이고
 * `focusMissingRequiredField`도 그 버튼으로 하강한다(두 안내가 같은 것을 가리켜야 한다).
 * 문자열은 이 표시 층이 소유하고 값 층(shared `MissingRequiredField`)은 상태만 싣는다 —
 * 축이 늘면 아래 switch의 never 가드에서 tsc가 막는다(새 축이 값 문구로 조용히 떨어지지 않게).
 *
 * `requirement`를 **옵셔널로** 받는다 — 이 문구를 서버 400 응답(`details.missingRequired`)으로도
 * 만들고, 배포 창에서 구 서버는 그 축을 안 싣는다(그때는 값 문구로 degrade). 축을 «싣는» 쪽의
 * 강제는 `MissingRequiredField.requirement`가 required인 것으로 이미 서 있다.
 */
export function missingRequiredToastMessage(
  missing: readonly (Pick<MissingRequiredField, 'label'> &
    Partial<Pick<MissingRequiredField, 'requirement'>>)[],
): string {
  const rest = missing.length > 1 ? ` (외 ${missing.length - 1}건)` : '';
  const label = `'${missing[0]?.label ?? ''}'`;
  // 빈 배열 호출(host 게이트는 length>0에서만 부른다)은 종전 문구 유지 — 값 축 기본.
  const requirement: MissingRequirement = missing[0]?.requirement ?? 'value';
  switch (requirement) {
    case 'row':
      return `${label}에 행을 추가해주세요.${rest}`;
    case 'value':
      // 조사는 라벨 끝 글자의 읽는 소리로 고른다(따옴표는 건너뛴다 · 숫자·영문도 읽는다) — 기호로 끝나면 shared 헬퍼가 병기를 남긴다
      return `${withObjectParticle(label)} 입력해주세요.${rest}`;
    default: {
      const exhaustive: never = requirement;
      return exhaustive;
    }
  }
}

/**
 * required 미입력 항목으로 스크롤+포커스 — findWriteFieldElement(접두 매칭·래퍼 앵커 폴백 포함)로
 * 그 위젯을 찾아 화면 중앙으로 스크롤하고 포커스한다(preventScroll — 스크롤은 위에서 이미).
 * 반복 행 안 위젯은 행마다 `wf-{id}--{행}`이라 접두 매칭이 첫 행 인스턴스를 잡는다(행 특정은 검증
 * 반환 계약에 행 경로가 없어 불가 — 라벨의 "N번째 행 · " 안내가 그 몫).
 *
 * **축이 둘이다**(2026-09-29 — census `writeFieldAnchorCensus.test.tsx`가 갈라 잰다):
 *   ① 스크롤 도달 — 래퍼(div/fieldset)에 앵커가 있어도 `scrollIntoView`는 동작한다.
 *   ② 포커스 도달 — `fieldset`·`div`는 **포커스 대상이 아니다**. 그래서 앵커 요소가 포커스 불가면
 *      그 안 **첫 컨트롤로 하강**한다(radio → 첫 옵션 input, 목록형 → "+ 항목 추가" button,
 *      table → 첫 셀 컨트롤). 하강 대상이 없으면(image·signature 등 표시 전용) 스크롤까지가 전부다 —
 *      그 종류는 채울 컨트롤 자체가 없으므로 이것이 도달 가능한 최선이다.
 * 요소를 아예 못 찾으면 no-op(host는 토스트만으로 안내) — 그 침묵이 census가 재는 결함이었다.
 */
export function focusMissingRequiredField(nodeId: string, doc?: Document): void {
  const el = findWriteFieldElement(nodeId, doc);
  if (!el) return;
  if (typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  const target = isWriteFieldFocusable(el)
    ? el
    : (el.querySelector<HTMLElement>(WRITE_FIELD_FOCUSABLE_SELECTOR) ?? null);
  if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
}

/**
 * 단일 InputWidget을 widget/mode별로 렌더(값은 value 인자). aiSelect 삽입 전 순수 노드 렌더.
 * hidden 노드는 null 반환(입력 UI 미노출).
 * labelSlot(옵션): 캔버스 라벨 인라인 편집 시 라벨 자리에 스왑할 요소(미지정=평소 라벨, web 실폼 회귀0).
 *
 * ⚠️ 외부 진입점은 `renderWriteNodeCore`(래퍼 앵커 부착) → `renderWriteNode`(required `*` 배지)다 —
 * 라벨 미표시·배지·앵커를 위젯 종류 공통으로 한 지점에서 얹기 위해 본체를 여기로 분리했다
 * (위젯별 복붙 금지). 이 함수 자신은 switch 19갈래라 **반환 지점이 여럿**이므로 공통 처리를 둘 수 없다.
 */
function renderWriteNodeControl(
  node: InputWidget,
  value: unknown,
  onChange: (bindingKey: string, value: unknown) => void,
  labelSlot?: ReactNode,
  /** formTable 셀 안 렌더 — 컨트롤/readonly 값 표시가 셀 폰트(td)를 상속(기본 false = 기존 시각 유지). */
  inheritCellFont?: boolean,
  /**
   * 내장 데이터셋 선언(template.dataDefs) — widget:'table' 구분 그룹의 그룹 틀을 기본 행에서
   * **자동 파생**하는 데 사용(행이 전부 삭제돼도 그룹 유지 — 운영자 수동 목록 불요, 2026-07-15).
   * 미전달이면 groupValues(수동 지정) 또는 현재 행에서만 파생(기존 동작).
   */
  dataDefs?: DataDef[],
  /**
   * 현재 스코프 전체 값 맵 — sortableSelect가 optionsSource(다른 키)의 후보 rows를 resolve하는 데 사용.
   * 미전달이면 빈 맵으로 취급(다른 위젯은 value 인자만 쓰므로 무영향).
   */
  values?: Record<string, unknown>,
  /**
   * host가 런타임 resolve한 후보 rows(소스 키별) — sortableSelect rows resolve 최우선(P3).
   * 미전달이면 values/dataDefs fallback(기존 동작).
   */
  resolvedRows?: Record<string, Record<string, unknown>[]>,
  /**
   * enabledWhen 미충족 비활성 — 호출부(renderInner)가 isWidgetDisabledByCondition으로 판정해 전달
   * (위젯 base 속성이라 이 렌더 함수는 조건을 직접 읽지 않는다). true면 컨트롤 disabled(값은 유지·표시).
   */
  disabled?: boolean,
  /**
   * combobox 액션 항목(actionItem) 클릭 → 같은 트리 스코프 modalTrigger 모달 열기 요청
   * (cross-widget 채널 — ComposerWidgetTreeView가 주입). 미전달(단일 위젯 직접 렌더 — rc 유닛
   * spec)이면 액션 클릭 no-op — **파생 경로는 actionItem을 채우지 않으므로** 실질 무영향
   * (2026-09-30 S2 이후 타입은 두 출처가 같지만 «값을 싣는 것»은 트리 저작뿐이다. S3-b로 파생 노드도
   * 트리 경로를 타므로 실폼에서는 이 인자가 언제나 주입된다).
   */
  onOpenModalRequest?: (widgetId: string) => void,
  /**
   * 범용 검색 제안 축(suggestionsFrom) 호스트 콜백 — text·textarea-list/text-list만 소비(그 외
   * widget은 선언이 있어도 무시). suggestionsFrom은 **트리 저작만 채우는 축**이다(writes 규약 동형) —
   * 2026-09-30(S2)로 두 경로의 타입이 `InputWidget` 하나가 됐지만 `deriveWriteForm`은 이 축을
   * 채우지 않으므로 파생 경로엔 여전히 값이 없다(타입이 같아진 것과 값이 실리는 것은 다르다).
   * 미전달(캔버스 저작·미배선 호스트·파생 경로)이면 제안 없이 일반 입력으로 degrade.
   * 결정: 2026-08-05-composer-suggestion-source-axis.
   */
  suggest?: {
    onSearch?: ComposerSuggestSearch;
    onSelect?: ComposerSuggestSelect;
    /**
     * 저작 캔버스(interactive) 렌더임 — 검색은 캔버스 규약상 **일부러** 비활성이다(저작 중 서버
     * 검색·writes 전파로 샘플 값이 오염되는 것 방지). 그래서 이 경우의 미배선은 "호스트가 못 넘긴
     * 것"이 아니라 정상 상태이므로, 선택 전용 칸을 읽기 전용으로 degrade하지 **않는다** —
     * 캔버스가 실폼과 다른 모양이 되면 WYSIWYG가 깨진다. 실폼(web)에선 미전달.
     */
    authoringCanvas?: boolean;
  },
  /**
   * 본문(최상위) 스코프 값 — 반복(repeat) 행 서브트리 렌더 시 호출부가 얹는다. `values`가 행 객체로
   * 좁혀진 스코프에서 표시 형식(format)의 보조 경로(기준표 rangesKey 등)가 본문 키를 읽게 하는
   * 폴백 채널이다(branch condition의 branchScope·등록형 위젯 context.values와 같은 규약).
   * 최상위 렌더에선 미전달(values가 곧 본문) — 회귀 0.
   */
  outerValues?: Record<string, unknown>,
  /**
   * photo 위젯 업로드 호스트 콜백 — 파일 업로드 후 저장할 URL 반환. 미전달(external 공유·flat
   * 레거시·미배선 호스트)이면 photo는 안내 placeholder로 degrade(캔버스는 suggest.authoringCanvas
   * 표식으로 실폼과 같은 버튼 모양을 유지 — inert). 다른 위젯은 미소비.
   */
  uploadPhoto?: ComposerPhotoUpload,
  /**
   * 행 인스턴스 스코프 DOM id 접미 — 반복(repeat) 행·중첩 인스턴스에서 호출부가 내린다
   * (writeFieldDomId doc 참조). 미전달(최상위·flat 레거시)이면 `wf-{id}` 그대로.
   */
  domIdSuffix?: string,
  /**
   * 서명 슬롯 resolved 표시값+상호작용(P2) — 호출부(트리 경로)가 signatureSlotViewProps로
   * 해소해 내린다(값 채널은 content가 아니라 host 렌더 맵 — §3-2). 미전달(flat 레거시·
   * 반복 밖·미배선 host)이면 표시 전용 빈 칸(종전 동작).
   */
  signatureSlot?: Pick<
    SignatureSlotViewProps,
    'value' | 'signerName' | 'onRequestSign' | 'onRequestDelete'
  >,
) {
  // hidden: 작성 페이지에 입력 UI를 노출하지 않음(값은 compose 시 자동 박제).
  if (node.mode === 'hidden') return null;

  // 컨트롤 인라인 style 합성 — 시안 모양(visual) → 셀 상속/정렬 → 비활성 시각 순으로 덮는다.
  // formTable 셀 경로(inheritCellFont)에는 시안 모양을 얹지 않는다: 셀은 별도 밀도 규약
  // (font:inherit·radius 4·패딩 6/8)이라 높이 42가 들어가면 표 행 높이가 통째로 밀린다.
  const composeControlStyle = (visual: CSSProperties): CSSProperties => ({
    ...(inheritCellFont ? inputInheritCellFontStyle : { ...visual, ...inputInheritAlignStyle }),
    ...(disabled ? DISABLED_CONTROL_STYLE : {}),
  });
  const controlStyle: CSSProperties = composeControlStyle(CONTROL_VISUAL_STYLE);
  const textareaControlStyle: CSSProperties = composeControlStyle(TEXTAREA_VISUAL_STYLE);

  // 행 인스턴스마다 유일 — 최상위(suffix 없음)는 `wf-{id}` 그대로(회귀 0).
  const inputId = writeFieldDomId(node.id, domIdSuffix);
  const required = node.required;
  // 라벨 미표시(labelPlacement:'none') — 라벨 요소와 부속(병기·필수 *·보조 설명)을 통째로
  // 미렌더하고, `<label htmlFor>`로 얻던 접근명은 컨트롤 aria-label로 보전한다.
  // labelSlot(캔버스 라벨 인라인 편집)은 예외로 그대로 살린다 — 저작자가 지금 그 라벨 텍스트를
  // 고치는 중이라 에디터가 보여야 한다(편집이 끝나면 다시 미표시로 돌아간다).
  const labelHidden = isLabelHidden(node);
  const controlAriaLabel = controlAriaLabelFor(node);

        const labelPres = labelPresentationFor(node);
        // 내용이 전무한 라벨(표 셀 위젯의 라벨 억제 등)은 요소째 미렌더 — 빈 <label>도
        // mb-1(4px)을 차지해 셀/행 높이를 키움(2026-07-14 구 SMS 행높이 대조에서 발견).
        // 라벨 미표시('none')는 부속까지 함께 사라진다 — 병기·필수 *·보조 설명은 라벨 텍스트에
        // 붙는 장식이라 라벨이 없으면 붙을 자리가 없다(required 검증 자체는 불변).
        // 내용 조립(라벨→병기→*→hint)은 WidgetLabelContent 단일 정본.
        const labelEl =
          labelSlot ??
          (shouldRenderLabel(node, values) ? (
            <label htmlFor={inputId} className={labelPres.className} style={labelPres.style}>
              <WidgetLabelContent node={node} values={values} />
            </label>
          ) : null);

        // side 배치: 단순 컨트롤(text/number/date/time/select/textarea)만 라벨+컨트롤 나란히.
        const isSideLayout =
          node.mode !== 'readonly' &&
          node.labelPlacement === 'side' &&
          SIDE_LAYOUT_WIDGETS.has(node.widget);
        const fieldWrapClass = isSideLayout ? 'flex items-center gap-2' : undefined;

        // readonly: 편집 위젯 대신 값만 비활성 표시(편집 불가).
        // 예외 위젯(자기 case 렌더로 내려감)은 READONLY_SELF_RENDER_WIDGETS 정의 참조.
        if (
          node.mode === 'readonly' &&
          !READONLY_SELF_RENDER_WIDGETS.has(node.widget)
        ) {
          // 공통 readonly 텍스트 표현(text/number/date/time/textarea/select/radio/checkbox 등).
          // 빈 값 + placeholder 저작 시 '—' 대신 안내 문구를 회색으로 — 자동 반영 칸의 빈 상태
          // 안내(구 SMS "기본 정보에서 사업장 담당자를 선택하세요" 동형, 2026-07-18).
          const isEmptyReadonly = value === undefined || value === null || value === '';
          // 표시 형식(node.format) — **읽기 표시 전용 축**이라 이 분기에서만 소비한다(편집 컨트롤의
          // value에는 절대 적용하지 않는다: 포맷된 문자열이 편집값으로 새면 저장이 오염된다.
          // shared InputWidget.format 규약). scope 합성 근거는 writeFormatScope doc.
          const formatScope = writeFormatScope(values, resolvedRows, outerValues);
          const readonlyText =
            readonlyFormattedText(node, value, formatScope) ??
            readonlyDisplayValue(node, value, values, resolvedRows, outerValues);
          // rangeMap 표시(display:'badge' 알약 + annotationKey 병기) — 본문 렌더와 **같은 모듈**로
          // 그린다. 배지 미매칭(비숫자·구간 밖·기준표 미주입)이면 텍스트로 degrade하되 병기는 남고
          // (가짜 배지·가짜 색 금지), 둘 다 없으면 undefined라 종전 경로 그대로다.
          // 배지는 한 줄 알약이라 접기(collapseLines) 대상이 아니다 — 감싼 뒤 그대로 배치한다.
          // 반면 **문자열로 돌아온 경우**(배지 없이 병기만 붙은 표시)는 여전히 긴 텍스트일 수 있어
          // 접기 경로를 그대로 태운다 — 병기가 붙었다고 접기가 꺼지면 그게 회귀다.
          const rangeNode = rangeDisplayNode(value, node.format, formatScope, readonlyText);
          const rangeBadgeEl = typeof rangeNode === 'string' ? undefined : rangeNode;
          return (
            <div key={node.id}>
              {labelEl}
              <div
                id={inputId}
                className={readonlyValueClass}
                style={inheritCellFont ? readonlyInheritCellFontStyle : undefined}
                aria-readonly="true"
                aria-label={controlAriaLabel}
              >
                {isEmptyReadonly && node.placeholder ? (
                  <span className="text-gray-400">{node.placeholder}</span>
                ) : (
                  rangeBadgeEl ?? (
                    // 긴 값 접기(collapseLines) — 대상 위젯·초과 길이일 때만 클램프+토글, 아니면
                    // 텍스트 그대로(회귀 0). format 적용 결과 문자열에 접기가 걸린다(순서: format → 접기).
                    // 목록형 readonly도 여기로 내려와 블록 단위로 접힌다.
                    <CollapsibleText
                      text={typeof rangeNode === 'string' ? rangeNode : readonlyText}
                      collapseLines={collapseLinesFor(node)}
                      // 읽기 전용 표시 — 클릭이 경쟁할 다른 뜻이 없으므로 제목 줄도 토글 손잡이.
                      toggleOnTextClick
                    />
                  )
                )}
              </div>
            </div>
          );
        }

        switch (node.widget) {
          case 'text': {
            // inputFormat — phone/business-number는 숫자 외 제거 + 자동 하이픈(진행형 포맷터,
            // 끝 입력 기준 value 교체 — 중간 편집 커서 점프는 감수), email은 필터 없이 inputMode만
            // (자유 문자라 필터 불가 — type은 text 유지, 값 규약 불변). maxLength: phone 13 =
            // '010-1234-5678' 최장 표시형(02-XXXX-XXXX는 12) / business-number 12 = '123-45-67890'.
            // 완결 형식 게이트는 submitAction 모달(collectInvalidInputFormat)만.
            const isPhone = node.inputFormat === 'phone';
            const isBizNo = node.inputFormat === 'business-number';
            const isEmail = node.inputFormat === 'email';
            const formatInput = isPhone
              ? formatPhoneInput
              : isBizNo
                ? formatBusinessNumberInput
                : undefined;
            // 범용 검색 제안 축(suggestionsFrom) — InputWidget 축이라 트리 경로 캐스트에서만 실려
            // 온다(writes 규약 동형). 호스트 onSearch 미배선/캔버스(suggest 미전달)면 일반 input
            // degrade. filter는 현재 값 스코프(반복 행 안=행 객체)에서 검색 시점 재조립.
            const sfText = (node as Partial<Pick<InputWidget, 'suggestionsFrom'>>).suggestionsFrom;
            const onTextSuggestSearch = suggest?.onSearch;
            const textSuggestSearch =
              sfText && onTextSuggestSearch
                ? (q: string) =>
                    onTextSuggestSearch(
                      sfText.catalogKey,
                      q,
                      buildSuggestFilter(sfText.filterByRowField, values ?? {}),
                    )
                : undefined;
            // 선택 전용 칸의 커밋 게이트는 **선언**에 건다(sctl 유무가 아니라) — 호스트가
            // onSuggestSearch를 안 넘긴 화면(외부 위임 작성: 검색 API가 인증 뒤)에서 sctl이
            // undefined가 되어 타이핑이 그대로 커밋되는 것을 막는다. 배선이 없으면 값을 만들 방법이
            // 없으므로 읽기 전용 degrade(은닉 금지 — 칸·값은 그대로 두고 이유를 한 줄로 말한다).
            const textRequireSelection =
              isTypedCommitBlocked(sfText) && suggest?.authoringCanvas !== true;
            const textSuggestUnavailable = textRequireSelection && !textSuggestSearch;
            const textControl = (
              ctl?: ClickToEditControlProps,
              sctl?: SuggestionControlBindings,
            ) => (
              <input
                id={inputId}
                ref={ctl?.focusRef}
                type="text"
                aria-label={controlAriaLabel}
                inputMode={isPhone ? 'tel' : isBizNo ? 'numeric' : isEmail ? 'email' : undefined}
                maxLength={isPhone ? 13 : isBizNo ? 12 : undefined}
                aria-autocomplete={sctl ? 'list' : undefined}
                autoComplete={sctl ? 'off' : undefined}
                className={inputClass}
                style={controlStyle}
                // 선택 전용 칸은 검색어 초안을 보여준다(확정값은 제안 선택에서만 바뀐다).
                value={sctl?.draftValue ?? ((value as string) ?? '')}
                placeholder={
                  node.placeholder ??
                  (sctl?.requireSelection ? SUGGEST_REQUIRE_SELECTION_PLACEHOLDER : undefined)
                }
                required={required}
                disabled={disabled}
                onBlur={() => {
                  sctl?.onBlur();
                  ctl?.onBlur();
                }}
                onKeyDown={(e) => {
                  // 제안 드롭다운 선처리(↑↓/Enter/Esc 소비) — 소비 시 편집 진입 축 Esc(표시 복귀)
                  // 미도달: 드롭다운 열림 중 Esc는 "제안 닫기"다.
                  if (sctl?.onKeyDown(e)) return;
                  ctl?.onKeyDown(e);
                }}
                onChange={(e) => {
                  const next = formatInput ? formatInput(e.target.value) : e.target.value;
                  // 선택 전용 칸: 친 글자는 검색어일 뿐이라 값에 기록하지 않는다(게이트=선언).
                  if (!textRequireSelection) onChange(node.bindingKey, next);
                  sctl?.onQueryChange(next);
                }}
              />
            );
            // 제안 활성 시 컨트롤을 드롭다운(render-prop)으로 감싼다 — 값 확정은 여기서(bindingKey에
            // 제안 text 기록), writes 전파·선택 통지는 공용 헬퍼. editEntry:'click'과 결합 시
            // 편집 상태에서만 컨트롤이 존재하므로 제안도 편집 중에만 활성(자연 성립).
            const textControlWithSuggest = (ctl?: ClickToEditControlProps) =>
              sfText && textSuggestSearch ? (
                <SuggestionDropdown
                  search={textSuggestSearch}
                  requireSelection={sfText.requireSelection}
                  onPick={(row) => {
                    onChange(node.bindingKey, row.text);
                    applySuggestionPick(sfText, row, values ?? {}, onChange, suggest?.onSelect);
                  }}
                >
                  {(sctl) => textControl(ctl, sctl)}
                </SuggestionDropdown>
              ) : (
                textControl(ctl)
              );
            // 검색 미배선 + 선택 전용 → 읽기 전용 degrade. editEntry:'click'이어도 편집 진입을
            // 막는다(진입해 봐야 아무것도 커밋할 수 없는 칸이므로 disabled로 표시 상태 유지).
            if (textSuggestUnavailable) {
              return (
                <div key={node.id} className={fieldWrapClass}>
                  {labelEl}
                  {node.editEntry === 'click' ? (
                    <ClickToEditField
                      displayValue={String(value ?? '')}
                      placeholder={node.placeholder}
                      inheritCellFont={inheritCellFont}
                      disabled
                      collapseLines={collapseLinesFor(node)}
                      controlBoxStyle={clickDisplayBoxStyle(controlStyle)}
                      renderControl={textControlWithSuggest}
                    />
                  ) : (
                    <input
                      id={inputId}
                      type="text"
                      aria-label={controlAriaLabel}
                      className={inputClass}
                      style={{ ...controlStyle, ...SUGGEST_UNAVAILABLE_CONTROL_STYLE }}
                      value={(value as string) ?? ''}
                      placeholder={node.placeholder}
                      readOnly
                    />
                  )}
                  <SuggestNote text={SUGGEST_REQUIRE_SELECTION_UNAVAILABLE} />
                </div>
              );
            }
            return (
              <div key={node.id} className={fieldWrapClass}>
                {labelEl}
                {node.editEntry === 'click' ? (
                  <ClickToEditField
                    displayValue={String(value ?? '')}
                    placeholder={node.placeholder}
                    inheritCellFont={inheritCellFont}
                    disabled={disabled}
                    collapseLines={collapseLinesFor(node)}
                    controlBoxStyle={clickDisplayBoxStyle(controlStyle)}
                    renderControl={textControlWithSuggest}
                  />
                ) : (
                  textControlWithSuggest()
                )}
              </div>
            );
          }

          case 'number':
            return (
              <div key={node.id} className={fieldWrapClass}>
                {labelEl}
                <input
                  id={inputId}
                  type="number"
                  aria-label={controlAriaLabel}
                  className={inputClass}
                  // numberCellControlStyle은 inputInheritCellFontStyle 별칭 — controlStyle이 셀 상속
                  // + disabled 합성을 이미 담당하므로 동일(disabled 시각 누락 방지 일원화).
                  style={controlStyle}
                  value={value === undefined || value === null ? '' : (value as number)}
                  placeholder={node.placeholder}
                  required={required}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange(node.bindingKey, e.target.value === '' ? undefined : Number(e.target.value))
                  }
                />
              </div>
            );

          case 'date':
            return (
              <div key={node.id} className={fieldWrapClass}>
                {labelEl}
                <DateFieldInput
                  id={inputId}
                  ariaLabel={controlAriaLabel}
                  value={value}
                  placeholder={node.placeholder}
                  required={required}
                  disabled={disabled}
                  className={inputClass}
                  controlStyle={controlStyle}
                  onChange={(next) => onChange(node.bindingKey, next)}
                />
              </div>
            );

          // 기간 — date 컨트롤 2개('~'로 연결). 값은 객체 하나({ start?, end? })라 한쪽만 바뀌어도
          // 반대쪽을 보존해 병합한다(DateRangeFieldInput 소유). placeholder 저작값은 시작 칸에
          // 준다(종료 칸은 기본 문구) — 저작 축이 하나뿐이라 둘 중 앞 칸에 싣는 게 자연스럽다.
          case 'date-range':
            return (
              <div key={node.id} className={fieldWrapClass}>
                {labelEl}
                <DateRangeFieldInput
                  id={inputId}
                  ariaLabel={controlAriaLabel}
                  value={value}
                  startPlaceholder={node.placeholder}
                  required={required}
                  disabled={disabled}
                  className={inputClass}
                  controlStyle={controlStyle}
                  onChange={(next) => onChange(node.bindingKey, next)}
                />
              </div>
            );

          case 'time':
            return (
              <div key={node.id} className={fieldWrapClass}>
                {labelEl}
                <input
                  id={inputId}
                  type="time"
                  aria-label={controlAriaLabel}
                  className={inputClass}
                  style={controlStyle}
                  value={(value as string) ?? ''}
                  required={required}
                  disabled={disabled}
                  onChange={(e) => onChange(node.bindingKey, e.target.value)}
                />
              </div>
            );

          case 'textarea': {
            const textareaControl = (ctl?: ClickToEditControlProps) => (
              <textarea
                id={inputId}
                ref={ctl?.focusRef}
                aria-label={controlAriaLabel}
                className={textareaClass}
                style={textareaControlStyle}
                // 기본 2행 유지 — 실제 높이는 시안 .field-textarea의 min-height 96px가 하한이다.
                // 행 수 저작 속성은 없음(기본만).
                rows={2}
                value={(value as string) ?? ''}
                placeholder={node.placeholder}
                required={required}
                disabled={disabled}
                onBlur={ctl?.onBlur}
                onKeyDown={ctl?.onKeyDown}
                onChange={(e) => onChange(node.bindingKey, e.target.value)}
              />
            );
            return (
              <div key={node.id} className={fieldWrapClass}>
                {labelEl}
                {node.editEntry === 'click' ? (
                  <ClickToEditField
                    displayValue={String(value ?? '')}
                    placeholder={node.placeholder}
                    inheritCellFont={inheritCellFont}
                    disabled={disabled}
                    collapseLines={collapseLinesFor(node)}
                    controlBoxStyle={clickDisplayBoxStyle(textareaControlStyle)}
                    renderControl={textareaControl}
                  />
                ) : (
                  textareaControl()
                )}
              </div>
            );
          }

          case 'select': {
            // 옵션 동적 소스(optionsFrom, 개정 4) — 카탈로그 rows(resolvedRows/values 채널, 반복 행
            // 안에서는 본문 값 outerValues 폴백)에서 옵션을 동적 생성. rows 미해소면 정적 options
            // 폴백(external 등 무크래시).
            const selectOptions =
              resolveDynamicSelectOptions(node.optionsFrom, values, resolvedRows, outerValues) ??
              node.options;
            return (
              <div key={node.id} className={fieldWrapClass}>
                {labelEl}
                <select
                  id={inputId}
                  aria-label={controlAriaLabel}
                  className={inputClass}
                  style={controlStyle}
                  // value는 String 규격 — dataType==='number' 값(기준표 level 등)도 String으로
                  // 옵션 매칭(선택 표시). onChange가 dataType 기준으로 Number 되돌림 기록.
                  value={String(value ?? '')}
                  required={required}
                  disabled={disabled}
                  // 자기 값 기록 + 선택 옵션의 원천 행(raw) writes 전파(개정 5 — 콤보 선택 규약
                  // 동형). writes는 InputWidget 축이라 트리 경로에서만 실려 옴(flat 레거시=자기
                  // 값만, 하위호환). select는 옵션 간 전환뿐이라 콤보식 stale 회수 없음.
                  onChange={(e) =>
                    applyDynamicSelectSelection(
                      node,
                      e.target.value,
                      values ?? {},
                      onChange,
                      resolvedRows,
                      outerValues,
                    )
                  }
                >
                  <option value="">선택</option>
                  {selectOptions?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          case 'radio': {
            const radioOptions = node.options ?? [];
            // 표시 변형(shared InputWidget.displayVariant) — 'buttons'면 등분 그리드의 가로 꽉 찬
            // 버튼(구 TBM 예/아니오 토글 동형). 옵션이 없으면 그릴 버튼이 없어 기존 렌더로 degrade
            // (빈 그리드가 라벨만 남기고 자리를 먹는 것보다 낫다).
            const asButtons = node.displayVariant === 'buttons' && radioOptions.length > 0;
            // 저작 캔버스에서는 실폼과 **같은 모양**을 유지하되 클릭을 값 편집이 아니라 위젯 선택으로
            // 흘린다(photo·modalTrigger inert 규약 동형). 네이티브 라디오는 `<label>` 안에 있어
            // selectable 래퍼의 label preventDefault가 값 변경을 대신 막아 주지만, 버튼은 그 검사에
            // 걸리지 않아 여기서 직접 inert로 만든다(stopPropagation 없음 — 클릭이 래퍼로 올라가야
            // 위젯이 선택된다).
            const canvasInert = suggest?.authoringCanvas === true;
            return (
              // 라벨 미표시('none')면 legend가 사라져 그룹 이름이 없어지므로 fieldset 자신에
              // 접근명을 준다(개별 radio는 옵션 라벨이 감싸고 있어 이름이 있다).
              <fieldset key={node.id} aria-label={controlAriaLabel}>
                {/* radio는 구조상 side 배치 미지원 — 라벨 스타일/hint만 반영, 위치는 top 고정. */}
                {labelSlot ??
                  (labelHidden ? null : (() => {
                    const legendPres = labelPresentationFor({ ...node, labelPlacement: 'top' });
                    return (
                      <legend className={legendPres.className} style={legendPres.style}>
                        <WidgetLabelContent node={node} values={values} />
                      </legend>
                    );
                  })())}
                {asButtons ? (
                  // 택1 시맨틱은 부모가 radiogroup, 각 버튼이 radio+aria-checked를 쥔다
                  // (구 TBM Chip 택1 그룹 규약 동형 — Chip은 그때 aria-pressed를 내보내지 않는다).
                  <div
                    role="radiogroup"
                    aria-label={controlAriaLabel}
                    style={{
                      ...RADIO_BUTTONS_GROUP_STYLE,
                      gridTemplateColumns: `repeat(${radioOptions.length}, minmax(0, 1fr))`,
                    }}
                  >
                    {radioOptions.map((opt) => {
                      const on = value === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          disabled={disabled}
                          style={{
                            ...RADIO_BUTTON_BASE_STYLE,
                            ...(on ? RADIO_BUTTON_ON_STYLE : RADIO_BUTTON_OFF_STYLE),
                            ...(inheritCellFont ? readonlyInheritCellFontStyle : {}),
                            // 비활성은 회색 면으로 덮는다 — backgroundImage(선택 그라데이션)를
                            // 함께 끄지 않으면 backgroundColor만 바뀌고 그라데이션이 위에 남아
                            // "비활성인데 강조돼 보이는" 상태가 된다.
                            ...(disabled
                              ? { ...DISABLED_CONTROL_STYLE, backgroundImage: 'none' }
                              : {}),
                          }}
                          // 네이티브 라디오 경로에 onBlur가 없다(트리 radio 계약 — blur 훅은
                          // 제안 컨트롤(suggest) 전용). 버튼 변형도 같은 계약을 그대로 승계한다.
                          onClick={
                            canvasInert ? undefined : () => onChange(node.bindingKey, opt.value)
                          }
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {radioOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center gap-1.5"
                        style={{
                          ...CHOICE_LABEL_STYLE,
                          ...(inheritCellFont ? readonlyInheritCellFontStyle : {}),
                          ...(disabled ? DISABLED_LABEL_STYLE : {}),
                        }}
                      >
                        <input
                          type="radio"
                          // name = inputId(행 인스턴스 스코프) — 위젯 축 고정이면 행끼리 **한 라디오
                          // 그룹**이 돼 다른 행 선택이 서로를 지우고 ↑↓ 이동이 행을 넘나든다.
                          name={inputId}
                          value={opt.value}
                          checked={value === opt.value}
                          disabled={disabled}
                          style={CHOICE_CONTROL_STYLE}
                          onChange={() => onChange(node.bindingKey, opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>
            );
          }

          case 'checkbox':
            // 최소 슬라이스: 옵션 없는 단일 boolean 체크박스
            return (
              <div key={node.id} className="flex items-center gap-2">
                <input
                  id={inputId}
                  type="checkbox"
                  aria-label={controlAriaLabel}
                  checked={Boolean(value)}
                  disabled={disabled}
                  style={CHOICE_CONTROL_STYLE}
                  onChange={(e) => onChange(node.bindingKey, e.target.checked)}
                />
                {labelSlot ?? (labelHidden ? null : (
                  <label
                    htmlFor={inputId}
                    style={{
                      ...CHOICE_LABEL_STYLE,
                      ...(inheritCellFont ? readonlyInheritCellFontStyle : {}),
                      ...(disabled ? DISABLED_LABEL_STYLE : {}),
                    }}
                  >
                    <WidgetLabelContent node={node} values={values} />
                  </label>
                ))}
              </div>
            );

          case 'table': {
            // dataDef(N2-B) 표 편집 위젯 — 행 추가/삭제 + 셀 텍스트 편집.
            // 값 = Record<string, unknown>[] (없으면 빈 배열).
            // 칼럼 해소: 사본 우선 → 정본(dataDefs) 폴백 + fieldDisplay 표시 오버라이드 병합 —
            // resolveTableFields가 단일 정본(결정 2026-08-07-datadef-reference-model-design).
            const fields = resolveTableFields(node, dataDefs);
            const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];

            if (fields.length === 0) {
              return (
                <div key={node.id}>
                  {labelEl}
                  <p className="text-xs text-gray-400 px-3 py-2 border border-dashed border-gray-300 rounded-card">
                    칼럼 정의가 없어 표를 편집할 수 없습니다.
                  </p>
                </div>
              );
            }

            // 한 행의 여러 칼럼을 **한 번에** 갱신 — 제안 선택 전파(suggestionWrites)처럼 여러 셀을
            // 함께 쓰는 경로는 updateCell을 연달아 부르면 안 된다(둘 다 같은 rows 클로저에서
            // 파생돼 뒤 호출이 앞 호출을 덮는다).
            const rowsWithPatch = (
              rowIdx: number,
              patch: Record<string, unknown>,
            ): Record<string, unknown>[] => rows.map((r, i) => (i === rowIdx ? { ...r, ...patch } : r));
            const updateRowCells = (rowIdx: number, patch: Record<string, unknown>) => {
              onChange(node.bindingKey, rowsWithPatch(rowIdx, patch));
            };
            const updateCell = (rowIdx: number, fieldKey: string, cellValue: unknown) => {
              updateRowCells(rowIdx, { [fieldKey]: cellValue });
            };
            // 셀 검색 제안(DataDefField.suggestionsFrom) — 위젯 축(InputWidget)의 표 셀 판.
            // 값 스코프가 **그 행**이라 filterByRowField도 같은 행에서 읽고, 선택 전파
            // (suggestionWrites)는 같은 행의 다른 칼럼에 쓴다(대상이 bindingKey가 아닌 칼럼 키인
            // 것만 위젯 writes와 다르다). 호스트 onSearch 미배선(캔버스 등)이면 undefined —
            // 일반 입력으로 degrade한다.
            const onCellSuggestSearch = suggest?.onSearch;
            const cellSuggestFor = (
              f: DataDefField,
              listIdx: number,
              row: Record<string, unknown>,
            ) => {
              const sf = f.suggestionsFrom;
              if (!sf || !onCellSuggestSearch) return undefined;
              return {
                search: (q: string) =>
                  onCellSuggestSearch(sf.catalogKey, q, buildSuggestFilter(sf.filterByRowField, row)),
                // 선택 전용 칸 — 타이핑은 검색어일 뿐이고 아래 onPick만이 값을 만든다.
                requireSelection: sf.requireSelection === true,
                onPick: (pick: ComposerSuggestionRow) => {
                  // 선택 값은 그 셀에, 전파 매핑은 같은 행의 대상 칼럼에 — **한 번에** 쓴다.
                  const patch: Record<string, unknown> = { [f.key]: pick.text };
                  for (const w of f.suggestionWrites ?? []) {
                    const v = pick.raw?.[w.source];
                    if (v !== undefined) patch[w.target] = v;
                  }
                  const patched = rowsWithPatch(listIdx, patch);
                  // 선택 전용 칸은 **고른 순간이 곧 확정**이다 — blur를 기다릴 이유가 없다.
                  // (제안 항목 mousedown은 preventDefault로 입력 포커스를 유지하므로 blur가 아예
                  // 오지 않는다. 그래서 종전에는 딴 데를 눌러야 잠금이 걸려, 값이 들어온 뒤에도
                  // 셀이 입력 상태로 남아 접힘 표시가 나오지 않았다 — 2026-08-10 제보.)
                  // 값 쓰기와 확정을 **한 번의 onChange로** 커밋한다: 나눠 부르면 확정 판정이
                  // 옛 rows를 보고 "미입력 행"으로 취급해 잠금이 걸리지 않는다.
                  const confirmed =
                    sf.requireSelection === true
                      ? rowsAfterAddedRowConfirm(patched, listIdx, readonlyFieldKeys)
                      : null;
                  onChange(node.bindingKey, confirmed ?? patched);
                  suggest?.onSelect?.(sf.catalogKey, pick);
                },
              };
            };
            // 선택 전용 칸(suggestionsFrom.requireSelection) — 검색 결과에서 고른 값만 들어오는 칼럼.
            // 이 표에 그런 칼럼이 하나도 없으면 아래 추가·편집 종료 처리는 종전 경로 그대로다(회귀 0).
            const requireSelectionKeys = fields
              .filter((f) => f.suggestionsFrom?.requireSelection === true)
              .map((f) => f.key);
            const removeRow = (rowIdx: number) => {
              onChange(
                node.bindingKey,
                rows.filter((_, i) => i !== rowIdx),
              );
            };

            // 구분 그룹 표시 — 지정 필드 값별 "그룹 제목 + 표 + 그룹별 추가"(구 SMS 점검현황 동형).
            // 데이터는 단일 리스트 유지(수정/삭제는 전체 리스트 인덱스 기준 — 분단 표시와 동일 패턴),
            // 구분 칼럼은 표에서 숨긴다. 미지정/없는 필드 키면 기존 표시 그대로(회귀 0).
            const groupByField =
              node.groupByField && fields.some((f) => f.key === node.groupByField)
                ? node.groupByField
                : undefined;
            // 구분 칸이 **선택 전파로 자동으로 채워지는** 표인가 — 어느 칼럼의 제안 선택
            // (suggestionWrites)이 구분 칼럼에 값을 써 넣으면, 고르는 순간 그룹이 정해진다.
            // 그런 표에서 그룹별 추가는 preset이 곧 덮어써지므로 무의미하고, 두 버튼("+ 항목
            // 추가" / "+ 다른 항목 추가")의 차이가 화면에 드러나지 않아 혼란만 준다
            // (2026-08-10 제보 — SI 관련법규). 그래서 추가 통로를 표 끝의 하나로 모은다.
            // ⚠️ 새 저작 옵션을 만들지 않는다 — 판정은 기존 배선에서 유도한다. 구분 값을 사람이
            // 직접 입력하는 표(SMS 점검현황 category 등)는 그룹별 추가가 실질 편의라 현행 그대로.
            const groupFilledBySuggestionWrite =
              groupByField !== undefined &&
              fields.some((f) =>
                (f.suggestionWrites ?? []).some((w) => w.target === groupByField),
              );
            // 칼럼 표시 순서 override — fieldOrder(필드 키 배열) 순으로 정렬, 미포함 필드는
            // 원래 순서로 뒤에(새 필드 추가에 견고). 표시 전용 — 행 데이터/저장 형상 무영향.
            const orderedFields = (() => {
              const ord = node.fieldOrder;
              if (!ord || ord.length === 0) return fields;
              const byKey = new Map(fields.map((f) => [f.key, f] as const));
              const picked = ord
                .map((k) => byKey.get(k))
                .filter((f): f is DataDefField => f != null);
              const rest = fields.filter((f) => !ord.includes(f.key));
              return [...picked, ...rest];
            })();
            const visibleFields = groupByField
              ? orderedFields.filter((f) => f.key !== groupByField)
              : orderedFields;

            // 읽기전용 — 표 전체(표시 모드 readonly: 행 추가/삭제까지 제거)와 칼럼 단위
            // (readonlyFields: 그 칼럼만 값 표시, 행 조작·타 칼럼 편집은 유지) 두 축.
            // 칼럼 단위에서 "+ 추가" 행(__custom 마킹)은 **최초 1회 입력**만 허용 — 입력 확정
            // (포커스 이탈 시 읽기전용 칼럼이 모두 채워짐)되면 마킹을 제거해 기본 행과 동일하게
            // 잠근다(구 SMS 동형: 추가할 때만 입력, 이후 수정 불가 — 정정은 삭제 후 재추가.
            // 2026-07-15 사용자 확정).
            const isReadonly = node.mode === 'readonly';
            const readonlyFieldKeys = node.readonlyFields ?? [];
            const readonlyFieldSet = new Set(readonlyFieldKeys);
            const cellIsReadonly = (f: DataDefField, row: Record<string, unknown>): boolean =>
              isReadonly || (readonlyFieldSet.has(f.key) && row.__custom !== true);
            // 행 클릭 체크 토글 — 읽기전용 표시 셀 클릭 = 지정 체크박스 칼럼 토글(구 SMS
            // "항목 클릭=점검 토글" 동형). 체크박스 필드가 아니거나 표 전체 readonly면 비활성
            // (enabledWhen disabled도 동일 — 토글은 값 쓰기라 차단).
            const rowClickToggleField =
              !isReadonly &&
              !disabled &&
              node.rowClickToggleField &&
              fields.some(
                (f) => f.key === node.rowClickToggleField && f.widget === 'checkbox',
              )
                ? node.rowClickToggleField
                : undefined;
            // 추가 행 입력 확정 — blur 시점에 그 행의 읽기전용 칼럼이 전부 채워져 있으면 __custom
            // 제거(잠금 확정, 영속에도 잠긴 상태로 저장). 일부 미입력이면 유지(계속 입력 가능).
            // 판정은 순수 헬퍼 소유(rowsAfterAddedRowConfirm) — 제안 선택 경로가 같은 판정을
            // **방금 쓴 값이 반영된 목록**으로 쓰기 때문에 목록을 인자로 받는다.
            const confirmCustomRow = (listIdx: number): void => {
              const next = rowsAfterAddedRowConfirm(rows, listIdx, readonlyFieldKeys);
              if (next) onChange(node.bindingKey, next);
            };
            /**
             * 셀 편집 종료(포커스 이탈) — 선택 전용 칸이 있는 표에서는 **고르지 않고 끝낸 추가 행**을
             * 남기지 않는다(목록형 listAfterEditEnd와 같은 규약, 판정은 순수 헬퍼 소유).
             * 같은 표의 버튼(행 삭제 ×·행 추가)으로 넘어간 blur는 그 버튼에 조작을 양보한다 —
             * 여기서 행을 지우면 인덱스가 밀려 엉뚱한 행이 지워진다(목록형 data-list-field-action 동형).
             */
            const endCellEdit = (listIdx: number, ev?: ReactFocusEvent<HTMLElement>): void => {
              if (requireSelectionKeys.length > 0) {
                const to = ev?.relatedTarget;
                if (to instanceof HTMLElement) {
                  if (to.closest('[data-table-row-action]') !== null) return;
                  // 같은 행 안 이동(옆 칸으로 Tab)은 편집 세션이 **이어지는** 것이라 취소 대상이 아니다.
                  // 이 가드가 없으면 편집 가능한 칼럼이 둘 이상 보이는 표에서, 방금 추가한 행의 검색
                  // 칸에서 옆 칸으로 Tab하는 순간 그 행이 눈앞에서 사라진다.
                  if (
                    to.closest('[data-table-row-index]')?.getAttribute('data-table-row-index') ===
                    String(listIdx)
                  )
                    return;
                }
                // 검색어를 쳐 둔 채 빠져나온 행은 **취소로 보지 않는다** — 값으로 커밋되진
                // 않았어도 "찾다 만 행"은 사용자의 작업이고, 추가만 하고 아무것도 안 한 행과는
                // 다르게 대접해야 한다. 선택 전용 칸의 초안(draftValue)은 값이 아니라 화면 상태라
                // `rows`에 나타나지 않으므로, 방금 빠져나온 컨트롤의 **표시값**으로 판정한다
                // (cellText가 초안을 그대로 표시한다 — 그 값이 곧 초안이다).
                // 2026-08-07 실측: 행 추가에 커서가 들어가게 되면서 이 상황을 자주 만나게 됐다
                // (종전엔 사용자가 칸을 직접 눌러야만 생겼다). 법령을 찾다 딴 데를 보면 행이 통째로
                // 사라졌다. 다음 "+ 항목 추가"에서는 rowsBeforeAddRow가 규약대로 걷어낸다.
                const leaving = ev?.target;
                const hasDraft =
                  (leaving instanceof HTMLInputElement ||
                    leaving instanceof HTMLTextAreaElement) &&
                  leaving.value.trim() !== '';
                const next = hasDraft
                  ? null
                  : rowsAfterRequiredSelectionEditEnd(rows, listIdx, requireSelectionKeys);
                if (next) {
                  onChange(node.bindingKey, next);
                  return;
                }
              }
              confirmCustomRow(listIdx);
            };

            // 행 추가 — 잠금 판정(cellIsReadonly)·표시 순서(visibleFields)를 쓰므로 그 선언 뒤에 둔다.
            // preset: 구분 그룹 뷰의 그룹별 추가 — 새 행에 그 구분값을 자동 주입(그룹 소속 유지).
            // 읽기전용 칼럼(readonlyFields)이 있는 표에선 새 행에 사용자 추가 마킹(__custom)을 남겨
            // 그 행만 읽기전용 칼럼도 입력 가능하게 한다(기본 제공 행 잠금 + 추가 항목 자유 입력 —
            // 마킹은 행 데이터에 영속돼 다음 보고서(현장 재사용)에서도 커스텀 행은 계속 편집 가능).
            // from: 클릭한 추가 버튼(포커스 대상 표 인스턴스를 찾는 앵커 — 아래 주석 참조).
            const addRow = (preset?: Record<string, unknown>, from?: HTMLElement | null) => {
              const blank: Record<string, unknown> = {};
              // 위젯별 빈 초기값은 행 그리드와 같은 규약 한 벌(`blankFieldValue` — 기간 칼럼만 빈
              // **객체**, 그 외 ''). 표 칼럼 어휘엔 목록 위젯·defaultValue가 없어 결과는 종전과 같다.
              for (const f of fields) blank[f.key] = blankFieldValue(f);
              // 선택 전용 칸(suggestionsFrom.requireSelection)이 있는 표도 같은 마킹이 필요하다 —
              // "고르지 않고 끝낸 추가 행"을 취소하려면 그 행이 **사용자가 방금 추가한 것**임을
              // 알아야 한다(rowsAfterRequiredSelectionEditEnd가 __custom만 지운다). 읽기전용 칼럼
              // 없이 이 축만 쓰는 표에서는 마킹이 안 붙어 빈 행이 조용히 남았다.
              const needsCustomMark =
                (node.readonlyFields?.length ?? 0) > 0 ||
                fields.some((f) => f.suggestionsFrom?.requireSelection === true);
              const marker = needsCustomMark ? { __custom: true } : {};
              // 아무것도 고르지 않은 채 남아 있던 추가 행은 먼저 걷어낸다 — 목록형 listBeforeAddItem과
              // 같은 규약("추가는 언제나 빈 행 1개만"). 추가 버튼으로 넘어간 blur는 endCellEdit이
              // 양보하고 지나가므로, 이 정리가 없으면 반복 추가가 빈 행을 저장값에 쌓는다.
              const base = rowsBeforeAddRow(rows, requireSelectionKeys);
              const added = { ...blank, ...preset, ...marker };
              onChange(node.bindingKey, [...base, added]);
              // 방금 만든 행의 첫 입력 칸으로 커서 — 목록형 "+ 항목 추가"(새 항목이 곧바로 편집으로
              // 열린다)와 같은 규약이다. 표 셀은 편집 진입 state 없이 언제나 입력이라, 남은 문제는
              // "그 입력에 포커스를 주는 것"뿐이다.
              // ⚠️ 저작 캔버스는 제외한다 — 저작 중 포커스가 캔버스로 튀면 속성 패널 조작이 끊기고,
              // 선택 전용 칸이 열리면 서버 검색이 도는 것도 캔버스 규약 위반이다.
              if (suggest?.authoringCanvas === true || disabled) return;
              const focusKey = firstTypeableCellKey(
                visibleFields.map((f) => ({
                  key: f.key,
                  widget: f.widget,
                  locked: cellIsReadonly(f, added),
                  // 선언은 선택 전용인데 호스트 검색이 미배선이면 그 칸은 readOnly로 degrade된다
                  // (renderEditableCell suggestUnavailable과 같은 판정 — 값을 만들 방법이 없다).
                  unavailable:
                    f.suggestionsFrom?.requireSelection === true && !onCellSuggestSearch,
                })),
              );
              if (!focusKey) return;
              // 반복(repeat) 안에서는 같은 node.id의 표가 발견사항 수만큼 그려진다 — 클릭한 버튼에서
              // 올라가 **그 인스턴스**를 앵커로 잡는다(document 전역 조회는 남의 표를 잡는다).
              focusAddedTableCell(from?.closest(`[${TABLE_WIDGET_ATTR}]`), base.length, focusKey);
            };

            // 작성 편의 표시 단 수(데이터/양식 출력 단 수와 무관, 작성 페이지 표시 전용).
            // 미지정/1 = 단일 단 표시(회귀0). 2 이상이면 목록을 좌우 N단으로 나눠 표시한다.
            // 구분 그룹 표시와 조합 시 **각 그룹 안에서** 적용(2026-07-15 사용자 — 그룹+2단 병용).
            const displayColumns = Math.max(1, Math.floor(node.displayColumns ?? 1));

            type RowEntry = { row: Record<string, unknown>; listIdx: number };

            // 칼럼 너비(%) — 필드 키별 지정, 미지정은 자동 분배(th에 부여 — table-layout auto에서
            // 헤더 너비가 열 너비 기준이 됨). 표시 전용.
            const fieldWidths = node.fieldWidths ?? {};
            // N단 = **하나의 표** 안에서 칼럼 세트 반복(구 SMS 작성 스텝 동형 — 분리된 표 N개가
            // 아니라 한 표를 칼럼으로 나눔, 2026-07-15 사용자 스크린샷 대조). 분배는 **가로 채우기**
            // (읽기 순서 왼→오·위→아래, 시각 행 k의 세트 si = entries[k*N+si]) — 구 SMS 작성 스텝과
            // 동일하고, 행 추가(리스트 끝 append)가 마지막 행의 왼쪽부터 채워져 기대와 일치
            // (세로 채우기는 추가 시 M=ceil(n/N) 재계산으로 단 경계가 밀리는 현상 — 2026-07-15 제보).
            // 수정/삭제는 각 슬롯의 listIdx(전체 리스트 인덱스) 기준 — 분단은 표시 전용.
            // 테두리 프리셋(표시 전용) — rows(행 구분선, 기본)/grid(격자)/none. grid는 셀 개별
            // border가 경계를 담당하므로 tr 구분선은 생략, none은 구분선 전부 제거.
            const tableBorder = node.tableBorder ?? 'rows';
            const gridCellCls = tableBorder === 'grid' ? ' border border-gray-200' : '';
            const headTrCls = tableBorder === 'rows' ? 'border-b border-gray-100' : '';
            const bodyTrCls = tableBorder === 'rows' ? 'border-b border-gray-50' : '';
            const renderTableEl = (entries: RowEntry[], showEmptyMessage: boolean) => {
              // 채워지는 세트만 출력 — 행 수가 단수를 못 채우면(n<N) 빈 세트 칼럼이 auto 레이아웃에서
              // collapse돼 채워진 세트만 넓어지는 왜곡(2026-07-15 스크린샷 제보). 실제 사용 세트 수만
              // 렌더해 전체 폭을 나눠 갖는다(행 0개=세트 1개에 빈 안내).
              const setCount = Math.max(1, Math.min(displayColumns, entries.length));
              const rowCount = Math.ceil(entries.length / setCount);
              const setCols = visibleFields.length + (isReadonly ? 0 : 1); // 편집 시 + 삭제 열
              const setDivider = (si: number, fi: number): string =>
                si > 0 && fi === 0 ? ' border-l border-l-gray-100' : '';
              // 슬롯(시각 행 k·세트 si) → 리스트 인덱스: 가로 우선(기본)=k*setCount+si(왼→오·위→아래) /
              // 세로 우선(fillDirection='column')=si*rowCount+k(한 단 위→아래 다 채우고 다음 단, 구 SMS
              // 점검현황 좌/우 세로 나열). 세로 우선은 마지막 단이 짧을 수 있어 빈 슬롯은 아래 !e 가드가 처리.
              const columnFill = node.fillDirection === 'column' && setCount >= 2;
              const slotEntry = (k: number, si: number): RowEntry | undefined =>
                columnFill ? entries[si * rowCount + k] : entries[k * setCount + si];
              return (
                <table className="w-full text-sm">
                  {/* 헤더 숨김(hideHeader) 시 열 너비 기준이 사라지므로 colgroup으로 이관 —
                      th의 fieldWidths %·체크박스 고정폭(w-10=40px)·삭제 열 폭과 동일 규칙. */}
                  {node.hideHeader ? (
                    <colgroup>
                      {Array.from({ length: setCount }, (_, si) => (
                        <Fragment key={si}>
                          {visibleFields.map((f) => (
                            <col
                              key={f.key}
                              style={
                                typeof fieldWidths[f.key] === 'number'
                                  ? { width: `${fieldWidths[f.key]}%` }
                                  : f.widget === 'checkbox'
                                    ? { width: 40 }
                                    : undefined
                              }
                            />
                          ))}
                          {!isReadonly && <col style={{ width: 40 }} />}
                        </Fragment>
                      ))}
                    </colgroup>
                  ) : (
                  <thead>
                    <tr className={headTrCls}>
                      {Array.from({ length: setCount }, (_, si) => (
                        <Fragment key={si}>
                          {visibleFields.map((f, fi) => (
                            <th
                              key={f.key}
                              // 체크박스 칼럼 헤더 = 가운데 + 고정 좁은 폭(구 SMS 점검 칼럼 w-10 동형).
                              // 행 경계는 thead tr border가 담당(th 개별 border 없음 — grid 프리셋 제외).
                              className={`py-2 text-xs font-medium text-gray-500 ${
                                f.widget === 'checkbox'
                                  ? 'px-2 text-center whitespace-nowrap w-10'
                                  : 'px-4 text-left'
                              }${setDivider(si, fi)}${gridCellCls}`}
                              // 칼럼 정렬(f.align)은 헤더도 따른다 — 셀만 옮기고 헤더가 남으면
                              // 열이 어긋나 보인다. 미지정이면 종전 style(너비만)과 동일(회귀 0).
                              style={(() => {
                                const width =
                                  typeof fieldWidths[f.key] === 'number'
                                    ? { width: `${fieldWidths[f.key]}%` }
                                    : undefined;
                                const ta = cellTextAlignForField(f);
                                if (width === undefined && ta === undefined) return undefined;
                                return { ...width, ...(ta ? { textAlign: ta } : {}) };
                              })()}
                            >
                              {f.label}
                            </th>
                          ))}
                          {!isReadonly && <th className={`w-10${gridCellCls}`} />}
                        </Fragment>
                      ))}
                    </tr>
                  </thead>
                  )}
                  <tbody>
                    {rowCount === 0 && showEmptyMessage ? (
                      <tr>
                        <td
                          colSpan={setCols * setCount}
                          className={`px-2 py-3 text-center text-xs text-gray-400${gridCellCls}`}
                        >
                          항목이 없습니다. 아래 버튼으로 추가하세요.
                        </td>
                      </tr>
                    ) : (
                      Array.from({ length: rowCount }, (_, k) => (
                        <tr
                          key={k}
                          className={`${bodyTrCls}${!isReadonly ? ' hover:bg-gray-50' : ''}`}
                        >
                          {Array.from({ length: setCount }, (_, si) => {
                            const e = slotEntry(k, si);
                            if (!e) {
                              // 마지막 행의 빈 슬롯 — 세트 폭 유지용 빈 셀(삭제 버튼 없음).
                              return (
                                <td
                                  key={si}
                                  colSpan={setCols}
                                  className={
                                    `${si > 0 ? 'border-l border-l-gray-100' : ''}${gridCellCls}` ||
                                    undefined
                                  }
                                />
                              );
                            }
                            return (
                              <Fragment key={si}>
                                {visibleFields.map((f, fi) => {
                                  // 행 클릭 토글 대상 셀 = 읽기전용 표시 셀(편집 셀은 입력 포커스
                                  // 우선) — 토글 칼럼 자신은 제외(그 칼럼이 readonly로 잠긴 경우
                                  // 값 셀 클릭으로 잠금을 우회하지 않게).
                                  const clickToggles =
                                    rowClickToggleField !== undefined &&
                                    f.key !== rowClickToggleField &&
                                    cellIsReadonly(f, e.row);
                                  return (
                                  <td
                                    key={f.key}
                                    // 같은 행 판별 앵커 — 편집 종료(endCellEdit)가 "옆 칸으로 Tab"을
                                    // 추가 취소로 오판하지 않게 한다. N단 표시에서는 한 <tr>이 여러
                                    // 행(listIdx)을 담으므로 <tr>이 아니라 셀에 붙인다.
                                    data-table-row-index={e.listIdx}
                                    // 칼럼 앵커 — 행 추가 직후 "첫 입력 칸"을 찾아 포커스하는 데 쓴다
                                    // (addedRowFocus). 행 앵커와 같은 셀에 있어야 한 번에 특정된다.
                                    data-table-cell-field={f.key}
                                    className={`${
                                      f.widget === 'checkbox' ? 'px-2 py-2 text-center' : 'px-4 py-2'
                                    }${setDivider(si, fi)}${
                                      clickToggles ? ' cursor-pointer' : ''
                                    }${gridCellCls}`}
                                    // 토글 셀 텍스트 선택 차단 — 빠른 재클릭(토글 on→off)이 더블클릭으로
                                    // 인식되면 단어 선택이 시작됨(2026-07-15 제보). userSelect는 인라인
                                    // (rc 클래스 purge 무관 임계 스타일) + mousedown detail>1 preventDefault
                                    // 이중 방어(선택 시작 자체 차단).
                                    // 칼럼 정렬(f.align) — 읽기전용 표시 셀(인라인/블록 span)과
                                    // checkbox 인라인 컨트롤은 td textAlign이 정렬한다(w-full 편집
                                    // 컨트롤은 renderEditableCell이 자체 textAlign). 미지정=종전 동일.
                                    style={(() => {
                                      const ta = cellTextAlignForField(f);
                                      if (!clickToggles && ta === undefined) return undefined;
                                      return {
                                        ...(clickToggles ? { userSelect: 'none' as const } : {}),
                                        ...(ta ? { textAlign: ta } : {}),
                                      };
                                    })()}
                                    onMouseDown={
                                      clickToggles
                                        ? (ev) => {
                                            if (ev.detail > 1) ev.preventDefault();
                                          }
                                        : undefined
                                    }
                                    onClick={
                                      clickToggles
                                        ? () =>
                                            updateCell(
                                              e.listIdx,
                                              rowClickToggleField,
                                              !e.row[rowClickToggleField],
                                            )
                                        : undefined
                                    }
                                  >
                                    {cellIsReadonly(f, e.row) ? (
                                      f.widget === 'photo' ? (
                                        // photo 칼럼 readonly — URL을 텍스트로 흘리지 않고 썸네일
                                        // <img>(빈 값 '—'). 문자열 정본(readonlyCellDisplay)보다
                                        // 먼저 분기한다(표시가 텍스트가 아닌 유일한 칼럼).
                                        photoReadonlyCellNode(e.row[f.key], f.label || f.key)
                                      ) : (
                                      // 읽기전용 셀 — 값만 표시(체크박스=✓/—, select/radio=옵션 라벨).
                                      // 행 토글 표(rowClickToggleField 지정)에서는 토글 on 행을 진한
                                      // 글씨로(구 SMS 체크 시 text-gray-900 동형), 미지정 표는 gray-600 유지.
                                      // 여러 줄 셀(textarea)은 줄바꿈을 보존하고, collapseLines가 있으면
                                      // N줄까지만 보이고 접는다(표시 상태 전용 축 — 본문·print 무관).
                                      <span
                                        className={
                                          rowClickToggleField !== undefined && e.row[rowClickToggleField]
                                            ? 'text-gray-900'
                                            : 'text-gray-600'
                                        }
                                        style={
                                          f.widget === 'textarea'
                                            ? { display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
                                            : undefined
                                        }
                                      >
                                        <CollapsibleText
                                          text={readonlyCellDisplay(e.row[f.key], f)}
                                          collapseLines={collapseLinesForField(f)}
                                          // 읽기전용 셀 — 제목 줄 클릭으로도 접었다 편다.
                                          // (행 토글 표에서는 stopPropagation이 행 토글을 막지만,
                                          //  접기 대상은 긴 텍스트 칼럼이라 토글 칼럼과 겹치지 않는다.)
                                          toggleOnTextClick
                                        />
                                      </span>
                                      )
                                    ) : (
                                      renderEditableCell(
                                        e.row[f.key],
                                        f,
                                        (next) => updateCell(e.listIdx, f.key, next),
                                        // blur 확정 대상 — 읽기전용 칼럼의 추가 행 입력 셀(최초 1회
                                        // 입력 잠금) + 선택 전용 칸(고르지 않은 추가 행 취소).
                                        readonlyFieldSet.has(f.key) ||
                                        f.suggestionsFrom?.requireSelection === true
                                          ? (ev) => endCellEdit(e.listIdx, ev)
                                          : undefined,
                                        disabled,
                                        cellSuggestFor(f, e.listIdx, e.row),
                                        suggest?.authoringCanvas === true,
                                        uploadPhoto,
                                      )
                                    )}
                                  </td>
                                  );
                                })}
                                {!isReadonly && (
                                  <td className={`px-2 py-2 text-center${gridCellCls}`}>
                                    <button
                                      type="button"
                                      // 편집 중 셀에서 이 버튼으로 넘어간 blur는 편집 종료 처리를
                                      // 건너뛴다(자기 조작 양보 — endCellEdit 주석 참조).
                                      data-table-row-action="remove"
                                      className="text-gray-400 hover:text-red-500 px-1.5 text-xs"
                                      style={disabled ? DISABLED_CONTROL_STYLE : undefined}
                                      aria-label="행 삭제"
                                      disabled={disabled}
                                      onClick={() => removeRow(e.listIdx)}
                                    >
                                      ×
                                    </button>
                                  </td>
                                )}
                              </Fragment>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              );
            };

            // 분단 표 렌더(일반/그룹 공용) — 항상 단일 표(1단=세트 1개, N단=세트 반복).
            // aria-readonly: readonly 표시 모드에서 캔버스 더블클릭 라벨 편집 제외 판정에 사용(값 박스 동형).
            const renderSplitTables = (entries: RowEntry[]): ReactNode => (
              <div
                // 'none' 프리셋은 외곽 박스까지 제거(담백한 목록 결) — rows/grid는 외곽 박스 유지.
                className={
                  tableBorder === 'none'
                    ? 'overflow-x-auto'
                    : 'overflow-x-auto border border-gray-300 rounded-card'
                }
                aria-readonly={isReadonly || undefined}
              >
                {renderTableEl(entries, true)}
              </div>
            );

            // 구분 그룹 뷰 — 값별 부분 뷰(그룹 제목 + 표 + 그룹별 "+ 행 추가"). 그룹 순서는 행 등장 순
            // (defaultRows 순서 보존). 표시 단수는 각 그룹 안에서 적용. 행이 하나도 없으면 안내 +
            // 일반 추가(구분이 빈 행은 제목 밴드 없는 그룹으로 노출).
            if (groupByField) {
              const groups: { value: string; rows: { row: Record<string, unknown>; listIdx: number }[] }[] = [];
              const groupIdxByValue = new Map<string, number>();
              // 그룹 틀 선시드 — 행이 전부 삭제돼도 그룹(제목+빈 표+행 추가)이 유지되게(그룹은
              // 행 파생이라 시드 없이는 빈 그룹이 소멸 → 재추가 불가, 2026-07-15 요청).
              // 시드 소스: 내장 데이터셋 기본 행(defaultRows) **자동 파생**이 정본(운영자 무설정).
              // groupValues는 deprecated reader 하위호환(과거 저장분만 — 신규 저작 불가, 있으면 우선).
              const seedValues: string[] = node.groupValues?.length
                ? node.groupValues
                : (() => {
                    const def = dataDefs?.find((d) => d.key === node.bindingKey);
                    const vals: string[] = [];
                    for (const r of def?.defaultRows ?? []) {
                      const gv = String((r as Record<string, unknown>)[groupByField] ?? '').trim();
                      if (gv && !vals.includes(gv)) vals.push(gv);
                    }
                    return vals;
                  })();
              for (const gv of seedValues) {
                if (!gv || groupIdxByValue.has(gv)) continue; // 빈 줄(편집 중 입력) 무시
                groupIdxByValue.set(gv, groups.length);
                groups.push({ value: gv, rows: [] });
              }
              rows.forEach((row, listIdx) => {
                const gv = String(row[groupByField] ?? '');
                let gi = groupIdxByValue.get(gv);
                if (gi === undefined) {
                  gi = groups.length;
                  groupIdxByValue.set(gv, gi);
                  groups.push({ value: gv, rows: [] });
                }
                groups[gi].rows.push({ row, listIdx });
              });
              return (
                // 표 인스턴스 경계 — 반복(repeat) 안에서는 같은 node.id가 여러 벌 그려지므로,
                // 행 추가 포커스가 클릭한 버튼에서 올라와 자기 표만 보게 하는 앵커다(addedRowFocus).
                <div key={node.id} data-table-widget={node.id}>
                  {labelEl}
                  {groups.length === 0 ? (
                    <>
                      <div className="border border-gray-300 rounded-card px-2 py-3 text-center text-xs text-gray-400">
                        항목이 없습니다. 아래 버튼으로 추가하세요.
                      </div>
                      {!isReadonly && (
                        <button
                          type="button"
                          data-table-row-action="add"
                          className="mt-2 hover:bg-brand-50"
                          style={
                            disabled
                              ? { ...addItemButtonStyle, ...DISABLED_CONTROL_STYLE }
                              : addItemButtonStyle
                          }
                          disabled={disabled}
                          onClick={(ev) => addRow(undefined, ev.currentTarget)}
                        >
                          + 항목 추가
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="space-y-6">
                      {/* 그룹 = 카드형(구 SMS 점검현황 그룹 동형, 2026-07-15 사용자 요청) —
                          테두리 카드 + gray-50 헤더 밴드(제목) + 표(카드가 테두리 담당) +
                          하단 "+ 항목 추가" 밴드. */}
                      {groups.map((g) => (
                        <Card
                          key={g.value}
                          padding="none"
                          elevation="none"
                          className="overflow-hidden"
                          aria-readonly={isReadonly || undefined}
                        >
                          {/* 구분이 빈 그룹(방금 추가돼 아직 분류되지 않은 행)은 제목 밴드를
                              그리지 않는다 — 자리표시 라벨은 노이즈일 뿐이고, 구분이 채워지는
                              순간 그 행은 제 그룹으로 옮겨가 밴드가 생긴다. */}
                          {g.value ? (
                            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2.5">
                              <h4 className="text-sm font-semibold text-gray-800">{g.value}</h4>
                            </div>
                          ) : null}
                          <div className="overflow-x-auto">{renderTableEl(g.rows, true)}</div>
                          {!isReadonly && !groupFilledBySuggestionWrite && (
                            <div className="border-t border-gray-100 px-4 py-2">
                              <button
                                type="button"
                                data-table-row-action="add"
                                className="hover:bg-brand-50"
                                style={
                                  disabled
                                    ? { ...addItemButtonStyle, ...DISABLED_CONTROL_STYLE }
                                    : addItemButtonStyle
                                }
                                disabled={disabled}
                                onClick={(ev) => addRow({ [groupByField]: g.value }, ev.currentTarget)}
                              >
                                + 항목 추가
                              </button>
                            </div>
                          )}
                        </Card>
                      ))}
                      {/* 그룹 밖 추가 — 기존 그룹 중 어디에도 속하지 않는 새 구분 값의 항목을
                          넣는 유일한 통로다(그룹은 행에서 파생되므로, 그룹별 추가만 있으면 새
                          구분은 영영 만들 수 없다). 구분이 빈 행으로 시작해 제목 없는 그룹에
                          담기고, 구분을 입력하거나 검색 제안이 구분 칼럼에 값을 써 넣으면 그
                          그룹으로 옮겨간다(그룹 자동 생성).
                          구분이 선택 전파로 채워지는 표에서는 그룹별 추가가 없어 이것이 **유일한**
                          추가 버튼이다 — 그때는 '다른'을 붙이지 않는다(구별할 상대가 없다). */}
                      {!isReadonly && (
                        <button
                          type="button"
                          data-table-row-action="add"
                          className="hover:bg-brand-50"
                          style={
                            disabled
                              ? { ...addItemButtonStyle, ...DISABLED_CONTROL_STYLE }
                              : addItemButtonStyle
                          }
                          disabled={disabled}
                          onClick={(ev) => addRow(undefined, ev.currentTarget)}
                        >
                          {groupFilledBySuggestionWrite ? '+ 항목 추가' : '+ 다른 항목 추가'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            return (
              // 표 인스턴스 경계 — 위 그룹 뷰와 같은 앵커(addedRowFocus doc 참조).
              <div key={node.id} data-table-widget={node.id}>
                {labelEl}
                {renderSplitTables(rows.map((row, listIdx) => ({ row, listIdx })))}
                {!isReadonly && (
                  <button
                    type="button"
                    data-table-row-action="add"
                    className="mt-2 hover:bg-brand-50"
                    style={
                      disabled
                        ? { ...addItemButtonStyle, ...DISABLED_CONTROL_STYLE }
                        : addItemButtonStyle
                    }
                    disabled={disabled}
                    // preset은 명시 전달 — 핸들러를 직접 넘기면 MouseEvent가 preset으로 샌다(tsc 적발).
                    onClick={(ev) => addRow(undefined, ev.currentTarget)}
                  >
                    + 항목 추가
                  </button>
                )}
              </div>
            );
          }

          case 'textarea-list':
          case 'text-list': {
            // 문자열 배열(string[]) 편집 목록 — 행 그리드 필드 어휘를 InputWidget 정식 컨트롤로
            // 편입(shared WidgetType doc 참조). 항목 편집 UI는 행 그리드와 같은 컴포넌트
            // (TextareaListField — 단일 정본, 중복 구현 금지). text-list는 각 항목이 한 줄 input
            // (multiline=false), textarea-list는 여러 줄 textarea. 값 정규화는 normalizeList가 담당
            // (비배열 스칼라 → 단일 항목 배열)이라 어떤 저장값에도 무크래시.
            // 범용 검색 제안 축(suggestionsFrom) — text 케이스와 동일 게이트(트리 경로 캐스트 +
            // 호스트 onSearch 배선 시에만). 항목별 배선·기추가 항목 제외는 TextareaListField 소유,
            // 여기는 검색 closure(현재 값 스코프 filter)와 선택 부속(writes 전파·통지)만 공급.
            const sfList = (node as Partial<Pick<InputWidget, 'suggestionsFrom'>>).suggestionsFrom;
            const onListSuggestSearch = suggest?.onSearch;
            const listSuggest =
              sfList && onListSuggestSearch
                ? {
                    search: (q: string) =>
                      onListSuggestSearch(
                        sfList.catalogKey,
                        q,
                        buildSuggestFilter(sfList.filterByRowField, values ?? {}),
                      ),
                    // 선택 전용 칸 — 타이핑은 항목 값이 되지 않고, 고르지 않고 끝낸 항목은
                    // 기존 규약(listAfterEditEnd)대로 목록에서 사라진다(추가 취소).
                    requireSelection: sfList.requireSelection === true,
                    onPick: (row: ComposerSuggestionRow) =>
                      applySuggestionPick(sfList, row, values ?? {}, onChange, suggest?.onSelect),
                  }
                : undefined;
            // 선택 전용 목록인데 호스트 검색이 미배선이면 항목을 만들 방법이 없다 — 읽기 전용
            // degrade를 목록 컴포넌트에 알린다(선언은 listSuggest 유무와 무관하게 sfList가 소유).
            const listSuggestUnavailable =
              isTypedCommitBlocked(sfList) && !listSuggest && suggest?.authoringCanvas !== true;
            const listEl = (
              <div key={node.id}>
                {labelEl}
                <TextareaListField
                  value={value}
                  editable
                  multiline={node.widget === 'textarea-list'}
                  entryMode={node.editEntry}
                  // 접기는 항목 단위 표시 상태(click 진입)에만 — 편집 중 항목은 컨트롤이 전문.
                  collapseLines={collapseLinesFor(node)}
                  suggest={listSuggest}
                  suggestUnavailable={listSuggestUnavailable}
                  // 캔버스는 "+ 항목 추가" 자동 포커스를 끈다(저작 중 포커스 스틸 방지).
                  authoringCanvas={suggest?.authoringCanvas === true}
                  onChange={(next) => onChange(node.bindingKey, next)}
                />
              </div>
            );
            // enabledWhen 비활성 — 항목 추가/삭제 버튼이 있는 복합 위젯이라 개별 컨트롤 disabled
            // 대신 서브트리 통째 반투명+포인터 차단(sortableSelect 규약 동형, 값·목록 표시는 유지).
            return disabled ? (
              <div key={node.id} style={DISABLED_SUBTREE_STYLE} aria-disabled="true">
                {listEl}
              </div>
            ) : (
              listEl
            );
          }

          case 'sortableSelect': {
            // 선택 정렬 목록 — 후보 rows(optionsSource)를 체크 선택 + 드래그 정렬 → 조립 텍스트.
            // dnd state는 별도 컴포넌트(SortableSelectWidget)가 소유. readonly도 그 컴포넌트가 처리.
            const ss = (
              <SortableSelectWidget
                node={node}
                value={value}
                rows={resolveNormalizedSortableRows(node, values ?? {}, dataDefs, resolvedRows)}
                onChange={onChange}
                labelEl={labelEl}
                domIdSuffix={domIdSuffix}
              />
            );
            // enabledWhen 비활성 — dnd 복합 위젯이라 개별 컨트롤 disabled 대신 서브트리 통째
            // 반투명+포인터 차단(레이아웃 중립 block 래퍼, 값·목록 표시는 유지).
            return disabled ? (
              <div key={node.id} style={DISABLED_SUBTREE_STYLE} aria-disabled="true">
                {ss}
              </div>
            ) : (
              ss
            );
          }

          case 'combobox':
            // 제안형 콤보 — 자유 텍스트 입력 + 후보 rows(sortableSelect와 동일 소스 축) 부분일치 제안.
            // 값=string. 제안 선택 시에만 writes 전파(자유 타이핑은 전파 없음 — shared 규약).
            // 결정: 2026-07-16-composer-combobox-dependent-value-axes (P1·P2).
            return (
              <ComboboxWidget
                key={node.id}
                node={node}
                value={value}
                rows={resolveNormalizedSortableRows(node, values ?? {}, dataDefs, resolvedRows)}
                values={values ?? {}}
                onChange={onChange}
                labelEl={labelEl}
                fieldWrapClass={fieldWrapClass}
                controlStyle={controlStyle}
                inputId={inputId}
                disabled={disabled}
                onOpenModalRequest={onOpenModalRequest}
              />
            );

          case 'image': {
            // 읽기 전용 이미지 표시 — bindingKey 값(URL 문자열)을 이미지로 표시(입력 아님, mode 무관).
            // 값 없으면 회색 placeholder 박스(구 SMS 이행상태 카드 "사진 없음" 동형). host 치환 채널이
            // 작성폼 값에는 없어 값을 그대로 src로 사용(rc 도메인 무관 — 절대/상대 URL은 호스트가 정합).
            // rc 패키지 규약: 임계 시각값은 인라인 style(앱 Tailwind JIT 미생성 회피).
            const imageUrl = typeof value === 'string' ? value.trim() : '';
            return (
              <div key={node.id}>
                {labelEl}
                {readonlyImageNode(imageUrl, node.label ?? '', node.placeholder)}
              </div>
            );
          }

          case 'annotated-image':
            // 주석 이미지 표시 — 값이 { url, shapes, strokes } 객체면 오버레이, 문자열(URL)이면
            // 평면 이미지(image 하위호환), 빈 값이면 placeholder 박스. image와 동일하게 입력 아님.
            return (
              <div key={node.id}>
                {labelEl}
                <AnnotatedImageView
                  value={value}
                  alt={node.label ?? ''}
                  placeholder={node.placeholder}
                />
              </div>
            );

          case 'signature':
            // 서명 슬롯 칸 — 값(서명 이미지)이 있으면 그리고, 없으면 빈 서명 박스. 입력 아님
            // (mode 무관 표시 전용) — 서명은 이 칸에 타이핑하는 값이 아니라 별도로 걷는 증빙이라
            // content를 편집하지 않는다.
            //
            // P2: 값·상호작용(탭→서명 패드·지우기)은 resolved 채널(signatureSlot — 트리 경로가
            // signatureSlotViewProps로 해소)에서 온다. content 행 값(value)은 읽지 않는다(§3-2 —
            // 작성자 자유 편집 데이터가 증빙으로 보이면 안 된다). 미전달(flat 레거시·캔버스·반복 밖)
            // 이면 표시 전용 빈 칸.
            //
            // 서명자 이름은 **행 스코프 values**에서 읽는다: 반복(repeat) 안 children은 그 행 객체를
            // values로 받으므로 signerNameField가 곧 그 행의 이름 칸이다. 반복 밖(행 없는 자리)에
            // 놓이면 그 키가 없어 undefined — 이름 없는 "서명" 칸이 되고, 그게 잘못된 이름을 찍는
            // 것보다 낫다.
            return (
              <div key={node.id}>
                {labelEl}
                <SignatureSlotView
                  value={signatureSlot?.value}
                  signerName={signatureSlot?.signerName ?? rowSignerName(values, node)}
                  placeholder={node.placeholder}
                  onRequestSign={signatureSlot?.onRequestSign}
                  onRequestDelete={signatureSlot?.onRequestDelete}
                />
              </div>
            );

          case 'photo': {
            // 사진 업로드 입력 — 값=string(URL), 빈 값 ''. readonly는 image 위젯 동형 표시
            // (READONLY_SELF_RENDER_WIDGETS로 조기 텍스트 분기를 우회해 여기 도달).
            const photoUrl = typeof value === 'string' ? value.trim() : '';
            if (node.mode === 'readonly') {
              // image 위젯 동형 표시 — 한 벌 헬퍼(readonlyImageNode) 공유(축자 복사 수렴, W8).
              return (
                <div key={node.id}>
                  {labelEl}
                  {readonlyImageNode(photoUrl, node.label ?? '', node.placeholder)}
                </div>
              );
            }
            // 캔버스(저작)는 업로드 콜백 미주입이 규약(저작 화면은 실행 안 함)이지만 모양은 실폼과
            // 같아야 한다(WYSIWYG — suggest.authoringCanvas 표식 동형). 그 외 미배선 호스트
            // (external 공유 등)는 저장된 값(URL)이 있으면 readonly 이미지로 표시(증빙이 가려지면
            // 안 된다 — W3), 빈 값일 때만 안내 placeholder.
            const canvasInert = suggest?.authoringCanvas === true;
            if (!uploadPhoto && !canvasInert) {
              return (
                <div key={node.id}>
                  {labelEl}
                  {photoUrl !== '' ? (
                    readonlyImageNode(photoUrl, node.label ?? '')
                  ) : (
                    <p className="text-xs text-gray-400 px-3 py-2 border border-dashed border-gray-300 rounded-card">
                      사진 업로드는 내부 작성 화면에서 할 수 있습니다.
                    </p>
                  )}
                </div>
              );
            }
            return (
              <PhotoUploadWidget
                key={node.id}
                value={value}
                alt={node.label ?? ''}
                onValueChange={(next) => onChange(node.bindingKey, next)}
                labelEl={labelEl}
                uploadPhoto={uploadPhoto}
                disabled={disabled}
                authoringCanvas={canvasInert}
              />
            );
          }

          case 'aiTrigger':
            // 최소 슬라이스 미지원: 안내만 표시, 입력 무시
            return (
              <div key={node.id}>
                {labelEl}
                <p className="text-xs text-gray-400 px-3 py-2 border border-dashed border-gray-300 rounded-card">
                  AI 생성은 추후 지원 예정입니다.
                </p>
              </div>
            );

          default:
            return null;
        }
}

/**
 * 라벨 미표시 + required일 때 컨트롤 우상단에 얹는 `*` 배지 — **레이아웃 중립**이 규약이다.
 * absolute 오버레이라 흐름에서 폭·높이를 차지하지 않고(래퍼는 position:relative만 얹는 블록),
 * 포인터 이벤트도 통과시켜 컨트롤 클릭 히트 영역을 갉지 않는다(rc 인라인 style 규약 — 새 Tailwind
 * 유틸은 앱 CSS에 미생성될 수 있어 임계 시각값은 클래스로 두지 않는다. 색만 기존 라벨 `*`와 같은
 * `text-red-500`을 재사용 — 앱에 이미 생성된 클래스다).
 */
const REQUIRED_BADGE_WRAP_STYLE: CSSProperties = { position: 'relative', display: 'block' };
const REQUIRED_BADGE_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  fontSize: 12,
  lineHeight: 1,
  pointerEvents: 'none',
};

/**
 * required `*` 오버레이 배지 부착 대상 판정 — **라벨 부재 컨텍스트 보전**의 단일 판정 지점.
 *
 * 왜 필요한가: `*` 표식은 라벨 요소 **안에만** 있어서 `labelPlacement:'none'`이면 화면에서 통째로
 * 사라진다(required 검증·제출 게이트는 그대로라, 사용자에겐 "왜 막히는지 안 보이는" 칸이 된다).
 *
 * 대상은 **`labelPlacement:'none'`뿐**이다:
 * - 'top'/'side'는 라벨 안 `*`가 그대로 있으므로 미부착(이중 표식 금지).
 * - repeat 표형 셀 사본은 'none'을 쓰지만 **REPEAT_TABLE_CELL_SUPPRESSED 등록으로 제외** — 열
 *   헤더의 `*`(repeatTableColumnRequired)가 그 역할을 대신한다(이중 표식 금지).
 * - formTable 고정 셀 `suppressed`는 `label: undefined` 스프레드라 'none'이 아니어서 애초에 이
 *   조건에 걸리지 않는다(그쪽 헤더는 저작자 소유 텍스트 셀이라 자동 병기 대상이 아니다).
 */
function shouldShowRequiredBadge(node: InputWidget): boolean {
  return (
    isLabelHidden(node) && node.required === true && !REPEAT_TABLE_CELL_SUPPRESSED.has(node)
  );
}

/**
 * required `*` 오버레이 배지 래퍼 — shouldShowRequiredBadge가 참인 노드의 렌더 결과를 감싼다.
 * 래퍼는 div(내용이 블록 요소라 span으로 감싸면 HTML 유효성 위반) + relative만 얹는 레이아웃 중립.
 * 부착 지점은 renderInner case 'input'의 3분기(registered/dataSourceRows/일반) **공통 합성 지점**
 * — 위젯 case가 20종 넘어 각 case에 복붙하면 새 위젯마다 누락되고, renderWriteNode 안에만 두면
 * host 소유 렌더(registered·dataSourceRows)가 빠진다(2026-08-24 이동). 단일 위젯 직접 렌더
 * (`renderWriteNode` — 지금은 rc 유닛 spec 전용 진입점)는 그 래퍼가 같은 판정을 자체 적용한다.
 */
function withRequiredBadge(node: InputWidget, el: ReactNode): ReactNode {
  if (el == null || !shouldShowRequiredBadge(node)) return el;
  return (
    <div key={node.id} style={REQUIRED_BADGE_WRAP_STYLE}>
      {el}
      <span
        data-required-badge=""
        className="text-red-500"
        style={REQUIRED_BADGE_STYLE}
        aria-hidden="true"
      >
        *
      </span>
    </div>
  );
}

/**
 * 위젯 루트에 래퍼 앵커(`data-write-field`)를 얹는다 — required 미입력 「그 칸으로 데려가기」의
 * 2차 채널(WRITE_FIELD_ANCHOR_ATTR doc 참조). **무조건**이다(required 여부·배지 여부와 무관) —
 * 조건부로 두면 「required가 아니었다가 켜진」 칸에서 앵커가 없어진다.
 *
 * 새 DOM 요소를 만들지 않는다: 이미 있는 루트에 `cloneElement`로 속성만 더한다. rc는 캔버스↔실폼
 * WYSIWYG가 계약이라 상시 래퍼 div 하나가 grid 행·flex gap을 만들 수 있고(2026-07-14 제보 계열),
 * `display:contents` 래퍼는 박스가 없어 `scrollIntoView`가 죽는다.
 *
 * ⚠️ 루트가 **DOM 요소일 때만** 붙는다(`typeof el.type === 'string'`). 컴포넌트 루트에 넘기면 쓰이지
 * 않는 prop이 되어 조용히 사라지므로, 자기 DOM 루트를 소유하는 위젯 컴포넌트는 루트에
 * `writeFieldAnchorProps(...)`를 **직접** 스프레드해야 한다(현재 `SortableSelectWidget` 한 곳 —
 * `ComboboxWidget`은 컨트롤에 `inputId`를 쥐어 1차 채널로 이미 도달한다). 그 누락은
 * `writeFieldAnchorCensus.test.tsx`가 위젯 종류 전수로 지킨다.
 *
 * 같은 값의 앵커가 둘 생기는 경우(enabledWhen 비활성 래퍼 + SortableSelectWidget 자기 루트)는
 * 무해하다 — `querySelector`가 문서 순서상 **바깥**을 잡고 그 바깥이 위젯을 감싸므로 스크롤·하강
 * 결과가 같다. `id`와 달리 data 속성은 중복이 다른 소비처를 깨지 않는다.
 */
function withWriteFieldAnchor(
  node: InputWidget,
  el: ReactNode,
  domIdSuffix?: string,
): ReactNode {
  // 타입 인자를 명시한다 — 생략하면 `ReactElement<unknown>`으로 좁혀져 `Partial<unknown>`(= `{}`)이
  // props 타입이 되고, 그러면 data 속성 한 벌을 넘기는 것이 타입 층에서 애매해진다.
  if (!isValidElement<Record<string, unknown>>(el) || typeof el.type !== 'string') return el;
  return cloneElement(el, writeFieldAnchorProps(node.id, domIdSuffix));
}

/**
 * 위젯 렌더 + 래퍼 앵커 — 트리 경로(renderInner case 'input')와 flat 레거시 경로가 **둘 다** 이
 * 함수를 부르므로 앵커 부착 지점이 하나로 모인다(호출부는 그 둘뿐).
 *
 * ⚠️ 앵커를 `renderInner`의 3분기 공통 합성 지점(withRequiredBadge 부착 자리)에 두면 host 소유 렌더
 * (`registered`·`dataSourceRows`)에도 앵커가 생긴다 — 그 둘은 자기 DOM을 소유해 앵커 축의 주인이
 * 다르고(census EXEMPT) 이 함수에는 애초에 도달하지 않는다. 그래서 부착은 여기가 맞다.
 */
function renderWriteNodeCore(...args: Parameters<typeof renderWriteNodeControl>): ReactNode {
  // domIdSuffix는 renderWriteNodeControl의 14번째 인자(0-based 13) — 위 시그니처 참조.
  //
  // ⚠️ **이 매직 인덱스의 탐지기는 실재한다 — 지우거나 「무방비」로 읽지 말 것**(2026-10-05 리뷰에서
  // 「같은 타입 인자가 중간에 끼면 tsc가 조용하고 census는 suffix 없이 렌더하므로 탐지기가 없다」로
  // 지적됐으나, 확인해 보니 그 축의 소유자가 있다):
  //   `__tests__/writeFieldAnchorCensus.test.tsx` describe
  //   「래퍼 앵커의 행 인스턴스 스코프 — domIdSuffix가 앵커까지 닿는다」
  // 그 `[되돌림]` 케이스가 repeat 2행 × radio로 렌더해 앵커 값을
  // `['wf-w-top-rd','wf-rp-rd','wf-w-rd--r1','wf-w-rd--r2']`로 못박고, 주석이 **「위치 인덱스가
  // 어긋나는 경우 포함」**을 탐지 대상으로 명시한다 — 인덱스가 밀리면 행 두 값이 접미를 잃어 red다.
  // (census의 종류 루프 `measure()`는 suffix 없이 렌더하는 것이 맞다 — 그 describe가 별도 축이다.
  //  한 자리만 보고 「탐지기 없음」으로 읽은 것이 그 지적의 경로였다.)
  //
  // 꼬리 인자를 옵션 객체로 모으는 리팩터는 **하지 않았다**(2026-10-05 판단). 근거:
  //   · 호출부가 19곳이고 꼬리(index ≥ 11)에 닿는 것이 6곳(트리 경로 1 + rc 유닛 spec 4 +
  //     RowsCollectionEditor 1) — 작지 않다. `renderWriteNodeControl`은 1,600줄이고 그 안
  //     `domIdSuffix` 참조가 27곳이다.
  //   · 위 탐지기가 이미 그 하자(인덱스 어긋남)를 red로 잡으므로 남는 이득은 **가독성**뿐인데,
  //     여기는 캔버스↔실폼 WYSIWYG 계약을 쥔 렌더 코어다. 조용한 인자 오배선의 대가는
  //     「사진 업로드·서명 슬롯이 말없이 사라지는」 종류다.
  //   · worktree에 node_modules가 없어 tsc·vitest를 **한 번도 돌릴 수 없었다** — 검증 0으로
  //     그 규모를 건드리는 쪽이 비용이 크다.
  // 되짚을 조건: 이 꼬리에 인자를 **더** 달아야 할 때. 그때는 같은 커밋에서 옵션 객체로 모으고,
  // 위 census describe를 그 변경의 게이트로 쓸 것(그것이 인덱스 축을 쥐고 있다).
  return withWriteFieldAnchor(args[0], renderWriteNodeControl(...args), args[13]);
}

/**
 * 단일 InputWidget 렌더 **공개 진입점** — 본체(renderWriteNodeCore) 결과에 라벨 부재 컨텍스트
 * 보전(required `*` 배지 — shouldShowRequiredBadge/withRequiredBadge doc)을 얹는다.
 * ⚠️ 트리 경로(renderInner case 'input')는 이 래퍼가 아니라 renderWriteNodeCore를 직접 부르고
 * 3분기 공통 합성 지점에서 withRequiredBadge를 얹는다(이중 부착 금지).
 *
 * ⚠️ 2026-09-30(S3-b) 이후 이 래퍼의 소비처는 **rc 유닛 spec뿐**이다(flat 조립 렌더러가 사라져
 * 실폼·캔버스는 전부 트리 경로를 탄다). 죽은 코드가 아니라 **위젯 한 개를 트리 없이 재는 진입점**이
 * 그 소유자다(widget별 spec 14파일) — 지우면 그 spec들이 트리 골격을 지어야 하고, 그러면 위젯 렌더
 * 계약과 트리 배치 계약이 한 케이스에 섞인다. 새 host가 이걸 부르려 하면 트리 경로를 쓸 것.
 */
export function renderWriteNode(...args: Parameters<typeof renderWriteNodeCore>): ReactNode {
  return withRequiredBadge(args[0], renderWriteNodeCore(...args));
}

// ── 작성 페이지 위젯 트리 렌더러 (composerWidgetTree 소비) ──
// group 컨테이너 단 수(columns)를 반응형 grid로 표현 — 데스크톱은 N단, 좁은 뷰(바형/폴드형)는
// md 브레이크포인트 미만에서 자동 1단(Tailwind `md:grid-cols-*` degrade). heading/separator/spacer는
// 그리드에서 전 단 span. input은 renderWriteNode(위젯별 입력) 재사용.
// 결정: 2026-07-08-composer-writeform-editing-structure §3(반응형=렌더러 책임)

/** group columns → 반응형 grid 클래스(리터럴이라야 Tailwind가 스캔). 1/미지정=세로 스택. */
export const GROUP_GRID_CLASS: Record<number, string> = {
  2: 'grid grid-cols-1 gap-4 md:grid-cols-2',
  3: 'grid grid-cols-1 gap-4 md:grid-cols-3',
};

/**
 * group columns → **고정** grid 클래스(뷰별 명시 해소용, md: 자동 degrade 없음).
 * 저작 캔버스가 활성 뷰(`view`)로 단 수를 이미 해소하므로 CSS 브레이크포인트 재-degrade를 막는다.
 */
export const GROUP_GRID_FIXED_CLASS: Record<number, string> = {
  2: 'grid grid-cols-2 gap-4',
  3: 'grid grid-cols-3 gap-4',
};

/**
 * 활성 뷰의 그룹 단 수 해소 — `override[view].columns ?? base(위젯 직접 columns) ?? 1`.
 * base 뷰(또는 view 미지정)는 위젯 직접 columns. 결정 2026-07-09-composer-responsive-view-overrides §2.
 */
export function resolveGroupColumns(w: GroupWidget, view?: string): number {
  const base = Math.max(1, Math.floor(w.columns ?? 1));
  if (!view || view === COMPOSER_BASE_VIEW_KEY) return base;
  const override = w.responsive?.[view]?.columns;
  if (override != null) return Math.max(1, Math.floor(override));
  // 오버라이드 없으면: 좁은 뷰(md 미만)는 자동 1단(구 md: degrade 유지), 넓은 뷰는 base 상속.
  const def = COMPOSER_VIEWS.find((v) => v.key === view);
  if (def?.authoringWidthPx != null && def.authoringWidthPx < MD_BREAKPOINT_PX) return 1;
  return base;
}

/**
 * span 원값(base 또는 뷰 델타)을 **비율**로 정규화 — 미지정/해제는 undefined.
 * `'auto'`=명시적 해제(미지정과 같은 폭 산출), 0 이하·NaN=무효 취급, 1 초과는 1로 clamp.
 * 옛 저작물 호환: DB JSON에 남아 있을 수 있는 문자열 `'full'`을 1로 읽는다(타입은 이미 숫자축이라
 * 캐스트로만 닿는다 — 저장 경로는 숫자만 쓴다).
 */
function normalizeSpanRatio(raw: number | 'auto' | undefined): number | undefined {
  if (raw == null) return undefined;
  // 문자열 가지: 'auto'(명시적 해제)는 미지정과 같은 산출이고, 타입에서는 이미 사라졌지만 DB JSON에
  // 남아 있을 수 있는 옛 값 'full'만 1로 되살린다(저장 경로는 숫자만 쓴다).
  if (typeof raw !== 'number') return (raw as string) === 'full' ? 1 : undefined;
  if (!Number.isFinite(raw) || raw <= 0) return undefined;
  return Math.min(1, raw);
}

/**
 * 활성 뷰의 span **비율** 해소 — `override[view].span ?? base(w.span)`을 비율로 정규화.
 * 반환 undefined=미지정(줄 안 자동 산출 대상) / 1=줄 전체 / 그 사이=그 비율.
 * override='auto'면 base가 지정돼 있어도 이 뷰만 미지정으로 해제. 결정 §3.
 */
export function resolveWidgetSpanRatio(w: ComposerWidget, view?: string): number | undefined {
  const overridden =
    view && view !== COMPOSER_BASE_VIEW_KEY ? w.responsive?.[view]?.span : undefined;
  return normalizeSpanRatio(overridden ?? w.span);
}

/**
 * 활성 뷰에서 이 위젯이 grid 전체 폭인지 — 해소 비율이 1(구 `'full'`)인지.
 * 비율(0<r<1)은 전폭이 아니므로 false — 비율 배치는 행 컨테이너 경로(computeGroupRatioRowLayout)가
 * 전담하고, 이 함수는 "혼자 한 줄" 판정만 책임진다. 결정 §3.
 */
export function resolveWidgetSpanFull(w: ComposerWidget, view?: string): boolean {
  return resolveWidgetSpanRatio(w, view) === 1;
}

/**
 * 활성 뷰 **최종 전폭 판정** — 다단 섹션 안에서는 **단(grid)이 우선**: 모든 위젯이 기본 1칸
 * (자기 칸의 폭을 가득 채움)이고, 행 전체를 가로지르려면 `span:1`(구 `'full'`)을 명시해야 한다.
 * (2026-07-15 사용자 결정: "섹션 안에서는 단 우선 — 2단이면 절반 영역을 채우는 게 전폭".
 * 과거엔 디자인(텍스트·구분선·여백)·표 위젯이 무조건 행 전체를 강제했으나, 입력 위젯과
 * 비일관 + 단 설정을 위젯이 깨는 모델이라 폐기 — dev DB·코드 시드 전수 스캔으로 다단 섹션 안
 * 디자인 위젯 사용 0건 확인, 회귀 없음.) 전폭 판정은 전부 이 함수로 일원화.
 */
export function resolveWidgetFullWidth(w: ComposerWidget, view?: string): boolean {
  return resolveWidgetSpanRatio(w, view) === 1;
}

/** 활성 뷰에서 이 위젯이 숨김인지 — 비-base 뷰의 responsive[view].hidden만(base는 글로벌 mode 사용). */
export function resolveWidgetHidden(w: ComposerWidget, view?: string): boolean {
  return !!(view && view !== COMPOSER_BASE_VIEW_KEY && w.responsive?.[view]?.hidden);
}

/**
 * visibleWhen 억제(표시 조건 미충족) 판정 — 지정 키의 현재 스코프 값이 비어 있으면 true
 * (shared 규약: `!isEmptyConditionValue(values[visibleWhen])`이면 표시 — null/undefined/''에 더해
 * **빈 배열**도 빈값. 데이터 표 키를 조건 대상으로 쓰면 "행 없으면 숨김"이 성립). 자식 렌더
 * (실폼 null/캔버스 반투명)와 formTable 셀·fillLastRow 흐름 계산이 같은 판정을 공유하는 단일 진실 헬퍼.
 * 결정: 2026-07-16-composer-combobox-dependent-value-axes (P3).
 */
export function isWidgetVisSuppressed(
  w: ComposerWidget,
  values: Record<string, unknown>,
): boolean {
  return w.visibleWhen !== undefined && isEmptyConditionValue(values[w.visibleWhen]);
}

/**
 * enabledWhen 비활성(활성 조건 미충족) 판정 — 지정 키의 현재 스코프 값이 비어 있으면 true
 * (shared 규약: `!isEmptyConditionValue(values[enabledWhen])`이면 활성 — 빈 배열 포함).
 * visibleWhen(isWidgetVisSuppressed)과
 * 평가 규약 동일·표현만 다름(숨김 vs 컨트롤 disabled — 값은 유지, 구 SMS 장비명 비면 측정 필드
 * 회색 비활성 동형). 렌더(컨트롤 disabled)와 required 검증 제외가 공유하는 단일 진실 헬퍼라
 * ComposerWidget 외 구조 타입(requiredValidation checkInput)도 받게 필드 시그니처로 둔다.
 */
export function isWidgetDisabledByCondition(
  w: { enabledWhen?: string },
  values: Record<string, unknown>,
): boolean {
  return w.enabledWhen !== undefined && isEmptyConditionValue(values[w.enabledWhen]);
}

/**
 * 실폼(비 interactive)에서 이 위젯이 visibleWhen 미충족으로 **자리째 미렌더**되는지 —
 * 억제이면서 자리 유지(visibleWhenReserveSpace) 아님. renderWidget의 null 반환과
 * computeGroupFillLastRowLayout의 흐름 제외(isSuppressed 프레디킷)가 같은 함수를 써
 * 렌더↔흐름이 항상 정합한다(자리 유지 위젯은 실제로 그리드 슬롯을 점유하므로 흐름 포함 유지).
 * 뷰별 hidden(responsive)은 별개 축 — 각 소비처에서 resolveWidgetHidden이 선행 처리.
 */
export function isWidgetFlowRemovedInLiveForm(
  w: ComposerWidget,
  values: Record<string, unknown>,
): boolean {
  // visibleWhenHint가 있으면 실폼에서 게이트 블록(라벨+안내 박스)을 렌더하므로 위젯은 흐름에 남는다
  // (자리 유지 옵션과 동형 — 렌더↔흐름 정합을 위해 여기서도 제거 아님으로 판정).
  return (
    isWidgetVisSuppressed(w, values) &&
    !w.visibleWhenReserveSpace &&
    w.visibleWhenHint === undefined
  );
}

/**
 * visibleWhen 미충족 + visibleWhenHint 지정 시 실폼에서 위젯 컨트롤 대신 렌더하는 게이트 블록 —
 * "위젯 라벨(제목) + 회색 안내 박스(hint 문구)"(구 SMS 개선 후 위험도 게이트 동형). 라벨은
 * titleVariant 무관 plain 필드 라벨 스타일(group→title, input→label). rc 전용 Tailwind 유틸
 * 금지 규약에 따라 시각 값은 인라인 style.
 */
function renderVisHintGate(w: ComposerWidget): ReactNode {
  const label = w.kind === 'group' ? w.title : w.kind === 'input' ? w.label : undefined;
  return (
    <div>
      {label ? (
        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: 4 }}>
          {label}
        </div>
      ) : null}
      <div
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: '8px 12px',
          background: '#f9fafb',
          fontSize: '0.875rem',
          color: '#9ca3af',
        }}
      >
        {w.visibleWhenHint}
      </div>
    </div>
  );
}

/** grid 안 전체 폭 클래스 — design 위젯(항상) 또는 span=full 해소. 뷰 지정 시 고정 col-span, 아니면 md:. */
function widgetSpanClass(w: ComposerWidget, gridded: boolean, view?: string): string {
  if (!gridded) return '';
  const full = resolveWidgetFullWidth(w, view);
  if (!full) return '';
  return view ? 'col-span-full' : 'md:col-span-full';
}

export function isComposerDesignWidget(w: ComposerWidget): boolean {
  return w.kind === 'heading' || w.kind === 'separator' || w.kind === 'spacer';
}

/** 섹션 마지막 행 채우기 레이아웃(LCM 스킴 산출값). null=미적용(기존 클래스 경로 유지). */
export interface GroupFillLastRowLayout {
  /** gridTemplateColumns repeat 인자 = lcm(cols, L). */
  totalCols: number;
  /** 일반 아이템 span = totalCols/cols. */
  normalSpan: number;
  /** 마지막 행 아이템 span = totalCols/L(균등 분할). */
  lastRowSpan: number;
  /** 마지막 행에 속한(균등 분할될) 자식의 ordered-index 집합. */
  lastRowIndexes: Set<number>;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}
function lcm(a: number, b: number): number {
  return (a / gcd(a, b)) * b;
}

/**
 * 섹션 그리드 마지막 행 채우기 레이아웃 계산(순수). 보이는 자식(뷰별 hidden 제외)을 좌→우 그리드
 * 흐름으로 시뮬레이션한다. 전폭 아이템(design 위젯 또는 span=full 해소)은 단독 행(컬럼 흐름 리셋),
 * 일반 아이템은 1칸. **마지막 행이 일반 아이템 L개(0<L<cols)** 일 때만 채움 대상 → LCM 스킴 산출.
 * 반환 null = 채움 미적용(마지막 행이 전폭 아이템·정확히 채워짐(L===cols)·비어 있음, 또는 cols<=1).
 * (dropSlot은 자식 배열에 없으므로 흐름 계산에서 자연 제외.)
 */
export function computeGroupFillLastRowLayout(
  children: ComposerWidget[],
  cols: number,
  view: string | undefined,
  /**
   * true인 자식을 흐름에서 제외(뷰별 숨김과 동일 취급) — 실폼의 visibleWhen 미렌더 반영용
   * (호출부가 isWidgetFlowRemovedInLiveForm으로 구성). 순수성 유지를 위해 values 대신 프레디킷.
   * 미전달=기존 동작(캔버스 — 억제 위젯도 반투명으로 슬롯을 점유하므로 흐름 포함).
   */
  isSuppressed?: (child: ComposerWidget) => boolean,
): GroupFillLastRowLayout | null {
  if (cols <= 1) return null;
  // 각 행을 자식 ordered-index로 수집. 전폭 아이템은 단독 행(full=true).
  const rows: { indexes: number[]; full: boolean }[] = [];
  let current: number[] = [];
  const flush = (): void => {
    if (current.length > 0) {
      rows.push({ indexes: current, full: false });
      current = [];
    }
  };
  children.forEach((child, index) => {
    if (resolveWidgetHidden(child, view)) return; // 뷰별 숨김은 흐름에서 제외
    if (isSuppressed?.(child)) return; // 실폼 미렌더(visibleWhen 미충족·자리 유지 없음)도 제외
    if (resolveWidgetFullWidth(child, view)) {
      flush();
      rows.push({ indexes: [index], full: true });
      return;
    }
    current.push(index);
    if (current.length === cols) flush();
  });
  flush();
  if (rows.length === 0) return null;
  const last = rows[rows.length - 1];
  if (last.full) return null; // 마지막이 전폭 아이템 = 이미 전폭
  const L = last.indexes.length;
  if (L <= 0 || L >= cols) return null; // 정확히 채워짐/비어 있음 = 채움 불필요
  const totalCols = lcm(cols, L);
  return {
    totalCols,
    normalSpan: totalCols / cols,
    lastRowSpan: totalCols / L,
    lastRowIndexes: new Set(last.indexes),
  };
}

/**
 * 채움 없는 **순수 N단 grid** 레이아웃(GroupFillLastRowLayout 재사용). 클래스 맵(GROUP_GRID_CLASS,
 * 2·3단만)에 없는 cols(4~6단)를 rc 전용 Tailwind 유틸 없이 렌더하기 위한 것 — 앱 CSS에 `md:grid-cols-4/5/6`
 * 가 없어 클래스로 쓰면 web 실폼에서 조용히 무스타일이 되므로(rc purge 함정), 채움 스킴과 동일한
 * 인라인/`<style>` 주입 grid 경로(gridContainer)를 그대로 탄다. 마지막 행 균등 분할 없이(lastRowIndexes 공백)
 * 각 아이템 1칸(normalSpan=1), 전폭 아이템(design/span=full)만 fillChildGridColumn에서 1/-1.
 */
export function computePlainGridLayout(cols: number): GroupFillLastRowLayout {
  return {
    totalCols: cols,
    normalSpan: 1,
    lastRowSpan: 1,
    lastRowIndexes: new Set(),
  };
}

/**
 * 채움 스킴 활성 시 자식의 인라인 `grid-column` 값 산출.
 * 마지막 행 아이템 → 균등 분할(span lastRowSpan) / 전폭·숨김(캔버스 dimmed) → 전폭(1/-1) / 그 외 → span normalSpan.
 * (숨김은 흐름 계산에서 제외됐으므로 캔버스에서 전폭 단독 행으로 표시해 보이는 아이템 배치를 침범하지 않음.)
 */
function fillChildGridColumn(
  w: ComposerWidget,
  index: number,
  layout: GroupFillLastRowLayout,
  view: string | undefined,
): string {
  if (resolveWidgetHidden(w, view)) return '1 / -1';
  if (layout.lastRowIndexes.has(index)) return `span ${layout.lastRowSpan}`;
  if (resolveWidgetFullWidth(w, view)) return '1 / -1';
  return `span ${layout.normalSpan}`;
}

/** 비율 배치의 한 행 — indexes[i]의 폭 몫이 tracks[i]. filler>0이면 우측에 남기는 빈 트랙. */
export interface ComposerRatioRow {
  /** 이 행에 속한 자식의 ordered-index(좌→우). */
  indexes: number[];
  /** indexes와 같은 길이의 폭 몫(fr 값으로 그대로 방출 — 합이 1을 넘으면 fr이 정규화한다). */
  tracks: number[];
  /** 우측 잔여 몫(0=없음). 줄을 다 쓰지 않는 행에서 오른쪽을 비워 두기 위한 트랙. */
  filler: number;
}

/** 비율 배치 레이아웃 — 행 컨테이너 경로(각 행이 자기 grid-template-columns를 가진다). */
export interface ComposerRatioRowLayout {
  rows: ComposerRatioRow[];
}

/**
 * 섹션 안 **비율 배치**(span이 숫자로 지정된 위젯이 하나라도 있을 때) 행 분할 계산(순수).
 *
 * 반환 null = 비율 지정이 없음 → 호출부는 기존 경로(클래스 grid / LCM 채움)를 그대로 탄다(회귀 0).
 *
 * 행 경계는 **기존과 동일하게 columns가 끊는다**(cols개마다 새 행) + 비율 1(구 'full')은 단독 행.
 * 각 행의 폭 분배:
 *  - 그 행이 줄을 **채우는 행**(자식이 정확히 cols개 = 꽉 찬 행 / 또는 마지막 행이면서 fillLastRow)
 *    이면 미지정 위젯들이 **남은 폭(1 - 지정합)을 균등 분할**한다(3단 `[0.5,-,-]` → 0.5/0.25/0.25,
 *    전부 미지정이면 1/cols씩 = 현행과 동일).
 *  - 채우지 않는 행(마지막에 cols보다 적게 남았고 fillLastRow 아님 / 전폭 위젯 앞에서 끊긴 행)은
 *    미지정이 **자기 칸 몫(1/cols)**을 쓰고 남는 폭은 filler로 비워 둔다(현행 배치와 동일).
 *  - 지정합이 1 이상이라 남은 폭이 없으면 미지정도 1/cols를 청구한 뒤 fr 정규화로 함께 축소된다
 *    (0으로 눌러 사라지게 하지 않는다).
 *
 * 흐름 제외 규약은 computeGroupFillLastRowLayout과 동일: isSuppressed 전달=실폼(그 자식은 미렌더라
 * 행에서도 제외 + 뷰 숨김도 제외), 미전달=저작 캔버스(뷰 숨김 자식은 dimmed로 남으므로 **단독 행**
 * — 현행 스킴이 숨김 자식에 `1 / -1`을 주던 것과 같은 배치).
 */
export function computeGroupRatioRowLayout(
  children: ComposerWidget[],
  cols: number,
  view: string | undefined,
  isSuppressed?: (child: ComposerWidget) => boolean,
  fillLastRow?: boolean,
): ComposerRatioRowLayout | null {
  if (cols <= 1) return null;
  // 비율(0<r<1) 지정이 하나도 없으면 기존 경로로 — 1(전폭)만 있는 섹션은 현행 스킴이 이미 정확하다.
  const hasRatio = children.some((c) => {
    const r = resolveWidgetSpanRatio(c, view);
    return r !== undefined && r < 1;
  });
  if (!hasRatio) return null;

  // 1단계: 행 경계만 수집(폭 분배는 마지막 행 여부가 정해진 뒤에).
  const rowIndexes: number[][] = [];
  let current: number[] = [];
  const flush = (): void => {
    if (current.length > 0) {
      rowIndexes.push(current);
      current = [];
    }
  };
  children.forEach((child, index) => {
    const hidden = resolveWidgetHidden(child, view);
    if (hidden || isSuppressed?.(child)) {
      // 실폼: 미렌더라 행 자체를 만들지 않는다(빈 행 컨테이너가 남으면 gap이 한 칸 생긴다).
      if (isSuppressed) return;
      flush();
      rowIndexes.push([index]); // 캔버스: dimmed 자식은 단독 행(이웃 배치 불침범)
      return;
    }
    if (resolveWidgetSpanRatio(child, view) === 1) {
      flush();
      rowIndexes.push([index]);
      return;
    }
    current.push(index);
    if (current.length === cols) flush();
  });
  flush();
  if (rowIndexes.length === 0) return null;

  // 2단계: 행별 폭 분배.
  const rows = rowIndexes.map((indexes, rowPos) => {
    const isLast = rowPos === rowIndexes.length - 1;
    // 캔버스 dimmed 단독 행(위 flush 분기) — 채움 스킴(fillChildGridColumn)이 숨김 자식에 주는
    // `1 / -1`과 같은 **전폭**이다. 일반 분배로 흘리면 1/cols+filler가 되어 두 스킴의 캔버스
    // 표시가 갈린다(지정 비율도 무시 — 숨김 표시는 배치 침범 없음이 목적이지 폭 저작 대상이 아니다).
    if (indexes.length === 1 && resolveWidgetHidden(children[indexes[0]], view)) {
      return { indexes, tracks: [1], filler: 0 };
    }
    const ratios = indexes.map((i) => resolveWidgetSpanRatio(children[i], view));
    const specifiedSum = ratios.reduce<number>((acc, r) => acc + (r ?? 0), 0);
    const unspecified = ratios.filter((r) => r === undefined).length;
    // 줄을 채우는 행인지 — 꽉 찬 행이거나, 마지막 행이면서 채우기(fillLastRow)가 켜진 경우.
    const fillsRow = indexes.length === cols || (isLast && !!fillLastRow);
    const remain = 1 - specifiedSum;
    const fallback =
      unspecified === 0 ? 0 : fillsRow && remain > 0 ? remain / unspecified : 1 / cols;
    const tracks = ratios.map((r) => r ?? fallback);
    const total = tracks.reduce((acc, t) => acc + t, 0);
    // 합이 1에 못 미치면 **남는 폭을 빈 트랙(filler)으로 둔다 — 정규화하지 않는다**(2026-08-06
    // 사용자 확정). 정규화하면 `1/2`를 고른 칸이 화면에서 62.5%로 나와 지정값과 보이는 것이
    // 어긋나고, 합을 못 맞춘 실수가 조용히 흡수돼 드러나지 않는다. 빈칸으로 남기면 저작자가
    // 보고 고칠 수 있다. (합>1은 fr이 상대 단위라 CSS가 알아서 정규화한다 — 넘침이 없다.)
    // ⚠️ filler는 **줄 끝**에만 생긴다. 중간·앞쪽 여백은 여백(spacer) 위젯으로 만든다.
    return { indexes, tracks, filler: total < 1 ? 1 - total : 0 };
  });
  return { rows };
}

/** 행의 grid-template-columns 값 — 몫을 fr로 방출(합>1이면 fr이 곧 정규화). filler는 마지막 트랙. */
export function ratioRowTemplateColumns(row: ComposerRatioRow): string {
  const parts = row.tracks.map((t) => `${Number(t.toFixed(6))}fr`);
  if (row.filler > 0) parts.push(`${Number(row.filler.toFixed(6))}fr`);
  return parts.join(' ');
}

/**
 * 섹션(group) 안 편집 가능 입력이 **모두 채워져 있는지** — collapseWhenFilled(채워져 있으면 접힘,
 * 구 SMS 사업장 개요 동형) 판정. 대상 = 하위(중첩 섹션 포함) input 위젯 + formTable 고정 셀
 * 위젯 중 mode가 editable인 것(readonly/hidden은 사용자가 채울 수 없어 제외 — 포함 시 영원히
 * 열림). registered/dataSourceRows/반복 행은 값 형태가 다양해 판정 제외. 대상이 없으면 false(열림).
 */
function sectionInputsFilled(group: GroupWidget, values: Record<string, unknown>): boolean {
  const keys: string[] = [];
  const walk = (ws: ComposerWidget[]): void => {
    for (const w of ws) {
      if (w.kind === 'group') {
        walk(w.children);
        continue;
      }
      if (w.kind === 'input') {
        const iw = w as InputWidget;
        if (iw.mode === 'hidden' || iw.mode === 'readonly') continue;
        if (iw.widget === 'registered' || iw.widget === 'dataSourceRows') continue;
        keys.push(iw.bindingKey);
        continue;
      }
      if (w.kind === 'formTable') {
        for (const row of (w as FormTableWidget).rows) {
          if (row.type !== 'fixed') continue;
          for (const c of row.cells) {
            if (!c.widget || c.widget.mode === 'hidden' || c.widget.mode === 'readonly') continue;
            keys.push(c.widget.bindingKey);
          }
        }
      }
    }
  };
  walk(group.children);
  if (keys.length === 0) return false;
  return keys.every((k) => {
    const v = values[k];
    if (v == null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  });
}

/**
 * 위젯 서브트리에서 처음 만나는 repeat의 over 키(탭 badge 행 수 산출용). group/branch로 하강.
 * 미발견=undefined(badge 생략). repeat 자신은 over 반환.
 */
function findFirstRepeatOver(widget: ComposerWidget): string | undefined {
  if (widget.kind === 'repeat') return widget.over;
  const kids: ComposerWidget[] =
    widget.kind === 'group'
      ? widget.children
      : widget.kind === 'branch'
        ? widget.branches.flatMap((b) => b.children)
        : [];
  for (const k of kids) {
    const found = findFirstRepeatOver(k);
    if (found !== undefined) return found;
  }
  return undefined;
}

// 탭 바(구 SMS SectionTabBar 룩) 인라인 스타일 — rc 전용 Tailwind 유틸 금지 규약에 따라 신규
// 시각(탭 바)은 인라인 style로 소유(호스트 바탕 비의존).
const TAB_NAV_STYLE: CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid #e5e7eb',
};
function tabButtonStyle(active: boolean): CSSProperties {
  return {
    flex: '1 1 0%',
    padding: '12px 0',
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    background: 'transparent',
    borderBottom: `2px solid ${active ? '#6366f1' : 'transparent'}`, // brand-500
    color: active ? '#4f46e5' : '#6b7280', // brand-600
    transition: 'color 0.15s, border-color 0.15s',
    cursor: 'pointer',
  };
}
function tabBadgeStyle(active: boolean): CSSProperties {
  return {
    marginLeft: 6,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px 6px',
    fontSize: 12,
    borderRadius: 9999,
    background: active ? '#e0e7ff' : '#f3f4f6', // brand-100
    color: active ? '#4f46e5' : '#4b5563', // brand-600
  };
}

/**
 * group displayVariant='tabs' 본문 — 직계 자식 group들을 탭으로(활성 탭 자식만 렌더). 탭 라벨=자식
 * group.title, badge=그 자식 안 첫 repeat 행 수(없으면 생략). 비group 자식은 탭 바 위에 그대로 렌더(방어).
 * renderedKids는 childOrdered와 1:1(정상 렌더 경로 재사용) — 활성/비탭 인덱스만 트리에 삽입한다.
 * 활성 탭 인덱스는 로컬 state(저장 안 함). 실폼·캔버스 동일 시각(WYSIWYG) — 캔버스에선 탭 클릭=전환(저작
 * 어포던스), 자식 선택/드릴 채널은 renderedKids가 이미 보유(기존 group 규약 유지).
 */
function ComposerGroupTabsBody({
  childOrdered,
  renderedKids,
  values,
}: {
  childOrdered: ComposerWidget[];
  renderedKids: ReactNode[];
  values: Record<string, unknown>;
}) {
  const entries = childOrdered.map((c, i) => ({ c, i }));
  const tabEntries = entries.filter((e) => e.c.kind === 'group');
  const nonTabEntries = entries.filter((e) => e.c.kind !== 'group');
  const [active, setActive] = useState(0);
  const activeIdx = tabEntries.length > 0 ? Math.min(Math.max(0, active), tabEntries.length - 1) : 0;
  return (
    <div>
      {nonTabEntries.map((e) => (
        <Fragment key={e.i}>{renderedKids[e.i]}</Fragment>
      ))}
      {tabEntries.length > 0 && (
        <div>
          <nav role="tablist" style={TAB_NAV_STYLE}>
            {tabEntries.map((e, ti) => {
              const g = e.c as GroupWidget;
              const over = findFirstRepeatOver(g);
              const badge = over !== undefined ? normalizeRows(values[over]).length : undefined;
              const isActive = ti === activeIdx;
              return (
                <button
                  key={g.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(ti)}
                  style={tabButtonStyle(isActive)}
                >
                  {g.title || '탭'}
                  {badge !== undefined && badge > 0 && (
                    <span style={tabBadgeStyle(isActive)}>{badge}</span>
                  )}
                </button>
              );
            })}
          </nav>
          <div style={{ paddingTop: 16 }}>{renderedKids[tabEntries[activeIdx].i]}</div>
        </div>
      )}
    </div>
  );
}

/**
 * 그룹(UI 용어 "섹션") 컨테이너 렌더 — title/collapsible 지원. 접힘 상태는 렌더러 로컬 state
 * (영속 불요 — 리마운트 시 defaultOpen 복귀). title/collapsible 둘 다 없으면 기존 렌더 경로와
 * **바이트 동일**(회귀0). 카드/헤더 스타일은 시안(remote-files/safetyhub_안전관리상태보고서.html)의
 * .acc-section/.acc-h 결 — 모양 값은 인라인 style이 소유한다(rc Tailwind purge 규약).
 * 실폼(비 interactive): 헤더 전체 클릭=토글. 저작 캔버스(interactive): chevron 버튼만
 * 토글(헤더 클릭=선택과 충돌 방지), 라벨 클릭은 상위 선택 래퍼로 버블링.
 * autoCollapse(옵션) = collapseWhenFilled 모드 — ready 시점에 filled를 1회 판정해 초기 상태를
 * 정하고(채움=접힘/비움=열림), 사용자가 토글하면 그 선택이 우선(자동 판정 종료).
 */
function ComposerGroupBlock({
  widget,
  cols,
  gridClass,
  fillLayout,
  rowLayout,
  view,
  kids,
  dropSlot,
  interactive,
  editingTitle,
  onStartTitleEdit,
  onTitleCommit,
  onTitleCancel,
  autoCollapse,
}: {
  widget: GroupWidget;
  cols: number;
  gridClass: string;
  /**
   * 인라인/주입 grid 레이아웃 — 활성 시 gridClass 대신 grid 컨테이너 사용. null=클래스 경로(회귀0).
   * 마지막 행 채우기(LCM) 또는 cols 4~6 순수 N단 grid(클래스 맵 부재) 둘 다 담는다.
   */
  fillLayout: GroupFillLastRowLayout | null;
  /**
   * 비율 배치 — 활성 시 자식이 **행 컨테이너(data-composer-row)**로 이미 묶여 오므로 이 컨테이너는
   * 행을 세로로 쌓기만 한다(행별 grid-template-columns는 뷰 지정 시 인라인, 실폼은 <style> 주입).
   * null=비율 미사용(fillLayout/gridClass 기존 경로).
   */
  rowLayout: ComposerRatioRowLayout | null;
  /** 활성 뷰 키 — 지정(저작 캔버스)=고정 inline grid / 미지정(web 실폼)=md 미만 스택 + md+ grid(<style>). */
  view?: string;
  kids: ReactNode;
  dropSlot: ReactNode | null;
  interactive: boolean;
  /** 캔버스 섹션명 인라인 편집 중 여부(저작 헤더에서만 사용). */
  editingTitle?: boolean;
  /** 저작 헤더 더블클릭 → 편집 시작(미전달=편집 비활성). */
  onStartTitleEdit?: () => void;
  onTitleCommit?: (value: string) => void;
  onTitleCancel?: () => void;
  /** collapseWhenFilled 모드 — ready 시 filled 1회 판정(채움=접힘). 미전달=정적 defaultOpen. */
  autoCollapse?: { ready: boolean; filled: boolean };
}) {
  const collapsible = !!widget.collapsible;
  // 박스 장식(채움/테두리/모서리) — 양식 GroupNode.style 대응물. 미설정=undefined라 각 렌더 경로가
  // style 미전달 → 기존과 완전 동일(회귀 0). boxStyle 지정 시 base padding도 이 박스 요소가 소유 —
  // renderWidget의 padding 래퍼는 테두리 **바깥**이라 "콘텐츠와 경계 사이" 의미가 깨짐(카드 내용이
  // 테두리에 붙고 카드만 안쪽으로 밀림, 2026-07-15 개선 의견 카드 제보). renderWidget은 boxStyle
  // group의 padStyle을 스킵해 이중 적용을 막는다.
  const boxDecorStyle: CSSProperties | undefined = (() => {
    if (!widget.boxStyle) return undefined;
    const s: CSSProperties = {};
    applyRectDecoration(s, widget.boxStyle);
    if (widget.padding !== undefined) s.padding = boxSpacingToCss(widget.padding);
    return Object.keys(s).length > 0 ? s : undefined;
  })();
  const [open, setOpen] = useState(widget.defaultOpen ?? true);
  // 조건부 초기 상태(채워져 있으면 접힘) — ready(초기값/prefill 준비) 시점에 1회만 적용.
  // 사용자가 토글하면 종료(수동 우선). ref: 판정 완료 플래그(재렌더 무관).
  const autoDecidedRef = useRef(false);
  useEffect(() => {
    if (!collapsible || !autoCollapse || autoDecidedRef.current || !autoCollapse.ready) return;
    autoDecidedRef.current = true;
    setOpen(!autoCollapse.filled);
  }, [collapsible, autoCollapse]);
  const toggleOpen = (): void => {
    autoDecidedRef.current = true; // 수동 토글 = 자동 판정 종료
    setOpen((o) => !o);
  };
  const hasHeader = !!widget.title || collapsible;
  const showChildren = !collapsible || open;
  // 그리드 컨테이너 — fillLayout 활성 시 인라인/주입 grid(LCM 채움 또는 cols 4~6 순수 N단), 아니면 gridClass(회귀0).
  // 자식들은 각자 인라인 grid-column(fillChildGridColumn)을 지녀 web 실폼 flex 스택(md 미만)에선
  // 무효, md+ grid에서만 적용된다. rc 전용 Tailwind 유틸 금지 규약에 따라 리터럴 CSS만 사용.
  // 자식 간격(px) — gap=세로(행), gapX=다단 열(수평, 미지정=세로와 동일). 미지정=16(현행과 동일).
  const childGap = widget.gap ?? 16;
  const childGapX = widget.gapX ?? childGap;
  const gapSpecified = widget.gap != null || widget.gapX != null;
  const gridContainer = (content: ReactNode): ReactNode => {
    if (rowLayout) {
      // 비율 배치: 바깥은 행을 세로로 쌓기만(rowGap), 폭 분배는 각 행 컨테이너의 fr 트랙이 소유한다.
      // fr을 쓰는 이유 — 비율이 임의 소수라 단일 grid + `span k`(LCM 트랙)로는 정수 분모가 성립하지
      // 않고, 트랙 수를 키우면 column-gap이 트랙 수-1개만큼 쌓여 컨테이너를 넘긴다. fr은 gap을 뺀
      // 나머지를 비율대로 나누므로 gap 산술이 정확하고 정규화(합>1)도 공짜다.
      if (view) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', rowGap: childGap }}>{content}</div>
        );
      }
      // web 실폼: md 미만은 세로 스택(1단), md+는 행별 fr grid. 미디어쿼리는 인라인 불가라 <style> 주입.
      const sel = `[data-fillrow="${widget.id}"]`;
      const css =
        `${sel}{display:flex;flex-direction:column;row-gap:${childGap}px}` +
        `${sel}>[data-composer-row]{display:flex;flex-direction:column;gap:${childGap}px}` +
        `@media(min-width:${MD_BREAKPOINT_PX}px){` +
        rowLayout.rows
          .map(
            (r, i) =>
              `${sel}>[data-composer-row="${i}"]{display:grid;grid-template-columns:${ratioRowTemplateColumns(r)};row-gap:${childGap}px;column-gap:${childGapX}px}`,
          )
          .join('') +
        `}`;
      return (
        <>
          <style>{css}</style>
          <div data-fillrow={widget.id}>{content}</div>
        </>
      );
    }
    if (!fillLayout) {
      if (!gapSpecified) return <div className={gridClass}>{content}</div>;
      // 간격 지정: 2·3단은 grid 클래스 유지 + 인라인 row/columnGap 오버라이드, 1단은 flex column —
      // space-y(마진 기반)는 인라인으로 못 덮으므로 레이아웃 등가인 flex 스택으로 대체(gapX 무의미).
      if (cols > 1 && cols <= 3) {
        return (
          <div className={gridClass} style={{ rowGap: childGap, columnGap: childGapX }}>
            {content}
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: childGap }}>{content}</div>
      );
    }
    if (view) {
      // 고정 뷰(저작 캔버스): 폭이 이미 해소돼 미디어쿼리 불필요 — inline grid.
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${fillLayout.totalCols}, minmax(0, 1fr))`,
            rowGap: childGap,
            columnGap: childGapX,
          }}
        >
          {content}
        </div>
      );
    }
    // web 실폼: md 미만은 세로 스택(1단), md+는 LCM grid. 미디어쿼리는 인라인 style로 불가라 <style> 주입.
    const sel = `[data-fillrow="${widget.id}"]`;
    const css =
      `${sel}{display:flex;flex-direction:column;gap:${childGap}px}` +
      `@media(min-width:${MD_BREAKPOINT_PX}px){${sel}{display:grid;grid-template-columns:repeat(${fillLayout.totalCols},minmax(0,1fr));row-gap:${childGap}px;column-gap:${childGapX}px}}`;
    return (
      <>
        <style>{css}</style>
        <div data-fillrow={widget.id}>{content}</div>
      </>
    );
  };
  // 섹션명 폰트 — 기본 16.5px/800 + 색 상속(시안 .acc-h-title). titleStyle로 오버라이드.
  const titleStyleCss = textStyleCss(widget.titleStyle, COMPOSER_SECTION_TITLE_DEFAULT_STYLE);
  // 섹션 헤더 바탕 — 펼침 여부로 갈린다(시안 .acc-h / .acc-section.expanded .acc-h).
  // titleFill(저작 축) 지정 시 그 색이 우선(펼침/접힘 공통 — 2026-08-04 헤더/본문 채움 분리).
  const sectionHeaderStyle: CSSProperties = {
    ...SECTION_HEADER_BASE_STYLE,
    ...(showChildren ? SECTION_HEADER_EXPANDED_STYLE : SECTION_HEADER_COLLAPSED_STYLE),
    ...(widget.titleFill ? { background: widget.titleFill } : {}),
  };
  // 섹션 본문 바탕 — 기본은 투명(페이지 바탕이 비침, 기존 결). contentFill(저작 축) 지정 시 그 색.
  const sectionBodyStyle: CSSProperties | undefined = (() => {
    const base = boxDecorStyle ? undefined : SECTION_BODY_STYLE;
    if (!widget.contentFill) return base;
    return { ...(base ?? {}), background: widget.contentFill };
  })();
  const chevronIcon = (
    <svg
      className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`}
      style={{ color: '#94a3b8' }} // 시안 .acc-chev(ink-400)
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  // titleVariant='label': 회색 헤더 바·테두리 박스 없이 제목을 필드 라벨처럼 plain 렌더 +
  // 바로 아래 children(구 SMS "라벨 + 컨트롤" 룩). 실폼·저작 캔버스 동일 시각(WYSIWYG) —
  // 캔버스는 저작 어포던스(더블클릭 제목 인라인 편집·chevron 접기)만 얹되 레이아웃 중립.
  // rc 전용 Tailwind 유틸 금지 규약에 따라 라벨/게이트 시각 값은 인라인 style.
  if (hasHeader && widget.titleVariant === 'label') {
    // 필드 라벨 룩(구 SMS text-sm/medium/gray-700) — user titleStyle가 있으면 그 위에 병합.
    const labelTextStyle = textStyleCss(widget.titleStyle, {
      fontSize: 14,
      fontWeight: 500,
      color: '#374151',
    });
    const labelEl =
      interactive && editingTitle && onTitleCommit && onTitleCancel ? (
        <InlineTextEditor
          initial={widget.title ?? ''}
          textStyle={labelTextStyle}
          onCommit={onTitleCommit}
          onCancel={onTitleCancel}
        />
      ) : (
        <span
          style={labelTextStyle}
          onDoubleClick={
            interactive && onStartTitleEdit && !editingTitle
              ? (e) => {
                  e.stopPropagation();
                  onStartTitleEdit();
                }
              : undefined
          }
        >
          {widget.title || (interactive ? '섹션' : '')}
        </span>
      );
    // 라벨 행(marginBottom는 항상 래퍼 div가 소유) — collapsible이면 라벨 옆 chevron만(박스 없이).
    const labelRow = collapsible ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        {labelEl}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleOpen();
          }}
          onDoubleClick={interactive ? (e) => e.stopPropagation() : undefined}
          aria-expanded={open}
          aria-label={open ? '섹션 접기' : '섹션 펼치기'}
        >
          {chevronIcon}
        </button>
      </div>
    ) : (
      <div style={{ marginBottom: 4 }}>{labelEl}</div>
    );
    const isEmptyLabel = widget.children.length === 0;
    const body = showChildren
      ? interactive && isEmptyLabel
        ? (
          <div className="rounded border border-dashed border-gray-200 bg-white py-3 text-center text-xs text-gray-400">
            빈 섹션 — 풀에서 위젯을 배치하세요
            {dropSlot}
          </div>
        )
        : gridContainer(
            interactive ? (
              <>
                {kids}
                {dropSlot}
              </>
            ) : (
              kids
            ),
          )
      : null;
    return (
      <div style={boxDecorStyle}>
        {labelRow}
        {body}
      </div>
    );
  }

  if (!interactive) {
    // 순수(web 실폼): title/collapsible 없으면 경계 없이 grid만 — 기존과 바이트 동일(회귀0).
    // boxStyle 지정 시에만 장식 래퍼(미지정=래퍼 없이 그대로, 회귀0).
    if (!hasHeader) {
      return boxDecorStyle ? (
        <div style={boxDecorStyle}>{gridContainer(kids)}</div>
      ) : (
        <>{gridContainer(kids)}</>
      );
    }
    const header = collapsible ? (
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className="flex w-full items-center justify-between text-left transition-colors"
        style={sectionHeaderStyle}
      >
        <h3 style={titleStyleCss}>{widget.title}</h3>
        {chevronIcon}
      </button>
    ) : (
      <div className="flex w-full items-center justify-between" style={sectionHeaderStyle}>
        <h3 style={titleStyleCss}>{widget.title}</h3>
      </div>
    );
    return (
      <div className="overflow-hidden" style={{ ...SECTION_BOX_STYLE, ...boxDecorStyle }}>
        {header}
        {showChildren ? (
          <div className={boxDecorStyle ? 'p-5' : undefined} style={sectionBodyStyle}>
            {gridContainer(kids)}
          </div>
        ) : null}
      </div>
    );
  }

  const isEmpty = widget.children.length === 0;

  if (!hasHeader) {
    // 제목/접기 없는 순수 레이아웃 그룹 — 저작 식별 어포던스는 **레이아웃 중립**으로(2026-07-15 사용자
    // 제보: 이전의 점선 박스 패딩(px-2 pb-2 pt-1)·bg·흐름 내 메타 라벨이 캔버스를 배포 작성 페이지와
    // 갈라놓음). 경계=outline(레이아웃 무영향)+offset, 메타 라벨=absolute(흐름 밖, 경계 위) —
    // 캔버스 어포던스 레이아웃 중립 규약(캔버스↔실폼 결 차이는 결함). boxStyle 장식은 실폼 동형 유지.
    return (
      <div
        className="relative"
        style={{ outline: '1px dashed #d1d5db', outlineOffset: '2px', ...boxDecorStyle }}
      >
        <span
          className="pointer-events-none select-none absolute text-[10px] font-medium uppercase tracking-wide text-gray-400"
          style={{ top: 0, right: 2, transform: 'translateY(-100%)' }}
        >
          섹션 · {cols}단
        </span>
        {isEmpty ? (
          <div className="rounded border border-dashed border-gray-200 bg-white py-3 text-center text-xs text-gray-400">
            빈 섹션 — 풀에서 위젯을 배치하세요
            {dropSlot}
          </div>
        ) : (
          gridContainer(
            <>
              {kids}
              {dropSlot}
            </>,
          )
        )}
      </div>
    );
  }

  // 저작 모드 + 헤더 있음: 실폼과 동일한 시안 섹션 카드(WYSIWYG). 헤더 클릭=선택(버블), chevron만 토글.
  // 헤더 더블클릭 = 섹션명 인라인 편집(더블클릭만 편집 — 클릭 버블 선택 구조 유지).
  // 카드/헤더 스타일은 실폼 경로와 같은 상수(SECTION_BOX_STYLE/sectionHeaderStyle)를 쓴다 —
  // 본문 바탕은 여전히 미지정(투명)이라 캔버스만 흰 면으로 갈라지지 않는다(2026-07-14 대조).
  return (
    <div className="overflow-hidden" style={{ ...SECTION_BOX_STYLE, ...boxDecorStyle }}>
      <div
        className="flex w-full items-center justify-between"
        style={sectionHeaderStyle}
        onDoubleClick={
          onStartTitleEdit && !editingTitle
            ? (e) => {
                e.stopPropagation();
                onStartTitleEdit();
              }
            : undefined
        }
      >
        {editingTitle && onTitleCommit && onTitleCancel ? (
          <InlineTextEditor
            initial={widget.title ?? ''}
            textStyle={titleStyleCss}
            onCommit={onTitleCommit}
            onCancel={onTitleCancel}
          />
        ) : (
          <h3 style={titleStyleCss}>{widget.title || '섹션'}</h3>
        )}
        {collapsible ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleOpen();
            }}
            // chevron 더블클릭이 헤더 편집으로 번지지 않게 차단(토글 전용).
            onDoubleClick={(e) => e.stopPropagation()}
            aria-expanded={open}
            aria-label={open ? '섹션 접기' : '섹션 펼치기'}
          >
            {chevronIcon}
          </button>
        ) : null}
      </div>
      {showChildren ? (
        <div className={boxDecorStyle ? 'p-5' : undefined} style={sectionBodyStyle}>
          {isEmpty ? (
            <div className="rounded border border-dashed border-gray-200 bg-white py-3 text-center text-xs text-gray-400">
              빈 섹션 — 풀에서 위젯을 배치하세요
              {dropSlot}
            </div>
          ) : (
            gridContainer(
              <>
                {kids}
                {dropSlot}
              </>,
            )
          )}
        </div>
      ) : null}
    </div>
  );
}

/** 등록형 위젯의 표시 라벨 — shared 카탈로그 meta.label(없으면 widgetKey/`(위젯)`). */
function registeredWidgetLabel(widgetKey: string | undefined): string {
  const meta = REGISTERED_FORM_WIDGETS_META.find((m) => m.key === widgetKey);
  return meta?.label ?? widgetKey ?? '(위젯)';
}

/**
 * 등록형 폼 위젯 렌더 — host 레지스트리에서 widgetKey로 컴포넌트를 찾아 렌더.
 * 미주입/미등록(admin 미리보기 포함)은 안내 placeholder 박스(흔한 Tailwind 클래스만).
 */
function renderRegisteredWidget(
  w: InputWidget,
  values: Record<string, unknown>,
  onChange: (bindingKey: string, value: unknown) => void,
  registeredFormWidgets?: Record<string, ComposerRegisteredFormWidget>,
  context?: {
    companyId?: string;
    siteId?: string;
    resolvedRows?: Record<string, Record<string, unknown>[]>;
    values?: Record<string, unknown>;
  },
  labelSlot?: ReactNode,
): ReactNode {
  const Widget = w.widgetKey ? registeredFormWidgets?.[w.widgetKey] : undefined;
  if (Widget) {
    // 라벨(+required)은 위젯 구현이 소유(필요 시 자체 렌더) — 여기서 중복 출력하지 않음.
    return (
      <div key={w.id}>
        <Widget widget={w} values={values} onChange={onChange} context={context} />
      </div>
    );
  }
  // 캔버스 placeholder 라벨 — 일반 input 라벨과 동일 규약(위치/정렬/폰트 style + 빨간 * + labelHint,
  // 내용 조립은 WidgetLabelContent 단일 정본 — 병기(labelSuffixFrom)도 일반 input과 동형).
  // 라벨 프리미티브는 `InputWidget`을 직접 받으므로 변환 없이 그대로 넘긴다(캐스트 없음).
  const node = w;
  const labelPres = labelPresentationFor(node);
  const labelEl =
    labelSlot ??
    (w.label && !isLabelHidden(node) ? (
      <div className={labelPres.className} style={labelPres.style}>
        <WidgetLabelContent node={node} values={values} />
      </div>
    ) : null);
  return (
    <div key={w.id}>
      {labelEl}
      <div className="rounded-card border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-xs text-gray-500">
        <div className="font-medium text-gray-600">{registeredWidgetLabel(w.widgetKey)}</div>
        <div className="mt-0.5">작성 화면에서 동작하는 위젯입니다.</div>
      </div>
    </div>
  );
}

/**
 * 행 그리드(dataSourceRows) 위젯 렌더 계약 — host가 편집형 list 데이터셋 def를 bindingKey로
 * 조회해 rc DataSourceRowsField(RowsCollectionEditor)를 그린다. web 실폼·admin 캔버스 라이브
 * 모드가 같은 컴포넌트를 주입(WYSIWYG). 미주입(admin 샘플 모드·popout 미리보기)은 placeholder 박스.
 */
export type DataSourceRowsRenderer = (
  widget: InputWidget,
  value: unknown,
  onChange: (bindingKey: string, value: unknown) => void,
) => ReactNode;

/**
 * 행 그리드 위젯 placeholder — host renderer 미주입 시(admin 캔버스 샘플 모드) 등록형과 동일
 * 스타일 박스. 라이브 모드·실폼은 host가 DataSourceRowsField를 주입해 실제 행 그리드를 그린다.
 */
function renderDataSourceRowsPlaceholder(w: InputWidget, labelSlot?: ReactNode): ReactNode {
  const label = w.label ?? w.bindingKey;
  return (
    <div key={w.id}>
      <div className="rounded-card border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-xs text-gray-500">
        <div className="font-medium text-gray-600">{labelSlot ?? <>데이터셋: {label}</>}</div>
        <div className="mt-0.5">실제 작성 화면에서 행을 편집합니다.</div>
      </div>
    </div>
  );
}

// ── 작성폼 표 위젯(formTable) 렌더 헬퍼 ──
// rc 전용 Tailwind 유틸 금지 규약에 따라 표 구조(테두리·패딩·정렬·폭)는 인라인 style로만 부여.

/** 표 셀(td/th) 공통 패딩 — 인라인(앱 CSS 미의존). 구 SMS 질감: 셀 테두리 없음(외곽선+행 구분선만). */
const FORM_TABLE_CELL_STYLE: CSSProperties = {
  padding: '8px 12px',
  verticalAlign: 'top',
};
/**
 * 본문 셀 — 빈 셀/빈 행도 최소 높이 보장(table cell의 height는 min-height로 동작).
 * 헤더(th)는 텍스트 고정이라 제외(2단 헤더가 불필요하게 두꺼워지는 것 방지).
 */
const FORM_TABLE_BODY_CELL_STYLE: CSSProperties = {
  ...FORM_TABLE_CELL_STYLE,
  height: 36,
};
/** 계산(compute) 셀 값 표시 박스 — 구 SMS 계(합계) 박스 질감(gray-50/우측정렬). 폭은 셀 채움(block)
 * — B 모델(셀 전체=컨트롤, 2026-07-14): 80px 고정(inline-block+minWidth)은 셀 내 다른 컨트롤과의
 * 일관을 위해 제거(block은 베이스라인 라인박스 여백도 없어 행높이 정합 유지). */
/** 계산 셀 빈값 placeholder(박스 높이 유지) — 리터럴 nbsp 금지 수칙에 따라 코드로 생성(160=U+00A0). */
const NBSP = String.fromCharCode(160);
const FORM_TABLE_COMPUTE_BOX_STYLE: CSSProperties = {
  display: 'block',
  padding: '6px 8px',
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 4,
  // 정렬은 셀 textAlign 상속(B 모델 — 컨트롤 정렬=셀 정렬). 우측을 원하면 셀 정렬로 지정.
  textAlign: 'inherit',
  color: '#374151',
};
/** legacy align(left/center/right) → 신형 contentAlignH(AlignVal). reader 하위호환용. */
const LEGACY_ALIGN_TO_H: Record<'left' | 'center' | 'right', 'start' | 'center' | 'end'> = {
  left: 'start',
  center: 'center',
  right: 'end',
};

/**
 * 고정 셀의 정렬 style — 셀 지정(신형 contentAlignH/V, 미지정 시 legacy align 하위호환)만 반환.
 * 열(col.align)은 colStyle이 이미 부여하므로 여기선 셀 지정이 있을 때만 덮어쓴다.
 * 세로: verticalAlign(start→top/center→middle/end→bottom). 기본(start)은 콘텐츠 없음(colStyle의 top 유지).
 */
const fixedCellAlignStyle = (cell: FormTableFixedCell): CSSProperties => {
  const style: CSSProperties = {};
  const h = cell.contentAlignH ?? (cell.align ? LEGACY_ALIGN_TO_H[cell.align] : undefined);
  if (h) style.textAlign = h === 'start' ? 'left' : h === 'end' ? 'right' : 'center';
  const v = cell.contentAlignV;
  if (v && v !== 'start')
    style.verticalAlign = v === 'end' ? 'bottom' : 'middle';
  return style;
};

/**
 * 반복 행 셀 편집기 — 편집형 list def 필드(DataSourceFieldMap)의 위젯 어휘별 입력.
 * 미지정/'text'=텍스트, 'number'=숫자, 'textarea'(-list)=여러 줄, 'readonly'/비편집=값 표시,
 * 'registered'=host 주입 위젯이라 코어는 안내 placeholder(실제 편집은 web 실폼 Phase 2b),
 * 'annotated-image'=표시 전용 이미지(+도형 오버레이), 'signature'=서명 슬롯 칸(표시 전용),
 * 'date'/'date-range'=달력 컨트롤(트리·행 그리드와 공유 — DateFieldInput/DateRangeFieldInput).
 */
function renderFormTableRepeatCell(
  value: unknown,
  field: DataSourceFieldMap | undefined,
  onCellChange: (next: unknown) => void,
  /** 이 셀이 속한 데이터 행 — 서명 슬롯이 signerNameField로 이름을 읽는다(그 외 위젯은 미사용). */
  row?: Record<string, unknown>,
  /**
   * 서명 슬롯 채널(P2) — sourceKey(이 반복 행의 데이터 소스 키 = slotKey 파생 열쇠) + host
   * 컨텍스트. 미전달이면 표시 전용 빈 칸(종전 동작 — 캔버스·미배선 host).
   */
  signature?: { slots?: ComposerSignatureSlotsContext; sourceKey: string },
): ReactNode {
  const widget = field?.widget ?? 'text';
  const editable = field?.editable ?? true;
  // annotated-image는 값이 객체({url, shapes…})라 아래 텍스트 폴백을 타면 [object Object]가 샌다.
  // 표시 전용 위젯이므로 editable/readonly 판정보다 **먼저** 이미지 렌더로 라우팅한다
  // (RowsCollectionEditor의 image·registered 선라우팅과 같은 규약).
  if (widget === 'annotated-image') {
    return <AnnotatedImageView value={value} alt={field?.label ?? ''} />;
  }
  // 서명 슬롯도 같은 이유로 선라우팅한다 — 표시 전용이라 editable/readonly 판정 아래로 내려가면
  // 안 되고, 텍스트 폴백을 타면 서명 data URL이 셀에 통째로 새 나온다.
  // P2: 값·상호작용은 resolved 채널(3채널 공용 signatureSlotViewProps) — content 셀 값(value)은
  // 읽지 않는다(§3-2).
  if (widget === 'signature') {
    if (signature && field) {
      return (
        <SignatureSlotView
          {...signatureSlotViewProps(signature.slots, signature.sourceKey, field, row)}
        />
      );
    }
    return <SignatureSlotView value={undefined} signerName={rowSignerName(row, field ?? {})} />;
  }
  // 셀(td) 폰트/색/정렬이 값 표시·컨트롤에도 반영 — 고정 셀과 동일 계열(컨트롤 미상속 +
  // text-sm류 고정 클래스 차단, 2026-07-13 전수 감사 #1·#2). 반복 행은 항상 표 안이라 무조건 상속.
  if (!editable || widget === 'readonly') {
    // 날짜/기간은 String(value) 폴백을 타면 안 된다 — 기간은 객체라 [object Object]가 새고,
    // 단일 날짜도 ISO datetime prefill이 그대로 보인다(dateWidgetDisplayText가 두 경우 모두 접는다).
    const dateText = dateWidgetDisplayText(widget, value);
    const text =
      dateText !== undefined
        ? dateText === ''
          ? '—'
          : dateText
        : value === undefined || value === null || value === ''
          ? '—'
          : String(value);
    return (
      <span className="text-sm text-gray-600" style={readonlyInheritCellFontStyle}>
        {text}
      </span>
    );
  }
  switch (widget) {
    // 날짜/기간 셀도 작성폼 트리·행 그리드와 **같은 컨트롤**을 쓴다(달력 진입점이 surface마다
    // 달라지지 않게). 셀은 폰트/색을 td에서 상속하므로 컨트롤 결도 셀 입력 결로 준다.
    case 'date':
      return (
        <DateFieldInput
          value={value}
          className={cellInputClass}
          controlStyle={inputInheritCellFontStyle}
          onChange={(next) => onCellChange(next)}
        />
      );

    case 'date-range':
      return (
        <DateRangeFieldInput
          value={value}
          className={cellInputClass}
          controlStyle={inputInheritCellFontStyle}
          onChange={(next) => onCellChange(next)}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          className={cellInputClass}
          style={numberCellControlStyle}
          value={value === undefined || value === null || value === '' ? '' : (value as number)}
          onChange={(e) => onCellChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      );
    case 'textarea':
    case 'textarea-list':
      return (
        <textarea
          className={cellInputClass}
          style={inputInheritCellFontStyle}
          rows={2}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onCellChange(e.target.value)}
        />
      );
    case 'registered':
      return <span className="text-xs text-gray-400">작성 화면에서 편집</span>;
    // text-list(한 줄 목록)는 셀 폴백에서 단일 값 한 줄 input으로 표시(단일 값 형상 유지).
    case 'text':
    case 'text-list':
    default:
      return (
        <input
          type="text"
          className={cellInputClass}
          style={inputInheritCellFontStyle}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onCellChange(e.target.value)}
        />
      );
  }
}

/** sortableSelect 값 형태 — 체크 선택 + 정렬 순서 + 조립 텍스트(구 SMS 개선 의견 모달 동형). */
interface SortableSelectValue {
  selectedKeys: string[];
  orderedKeys: string[];
  composedText: string;
}

/**
 * sortableSelect 배지 순환 색 팔레트(값별 색 — 구 SMS 관리적/요약/결론 배지 동형).
 * 여기 blue는 브랜드가 아니라 **값을 구분하는 범주색**이라 brand 토큰으로 바꾸지 않는다
 * (바꾸면 첫 번째 값만 브랜드색이 돼 "이 값이 특별하다"는 거짓 신호가 된다).
 */
const SORTABLE_BADGE_PALETTE = [
  'bg-blue-50 text-blue-700',
  'bg-green-50 text-green-700',
  'bg-purple-50 text-purple-700',
];

// effectiveOptionsSources(후보 소스 단수/복수 정규화)는 `@safetyhub/shared`(composer-write-validation)로
// 이관(2026-08-24 — 서버 writeback/개선의견 파트 감지의 인라인 재구현 제거) — 기존 import 경로
// 유지를 위한 재-export(상단 import).
export { effectiveOptionsSources };

/**
 * sortableSelect 후보 rows resolve — 소스 키(단수 문자열 또는 복수 배열)를 **지정 순서대로** resolve해
 * concat한다. 키별 우선순위:
 *  ① resolvedRows[key] — host(web 작성 페이지)가 런타임 resolve한 후보 rows 주입 채널(P3).
 *     모달 스코프는 본문 values와 격리(modalValues는 비어 있음)라, AI 후보처럼 비동기로 도착하는
 *     데이터는 본문 values 대신 이 채널로 넣어야 모달 안 sortableSelect가 본다(childContext로 전달).
 *  ② values[key] — 현재 스코프 값(편집/prefill 배열).
 *  ③ dataDefs[key].defaultRows — 내장 데이터셋 fallback(table 위젯 rows 파생과 동일 경로).
 * 복수 키의 key 중복은 downstream(SortableSelectWidget rowByKey 최초 승리)이 처리 — 여기선 concat만.
 * 위젯 렌더와 modalTrigger hideWhenNoData 판정이 공유(중복 구현 금지 — 데이터 판정 일원화).
 */
export function resolveSortableRows(
  optionsSource: string | string[] | undefined,
  values: Record<string, unknown>,
  dataDefs?: DataDef[],
  resolvedRows?: Record<string, Record<string, unknown>[]>,
): Record<string, unknown>[] {
  const keys = Array.isArray(optionsSource)
    ? optionsSource
    : optionsSource
      ? [optionsSource]
      : [];
  if (keys.length === 0) return [];
  const resolveOne = (key: string): Record<string, unknown>[] => {
    const injected = resolvedRows?.[key];
    if (Array.isArray(injected)) return injected;
    const v = values[key];
    if (Array.isArray(v)) return v as Record<string, unknown>[];
    const def = dataDefs?.find((d) => d.key === key);
    return (def?.defaultRows as Record<string, unknown>[] | undefined) ?? [];
  };
  // 단일 키는 resolve 결과 배열을 그대로 반환(참조 동일성 유지 — 기존 호출·테스트 계약).
  if (keys.length === 1) return resolveOne(keys[0]);
  const out: Record<string, unknown>[] = [];
  for (const key of keys) out.push(...resolveOne(key));
  return out;
}

/** 정규 후보 행 — 소스별 매핑을 통과해 고정 필드(key/text/badge/flag)로 수렴된 sortableSelect 행. */
export interface NormalizedSortableRow {
  key: string;
  text: string;
  badge: string;
  flag: boolean;
  /**
   * 정규화 전 원본 행 참조 — combobox 제안 선택 시 writes source(카탈로그 returns 기준 raw 필드
   * 경로)를 읽는 데 사용(P2). resolveNormalizedSortableRows가 항상 채우지만, 소비처가 정규 필드만
   * 들고 행을 직접 구성할 수 있게 선택적(sortableSelect 소비처는 key/text/badge/flag만 읽어 무영향).
   */
  raw?: Record<string, unknown>;
  /**
   * 행 id — 소스 매핑에 idField(카탈로그 upsertIdField의 저작 시점 복사)가 있고 raw[idField]가
   * 비빈 문자열일 때만 실린다. combobox 제안 선택 시 content 합성 키(`${bindingKey}__selectedId`)에
   * 기록되는 값(자동 합성 채널 — 결정 2026-07-17). sortableSelect 소비처는 읽지 않는다.
   */
  id?: string;
}

/**
 * sortableSelect 후보 rows를 **소스별 필드 매핑으로 정규화**해 resolve한다(2026-07-16 소스별 매핑).
 * effective 소스 키별로 resolveSortableRows([key])를 재사용해 rows를 얻고(키별 우선순위 동일),
 * `node.sourceFieldMaps[key]`가 있으면 그 맵으로(keyField 기본 'key', textField 기본 'text'),
 * 없으면 위젯 레벨 itemKeyField 등(legacy fallback — 기존 SMS 템플릿 무변환 호환)으로
 * 정규 행 {key,text,badge,flag}에 매핑한 뒤 지정 순서대로 concat한다.
 *  - key 값은 원본 그대로(prefix 부여 금지 — 저장된 selectedKeys 복원 호환). 키 필드 부재
 *    (null/undefined) 시 `row-${i}` 폴백(i는 concat 전체 인덱스 — 기존 rowByKey 폴백과 동일 결과).
 *  - badge는 매핑 필드 미지정이면 ''(배지 없음), flag는 boolean화(미지정이면 false).
 * 위젯 렌더와 modalTrigger hideWhenNoData 판정이 공유(데이터 판정 일원화 — 중복 구현 금지).
 */
export function resolveNormalizedSortableRows(
  node: {
    widget?: string;
    bindingKey?: string;
    optionsSource?: string;
    optionsSources?: string[];
    sourceFieldMaps?: Record<string, SortableSourceFieldMap>;
    itemKeyField?: string;
    itemTextField?: string;
    itemBadgeField?: string;
    itemFlagField?: string;
    suggestionDictScope?: 'site' | 'organization';
  },
  values: Record<string, unknown>,
  dataDefs?: DataDef[],
  resolvedRows?: Record<string, Record<string, unknown>[]>,
): NormalizedSortableRow[] {
  const out: NormalizedSortableRow[] = [];
  let i = 0;
  for (const key of effectiveOptionsSources(node)) {
    for (const raw of resolveSortableRows([key], values, dataDefs, resolvedRows)) {
      out.push(normalizeSortableRowForSource(node, key, raw, `row-${i}`));
      i += 1;
    }
  }
  // '내 입력 누적'(combobox suggestionDictScope) — 사전 키(suggestionDictKey)를 **암묵 소스로
  // append**한다(저작자가 optionsSources·sourceFieldMaps를 만질 필요 없음 — 매핑은 { text: 'value' }
  // 상당을 내장 처리). rows는 서버 resolvePrefill 4-b가 값 채널(values[사전 키])로 주입한 행 배열
  // (`[{ value }]`). 명시 소스와 공존 시 뒤에 concat하되, **명시 소스에 이미 있는 텍스트와 겹치는
  // 사전 행은 생략**한다(카탈로그 항목을 작성자가 입력해 사전에도 쌓인 경우 드롭다운 이중 표시 방지
  // — 명시 소스 간 규약(dedup 없음)은 불변, 사전 append에만 한정). combobox 한정.
  if (node.widget === 'combobox' && node.suggestionDictScope && node.bindingKey) {
    const dictKey = suggestionDictKey(node.bindingKey);
    const seenTexts = new Set(out.map((r) => r.text));
    for (const raw of resolveSortableRows([dictKey], values, dataDefs, resolvedRows)) {
      const text = typeof raw.value === 'string' ? raw.value : '';
      // 깨진 행·명시 소스와 중복은 미표시. 사전 안에 같은 텍스트가 두 번 있는 비정상 데이터도
      // 여기서 걸러야 한다 — key=text라 그대로 내보내면 React key가 충돌한다.
      if (text === '' || seenTexts.has(text)) continue;
      seenTexts.add(text);
      out.push({ key: text, text, badge: '', flag: false, raw });
    }
  }
  return out;
}

/**
 * 소스별 매핑(sourceFieldMaps[sourceKey], 없으면 위젯 레벨 legacy 필드)으로 raw 행 1개를 정규
 * 행으로 변환한다 — resolveNormalizedSortableRows(목록)와 카탈로그 행 즉시 등록의 반환 행 콤보
 * 세팅(단건, ModalTriggerButton submitAction)이 공유하는 단일 정본(정규화 규칙 이원화 금지).
 * 맵이 있으면 그 맵으로만(위젯 레벨과 필드 단위 혼합 없음 — admin이 entry 단위로 저작/삭제).
 * 행 id(자동 합성 채널) — idField는 소스별 매핑 전용(위젯 레벨 legacy 필드에는 id 개념 없음).
 */
export function normalizeSortableRowForSource(
  node: {
    sourceFieldMaps?: Record<string, SortableSourceFieldMap>;
    itemKeyField?: string;
    itemTextField?: string;
    itemBadgeField?: string;
    itemFlagField?: string;
  },
  sourceKey: string,
  raw: Record<string, unknown>,
  fallbackKey: string,
): NormalizedSortableRow {
  const map = node.sourceFieldMaps?.[sourceKey];
  const keyField = (map ? map.keyField : node.itemKeyField) || 'key';
  const textField = (map ? map.textField : node.itemTextField) || 'text';
  const badgeField = map ? map.badgeField : node.itemBadgeField;
  const flagField = map ? map.flagField : node.itemFlagField;
  const idField = map?.idField;
  const idRaw = idField ? raw[idField] : undefined;
  return {
    key: String(raw[keyField] ?? fallbackKey),
    // 표시 템플릿(소스별 매핑 전용 축) 우선 — 미지정 시 기존 단일 필드 경로 그대로(하위호환).
    // 합성은 shared display-template 정본(admin 빌더 미리보기와 동일 구현). 콤보 확정값은
    // 이 text가 그대로 content에 커밋되므로(applyComboboxSelection) 추가 배선 없음.
    // 합성 결과가 ''(전 자리표시자 빈 값 행)이면 textField 폴백 — text ''인 행은 콤보가 선택을
    // 적용하지 않아(''=미선택 관례) 목록에 떠도 고를 수 없는 유령 행이 된다.
    // 결정: docs/decisions/2026-08-24-composer-display-template-axis.md
    text: map?.textTemplate
      ? renderDisplayTemplate(map.textTemplate, raw) || String(raw[textField] ?? '')
      : String(raw[textField] ?? ''),
    badge: map?.badgeTemplate
      ? renderDisplayTemplate(map.badgeTemplate, raw)
      : badgeField
        ? String(raw[badgeField] ?? '')
        : '',
    flag: flagField ? !!raw[flagField] : false,
    raw,
    ...(typeof idRaw === 'string' && idRaw !== '' ? { id: idRaw } : {}),
  };
}

/**
 * 트리(그룹/모달/표 고정 셀 재귀)에서 후보 소스를 쓰는 위젯(sortableSelect·combobox)의 effective
 * 후보 소스 키를 모두 수집(중복 제거). host(web)가 어떤 후보 소스를 런타임 resolve해 resolvedRows로
 * 주입할지 판정하는 데 사용(P3). combobox는 이 rows가 제안 목록(P1 — 미수집이면 카탈로그 소스
 * 제안이 영영 비어 일반 input으로만 degrade). hideWhenNoData/확인 카운트 판정은 sortableSelect
 * 전용 시맨틱이라 collectSortableSelectWidgets(별도)가 담당 — 이 함수만 combobox를 포함한다.
 */
export function collectSortableSelectOptionSources(tree: ComposerWidget[]): string[] {
  const keys = new Set<string>();
  const add = (w: { optionsSource?: string; optionsSources?: string[] }): void => {
    for (const k of effectiveOptionsSources(w)) keys.add(k);
  };
  const usesOptionSources = (iw: InputWidget): boolean =>
    iw.widget === 'sortableSelect' || iw.widget === 'combobox';
  // 위젯 select의 동적 옵션 소스(optionsFrom.catalogKey)도 host가 resolve해야 rows→옵션이 산다
  // (조립 위험도 select의 기준표 rows 등). host(web)는 이 함수 반환 키의 prefill rows를 resolvedRows에
  // 합류시키므로, 여기 수집만으로 web 배선 변경 없이 자동 반영(P4e).
  const addInput = (iw: InputWidget): void => {
    if (usesOptionSources(iw)) add(iw);
    else if (iw.widget === 'select' && iw.optionsFrom?.catalogKey) {
      keys.add(iw.optionsFrom.catalogKey);
    }
  };
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'input') addInput(w);
      else if (w.kind === 'group') walk(w.children);
      else if (w.kind === 'modalTrigger') walk(w.children);
      // repeat: 행 안 콤보/정렬 목록의 소스 키도 host가 resolve해야 제안이 산다 — 소스 키는
      // 행과 무관한 전역(카탈로그/dataDefs/AI 관례 키)이라 값 스코프 격리와 무관하게 수집.
      else if (w.kind === 'repeat') walk(w.children);
      // branch: 투명 컨테이너 — 어느 슬롯이 활성화되든 host가 소스를 resolve해야 하므로 전 슬롯 수집.
      else if (w.kind === 'branch') for (const slot of w.branches) walk(slot.children);
      else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) {
            if (cell.widget) addInput(cell.widget);
          }
        }
      }
    }
  };
  walk(tree);
  return [...keys];
}

/**
 * 트리의 submitAction(등록 모달)이 참조하는 카탈로그 키 수집 — host(web 공개 위임 페이지)가
 * 등록 콜백 배선 여부를 판정하는 데 사용(중복 제거).
 * 하강 규칙은 collectSortableSelectOptionSources와 동형(group/modalTrigger/repeat/branch 재귀).
 * formTable 고정 셀은 InputWidget만 담아 modalTrigger가 올 수 없어 제외한다.
 */
export function collectSubmitActionCatalogKeys(tree: ComposerWidget[]): string[] {
  const keys = new Set<string>();
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'modalTrigger') {
        if (w.submitAction?.catalogKey) keys.add(w.submitAction.catalogKey);
        walk(w.children);
      } else if (w.kind === 'group' || w.kind === 'repeat') {
        walk(w.children);
      } else if (w.kind === 'branch') {
        for (const slot of w.branches) walk(slot.children);
      }
    }
  };
  walk(tree);
  return [...keys];
}

/**
 * 진입 전 사용자 입력 패널 구성 — web 타입 선택 모달이 렌더에, admin 저작 UI가 override 대상 위젯
 * 특정에 쓴다(같은 해소 규칙을 두 앱이 공유 — 중복 구현 시 문구가 드리프트한다).
 */
export interface EntryUserInputMeta {
  /** 문구 override(`InputWidget.entryInput`)를 소유하거나 소유할 대상 sortableSelect 위젯 id. */
  widgetId: string;
  /** 입력 패널 라벨 — 위젯 override 우선 → 파트 메타 fallback. */
  label: string;
  /** 입력 placeholder — 위젯 override 우선 → 파트 메타 fallback(둘 다 없으면 ''). */
  placeholder: string;
  /** 이 입력을 동봉할 파트(사전 조회·소비 시 parts 파라미터로 전달). */
  parts: ImprovementCandidatePart[];
  /**
   * 해소 전 원본 — 대상 위젯의 `entryInput` 그대로(없으면 undefined)와 파트 메타 기본 문구.
   * 저작 UI가 "저작자가 실제로 입력한 값"과 "비웠을 때 쓰일 기본값"을 구분해 보여줄 때 쓴다
   * (렌더(web)는 해소된 label/placeholder만 쓰면 된다).
   */
  source: {
    override?: { label: string; placeholder?: string };
    fallback: { label: string; placeholder: string };
  };
}

/**
 * 템플릿 위젯 트리에서 진입 전 사용자 입력 패널의 대상 위젯·문구·파트를 해소한다(없으면 null).
 * 트리 하강 범위는 collectSortableSelectOptionSources와 동일(group/modalTrigger/repeat/branch 슬롯/
 * formTable 고정 셀). 여러 위젯이 걸리면 **첫 매칭 위젯**이 문구·override 대상이고 parts는 합집합.
 *
 * 문구 해소(2026-07-25 개정): 위젯 `entryInput` override 우선 → 파트 메타 fallback. 빈 문자열/공백만
 * 있는 override는 "지움"으로 보고 메타로 되돌린다(admin이 빈 입력 시 필드를 제거하지만 방어).
 *
 * **발동 조건은 override가 아니라 파트 메타의 `userInput` 선언**이다 — override가 있어도 그 위젯이
 * 쓰는 파트가 userInput을 선언하지 않으면 패널을 띄우지 않는다. 근거(2026-07-25 서버 흐름 확인):
 * 사용자 지시문은 `ReportRefinementService.generateImprovementCandidates(_, _, userGuidance, parts)`의
 * guidance 축으로만 흘러(`getGuidanceCachedOrFresh` → `generateManagementGuidance`가 프롬프트에
 * `## [최우선] 당일 현장 지도사항`으로 삽입) 실제로 생성에 반영된다. summary/conclusion 축은
 * `InspectionAiCacheService.getCachedOrFetchBoth` → `generateSummaryForCandidates`로 가는데 이 경로엔
 * userGuidance 파라미터 자체가 없다. 즉 그쪽 소스만 쓰는 위젯에서 패널을 띄우면 사용자가 입력한
 * 지시문이 서버까지 가고도 조용히 버려진다("입력했는데 아무 변화 없음"). 소비 파트가 늘어나면
 * 프롬프트와 짝을 이루는 파트 메타에 `userInput`을 추가하는 것이 정본 절차이고, 그 순간 이 함수는
 * 코드 변경 없이 그 파트에서도 발동한다(범용성은 override가 아니라 메타 선언이 담당).
 *
 * 노출 게이트는 이 함수가 아니라 **템플릿 전역 `ReportTemplate.entryUserInput`** — 호출처(모달)가
 * 그 스위치가 켜졌을 때만 이 함수로 문구·파트를 특정한다(2026-07-16 3차 정정 유지).
 */
export function findEntryUserInputMeta(
  tree: ComposerWidget[] | undefined,
): EntryUserInputMeta | null {
  if (!tree?.length) return null;
  const partKeys = Object.keys(
    COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS,
  ) as ImprovementCandidatePart[];
  const parts: ImprovementCandidatePart[] = [];
  let target: { widget: InputWidget; part: ImprovementCandidatePart } | undefined;

  const consider = (iw: InputWidget): void => {
    if (iw.widget !== 'sortableSelect') return;
    for (const key of effectiveOptionsSources(iw)) {
      const part = partKeys.find((p) => COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS[p] === key);
      // userInput 미선언 파트는 서버가 지시문을 소비하지 않는다 → 발동 제외(위 JSDoc 근거).
      if (!part || !COMPOSER_IMPROVEMENT_CANDIDATE_PART_META[part]?.userInput) continue;
      if (!parts.includes(part)) parts.push(part);
      if (!target) target = { widget: iw, part };
    }
  };
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'input') consider(w);
      else if (w.kind === 'group') walk(w.children);
      else if (w.kind === 'modalTrigger') walk(w.children);
      else if (w.kind === 'repeat') walk(w.children);
      else if (w.kind === 'branch') for (const slot of w.branches) walk(slot.children);
      else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) if (cell.widget) consider(cell.widget);
        }
      }
    }
  };
  walk(tree);

  if (!target) return null;
  const metaText = COMPOSER_IMPROVEMENT_CANDIDATE_PART_META[target.part].userInput;
  if (!metaText) return null;
  const override = target.widget.entryInput;
  return {
    widgetId: target.widget.id,
    label: override?.label?.trim() ? override.label : metaText.label,
    placeholder: override?.placeholder?.trim() ? override.placeholder : metaText.placeholder,
    parts,
    source: { ...(override ? { override } : {}), fallback: metaText },
  };
}

// ============================================================
// followKey — 반복 행 안 콤보의 본문 선택 추종(구 SMS 실시자→행 담당 전체 덮어쓰기 동형)
// 결정 근거: apps/web/.../new-safety-management-status.tsx 사업장 담당자 변경 useEffect.
// 순수 헬퍼로 분리(이 패키지엔 effect 실행 test 환경 부재 — renderToStaticMarkup은 effect 미실행).
// ============================================================

/**
 * 반복 위젯 children(중첩 group/branch/formTable 고정 셀 포함)에서 followKey를 지정한 combobox
 * follower 위젯을 수집. 중첩 repeat는 자체 스코프라 하강하지 않는다(행 스코프 격리).
 */
export function collectFollowerComboWidgets(tree: ComposerWidget[]): InputWidget[] {
  const out: InputWidget[] = [];
  const consider = (iw: InputWidget): void => {
    if (iw.widget === 'combobox' && typeof iw.followKey === 'string' && iw.followKey !== '') {
      out.push(iw);
    }
  };
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'input') consider(w);
      else if (w.kind === 'group') walk(w.children);
      else if (w.kind === 'modalTrigger') walk(w.children);
      else if (w.kind === 'branch') for (const slot of w.branches) walk(slot.children);
      else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) if (cell.widget) consider(cell.widget);
        }
      }
      // repeat 중첩은 하강 안 함(자체 행 스코프 — 상위 본문 추종 대상 아님).
    }
  };
  walk(tree);
  return out;
}

/** follower 콤보 1건의 본문 선택 해소 결과 — computeFollowKeyRows 입력(순수 계산 분리). */
export interface FollowKeyBodySelection {
  /** follower 위젯 id(시그니처/시드 추적 키). */
  followerId: string;
  /** follower 콤보의 bindingKey(행 스코프 기록 대상) + writes 전파 축. */
  node: Pick<InputWidget, 'bindingKey' | 'writes'>;
  /** 본문 선택에 대응하는 소스 행(재조회 결과). null=본문 값이 비어 no-op. */
  sourceRow: NormalizedSortableRow | null;
  /** 본문 선택 시그니처(text+id) — 변화 감지용. 본문 비면 ''. */
  sig: string;
}

/**
 * follower 콤보의 followKey가 가리키는 본문 콤보 선택(text+id)을 읽고, follower 자신의 optionsSources
 * (같은 담당자 목록)에서 그 선택에 대응하는 소스 행을 재조회한다 — 선택 id 우선, 없으면 텍스트
 * **완전 일치** 폴백(id 없는 소스에서도 확정 입력이 추종되게).
 *
 * 확정 게이트(2026-07-21 사용자 확정): 선택 id도 없고 완전 일치 행도 없으면 sourceRow=null(no-op) —
 * 타이핑 중간의 부분 텍스트가 전 행에 실시간 전파되던 것을 차단(구 SMS는 select라 확정 선택만
 * 존재). 선택 후 텍스트 수정(stale recall로 id 해제)도 같은 경로로 no-op. id는 있는데 rows에서
 * 행을 못 찾으면(소스 지연/불일치) raw 없이 text+id만 담은 합성 행(writes 전파 불가, 값·id는 채움).
 * 본문 값이 비면 sourceRow=null(no-op — 행 값 유지).
 */
export function resolveFollowKeySelection(
  follower: InputWidget,
  values: Record<string, unknown>,
  dataDefs?: DataDef[],
  resolvedRows?: Record<string, Record<string, unknown>[]>,
): FollowKeyBodySelection {
  const followKey = follower.followKey ?? '';
  const node = {
    bindingKey: follower.bindingKey,
    writes: follower.writes,
  } as Pick<InputWidget, 'bindingKey' | 'writes'>;
  const bodyText = values[followKey];
  const textStr = typeof bodyText === 'string' ? bodyText : '';
  const idRaw = values[comboboxSelectedIdKey(followKey)];
  const idStr = typeof idRaw === 'string' ? idRaw : '';
  if (textStr === '') {
    return { followerId: follower.id, node, sourceRow: null, sig: '' };
  }
  const candidates = resolveNormalizedSortableRows(follower, values, dataDefs, resolvedRows);
  const match =
    (idStr !== '' ? candidates.find((r) => r.id === idStr) : undefined) ??
    candidates.find((r) => r.text === textStr);
  if (idStr === '' && !match) {
    // 미확정(타이핑 중간/불일치 자유 텍스트) — 전파하지 않음(행 값 유지).
    return { followerId: follower.id, node, sourceRow: null, sig: '' };
  }
  const sourceRow: NormalizedSortableRow = match ?? {
    key: idStr || textStr,
    text: textStr,
    badge: '',
    flag: false,
    ...(idStr !== '' ? { id: idStr } : {}),
  };
  return { followerId: follower.id, node, sourceRow, sig: JSON.stringify([textStr, idStr]) };
}

/**
 * 소스 선택 1건을 한 행에 적용해 새 행 객체 반환 — applyComboboxSelection(자기 값+id 채널+writes
 * 전파 일괄)을 행 스코프 onChange로 태운다. 필드별로 현재 값과 같으면 기록하지 않아, 변화가 전혀
 * 없으면 원본 행 참조를 그대로 반환한다(불필요 기록 방지 — 구 SMS allMatch 가드 동형).
 */
export function applyFollowKeySelectionToRow(
  row: Record<string, unknown>,
  node: Pick<InputWidget, 'bindingKey' | 'writes'>,
  sourceRow: NormalizedSortableRow,
): Record<string, unknown> {
  let working = row;
  let changed = false;
  const onChange = (field: string, v: unknown): void => {
    const cur = field.includes('.') ? getByPath(working, field) : working[field];
    // undefined↔'' 는 동일 취급(host setValues 병합 관례 — 키 삭제 없음).
    if (cur === v || (cur === undefined && v === '')) return;
    // 단행 어댑터로 flat/dotted 불변 set(형제 필드 보존 — updateRowAtIndex 규약 재사용).
    working = updateRowAtIndex([working], 0, field, v)[0] as Record<string, unknown>;
    changed = true;
  };
  applyComboboxSelection(node, sourceRow, working, onChange);
  return changed ? working : row;
}

/**
 * followKey 추종의 순수 계산 — 본문 선택(selections)과 이전 시그니처/시드 상태를 받아 갱신된 행 배열을
 * 반환한다. follower별 트리거 구분:
 *  - 최초 관측(prevSig 미존재): 적용 안 함(현재 상태를 baseline으로만 기록 — 편집 재로드 시 저장값
 *    보존, 덮어쓰기는 이후 "변화"부터).
 *  - 본문 선택 변화(prevSig ≠ 현재): **전 행** 적용(수동 수정분도 덮어씀 — 구 SMS 동형).
 *  - 본문 선택 유지(prevSig = 현재): **시드 미기록 행(신규 추가 행)만** 적용 — 기존 행 미재덮어쓰기.
 * 본문 값이 빈 follower(sourceRow=null)는 no-op(행 값 유지). rowId 없는 행은 시드 추적 불가라
 * 유지 경로에선 건드리지 않는다(반복 추가 행은 createBlankRow가 rowId 부여).
 * @returns rows(갱신), changed(기록 필요 여부), nextSig/nextSeeded(다음 관측 상태).
 */
export function computeFollowKeyRows(
  rows: Record<string, unknown>[],
  selections: FollowKeyBodySelection[],
  prevSig: Record<string, string>,
  seededRowIds: ReadonlySet<string>,
): {
  rows: Record<string, unknown>[];
  changed: boolean;
  nextSig: Record<string, string>;
  nextSeeded: Set<string>;
} {
  let nextRows = rows;
  let changed = false;
  const nextSig: Record<string, string> = {};
  for (const sel of selections) {
    if (sel.sourceRow === null) {
      // 본문 비었으면 no-op — 시그니처만 ''로 기록(다음에 채워지면 "변화"로 감지).
      nextSig[sel.followerId] = '';
      continue;
    }
    nextSig[sel.followerId] = sel.sig;
    const prev = prevSig[sel.followerId];
    const firstSeen = prev === undefined;
    const bodyChanged = !firstSeen && prev !== sel.sig;
    if (firstSeen) continue; // baseline만 기록, 적용 안 함.
    nextRows = nextRows.map((row) => {
      const rowId = typeof row.rowId === 'string' ? row.rowId : undefined;
      const seeded = rowId !== undefined && seededRowIds.has(rowId);
      // 본문 변화=전 행 / 본문 유지=시드 안 된 신규 행만(rowId 없는 행은 유지 경로에서 제외).
      const shouldApply = bodyChanged || (rowId !== undefined && !seeded);
      if (!shouldApply) return row;
      const applied = applyFollowKeySelectionToRow(row, sel.node, sel.sourceRow!);
      if (applied !== row) changed = true;
      return applied;
    });
  }
  const nextSeeded = new Set<string>();
  for (const row of nextRows) {
    if (typeof row.rowId === 'string') nextSeeded.add(row.rowId);
  }
  return { rows: nextRows, changed, nextSig, nextSeeded };
}

// 행 스코프 쓰기 pending(RowScopedPending·makeRowScopedOnChange·reconcileRowScopedPending)의 정의는
// `dataSourceCollection.ts`에 산다 — 행 그리드(RowsCollectionEditor)도 같은
// 보호를 쓰는데 그 모듈은 이 파일을 import할 수 없다(순환). 이 모듈 경로는 재export로 유지.
export {
  makeRowScopedOnChange,
  reconcileRowScopedPending,
  type RowScopedPending,
} from './dataSourceCollection';

/**
 * combobox 제안 부분일치 필터 — 입력이 비면(공백 포함) 전체, 아니면 text lowercase includes.
 * ComboboxWidget 렌더와 테스트가 공유하는 순수 함수.
 */
export function filterComboboxRows(
  rows: NormalizedSortableRow[],
  input: string,
): NormalizedSortableRow[] {
  const q = input.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => r.text.toLowerCase().includes(q));
}

/**
 * combobox 마지막 적용 선택의 세션 메모리 — 타이핑으로 선택이 무효화되면 전파값을 회수(recall)하는 데
 * 사용. written에는 비배열(본문 위젯 bindingKey) target만 담는다 — 배열 일괄 target은 회수 대상 제외
 * (collectScalarRegisteredWrites 참조: 배열 행에 다른 데이터가 있어 되돌리기 위험).
 */
export interface ComboboxSelectionMemory {
  /** 선택 행 text — 입력 텍스트가 이와 달라지는 순간 선택 무효(회수 트리거). */
  rowText: string;
  /**
   * 전파 당시의 스칼라 target·값 — 현재 값이 그대로일 때만 회수(사용자 직접 수정분 보존).
   * 합성 id 키(`${bindingKey}__selectedId` — 자동 합성 채널)도 같은 메커니즘으로 실려 함께 회수된다.
   */
  written: { target: string; value: unknown }[];
}

/**
 * combobox 제안 행 선택 적용 — ① 자기 값 onChange(bindingKey, 행 text) ② 행 id가 있으면(소스 매핑
 * idField — 자동 합성 채널) content 합성 키(`${bindingKey}__selectedId`)에 기록 ③ writes가 있으면
 * 선택 행 raw 필드를 outputsValues로 applyRegisteredWrites 전파(P2 — RegisteredWidgetWrite 계약
 * 재사용, source=raw 필드 경로). 자유 타이핑 경로는 이 함수를 타지 않는다(전파 없음 — shared 규약).
 * 합성 키 기록은 회수 메모리 written에도 실려, 텍스트가 선택에서 벗어나면 recall이 함께 비운다
 * (재타이핑=신규 전환 — 결정 2026-07-17 "자동 합성 채널"). id 없는 행 선택 시 이전 선택의 잔존
 * id(행 교체·재진입)는 즉시 비운다 — id 채널이 아예 없는 소스는 값 자체가 없어 no-op(키 오염 없음).
 * @returns stale 회수용 선택 메모리 — 회수 대상(스칼라 전파·합성 id)이 없었으면 null.
 */
export function applyComboboxSelection(
  node: Pick<InputWidget, 'bindingKey' | 'writes'>,
  row: NormalizedSortableRow,
  values: Record<string, unknown>,
  onChange: (bindingKey: string, value: unknown) => void,
): ComboboxSelectionMemory | null {
  onChange(node.bindingKey, row.text);
  const idKey = comboboxSelectedIdKey(node.bindingKey);
  const written: ComboboxSelectionMemory['written'] = [];
  if (row.id) {
    onChange(idKey, row.id);
    written.push({ target: idKey, value: row.id });
  } else if (typeof values[idKey] === 'string' && values[idKey] !== '') {
    onChange(idKey, '');
  }
  if (row.raw) {
    applyRegisteredWrites(node.writes, row.raw, values, onChange);
    written.push(...collectScalarRegisteredWrites(node.writes, row.raw));
  }
  return written.length > 0 ? { rowText: row.text, written } : null;
}

/**
 * combobox stale 전파값 회수 — 입력 텍스트가 마지막 적용 선택의 rowText와 달라지면(선택 무효) 전파
 * 기록을 되돌린다. 각 target은 **현재 값이 전파 당시 값과 그대로 같을 때만** 비운다 — 그 사이 사용자가
 * 직접 수정한 값은 보존(사용자 소유 원칙). "비움" 값은 ''(텍스트 input을 지우는 onChange 경로가
 * 기록하는 값과 동일 관례 — isEmptyWriteValue/visibleWhen 모두 ''를 빈 값으로 취급하고, host
 * setValues는 `{...prev, [k]: v}` 병합이라 rc에 undefined 키 삭제 관례가 없음).
 * @returns 선택이 무효화되었으면 true — 호출측(위젯)은 메모리를 해제한다(one-shot).
 */
export function recallComboboxSelection(
  memory: ComboboxSelectionMemory,
  newText: string,
  values: Record<string, unknown>,
  onChange: (bindingKey: string, value: unknown) => void,
): boolean {
  if (newText === memory.rowText) return false;
  for (const w of memory.written) {
    if (values[w.target] === w.value) onChange(w.target, '');
  }
  return true;
}

/**
 * combobox 선택 해제 — requireSelection(선택 전용) 칸의 드롭다운 "선택 해제" 항목이 호출한다.
 * 타이핑이 확정값을 못 바꾸는 칸이라 회수(recall) 트리거가 없어, 해제만이 값을 비우는 경로다.
 * ① 확정값 '' ② 세션 메모리(writes 전파·합성 id) 회수 — recallComboboxSelection과 같은 규약
 * ('' clear + 사용자 직접 수정분 보존) ③ 메모리 없는 재진입(편집 재로드)에도 저장된 합성 id는
 * 비운다("id가 남는 경우=텍스트가 선택과 같은 경우뿐" invariant — 결정 2026-07-17 자동 합성 채널).
 * @returns null — 호출측 lastSelectionRef에 그대로 대입(해제 후 메모리 소거, one-shot).
 */
export function clearComboboxSelection(
  node: Pick<InputWidget, 'bindingKey'>,
  memory: ComboboxSelectionMemory | null,
  values: Record<string, unknown>,
  onChange: (bindingKey: string, value: unknown) => void,
): null {
  onChange(node.bindingKey, '');
  if (memory) {
    // 빈 텍스트는 rowText(선택 행 text — 비빈)와 항상 다르므로 회수가 확실히 발동한다.
    recallComboboxSelection(memory, '', values, onChange);
  }
  // 합성 id는 무조건 비운다(회수가 값 불일치로 건너뛴 경우까지 커버 — '' 재기록은 host 병합에 무해).
  const idKey = comboboxSelectedIdKey(node.bindingKey);
  if (typeof values[idKey] === 'string' && values[idKey] !== '') {
    onChange(idKey, '');
  }
  return null;
}

/**
 * 제안형 콤보 위젯(combobox) — 자유 텍스트 입력 + 후보 rows(정규 행) 부분일치 제안 드롭다운.
 * 값=string(입력/선택 텍스트). rows 0이면 드롭다운 채널 없이 일반 텍스트 input으로 degrade
 * (최초 데이터 없음 케이스). 제안 항목은 text + 회색 `(badge)` 병기(구 SMS 장비 "(dB)" 동형).
 * 표시는 포커스/입력 변경 시, 닫기는 바깥 클릭·Esc·선택(apps/web Combobox UX 참고 — rc는 web
 * 의존 금지라 자체 구현). 캔버스(interactive)에서도 동일 렌더·동작(WYSIWYG — 저작 어포던스는
 * selectable 래퍼 담당, 특례 없음). 드롭다운 positioning·z 등 임계 스타일은 인라인
 * (rc 신규 Tailwind 유틸 금지 — 메모리 rc-package-tailwind-class-purge-inline-style).
 * 결정: 2026-07-16-composer-combobox-dependent-value-axes (P1·P2).
 */
/** 콤보 드롭다운 최대 높이(px) — 위치 측정의 위/아래 펼침 판정과 listbox maxHeight가 공유. */
const COMBOBOX_MENU_MAX_H = 224;

function ComboboxWidget({
  node,
  value,
  rows,
  values,
  onChange,
  labelEl,
  fieldWrapClass,
  controlStyle,
  inputId,
  disabled,
  onOpenModalRequest,
}: {
  node: InputWidget;
  value: unknown;
  rows: NormalizedSortableRow[];
  values: Record<string, unknown>;
  onChange: (bindingKey: string, value: unknown) => void;
  labelEl: ReactNode;
  fieldWrapClass?: string;
  controlStyle: CSSProperties;
  inputId: string;
  /** enabledWhen 비활성 — input disabled(controlStyle에 비활성 시각은 이미 합성됨) + 드롭다운 미개방. */
  disabled?: boolean;
  /**
   * 액션 항목(actionItem) 클릭 → modalTrigger 모달 열기 요청(cross-widget 채널 —
   * ComposerWidgetTreeView의 요청 state 경유). 미전달(flat 레거시 경로 등)이면 클릭 no-op.
   */
  onOpenModalRequest?: (widgetId: string) => void;
}) {
  // 라벨 미표시('none')면 labelEl이 null로 들어오므로 컨트롤이 접근명을 잃는다 — node에서 직접
  // 접근명을 파생해 input에 붙인다(그 외엔 undefined라 속성 미출력, 회귀 0).
  const controlAriaLabel = controlAriaLabelFor(node);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // 액션 항목 클릭 → 모달이 닫히며 포커스가 input으로 복원될 때 onFocus가 드롭다운을 재개방하는
  // 것을 1회 억제(2026-07-18 사용자 정정 — 모달 닫힘 후 콤보가 열려 있으면 안 됨). input에 대한
  // 실제 포인터 조작(onMouseDown)은 억제를 해제해 정상 열림을 보존한다.
  const suppressFocusOpenRef = useRef(false);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  // 드롭다운 뷰포트 좌표 — body portal(fixed) 배치용. 래퍼 내 absolute는 조상(캔버스 섹션 카드/
  // 페이지)의 overflow 클리핑에 잘린다(2026-07-18 캔버스 실측). null=미측정(포탈 미렌더).
  const [menuPos, setMenuPos] = useState<{
    left: number;
    width: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  // 마지막 적용 선택 메모리(세션 ref) — 타이핑으로 텍스트가 rowText에서 벗어나면 전파값을 1회 회수
  // 후 해제(one-shot). 재진입(편집 재로드)·파생 경로(writes 값 없음)는 메모리가 없으므로 회수
  // 없음(저장값 보존 — 의도된 한계).
  const lastSelectionRef = useRef<ComboboxSelectionMemory | null>(null);
  // 선택 전용 칸(requireSelection — 규약 정본은 shared `InputWidget.requireSelection` doc):
  // 타이핑한 글자는 확정값이 아니라 **제안 필터 검색어 초안**이다 — suggestionsFrom
  // requireSelection의 draft 규약 동형(값은 pick에서만 커밋, blur 시 초안 폐기 → 확정값 표시 복귀).
  const requireSelection = node.requireSelection === true;
  // 검색어 초안 — null=초안 없음(확정값 그대로 표시). 자유 입력 칸은 항상 null(종전 경로 회귀 0).
  const [draft, setDraft] = useState<string | null>(null);
  const committed = typeof value === 'string' ? value : '';
  // 표시·필터 공용 텍스트 — 선택 전용 칸에서 초안이 있으면 초안(검색어), 아니면 확정값.
  const text = requireSelection && draft !== null ? draft : committed;
  const hasRows = rows.length > 0;
  // 액션 항목(actionItem) 선언 시 rows 0이어도 드롭다운 채널 유지 — "일치/데이터 없음"이 곧 신규
  // 등록 시점(결정 2026-07-17 콤보 드롭다운 액션 항목 축). 둘 다 없을 때만 일반 텍스트 degrade.
  const actionItem = node.actionItem;
  const hasDropdown = hasRows || !!actionItem;
  const filtered = filterComboboxRows(rows, text);
  // 선택 해제 항목(선택 전용 칸 한정) — 타이핑이 확정값을 못 바꾸는 칸이라 이 항목만이 값을 비우는
  // 경로다. 확정값이 있을 때만 표시(자유 입력 칸은 지우기=타이핑이라 불필요, 종전 그대로).
  const clearItemVisible = requireSelection && committed !== '';
  // 드롭다운 열림 실질 판정(옵션·액션·해제 항목 중 하나는 있어야 실제 표시) — aria-expanded와 포탈 렌더 공유.
  const menuVisible = open && (filtered.length > 0 || !!actionItem || clearItemVisible);

  // 바깥 클릭 닫기 — open 동안만 구독(전역 리스너 상시 누적 방지). 드롭다운은 body portal이라
  // wrapRef 밖 — listboxRef도 내부로 인정해야 옵션 mousedown이 "바깥"으로 오인되지 않는다.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: globalThis.MouseEvent): void => {
      const t = e.target as Node;
      if (
        wrapRef.current &&
        !wrapRef.current.contains(t) &&
        !(listboxRef.current && listboxRef.current.contains(t))
      )
        setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // 위치 측정 — open 동안 스크롤(capture: 캔버스 내부 스크롤 포함)·리사이즈마다 재측정해 입력에
  // 앵커 유지. 아래 공간이 부족하고 위가 더 넓으면 위로 펼침(bottom 앵커).
  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const measure = (): void => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < COMBOBOX_MENU_MAX_H + 12 && r.top > spaceBelow;
      setMenuPos(
        openUp
          ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4 }
          : { left: r.left, width: r.width, top: r.bottom + 4 },
      );
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  const pick = (row: NormalizedSortableRow): void => {
    // 선택 전용 칸: 초안은 역할을 다했다 — 값이 확정되므로 표시도 확정값으로 돌아간다(드롭다운 동형).
    setDraft(null);
    // writes는 **트리 저작만 채우는 축**이다 — 파생 경로(deriveWriteForm)는 값을 싣지 않으므로
    // 전파 없음(자유 입력과 동일). 2026-09-30(S2)로 두 경로의 타입은 하나가 됐지만 값은 그대로다.
    lastSelectionRef.current = applyComboboxSelection(
      node,
      row,
      values,
      onChange,
    );
    setOpen(false);
  };

  const inputEl = (
    <input
      id={inputId}
      type="text"
      aria-label={controlAriaLabel}
      role={hasDropdown ? 'combobox' : undefined}
      aria-expanded={hasDropdown ? menuVisible : undefined}
      aria-autocomplete={hasDropdown ? 'list' : undefined}
      autoComplete="off"
      className={inputClass}
      style={controlStyle}
      value={text}
      // 선택 전용 칸의 빈 입력도 "검색해서 고르는 칸"임이 보여야 한다(은닉 금지 — 드롭다운 규약 공유).
      placeholder={
        node.placeholder ?? (requireSelection ? SUGGEST_REQUIRE_SELECTION_PLACEHOLDER : undefined)
      }
      required={node.required}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.value;
        if (requireSelection) {
          // 선택 전용 칸: 친 글자는 값이 아니라 검색어다 — 초안에만 담고 확정(onChange 기록)은
          // 건너뛴다(suggestionsFrom requireSelection 동형). 확정값·합성 id·전파값이 그대로라
          // 아래 회수(recall)·재진입 메모리 경로는 발동 지점이 없다(텍스트가 선택에서 벗어나는
          // 일 자체가 없음).
          setDraft(next);
          if (hasDropdown) setOpen(true);
          return;
        }
        // 재진입(편집 재로드) — 세션 메모리는 없지만 저장된 합성 id가 남아 있으면 현재 텍스트를
        // 선택 텍스트로 간주해 메모리를 재구성한다. 타이핑으로 텍스트가 벗어나는 즉시 id가
        // 회수되어 "id가 남는 경우=텍스트가 선택과 같은 경우뿐" invariant를 재진입에도 유지
        // (결정 2026-07-17 "자동 합성 채널" — 재타이핑=신규 전환). writes 전파분 회수는 종전대로
        // 재진입 미지원(저장값 보존 — 의도된 한계).
        const idKey = comboboxSelectedIdKey(node.bindingKey);
        const savedId = values[idKey];
        if (!lastSelectionRef.current && typeof savedId === 'string' && savedId !== '') {
          lastSelectionRef.current = {
            rowText: text,
            written: [{ target: idKey, value: savedId }],
          };
        }
        // 타이핑으로 선택이 무효화되면 전파값 회수(one-shot — 회수 후 메모리 해제).
        const memory = lastSelectionRef.current;
        if (memory && recallComboboxSelection(memory, next, values, onChange)) {
          lastSelectionRef.current = null;
        }
        onChange(node.bindingKey, next);
        if (hasDropdown) setOpen(true);
      }}
      onFocus={
        hasDropdown
          ? () => {
              // 액션 클릭 직후 모달 포커스 복원으로 인한 재개방 1회 억제(suppressFocusOpenRef 주석).
              if (suppressFocusOpenRef.current) {
                suppressFocusOpenRef.current = false;
                return;
              }
              setOpen(true);
            }
          : undefined
      }
      onMouseDown={
        hasDropdown
          ? () => {
              suppressFocusOpenRef.current = false;
            }
          : undefined
      }
      onKeyDown={
        hasDropdown
          ? (e) => {
              if (e.key === 'Escape' && open) {
                e.preventDefault();
                setOpen(false);
              }
            }
          : undefined
      }
      onBlur={
        requireSelection
          ? () => {
              // 칸을 빠져나가면 고르지 않은 검색어는 값이 아니므로 버린다 — 표시가 확정값으로
              // 되돌아온다(드롭다운 onBlur 동형. Esc는 "제안 닫기"라 초안을 남긴다 — 같은 규약.
              // 제안·액션 항목 mousedown은 preventDefault로 blur를 막으므로 pick과 race 없음).
              setDraft(null);
            }
          : undefined
      }
    />
  );
  // rows 0 + 액션 항목 없음 = 일반 텍스트 input degrade(드롭다운 래퍼 자체 없음 — case 'text'와 동형 마크업).
  if (!hasDropdown) {
    // 선택 전용 칸은 자유 입력 degrade가 값 보장("골라진 값만")을 깨므로 읽기 전용으로 —
    // 값 표시·복사는 유지하고 왜 못 쓰는지 안내(suggestionsFrom 미배선 degrade 동형, shared doc 정본).
    if (requireSelection) {
      return (
        <div key={node.id} className={fieldWrapClass}>
          {labelEl}
          <input
            id={inputId}
            type="text"
            aria-label={controlAriaLabel}
            className={inputClass}
            style={{ ...controlStyle, ...SUGGEST_UNAVAILABLE_CONTROL_STYLE }}
            value={committed}
            placeholder={node.placeholder ?? SUGGEST_REQUIRE_SELECTION_PLACEHOLDER}
            readOnly
          />
          <SuggestNote text={SUGGEST_REQUIRE_SELECTION_UNAVAILABLE} />
        </div>
      );
    }
    return (
      <div key={node.id} className={fieldWrapClass}>
        {labelEl}
        {inputEl}
      </div>
    );
  }
  return (
    <div key={node.id} className={fieldWrapClass}>
      {labelEl}
      {/* w-full: side 배치(flex) 안에서도 종전 input과 동일하게 잔여 폭 채움. position은 임계라 인라인. */}
      <div ref={wrapRef} className="w-full" style={{ position: 'relative' }}>
        {inputEl}
        {/* 선택 전용 칸에서 검색어만 쳐 둔 상태의 안내 — 드롭다운(SuggestionDropdown) 규약 동형:
            **항상 렌더하고 visibility만 토글**해 표 셀 안에서도 행 높이 흔들림 0(줄바꿈까지 선점).
            자유 입력 칸은 이 안내 자체가 없어 종전 그대로(회귀 0). */}
        {requireSelection && (
          <div
            style={{ visibility: draft !== null && draft.trim() !== '' ? 'visible' : 'hidden' }}
            aria-hidden={draft === null || draft.trim() === '' ? true : undefined}
          >
            <SuggestNote text={SUGGEST_REQUIRE_SELECTION_HINT} />
          </div>
        )}
        {/* disabled 전환 시점에 열려 있던 드롭다운도 즉시 닫힘(잔존 open state 무시).
            body portal + fixed — 래퍼 내 absolute는 조상 overflow(캔버스 섹션/페이지)에 잘리므로
            뷰포트 좌표로 탈출. z 90=모달(z-[80]) 안 콤보도 위에 떠야 함(포커스 앵커 일시 UI). */}
        {!disabled && menuVisible && menuPos && typeof document !== 'undefined' &&
          createPortal(
          <div
            ref={listboxRef}
            role="listbox"
            // 드롭다운 임계 스타일(위치·z·상한·박스) 전부 인라인.
            style={{
              position: 'fixed',
              left: menuPos.left,
              width: menuPos.width,
              ...(menuPos.top !== undefined ? { top: menuPos.top } : { bottom: menuPos.bottom }),
              zIndex: 90,
              maxHeight: COMBOBOX_MENU_MAX_H,
              overflowY: 'auto',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
            }}
          >
            {filtered.map((r, idx) => (
              <button
                // key에 index 합성 — 정규 행 key가 텍스트 기반인 소스(동명이인 등 같은 텍스트 행
                // 2개)에서도 React key 고유 보장(duplicate key 경고·행 누락 방지, 전 콤보 공통 방어).
                key={`${r.key}#${idx}`}
                type="button"
                role="option"
                aria-selected={false}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                // mousedown 시점에 선택 — click까지 기다리면 input blur/바깥클릭 닫기가 먼저 닫아버림
                // (web Combobox 동형). preventDefault로 input 포커스 유지.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(r);
                }}
              >
                {r.text}
                {r.badge ? <span className="text-gray-400"> ({r.badge})</span> : null}
              </button>
            ))}
            {/* 선택 해제(선택 전용 칸 + 확정값 있을 때만, 액션 항목 위) — 값·합성 id·writes 전파를
                한 번에 회수(clearComboboxSelection). 중립 텍스트(값을 비우는 보조 동작 시맨틱). */}
            {clearItemVisible && (
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50"
                style={filtered.length > 0 ? { borderTop: '1px solid #e5e7eb' } : undefined}
                // mousedown 시점 처리 + preventDefault — 옵션 버튼과 동형(blur/바깥클릭 닫기 선점 방지).
                onMouseDown={(e) => {
                  e.preventDefault();
                  setDraft(null);
                  lastSelectionRef.current = clearComboboxSelection(
                    node,
                    lastSelectionRef.current,
                    values,
                    onChange,
                  );
                  setOpen(false);
                }}
              >
                선택 해제
              </button>
            )}
            {/* 고정 액션 항목(구 SMS "+새 담당자 추가" 동형) — 필터 결과 0이어도 표시(위 menuVisible
                게이트). 옵션 버튼과 동일 밀도 + 브랜드 텍스트(액션 시맨틱). 상단 구분선은 위에 옵션·해제
                항목이 있을 때만(항목 경계 임계 시각 — 인라인, 조건부라 클래스 불가). */}
            {actionItem ? (
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full text-left px-3 py-2 text-sm font-medium text-brand-600 hover:bg-gray-50"
                style={filtered.length > 0 || clearItemVisible ? { borderTop: '1px solid #e5e7eb' } : undefined}
                // mousedown 시점 처리 + preventDefault — 위 옵션 버튼과 동형(input blur/바깥클릭
                // 닫기가 click 전에 드롭다운을 닫아버리는 것 방지).
                onMouseDown={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  // 선택 전용 칸: 등록 모달로 넘어가는 순간 검색어 초안은 역할 종료 — 버려서
                  // 모달 닫힘 후 확정값(모달 commit 반영분 포함) 표시를 초안이 가리지 않게 한다.
                  setDraft(null);
                  // 모달 닫힘 시 포커스가 input으로 복원되며 onFocus가 재개방하는 것 1회 억제.
                  suppressFocusOpenRef.current = true;
                  onOpenModalRequest?.(actionItem.opensWidgetId);
                }}
              >
                {actionItem.label}
              </button>
            ) : null}
          </div>,
          document.body,
        )}
      </div>
    </div>
  );
}

/** sortableSelect 한 항목 — 드래그 핸들 + 체크박스 + 배지/보조배지 + 본문(구 SMS SortableItem 동형). */
function SortableSelectItem({
  id,
  text,
  badge,
  flag,
  flagLabel,
  checked,
  onToggle,
  domIdSuffix,
}: {
  id: string;
  text: string;
  badge: { label: string; className: string } | null;
  flag: boolean;
  flagLabel: string;
  checked: boolean;
  onToggle: (k: string) => void;
  /** 행 인스턴스 스코프 DOM id 접미 — 같은 sortableSelect가 반복 행마다 렌더될 때 항목 체크박스 id 충돌 방지. */
  domIdSuffix?: string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const checkboxId = domIdSuffix ? `sortable-item-${id}--${domIdSuffix}` : `sortable-item-${id}`;
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-2.5 px-3 py-2.5 border-b border-gray-100 last:border-b-0 bg-white ${isDragging ? 'shadow-md' : ''}`}
    >
      <button
        type="button"
        className="flex-shrink-0 mt-0.5 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
        {...attributes}
        {...listeners}
        aria-label="드래그하여 순서 변경"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M9 3a1 1 0 11-2 0 1 1 0 012 0zM9 8a1 1 0 11-2 0 1 1 0 012 0zM9 13a1 1 0 11-2 0 1 1 0 012 0zM13 3a1 1 0 11-2 0 1 1 0 012 0zM13 8a1 1 0 11-2 0 1 1 0 012 0zM13 13a1 1 0 11-2 0 1 1 0 012 0z" />
        </svg>
      </button>
      <input
        id={checkboxId}
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(id)}
        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
      />
      <label htmlFor={checkboxId} className="min-w-0 flex-1 cursor-pointer">
        {(badge || flag) && (
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            {badge && (
              <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${badge.className}`}
              >
                {badge.label}
              </span>
            )}
            {flag && (
              <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                {flagLabel}
              </span>
            )}
          </div>
        )}
        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line break-words">{text}</p>
      </label>
    </div>
  );
}

/**
 * 선택 정렬 목록 위젯(sortableSelect) — 후보 rows를 체크 선택 + dnd-kit 드래그 정렬 → 조립 텍스트 산출.
 * 값(controlled): `{ selectedKeys, orderedKeys, composedText }`. 구 SMS ImprovementCompositionModal 일반화 이식.
 * rows는 정규 행(resolveNormalizedSortableRows) — 소스별 필드 매핑은 resolve 시점에 끝나 있어
 * 여기선 고정 key/text/badge/flag만 읽는다(2026-07-16 소스별 매핑).
 * readonly 모드: 조작 없이 composedText만 표시(기존 readonly 규약).
 */
function SortableSelectWidget({
  node,
  value,
  rows,
  onChange,
  labelEl,
  domIdSuffix,
}: {
  node: InputWidget;
  value: unknown;
  rows: NormalizedSortableRow[];
  onChange: (bindingKey: string, value: unknown) => void;
  labelEl: ReactNode;
  /** 행 인스턴스 스코프 DOM id 접미(renderWriteNodeCore가 그대로 통과) — 항목 체크박스 id 유일화. */
  domIdSuffix?: string;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const flagLabel = node.itemFlagLabel || '캐시 사용';
  const prefix = node.composePrefix ?? '- ';
  const isReadonly = node.mode === 'readonly';

  // rows → key 순서/조회 맵(중복 키는 최초만). key 폴백(row-i)은 정규화가 이미 처리.
  const rowByKey = new Map<string, NormalizedSortableRow>();
  const rowKeys: string[] = [];
  rows.forEach((r) => {
    if (rowByKey.has(r.key)) return;
    rowByKey.set(r.key, r);
    rowKeys.push(r.key);
  });
  const textOf = (k: string): string => rowByKey.get(k)?.text ?? '';
  // 배지 값별 순환 색(등장 순 배정 — 렌더마다 결정적).
  const badgeColorByValue = new Map<string, string>();
  const badgeOf = (k: string): { label: string; className: string } | null => {
    const label = rowByKey.get(k)?.badge ?? '';
    if (!label) return null;
    let cls = badgeColorByValue.get(label);
    if (!cls) {
      cls = SORTABLE_BADGE_PALETTE[badgeColorByValue.size % SORTABLE_BADGE_PALETTE.length];
      badgeColorByValue.set(label, cls);
    }
    return { label, className: cls };
  };
  const flagOf = (k: string): boolean => !!rowByKey.get(k)?.flag;

  // 값 복원: 저장된 selectedKeys/orderedKeys를 현존 rows로 필터, 신규 rows는 뒤에 append.
  const saved =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Partial<SortableSelectValue>)
      : undefined;
  const savedOrdered =
    saved && Array.isArray(saved.orderedKeys)
      ? saved.orderedKeys.filter((k) => rowByKey.has(k))
      : [];
  const orderedKeys = [...savedOrdered, ...rowKeys.filter((k) => !savedOrdered.includes(k))];
  const selectedKeys = (
    saved && Array.isArray(saved.selectedKeys) ? saved.selectedKeys : []
  ).filter((k) => rowByKey.has(k));

  const buildComposed = (ordered: string[], selected: string[]): string =>
    ordered
      .filter((k) => selected.includes(k))
      .map((k) => textOf(k))
      .filter(Boolean)
      .map((t) => `${prefix}${t}`)
      .join('\n');

  const emit = (ordered: string[], selected: string[]): void =>
    onChange(node.bindingKey, {
      selectedKeys: selected,
      orderedKeys: ordered,
      composedText: buildComposed(ordered, selected),
    } satisfies SortableSelectValue);

  const toggle = (k: string): void =>
    emit(
      orderedKeys,
      selectedKeys.includes(k) ? selectedKeys.filter((x) => x !== k) : [...selectedKeys, k],
    );
  const onDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedKeys.indexOf(String(active.id));
    const newIndex = orderedKeys.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    emit(arrayMove(orderedKeys, oldIndex, newIndex), selectedKeys);
  };

  const composedText = buildComposed(orderedKeys, selectedKeys);
  // 래퍼 앵커(required 미입력 「그 칸으로 데려가기」) — 이 위젯은 **자기 DOM 루트를 소유**하므로
  // renderWriteNodeCore의 공통 cloneElement가 닿지 않는다(그 doc의 ⚠️ 절). 루트가 둘이라 양쪽에.
  const anchorProps = writeFieldAnchorProps(node.id, domIdSuffix);

  if (isReadonly) {
    return (
      <div key={node.id} {...anchorProps}>
        {labelEl}
        <Card
          padding="none"
          surface="soft"
          elevation="none"
          className="px-3 py-2.5 text-sm text-gray-700 whitespace-pre-line leading-relaxed"
          aria-readonly="true"
        >
          {composedText || <span className="text-gray-400">선택된 항목이 없습니다.</span>}
        </Card>
      </div>
    );
  }

  return (
    <div key={node.id} {...anchorProps}>
      {labelEl}
      {rowKeys.length > 0 ? (
        // 후보 목록만 스크롤(구 SMS 모달: 안내·미리보기·버튼 고정, 리스트 신축) — 높이 상한은
        // rc 임의값 유틸 금지 규약에 따라 인라인(45vh ≈ 구 70vh 모달에서 리스트 몫).
        <div
          className="border border-gray-200 rounded-card overflow-y-auto"
          style={{ maxHeight: '45vh' }}
        >
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={orderedKeys} strategy={verticalListSortingStrategy}>
              {orderedKeys.map((k) => (
                <SortableSelectItem
                  key={k}
                  id={k}
                  text={textOf(k)}
                  badge={badgeOf(k)}
                  flag={flagOf(k)}
                  flagLabel={flagLabel}
                  checked={selectedKeys.includes(k)}
                  onToggle={toggle}
                  domIdSuffix={domIdSuffix}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <p className="text-sm text-gray-400 text-center py-6 border border-gray-200 rounded-card">
          후보 항목이 없습니다.
        </p>
      )}
      {node.showPreview && (
        <div className="mt-3 space-y-1.5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            미리보기
            <span className="text-xs font-normal text-gray-500">({selectedKeys.length}개 선택됨)</span>
          </h4>
          {/* 미리보기 — 구 SMS 동형(min 72px, 최대 20vh 스크롤). 상한은 인라인(임의값 유틸 금지). */}
          <Card
            padding="none"
            surface="soft"
            elevation="none"
            className="px-3 py-2.5 min-h-[72px] overflow-y-auto"
            style={{ maxHeight: '20vh' }}
          >
            {composedText ? (
              <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                {composedText}
              </p>
            ) : (
              <p className="text-sm text-gray-400">항목을 선택하면 미리보기가 표시됩니다.</p>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

/** 트리(그룹 재귀 포함)에서 sortableSelect input 위젯을 수집 — modalTrigger hideWhenNoData 데이터 판정용. */
function collectSortableSelectWidgets(tree: ComposerWidget[]): InputWidget[] {
  const out: InputWidget[] = [];
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'input' && w.widget === 'sortableSelect') out.push(w);
      else if (w.kind === 'group') walk(w.children);
      else if (w.kind === 'modalTrigger') walk(w.children);
      // branch: 투명 컨테이너 — 전 슬롯 하강(어느 슬롯 활성이든 데이터 판정 대상).
      else if (w.kind === 'branch') for (const slot of w.branches) walk(slot.children);
    }
  };
  walk(tree);
  return out;
}

/** ComposerWidgetTreeView가 prop으로 받는 admin 편집 interactive 객체(선언 원본). */
type ComposerTreeInteractiveBase = NonNullable<
  Parameters<typeof ComposerWidgetTreeView>[0]['interactive']
>;

/** ComposerWidgetTreeView의 admin 편집 interactive 객체 타입(모달 저작·반복 행 스코프 파생 재사용 위해 이름화·export). */
export type ComposerTreeInteractive = ComposerTreeInteractiveBase & {
  /**
   * 반복 행 스코프에서 축소되기 **전의** 저작 채널 원본 — 이 행에서 연 모달 콘텐츠에만 쓴다.
   * 행 본문에는 여전히 축소된 채널이 적용된다(같은 위젯 id가 행마다 중복 draggable/droppable로
   * 등록되는 충돌 방지). 모달은 포커스 행에서만 열리므로(modalAuthoringId가 포커스 행에만 전달)
   * 모달 콘텐츠는 1회만 렌더돼 중복 등록이 발생하지 않는다.
   * 반복 밖(본문) 경로에선 미지정 — 모달 콘텐츠가 본문 채널(=축소 없음)을 그대로 쓴다.
   */
  modalAuthoringSrc?: ComposerTreeInteractive;
};

/** modalTrigger 모달 콘텐츠(children) 재귀 렌더에 넘길 렌더 컨텍스트 — 모달 스코프 values/onChange는 위젯이 소유. */
interface ModalChildRenderContext {
  dataSources?: DataSourceDef[];
  dataDefs?: DataDef[];
  dataSourceRowsRenderer?: DataSourceRowsRenderer;
  registeredFormWidgets?: Record<string, ComposerRegisteredFormWidget>;
  registeredWidgetContext?: {
    companyId?: string;
    siteId?: string;
    values?: Record<string, unknown>;
    improvementImage?: ComposerImprovementImageHost;
  };
  view?: string;
  /** host가 런타임 resolve한 후보 rows(소스 키별) — 모달 안 sortableSelect가 본문 격리를 넘어 본다(P3). */
  resolvedRows?: Record<string, Record<string, unknown>[]>;
  /**
   * 카탈로그 행 즉시 등록 호스트 콜백(submitAction 모달 확인 시 rc가 위임 — 결정 2026-07-17
   * "등록 모달 = 즉시 등록"). 성공 시 등록/갱신된 행(카탈로그 rows 형식, id 포함)을 반환하고
   * 호스트는 자기 소유 제안 rows(resolvedRows)에도 그 행을 반영한다. null=실패(모달 유지 + 오류
   * 문구). 미배선(external 무인증 등)이면 submitAction 모달을 여는 콤보 액션 항목이 숨겨진다.
   */
  onCatalogRowSubmit?: (
    catalogKey: string,
    values: Record<string, string>,
  ) => Promise<Record<string, unknown> | null>;
  /**
   * 범용 검색 제안 호스트 콜백(suggestionsFrom) — onCatalogRowSubmit 전파 경로와 동형으로
   * 모달 children·반복 행 서브트리까지 전 지점 전달(행 무관 순수 함수라 전 행 전달 무방).
   */
  onSuggestSearch?: ComposerSuggestSearch;
  /** 제안 행 선택 통지(usage 증가 등 호스트 부수효과) — onSuggestSearch와 동일 전파. */
  onSuggestSelect?: ComposerSuggestSelect;
  /** photo 업로드 호스트 콜백 — onSuggestSearch와 동일하게 모달·반복 행 서브트리까지 전 지점 전파. */
  onPhotoUpload?: ComposerPhotoUpload;
  /**
   * 서명 슬롯 host 채널(P2) — 반복 행 서브트리로 전파(onPhotoUpload 동형). 모달 children은
   * 값 스코프가 modalValues(행 아님)라 서명 위젯이 오더라도 주소 불성립 → 표시 전용.
   */
  signatureSlots?: ComposerSignatureSlotsContext;
  /**
   * admin 편집 interactive 객체(P1b 저작). 저작 열림 모드에서 모달 콘텐츠 트리에 선택/인라인 편집을
   * 주입한다(dnd wrapper는 제외 — portal 안 parentId 오배치 방지, 트리 패널이 드래그 경로). 미지정=실폼.
   */
  interactive?: ComposerTreeInteractive;
  /** 저작 열림 대상 modalTrigger id(admin). widget.id와 일치하면 모달을 열린 상태로 유지·저작. */
  authoringOpenId?: string;
  /** 저작 열림 모달 닫기 요청(Esc/취소/오버레이) — host가 authoring state 해제. */
  onAuthoringClose?: () => void;
  /**
   * 저작 활성 분기 슬롯 override(branch 위젯 id → 슬롯 index) — 중첩 인스턴스(모달 children·반복 행)
   * 서브트리의 branch도 부모와 동일하게 저작 슬롯 탭 전환에 반응하도록 전파한다. 미지정=라이브 평가.
   */
  activeBranchSlots?: Record<string, number>;
  /**
   * 행 인스턴스 스코프 DOM id 접미(writeFieldDomId 규약) — 반복 행 서브트리가 자기 조각을 덧붙이고
   * (appendRowDomIdSuffix), 모달 children은 **연 스코프의 접미를 그대로 물려받는다**(행 안에서 연
   * 모달의 입력이 다른 행 모달과 같은 id를 갖지 않게). 최상위 경로에선 미지정 = `wf-{id}` 그대로.
   */
  domIdSuffix?: string;
}

/**
 * 본문 트리에서 이 modalTrigger를 여는 콤보(actionItem.opensWidgetId 일치)를 찾는다 — 첫 매칭.
 * 하강 규칙은 collectSortableSelectOptionSources와 동형(input/group 재귀 + formTable 고정 셀).
 * 모달 children 콤보는 자기 스코프의 modalTrigger만 열 수 있는 규약이라 본문 탐색에선 하강 불필요.
 * submitAction 성공 시 반환 행을 이 콤보에 applyComboboxSelection 동형으로 적용하는 근거.
 */
function findActionComboboxFor(tree: ComposerWidget[], modalId: string): InputWidget | undefined {
  let found: InputWidget | undefined;
  const check = (iw: InputWidget): void => {
    if (!found && iw.widget === 'combobox' && iw.actionItem?.opensWidgetId === modalId) found = iw;
  };
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (found) return;
      if (w.kind === 'input') check(w);
      else if (w.kind === 'group') walk(w.children);
      else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) if (cell.widget) check(cell.widget);
        }
      }
    }
  };
  walk(tree);
  return found;
}

/**
 * 본문 트리에서 id로 modalTrigger를 찾는다(group 재귀 — 모달 버튼 중첩은 규약상 금지라 modalTrigger
 * children엔 하강하지 않음). 콤보 액션 항목의 대상 모달이 submitAction(즉시 등록)인지 판정용.
 */
function findModalTriggerById(
  tree: ComposerWidget[],
  id: string,
): ModalTriggerWidget | undefined {
  for (const w of tree) {
    if (w.kind === 'modalTrigger' && w.id === id) return w;
    if (w.kind === 'group') {
      const inner = findModalTriggerById(w.children, id);
      if (inner) return inner;
    }
    // branch: 투명 컨테이너 — 슬롯 children에 중첩된 modalTrigger도 역참조 대상.
    if (w.kind === 'branch') {
      for (const slot of w.branches) {
        const inner = findModalTriggerById(slot.children, id);
        if (inner) return inner;
      }
    }
  }
  return undefined;
}

/**
 * 커스텀 모달 버튼 위젯 — 버튼 배치 + 모달(코어 위젯 서브트리) + 확인 시 값 반영.
 * 값 스코프 계약(결정 2026-07-15-composer-custom-modal-widget): 본문 values ↔ 모달 스코프 로컬 state 분리.
 *  - 열림: 본문 bindingKey에 저장된 이전 UI 상태(object) 복원, 없으면 빈 객체.
 *  - 모달 안 onChange: 로컬 스코프에만 기록(본문 무변화).
 *  - 확인: purpose(valueCommit)의 commit[].from→to 본문 기록 + 모달 스코프 전체를 bindingKey로 본문
 *    저장(재오픈 복원용). 처리 방식은 목적 카테고리 dispatch(아래 switch)가 kind별로 소유
 *    (결정 2026-07-16-composer-modal-purpose-category).
 *  - 취소/Esc/오버레이: 로컬 스코프 폐기(본문 무변화).
 *  - autoOpen: 실폼(!interactive)에서만 mount 후 1회, 저장된 상태가 없을 때만(구 SMS modalShown 동형).
 * 저작 캔버스(interactive)에선 자동 오픈 없이 버튼 클릭으로 모달을 열어 내용 확인만(편집 저작 P1b).
 * rc 전용 Tailwind 유틸 금지 규약: 아이콘 치수 등 임계 시각값은 인라인 style(버튼/버튼 클래스는 앱 공통 유틸).
 */
function ModalTriggerButton({
  widget,
  bodyValues,
  onBodyChange,
  interactive,
  childContext,
  openRequest,
  linkedCombobox,
}: {
  widget: ModalTriggerWidget;
  bodyValues: Record<string, unknown>;
  onBodyChange: (bindingKey: string, value: unknown) => void;
  interactive: boolean;
  childContext: ModalChildRenderContext;
  /**
   * cross-widget 모달 열기 요청(콤보 드롭다운 액션 항목 — ComposerWidgetTreeView 채널 state).
   * widgetId가 자기 id일 때 seq 변화마다 1회 연다(seq 증가로 같은 모달 재요청 구분).
   */
  openRequest?: { widgetId: string; seq: number } | null;
  /**
   * 이 모달을 여는 본문 콤보(actionItem 역참조 — ComposerWidgetTreeView가 계산해 주입).
   * submitAction 성공 시 반환 행을 이 콤보에 선택 적용(텍스트+합성 id+writes 전파 — 제안 선택 동형).
   */
  linkedCombobox?: InputWidget;
}) {
  const [open, setOpen] = useState(false);
  const [modalValues, setModalValues] = useState<Record<string, unknown>>({});
  // 카탈로그 행 즉시 등록(submitAction) 진행 상태 — pending 동안 확인(등록) 비활성, error는
  // 실패 시 모달 유지 + 인라인 문구(다음 열림/재시도에 초기화).
  const [submitState, setSubmitState] = useState<{ pending: boolean; error: string | null }>({
    pending: false,
    error: null,
  });
  const autoOpenedRef = useRef(false);

  // P1b/P2 저작 열림: 이 modalTrigger가 "모달 편집 열기" 대상이면 캔버스에서 모달을 열린 상태로
  // 유지하고 콘텐츠를 interactive(선택/인라인 편집 + dnd 배치)로 렌더한다. 본문 캔버스와 동형의
  // 1급 저작 표면(결정 2026-07-15-composer-modal-authoring-nonmodal-dnd). portal 안 parentId
  // 오배치는 아래 ComposerWidgetTreeView에 rootParentId={widget.id}를 주입해 해결(P1b가 미룬 원인).
  const authoringOpen = interactive && childContext.authoringOpenId === widget.id;
  // 반복 행 안 modalTrigger면 childContext.interactive는 행 스코프 축소본(repeatRowScopedInteractive)
  // 이라 dnd/인라인 편집/표 셀 채널이 비어 있다 → 모달 콘텐츠만은 축소 전 원본(modalAuthoringSrc)을
  // 쓴다(포커스 행에서만 실려 오고, 모달 콘텐츠는 그 행에서 1회만 렌더 — 중복 draggable 등록 없음).
  // 하이라이트 채널(selectedId/selectedIds/hoveredId)도 원본을 쓴다: 축소본은 포커스 행에서 base와
  // 같은 값을 담으므로 결과가 동일하고(행 밖 경로도 원본=자기 자신), 채널을 섞지 않는 편이 단순하다.
  const src = childContext.interactive?.modalAuthoringSrc ?? childContext.interactive;
  const innerInteractive: ComposerTreeInteractive | undefined =
    authoringOpen && src
      ? {
          // 선택/hover/인라인 편집 — 본문과 동일 채널.
          selectedId: src.selectedId,
          selectedIds: src.selectedIds,
          hoveredId: src.hoveredId,
          onSelect: src.onSelect,
          onWidgetTextChange: src.onWidgetTextChange,
          editRequest: src.editRequest,
          // 더블클릭 드릴(모달 안 섹션 진입) — 본문과 동형.
          onWidgetDrill: src.onWidgetDrill,
          // dnd 배치·재정렬 — renderWrapper(draggable+insert-before droppable)/renderDropSlot(말미
          // append). rootParentId로 모달 children 부모가 modalTrigger id로 정확히 계산됨.
          renderWrapper: src.renderWrapper,
          renderDropSlot: src.renderDropSlot,
          // formTable 채널 — 모달 안에도 표가 배치될 수 있어 셀 포커스/편집/하이라이트를 통과.
          onFormTableCellFocus: src.onFormTableCellFocus,
          onFormTableCellTextChange: src.onFormTableCellTextChange,
          focusedCells: src.focusedCells,
          hoveredCell: src.hoveredCell,
          // 제외: modalAuthoringId/onModalAuthoringClose — 모달 안 모달 저작은 지원 안 함(모달 버튼
          // 중첩 금지 규약). 넘기면 중첩 modalTrigger가 열린 채로 유지되려 시도할 수 있어 차단.
        }
      : undefined;

  // 목적(purpose) 카테고리 dispatch — 단일 지점(결정 2026-07-16-composer-modal-purpose-category).
  // 카테고리가 소유하는 처리(확인 시 commit 매핑)와 데이터 판정 대상(hideWhenNoData·footer 확인
  // 카운트가 집계하는 sortableSelect — valueCommit 시맨틱)을 kind로 분기한다. 새 카테고리 추가 시
  // 이 switch에 case를 더한다(default의 never 가드가 tsc로 강제). purpose 미지정 템플릿은
  // effectiveModalPurpose가 top-level commit으로 valueCommit을 파생(하위호환 — 동작 불변).
  const purpose = effectiveModalPurpose(widget);
  const { commitMaps, sortableWidgets } = ((): {
    /** 확인 시 본문 기록 매핑(from→to) */
    commitMaps: ModalCommitMap[];
    /** hideWhenNoData 데이터 판정 + footer 확인 카운트 집계 대상 */
    sortableWidgets: InputWidget[];
  } => {
    switch (purpose.kind) {
      case 'valueCommit':
        return {
          commitMaps: purpose.commit,
          // hideWhenNoData: 모달 콘텐츠의 sortableSelect 후보 rows가 전부 0개면 버튼 미렌더 +
          // autoOpen 생략(구 SMS "후보 없으면 버튼 숨김" 동형). 판정 rows는 위젯 렌더와 동일 헬퍼
          // (resolveNormalizedSortableRows — length 판정이라 결과 동일, 단일 정본 유지)로 공유.
          // sortableSelect 위젯이 아예 없으면 판정 대상이 없으므로 숨기지 않는다(회귀 안전).
          sortableWidgets: collectSortableSelectWidgets(widget.children),
        };
      default: {
        // exhaustiveness 가드 — 새 ModalPurpose 카테고리의 dispatch 분기 누락을 tsc로 검출.
        // ⚠️ 객체(purpose)가 아니라 판별 키(purpose.kind)에 건다: 단일 멤버인 동안 ModalPurpose는
        //    유니온이 아니라 default에서 객체가 never로 안 좁혀짐(TS 판별 좁힘은 유니온 전용).
        //    kind(리터럴)는 단일/복수 무관하게 좁혀져 멤버 추가 시에도 동일하게 강제된다.
        const _exhaustive: never = purpose.kind;
        throw new Error(`modalTrigger: 알 수 없는 purpose kind '${String(_exhaustive)}'`);
      }
    }
  })();
  const suppressed =
    !!widget.hideWhenNoData &&
    sortableWidgets.length > 0 &&
    !sortableWidgets.some(
      (sw) =>
        resolveNormalizedSortableRows(
          sw,
          bodyValues,
          childContext.dataDefs,
          childContext.resolvedRows,
        ).length > 0,
    );

  // 본문 bindingKey에 저장된 이전 UI 상태(object)면 복원용 복사본, 아니면 빈 객체.
  const readSaved = (): Record<string, unknown> => {
    const saved = bodyValues[widget.bindingKey];
    return saved && typeof saved === 'object' && !Array.isArray(saved)
      ? { ...(saved as Record<string, unknown>) }
      : {};
  };
  // 모달 스코프 초기값 — 저장된 UI 상태가 있으면 그대로 복원, 없으면(최초 열림) children에 저작된
  // defaultValue로 시드. children bindingKey는 모달 스코프 전용이라 본문 flat 시드가 도달하지 못해
  // (collectWidgetDefaultValues doc 참조) 스코프 열림 시점이 기본값 시드의 유일한 정본 지점이다.
  // 확인 시 스코프 전체가 본문에 저장되므로 렌더 fallback과 달리 보이는 값=저장 값이 유지된다.
  // autoOpen 판정(저장 상태 유무)은 readSaved 기준 그대로 — 기본값 시드가 autoOpen을 막지 않는다.
  const initialModalValues = (): Record<string, unknown> => {
    const saved = readSaved();
    return Object.keys(saved).length > 0 ? saved : collectWidgetDefaultValues(widget.children);
  };
  const openModal = (): void => {
    setModalValues(initialModalValues());
    setSubmitState({ pending: false, error: null });
    setOpen(true);
  };
  // autoOpen: 실폼에서만 최대 1회(autoOpenedRef 가드) — 이미 확인해 저장된 상태가 있으면 생략.
  // deps=[suppressed]: 후보가 비동기로 도착(P3)하면 suppressed가 true→false로 풀리며 그 시점에 1회
  // 발화(구 SMS는 sessionStorage로 mount 시 이미 후보가 있었지만, composed는 mount 후 fetch라 마운트
  // 1회 판정(deps [])으론 후보 도착을 못 봄). suppressed=true 동안은 위 guard로 ref를 세우지 않는다.
  useEffect(() => {
    if (interactive || !widget.autoOpen || autoOpenedRef.current || suppressed) return;
    autoOpenedRef.current = true;
    if (Object.keys(readSaved()).length === 0) openModal();
  }, [suppressed]);

  // cross-widget 열기 요청(콤보 드롭다운 액션 항목) — 자기 id 요청의 seq 변화에 1회씩 반응.
  // 실폼·캔버스 라이브 모두 동작 — 요청은 사용자가 드롭다운 액션을 직접 클릭했을 때만 발생하므로
  // 캔버스 "버튼 클릭=선택만" 규약(버튼 클릭 모달은 속성창 '모달 편집 열기'로만)과 충돌하지 않는다.
  // suppressed 실폼은 아래 return null로 모달 자체가 미렌더라 자연 무시.
  const openRequestSeqRef = useRef(0);
  useEffect(() => {
    if (!openRequest || openRequest.widgetId !== widget.id) return;
    if (openRequest.seq === openRequestSeqRef.current) return;
    openRequestSeqRef.current = openRequest.seq;
    openModal();
  }, [openRequest]);

  // 저작 열림 진입 시 모달 스코프 값 시드(저장 상태 복원, 없으면 children defaultValue — 실폼
  // openModal과 동일 규칙으로 캔버스 WYSIWYG 유지). rc는 react-hooks 플러그인 미적용이라 deps는
  // authoringOpen 전이에만 반응(initialModalValues는 매 렌더 새 함수라 제외).
  useEffect(() => {
    if (authoringOpen) setModalValues(initialModalValues());
  }, [authoringOpen]);

  const setModalValue = (bindingKey: string, value: unknown): void =>
    setModalValues((prev) => ({ ...prev, [bindingKey]: value }));
  const confirm = (): void => {
    // commitMaps = purpose dispatch 산출(valueCommit) — purpose 미지정이면 top-level commit 파생.
    for (const m of commitMaps) {
      const raw = modalValues[m.from];
      // pick 해석: 명시 pick(legacy/특수 용도 — UI 미노출) > 위젯 종류별 **정본 출력** 자동 >
      // 값 그대로. 저작자가 인지하는 위젯 결과물은 미리보기에 보이는 것이라(2026-07-16 사용자 확정)
      // sortableSelect는 pick 미지정이어도 조립 텍스트(composedText)를 기록한다 — 값 객체가 통째로
      // 본문 텍스트 위젯에 실리는 무의미 기록을 정본 출력이 대체.
      const fromKind = collectSortableSelectWidgets(widget.children).some(
        (sw) => sw.bindingKey === m.from,
      )
        ? 'sortableSelect'
        : undefined;
      const effectivePick =
        m.pick ?? (fromKind === 'sortableSelect' ? 'composedText' : undefined);
      const picked =
        effectivePick && raw && typeof raw === 'object' && !Array.isArray(raw)
          ? (raw as Record<string, unknown>)[effectivePick]
          : raw;
      onBodyChange(m.to, picked);
    }
    onBodyChange(widget.bindingKey, modalValues); // 재오픈 복원용 UI 상태 저장
    setOpen(false);
  };
  const cancel = (): void => setOpen(false); // 로컬 스코프 폐기(본문 무변화)
  // 저작 열림 모드의 닫기 — 실폼 동작(commit/취소 폐기) 없이 host의 authoring state만 해제.
  const closeAuthoring = (): void => childContext.onAuthoringClose?.();

  // ── 카탈로그 행 즉시 등록(submitAction) — 확인 동작 교체(결정 2026-07-17 "등록 모달 = 즉시 등록") ──
  const submitAction = widget.submitAction;
  const submitMode = !!submitAction;
  // upsertKey(이름) 필드 매핑 필수 게이트 — 선언(submitAction.keyField, admin이 카탈로그
  // upsertKey를 저작 시점 자동 복사)이 정본. 미지정 구 저작은 연결 콤보의 제안 텍스트 필드로
  // 폴백 추론(textField=키 필드였던 도메인 규약 — 단 textField가 파생 표시 필드(titleName 등)면
  // 어긋나므로 선언이 필요하다). 콤보/매핑이 없어 유도 불가면 서버 400(인라인 오류)이 최종 방어선.
  const submitKeyField = submitAction
    ? (submitAction.keyField ??
      linkedCombobox?.sourceFieldMaps?.[submitAction.catalogKey]?.textField ??
      linkedCombobox?.itemTextField)
    : undefined;
  const submitFieldMapEmpty =
    !!submitAction && Object.keys(submitAction.fieldMap ?? {}).length === 0;
  const submitKeyFieldMissing =
    !!submitAction && !!submitKeyField && submitAction.fieldMap?.[submitKeyField] === undefined;
  const submitMisconfigured = submitFieldMapEmpty || submitKeyFieldMissing;
  // inputFormat children 완결 형식 게이트(phone/business-number/email) — 값이 있는데 미완성이면
  // 등록 차단(등록 모달 경로만 — 일반 작성폼 required 검증(collectMissingRequired)과 별개 축,
  // 값 저장 자체는 허용).
  const submitInvalidFormats = submitMode
    ? collectInvalidInputFormat(widget.children, modalValues, childContext.view)
    : [];
  // 필수(required) children 미입력 게이트 — 이름·연락처 같은 필수 칸이 비면 등록을 막는다
  // (2026-08-24 사용자 적발: 필수 미입력인데 등록 버튼이 활성 — 서버 writer skip으로만 막혀
  // 인라인 오류가 사후에 떴다). 일반 작성폼 제출 게이트(collectMissingRequired)와 같은 정본.
  const submitMissingRequired = submitMode
    ? collectMissingRequired(widget.children, modalValues, childContext.view)
    : [];
  const submitDisabled =
    submitMode &&
    (submitState.pending ||
      submitMisconfigured ||
      submitInvalidFormats.length > 0 ||
      submitMissingRequired.length > 0 ||
      !childContext.onCatalogRowSubmit);
  const submitCatalogRow = async (): Promise<void> => {
    if (!submitAction || !childContext.onCatalogRowSubmit || submitState.pending) return;
    if (submitInvalidFormats.length > 0) return; // disabled 우회(키보드 등) 방어
    if (submitMissingRequired.length > 0) return; // 필수 미입력 — disabled 우회 방어(위와 동형)
    // fieldMap대로 모달 스코프 값 수집(문자열 계약 — keyed upsert writer는 string 필드만).
    const submitValues: Record<string, string> = {};
    for (const [field, fromKey] of Object.entries(submitAction.fieldMap ?? {})) {
      const raw = modalValues[fromKey];
      submitValues[field] = typeof raw === 'string' ? raw : raw == null ? '' : String(raw);
    }
    setSubmitState({ pending: true, error: null });
    const row = await childContext
      .onCatalogRowSubmit(submitAction.catalogKey, submitValues)
      .catch(() => null);
    if (!row) {
      setSubmitState({
        pending: false,
        error: '등록에 실패했습니다. 입력 값을 확인해 주세요(신규 담당자는 이름·연락처 필수).',
      });
      return; // 실패 — 모달 유지(입력 보존)
    }
    setSubmitState({ pending: false, error: null });
    // 연결 콤보 세팅 — 제안 선택(applyComboboxSelection)과 동형 경로: 텍스트=새 행 텍스트,
    // 합성 id 키 기록, writes 부속 전파. 제안 rows 반영은 호스트 소유(콜백 구현부가 자기
    // resolvedRows에 반환 행을 합류) — rc는 로컬 rows를 따로 들지 않는다(채널 단일화).
    if (linkedCombobox) {
      const normalized = normalizeSortableRowForSource(
        linkedCombobox,
        submitAction.catalogKey,
        row,
        String(row.id ?? row.name ?? ''),
      );
      if (normalized.text !== '') {
        applyComboboxSelection(linkedCombobox, normalized, bodyValues, onBodyChange);
      }
    }
    // 모달 닫기 + children 값 초기화 — 등록 모달은 재오픈 시 빈 폼이 정본(commit 저장 안 함).
    onBodyChange(widget.bindingKey, {});
    setModalValues({});
    setOpen(false);
  };

  // hideWhenNoData 판정으로 숨김 — **실폼만**(버튼/모달 미렌더, autoOpen도 위 effect에서 생략).
  // 저작 캔버스에선 배치·선택이 가능해야 하므로 숨기지 않고 dimmed(opacity-40)로 남긴다
  // (visibleWhen 숨김 위젯의 캔버스 규약과 동형, 2026-07-15 사용자 지적).
  if (suppressed && !interactive) return null;
  const canvasSuppressed = suppressed && interactive;
  // 버튼 숨김(hideButton) — 실폼은 본문 트리거 버튼 미렌더(모달·commit 규약은 유지, cross-widget
  // 채널로만 열림). 캔버스는 선택·저작이 가능해야 하므로 반투명 + "(숨김 버튼)" 표기로 유지
  // (hideWhenNoData 캔버스 규약 동형).
  // submitAction 모달인데 호스트 콜백 미배선(external 등) — 등록이 불가능하므로 실폼에선 트리거
  // 버튼도 숨긴다(액션 항목 숨김과 동일 근거 — 콜백 부재 = 액션 비활성).
  const submitUnwired = submitMode && !childContext.onCatalogRowSubmit;
  const formHiddenButton = (!!widget.hideButton || submitUnwired) && !interactive;
  const canvasHiddenButton = !!widget.hideButton && interactive;

  const buttonEl = (
    <button
      type="button"
      className={`text-xs text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1${
        canvasSuppressed || canvasHiddenButton ? ' opacity-40' : ''
      }`}
      title={
        canvasSuppressed
          ? '후보 데이터 없음 — 작성 페이지에선 버튼이 숨겨집니다'
          : canvasHiddenButton
            ? '숨김 버튼 — 작성 페이지에는 표시되지 않습니다(콤보 드롭다운 액션 등으로만 모달이 열립니다)'
            : undefined
      }
      // 캔버스(interactive): 클릭=선택만(버블 → 선택 래퍼) — 모달은 속성창 "모달 편집 열기"로만
      // 연다(2026-07-15 사용자 요청: 선택할 때마다 모달이 뜨면 저작 방해). 실폼만 클릭으로 오픈.
      onClick={interactive ? undefined : () => openModal()}
    >
      <svg
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        style={{ width: '0.875rem', height: '0.875rem' }}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
      {widget.label}
      {canvasHiddenButton ? <span className="text-gray-400 font-normal">(숨김 버튼)</span> : null}
    </button>
  );
  // 칸 안 수평 정렬은 위젯 공통 축(ComposerWidgetBase.alignH — renderWidget 공통 래퍼)이 담당.
  // 확인 버튼 카운트 — 모달 안에 선택 정렬 리스트가 있으면 선택 수를 표기(구 SMS "확인 (N 선택)" 동형).
  // 도메인 중립: sortableSelect 값({selectedKeys})만 집계, 없으면 그냥 '확인'.
  const selectedCount = sortableWidgets.reduce((n, sw) => {
    const v = modalValues[sw.bindingKey];
    const keys = v && typeof v === 'object' ? (v as { selectedKeys?: unknown }).selectedKeys : undefined;
    return n + (Array.isArray(keys) ? keys.length : 0);
  }, 0);
  // submitAction 모달의 확인은 등록 액션 — 라벨/비활성/pending이 commit 확인과 다르다.
  const confirmLabel = submitMode
    ? submitState.pending
      ? '등록 중…'
      : '등록'
    : sortableWidgets.length > 0
      ? `확인 (${selectedCount} 선택)`
      : '확인';
  return (
    <>
      {formHiddenButton ? null : buttonEl}
      <Modal
        open={open || authoringOpen}
        onClose={authoringOpen ? closeAuthoring : cancel}
        title={authoringOpen ? `${widget.modalTitle} — 편집` : widget.modalTitle}
        size={widget.modalSize ?? 'md'}
        // 저작 열림은 non-modal — 오버레이가 트리·속성창을 덮지 않고 클릭을 통과해 병행 조작 가능.
        // 실폼(open, !authoringOpen)은 기존 modal 그대로(회귀 0). 모달 셸·크기는 동일(WYSIWYG).
        nonModal={authoringOpen}
        footer={
          // WYSIWYG: 저작 열림도 실폼과 동일한 '취소'+'확인' footer(캔버스 라이브 모드라 commit 실동작 —
          // onBodyChange가 캔버스 편집 오버레이에 기록). 저작에선 닫기까지 이어서(authoring state 해제).
          // 버튼 스타일=구 SMS 모달 동형.
          <>
            <button
              type="button"
              className={buttonClassName('secondary')}
              onClick={() => {
                cancel();
                if (authoringOpen) closeAuthoring();
              }}
            >
              취소
            </button>
            <button
              type="button"
              className={buttonClassName('primary')}
              disabled={!authoringOpen && submitDisabled}
              onClick={() => {
                // 저작 열림(캔버스 모달 편집)은 실등록 미실행 — submitAction 모달의 확인은 닫기만
                // (commit 모달은 종전대로 캔버스 라이브 commit 후 닫기). 실폼/캔버스 라이브의
                // 액션 경유 열림(open)은 submitMode면 등록 액션으로 교체.
                if (authoringOpen) {
                  if (!submitMode) confirm();
                  closeAuthoring();
                  return;
                }
                if (submitMode) {
                  void submitCatalogRow();
                  return;
                }
                confirm();
              }}
            >
              {confirmLabel}
            </button>
          </>
        }
      >
        {/* 모달 콘텐츠 = 코어 위젯 서브트리(모달 스코프 values). 저작 열림(P1b)이면 interactive(선택/인라인
            편집) 주입해 WYSIWYG 저작 — 같은 렌더러라 스타일은 실폼과 동일, 저작 어포던스만 얹힘.
            실폼/버튼 미리보기는 interactive 미전달(순수 렌더). 재귀 렌더는 ComposerWidgetTreeView 재사용. */}
        <ComposerWidgetTreeView
          tree={widget.children}
          values={modalValues}
          onChange={setModalValue}
          interactive={innerInteractive}
          // 모달 children의 최상위 부모 = 이 modalTrigger. dnd 드롭/재정렬이 본문 root가 아니라
          // 모달 스코프(widget.children)로 정확히 계산되게 주입(P1b가 미룬 parentId 오배치 해결).
          rootParentId={widget.id}
          dataSources={childContext.dataSources}
          dataDefs={childContext.dataDefs}
          dataSourceRowsRenderer={childContext.dataSourceRowsRenderer}
          registeredFormWidgets={childContext.registeredFormWidgets}
          registeredWidgetContext={childContext.registeredWidgetContext}
          view={childContext.view}
          resolvedRows={childContext.resolvedRows}
          onSuggestSearch={childContext.onSuggestSearch}
          onSuggestSelect={childContext.onSuggestSelect}
          onPhotoUpload={childContext.onPhotoUpload}
          signatureSlots={childContext.signatureSlots}
          activeBranchSlots={childContext.activeBranchSlots}
          domIdSuffix={childContext.domIdSuffix}
        />
        {/* submitAction 상태 문구 — 저작 미완(fieldMap 비어있음/키 필드 매핑 누락) 안내 + 등록 실패
            인라인 오류(모달 유지·입력 보존). 흔한 앱 유틸만 클래스로(rc 신규 유틸 금지 규약). */}
        {submitMode && !authoringOpen && submitMisconfigured ? (
          <p className="mt-2 text-xs text-amber-600">
            등록 필드 매핑이 완성되지 않았습니다(이름 필드 매핑 필수) — 양식 저작 설정을 확인해
            주세요.
          </p>
        ) : null}
        {/* inputFormat 미완성 안내 — misconfig(저작 문제)와 같은 amber 결(등록 실패 red와 구분).
            예시는 형식별(collectInvalidInputFormat이 동반) — 혼재 시 중복 제거해 나열. */}
        {submitMode && !authoringOpen && submitInvalidFormats.length > 0 ? (
          <p className="mt-2 text-xs text-amber-600">
            {submitInvalidFormats.map((f) => f.label).join(', ')} 형식이 완성되지 않았습니다(예:{' '}
            {[...new Set(submitInvalidFormats.map((f) => f.example))].join(', ')}).
          </p>
        ) : null}
        {submitMode && submitState.error ? (
          <p className="mt-2 text-xs text-red-600">{submitState.error}</p>
        ) : null}
      </Modal>
    </>
  );
}

// ── repeat 표형(displayVariant:'table') 헬퍼 ──
// 결정: 2026-08-24-repeat-table-display-variant. 표 골격/톤은 formTable(FORM_TABLE_*) 재사용.

/**
 * 표형에서 열을 만들지 않는 비시각 위젯 판정 — 대표: 숨김 버튼 modalTrigger(실폼에서 본문 트리거
 * 미렌더, 콤보 액션 채널로만 열림). 열에서만 제외하고 **행 렌더 인스턴스 안에서는 렌더 유지**해야
 * 모달 채널(actionItem→opensWidgetId, 행 인스턴스 스코프)이 보존된다. 과잉 일반화 금지 —
 * visibleWhen류 조건 숨김은 값에 따라 보이는 축이라 열 제외 대상이 아니다(빈 셀로 렌더).
 */
export function isRepeatTableNonColumnChild(w: ComposerWidget): boolean {
  return w.kind === 'modalTrigger' && !!w.hideButton;
}

/**
 * 표형 **열 도출 단일 정본** — thead 헤더·행 셀(tableCellsMode)·admin 캔버스 열 나열이 전부 이
 * 함수를 써야 th↔td 정합이 구조로 보장된다(2026-08-24 일원화):
 *  1. 비-base 뷰는 그 뷰 표시 순서(orderSiblingsForView), base/미지정은 배열 순서.
 *  2. 비시각 위젯(isRepeatTableNonColumnChild — hideButton modalTrigger)은 열 제외
 *     (행 인스턴스 안 렌더는 유지 — 모달 채널 보존, tableCellsMode가 마지막 셀 안에 렌더).
 *  3. **뷰 숨김(resolveWidgetHidden) 위젯도 열 제외** — 종전엔 헤더만 남고 셀은 미렌더라
 *     빈 "유령 열"이 생겼다(실폼 renderWidget이 hidden을 null 반환). 캔버스도 같은 판정
 *     (그 뷰에서 숨김 = 그 뷰 표에 열 없음 — WYSIWYG, 접근은 트리 패널).
 */
export function repeatTableColumnChildren(
  children: ComposerWidget[],
  view?: string,
): ComposerWidget[] {
  const ordered =
    view && view !== COMPOSER_BASE_VIEW_KEY ? orderSiblingsForView(children, view) : children;
  return ordered.filter(
    (c) => !isRepeatTableNonColumnChild(c) && !resolveWidgetHidden(c, view),
  );
}

/**
 * 활성 분기 슬롯 index 산출 — branch 렌더(renderWidget case 'branch')와 표형 헤더가 **공유**하는
 * 단일 판정 지점. 저작 override(activeBranchSlots[branch.id])가 있으면 condition을 무시하고 그
 * index를 clamp해 쓰고(캔버스 슬롯 탭 선택), 없으면 condition 첫 매칭(evaluateCondition)이다.
 * 매칭 슬롯이 없으면 -1(렌더는 null, 헤더는 빈 문자열).
 */
export function resolveActiveBranchSlotIndex(
  branches: BranchWidgetSlot[],
  scope: Record<string, unknown>,
  override: number | undefined,
): number {
  if (override !== undefined && branches.length > 0) {
    return Math.max(0, Math.min(override, branches.length - 1));
  }
  return branches.findIndex((slot) => evaluateCondition(slot.condition, scope));
}

/**
 * 표형 헤더 라벨 — child input/modalTrigger=label / group=title / **branch=활성 슬롯 이름**.
 *
 * branch 확장(2026-08-24): 조건부 열의 이름을 저작자가 **슬롯 이름(BranchWidgetSlot.label — 캔버스
 * 슬롯 탭에 이미 쓰는 저작 축)** 으로 짓게 한다(새 속성 없음). 예: 위험도 열의 평가 방식별 슬롯을
 * "위험도 (빈도×강도)"로 명명하면 그게 곧 헤더가 된다.
 *
 * 하강 규칙(라벨원 재귀): group은 title이 있으면 **그대로**(회귀 0 — 기존 동작 불변), 없을 때만
 * children의 첫 라벨원으로 하강한다. branch는 활성 슬롯 label → 그 슬롯 children 첫 라벨원 →
 * '' 순. 그 외 kind(design·formTable·repeat)는 ''.
 *
 * ⚠️ **헤더는 행 밖이라 top-level values로 평가한다** — 행 필드를 참조하는 조건이면 행마다 활성
 * 슬롯이 다를 수 있으나 헤더는 표 전체에 하나뿐이다. 그래서 top-level 스코프 기준 첫 매칭 슬롯의
 * 이름을 쓴다(캔버스도 같은 values 경로 = 활성 뷰 미리보기 값). 활성 슬롯 판정 자체는 branch
 * 렌더와 같은 resolveActiveBranchSlotIndex를 재사용해 두 경로가 갈라지지 않게 한다.
 */
export function repeatTableColumnHeaderLabel(
  w: ComposerWidget,
  /** 조건 평가 스코프(top-level values) — 미지정이면 빈 스코프(조건 미매칭 = else 슬롯만 매칭). */
  scope: Record<string, unknown> = {},
  /** 저작 활성 슬롯 override(branch id → 슬롯 index) — 캔버스 슬롯 탭 선택 반영. */
  activeBranchSlots?: Record<string, number>,
): string {
  return repeatTableColumnHeader(w, scope, activeBranchSlots).label;
}

/**
 * 표형 헤더 라벨의 **출처** 분류 — 저작자에게 "무엇을 고치면 이 열 이름이 바뀌는가"를 알리는 축.
 *
 * ⚠️ 텍스트와 출처는 repeatTableColumnHeader **단일 구현**이 한 분기에서 함께 산출한다
 * (이중 구현 금지 — Label/Source export는 그 얇은 래퍼).
 *
 * - `input` — 위젯 라벨(input·modalTrigger의 label)
 * - `groupTitle` — 그룹 제목(group.title)
 * - `groupDescend` — 그룹에 제목이 없어 **첫 자식의 라벨로 하강**한 경우
 * - `branchSlot` — 활성 분기 슬롯 이름(BranchWidgetSlot.label)
 * - `branchDescend` — 활성 슬롯에 이름이 없어 **그 슬롯 첫 자식 라벨로 하강**한 경우
 * - `none` — 헤더가 비어 있음(라벨·제목·슬롯 이름 어느 것도 없음 / 매칭 슬롯 없음 / 그 외 kind)
 */
export type RepeatTableColumnHeaderSource =
  | 'input'
  | 'groupTitle'
  | 'groupDescend'
  | 'branchSlot'
  | 'branchDescend'
  | 'none';

/** 위 분류 판정 — repeatTableColumnHeader(단일 구현)의 source만 반환하는 얇은 래퍼. */
export function repeatTableColumnHeaderSource(
  w: ComposerWidget,
  scope: Record<string, unknown> = {},
  activeBranchSlots?: Record<string, number>,
): RepeatTableColumnHeaderSource {
  return repeatTableColumnHeader(w, scope, activeBranchSlots).source;
}

/**
 * 표형 열 헤더 **텍스트+출처 단일 구현** — 위 repeatTableColumnHeaderLabel/Source doc의 분기를
 * 한 몸으로 판정한다(과거 두 함수가 같은 분기를 이중 구현 — 분기가 늘 때 한쪽만 고쳐 어긋나는
 * 위험을 구조로 제거, 2026-08-24). 기존 두 export는 얇은 래퍼로 시그니처 보존(admin이 import 중).
 */
export function repeatTableColumnHeader(
  w: ComposerWidget,
  scope: Record<string, unknown> = {},
  activeBranchSlots?: Record<string, number>,
): { label: string; source: RepeatTableColumnHeaderSource } {
  if (w.kind === 'group') {
    if (w.title) return { label: w.title, source: 'groupTitle' };
    const label = firstColumnHeaderLabelOf(w.children, scope, activeBranchSlots);
    return label ? { label, source: 'groupDescend' } : { label: '', source: 'none' };
  }
  if (w.kind === 'input' || w.kind === 'modalTrigger') {
    return w.label ? { label: w.label, source: 'input' } : { label: '', source: 'none' };
  }
  if (w.kind === 'branch') {
    const idx = resolveActiveBranchSlotIndex(w.branches, scope, activeBranchSlots?.[w.id]);
    const slot = idx >= 0 ? w.branches[idx] : undefined;
    if (!slot) return { label: '', source: 'none' };
    if (slot.label) return { label: slot.label, source: 'branchSlot' };
    const label = firstColumnHeaderLabelOf(slot.children, scope, activeBranchSlots);
    return label ? { label, source: 'branchDescend' } : { label: '', source: 'none' };
  }
  return { label: '', source: 'none' };
}

/** children 중 라벨원이 있는 첫 위젯의 헤더 라벨(없으면 ''). 표형 헤더 하강 재귀의 공용 스텝. */
function firstColumnHeaderLabelOf(
  children: ComposerWidget[],
  scope: Record<string, unknown>,
  activeBranchSlots?: Record<string, number>,
): string {
  for (const c of children) {
    const label = repeatTableColumnHeaderLabel(c, scope, activeBranchSlots);
    if (label) return label;
  }
  return '';
}

/**
 * 표형 열이 필수 입력을 품는지 — 헤더에 `*`를 병기할지 판정.
 * 표형은 셀 라벨을 **상시 억제**(suppressRepeatTableCellChrome)하므로 라벨 안 필수 `*`가 갈 자리를
 * 잃는다. 그 표식의 자리를 헤더가 대신한다(required **검증**은 불변 — 표시만 보전).
 * 판정은 **선언 기준**이다: input=자기 required / group=하위(중첩 포함) input 중 하나라도 required /
 * branch=**전 슬롯**(활성 여부 무관) children 중 하나라도 required. branch를 선언 기준으로 두는
 * 이유도 같다 — 활성 슬롯 기준이면 값에 따라 헤더 표식이 깜빡인다(런타임 상태 불관 원칙).
 * visibleWhen 등 런타임 상태는 보지 않는다(값에 따라 헤더 표식이 깜빡이면 그게 더 나쁘다).
 * 그 외 kind(design·formTable·repeat·modalTrigger)는 false — 열 자체가 입력 칸이 아니다.
 */
export function repeatTableColumnRequired(w: ComposerWidget): boolean {
  if (w.kind === 'input') return w.required === true;
  if (w.kind === 'group') return w.children.some(repeatTableColumnRequired);
  if (w.kind === 'branch')
    return w.branches.some((slot) => slot.children.some(repeatTableColumnRequired));
  return false;
}

/**
 * 표형 셀 사본(suppressRepeatTableCellChrome 산출물) 등록부 — **비직렬화 내부 표식**이다.
 * 사본은 `labelPlacement:'none'`을 쓰므로(아래 doc) 그대로 두면 renderWriteNode 계열의 required
 * `*` 오버레이 배지가 붙어 헤더 `*`와 이중 표식이 된다. ComposerWidgetBase에 축을 늘리는 대신
 * (shared 타입 오염 금지 — 직렬화 저장 축이 아니다) 사본 객체 자체를 여기 등록하고, 배지 판정
 * (shouldShowRequiredBadge)이 조회해 "셀 사본은 배지 미부착·네이티브 required·aria 유지"를
 * 성립시킨다. WeakMap 캐시(아래)로 사본이 위젯당 1회 생성·안정 참조라 WeakSet 조회가 유효하다.
 */
const REPEAT_TABLE_CELL_SUPPRESSED = new WeakSet<object>();
/** 위젯 → 억제 사본 캐시(위젯당 1회 — 렌더마다 새 객체를 만들면 위 WeakSet 등록도 흔들린다). */
const REPEAT_TABLE_CELL_SUPPRESS_CACHE = new WeakMap<ComposerWidget, ComposerWidget>();

/**
 * 표형 셀 안 위젯의 라벨 억제 — 헤더가 라벨을 대신한다(표시 사본 한정 — 템플릿 트리 선언·required
 * 검증은 불변).
 * - input: `labelPlacement:'none'` 지정 — 라벨 요소는 미렌더하되 **label·required는 유지**해
 *   접근명(controlAriaLabelFor → 컨트롤 aria-label)과 네이티브 required 속성을 보전한다
 *   (label:undefined 스프레드 시절엔 둘 다 사라졌다 — 2026-08-24 접근성 복원). 'none' 규약상
 *   라벨 부속(labelSuffixFrom 병기·labelHint)은 함께 사라진다 — 헤더가 라벨을 대신하는 표 맥락
 *   수용. required `*` 배지는 REPEAT_TABLE_CELL_SUPPRESSED 등록으로 미부착(헤더 `*`가 그 자리).
 * - group: title 억제(titleVariant 무관).
 * - branch: 활성 여부 무관 **전 슬롯 children에 하강 적용한 사본**(branches/slots 구조 보존,
 *   children만 억제 사본) — 헤더=슬롯 이름·셀=라벨 이중 표시 해소. 활성 슬롯만 바꾸면 값에 따라
 *   비활성 슬롯이 활성될 때 억제가 빠진다.
 * 그 외 kind는 그대로(모달 버튼 label은 버튼 텍스트라 유지).
 * (formTable 고정 셀 suppressed는 별도 경로 — 헤더 자동 병기가 없어 셀 `*`가 유일 표식이라 유지.)
 */
function suppressRepeatTableCellChrome(w: ComposerWidget): ComposerWidget {
  const cached = REPEAT_TABLE_CELL_SUPPRESS_CACHE.get(w);
  if (cached) return cached;
  let out: ComposerWidget = w;
  if (w.kind === 'input') {
    out = { ...w, labelPlacement: 'none' };
    REPEAT_TABLE_CELL_SUPPRESSED.add(out);
  } else if (w.kind === 'group') {
    out = { ...w, title: undefined };
  } else if (w.kind === 'branch') {
    out = {
      ...w,
      branches: w.branches.map((slot) => ({
        ...slot,
        children: slot.children.map(suppressRepeatTableCellChrome),
      })),
    };
  }
  if (out !== w) REPEAT_TABLE_CELL_SUPPRESS_CACHE.set(w, out);
  return out;
}

/** repeat 표형 헤더 셀 — 행 그리드 헤더 톤(12px/500/gray-500) + formTable 셀 패딩(인라인 — rc 유틸 금지 규약). */
const REPEAT_TABLE_HEADER_CELL_STYLE: CSSProperties = {
  ...FORM_TABLE_CELL_STYLE,
  fontSize: 12,
  fontWeight: 500,
  color: '#6b7280',
  textAlign: 'left',
  background: '#f9fafb',
  borderBottom: '1px solid #e5e7eb',
};

/** 표형↔카드 폴백 경계 media query — read/구독이 같은 문자열을 쓰도록 상수화. */
const MD_UP_MEDIA_QUERY = `(min-width:${MD_BREAKPOINT_PX}px)`;

/**
 * md 이상 뷰포트 여부(실폼 표형↔카드 폴백 판정) — 단일 렌더 분기용 matchMedia 구독.
 * ComposeWritePageShell readIsXlViewport 선례 동형: SSR 가드는 `typeof window === 'undefined'`부터,
 * matchMedia 부재(jsdom 기본)면 true(데스크톱=표) 폴백. 초기값 lazy 실측 + useEffect 구독 —
 * SSR(renderToStaticMarkup)에서 useLayoutEffect 경고를 내지 않는 관용구이고, 구독 시작 시
 * `apply()` 재실측이 초기 측정과 구독 사이 변화를 흡수한다.
 * enabled=false(표형이 아닌 반복 위젯)는 구독 자체를 생략(훅 호출은 규약상 무조건 — 인자로 분기).
 */
function useIsMdUpViewport(enabled: boolean): boolean {
  const read = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia(MD_UP_MEDIA_QUERY).matches;
  };
  const [isMdUp, setIsMdUp] = useState(read);
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(MD_UP_MEDIA_QUERY);
    const apply = (): void => setIsMdUp(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [enabled]);
  return isMdUp;
}

/**
 * 반복 행 인스턴스 서브트리에 주입할 행 스코프 저작 채널(P4a — 캔버스 행 내부 인스턴스 선택).
 * 규약(사용자 승인): 행 안 어느 위젯 인스턴스를 클릭해도 **행 템플릿의 그 위젯이 선택**되고
 * (속성 패널 연동), **선택 하이라이트는 클릭한 인스턴스(행)에만** 표시된다 — 같은 위젯 id가
 * 행마다 반복 렌더되므로 하이라이트 채널(selectedId/selectedIds/hoveredId)은 포커스 행에만
 * 전달한다(전 행 전달 시 모든 인스턴스가 동시 하이라이트).
 * 선택(onSelect)·드릴(onWidgetDrill — isolation 승격/캐럿 게이트가 이 유무로 분기해 본문과 동형
 * 동작)과 **formTable 셀 저작 4종**(onFormTableCellFocus/onFormTableCellTextChange/focusedCells/
 * hoveredCell — 2026-08-05 사용자 제보: 반복 안 표 셀이 캔버스에서 선택 불가)을 파생하고
 * 그 외 저작 축은 의도적으로 미전달:
 *  - renderWrapper/renderDropSlot(dnd): 같은 위젯 id가 행마다 중복 draggable/droppable로 등록돼
 *    충돌 — 행 템플릿 구조 편집은 트리 패널·속성 패널이 정본.
 *  - onWidgetTextChange(인라인 편집): 후속 판단(선택 규약 범위 밖).
 * 셀 포커스 **상태**(focusedCells/hoveredCell)는 tableId 기준이라 전 행에 주면 모든 인스턴스가
 * 동시 하이라이트 — 선택 하이라이트와 동일하게 포커스 행에만 싣는다. 콜백은 markFocusRow로
 * 클릭 행을 포커스 행으로 승격(행 템플릿 공유라 어느 행에서 고쳐도 정의는 전 행 반영).
 *
 * 모달 저작(modalAuthoringId)은 **하이라이트 채널과 동일하게 포커스 행에만** 전달한다 —
 * 전 행에 주면 같은 모달이 행 수만큼 열리고, 아예 안 주면(2026-07-22 이전 동작) 반복 행 안
 * modalTrigger는 `authoringOpen`이 영영 false라 "모달 편집 열기"를 눌러도 에디터만 어두워지고
 * 모달이 안 뜬다(2026-07-22 사용자 제보: 개선계획 '새 담당자 등록').
 * 그렇게 연 모달 **콘텐츠**는 행 본문과 달리 축소 없는 1급 저작 표면이어야 하므로(dnd 배치·인라인
 * 편집·표 셀 편집), 축소 전 원본을 `modalAuthoringSrc`로 포커스 행에만 실어 보낸다 — 소비는
 * ModalTriggerWidgetView의 모달 children 렌더 한 곳뿐이라 행 본문 동작은 불변이다(2026-07-22).
 * ⚠️ 남은 한계: 반복 행이 0개면 행 서브트리 자체가 렌더되지 않아 이 경로로도 열 수 없다.
 */
export function repeatRowScopedInteractive(
  base: ComposerTreeInteractive,
  isFocusRow: boolean,
  markFocusRow: () => void,
): ComposerTreeInteractive {
  const {
    onSelect,
    onWidgetDrill,
    onWidgetContextMenu,
    onModalAuthoringClose,
    onFormTableCellFocus,
    onFormTableCellTextChange,
  } = base;
  return {
    selectedId: isFocusRow ? base.selectedId : undefined,
    selectedIds: isFocusRow ? base.selectedIds : undefined,
    hoveredId: isFocusRow ? base.hoveredId : undefined,
    modalAuthoringId: isFocusRow ? base.modalAuthoringId : undefined,
    // formTable 셀 저작 — 상태는 포커스 행에만(전 행 동시 하이라이트 방지), 콜백은 클릭 행을
    // 포커스 행으로 승격 후 위임. 텍스트 커밋은 트리 정의 갱신(행 무관)이라 그대로 전달.
    focusedCells: isFocusRow ? base.focusedCells : undefined,
    hoveredCell: isFocusRow ? base.hoveredCell : undefined,
    onFormTableCellFocus: onFormTableCellFocus
      ? (tableId, rowId, columnId, opts) => {
          markFocusRow();
          onFormTableCellFocus(tableId, rowId, columnId, opts);
        }
      : undefined,
    onFormTableCellTextChange,
    // 닫기는 행과 무관하게 host(EditorPage)가 modalAuthoringId를 해제하는 콜백이라 그대로 전달.
    onModalAuthoringClose,
    // 모달 콘텐츠 전용 원본 채널 — 포커스 행(=모달이 열릴 수 있는 유일한 행)에만 싣는다.
    // 행 본문은 계속 아래 축소 채널만 쓰고, ModalTriggerWidgetView가 모달 children 렌더에서만
    // 이 원본을 꺼내 dnd 배치·인라인 편집·표 셀 편집을 본문과 동형으로 복원한다.
    modalAuthoringSrc: isFocusRow ? base : undefined,
    onSelect: (id, opts) => {
      markFocusRow();
      onSelect?.(id, opts);
    },
    onWidgetDrill: onWidgetDrill
      ? (id) => {
          markFocusRow();
          onWidgetDrill(id);
        }
      : undefined,
    // 우클릭도 클릭 행을 포커스 행으로(선택 하이라이트 일관) + host 메뉴 위임 — 행 안 위젯 우클릭이
    // 반복 컨테이너로 버블돼 컨테이너가 선택되던 결함 해소(2026-07-20 제보).
    onWidgetContextMenu: onWidgetContextMenu
      ? (id, e) => {
          markFocusRow();
          onWidgetContextMenu(id, e);
        }
      : undefined,
  };
}

/**
 * 반복(행 스코프 조립) 컨테이너 렌더 — P0 규약 "선언=A + 실행=B"의 실행부(B)
 * (결정: 2026-07-20-composer-repeat-widget-row-scope).
 *
 * 행마다 (rowValues=행 객체, rowOnChange=onChange(over, 행 배열 재구성)) 어댑터를 만들어
 * **기존 서브트리 렌더(ComposerWidgetTreeView)에 그대로 주입**한다 — 모달 격리(modalValues)와
 * 동형. 렌더 시그니처 (values, onChange) 쌍이 스코프 추상화 경계라서:
 *  - 콤보 writes·visibleWhen·enabledWhen·모달 commit·기본값 시드는 로직 변경 0으로 행 안 동작.
 *  - 콤보 합성 id(`__selectedId` 등 `__` 접두 합성 키)는 행 객체 내부 필드로 읽고 쓴다.
 *  - actionItem→modalTrigger(opensWidgetId) 매칭은 행별 중첩 트리 뷰의 자체 모달 열기 채널에서
 *    해소된다(행 렌더 인스턴스 스코프 — 같은 위젯 id가 행마다 반복 렌더되어도 안전).
 *
 * 골격(행 그리드 RowsCollectionEditor 자산 승계 — 스타일/헬퍼/버튼 공유, 중복 구현 금지):
 * 행별/모두 접기(요약=hidden 제외 처음 2개 텍스트류 필드 — def.fields 선언 기준), 행 추가
 * (createBlankRow — textarea-list=[] seed)/삭제(별개 축 — shared effectiveRowAddRemove(def)), seed 되돌리기.
 * 되돌리기는 **행 단위**(헤더 ⟲ — seed 행 통째 복원): 행 안이 자유 조립(임의 위젯 트리)이라
 * 행 그리드처럼 필드별 ⟲를 각 위젯 라벨에 주입할 지점이 없다(P1 판단 — 필드별 복원은 후속 검토).
 *
 * 폴백(크래시 금지): over 미해석(def 없음)=안내 placeholder / 행 배열 아님=빈 목록 취급
 * (normalizeRows). 저작 캔버스(interactive): 행 콘텐츠는 라이브 값으로 렌더(WYSIWYG)하되, 행
 * 서브트리에 행 스코프 선택 채널(repeatRowScopedInteractive — P4a)을 주입해 인스턴스 클릭이
 * 행 템플릿 위젯 선택으로 이어진다. 컨테이너 자체 선택은 종전대로 래퍼(renderWidget) 담당.
 */
function RepeatRowsBlock({
  widget,
  values,
  onChange,
  childContext,
  interactive,
}: {
  widget: RepeatWidget;
  /** 본문 스코프 values — values[widget.over]=행 배열, +ROWS_SEED_SUFFIX=seed 원본. */
  values: Record<string, unknown>;
  onChange: (bindingKey: string, value: unknown) => void;
  /** 행 서브트리 렌더에 통과시킬 렌더 컨텍스트(모달 childContext와 동일 어휘 — 저작 축 미사용). */
  childContext: ModalChildRenderContext;
  /** 저작 캔버스 채널(P4a) — 행 서브트리에 행 스코프 파생(repeatRowScopedInteractive)으로 주입. 실폼(web)은 미전달. */
  interactive?: ComposerTreeInteractive;
}) {
  // 행별 접힘 상태(로컬 UI, 저장 안 함) — rowId 축이라 삭제/재정렬에 불변(행 그리드 동형).
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(() => new Set());
  // 마지막 클릭 인스턴스의 행 key(저작 전용 로컬 UI) — 선택 하이라이트를 그 행에만 표시.
  const [focusRowKey, setFocusRowKey] = useState<string | null>(null);
  // 표형(displayVariant:'table')의 md 미만 카드 폴백 판정 — 훅 규약상 무조건 호출,
  // 구독은 표형만(enabled 인자 — 카드형 반복이 matchMedia를 불필요 구독하지 않게).
  const isMdUpViewport = useIsMdUpViewport(widget.displayVariant === 'table');

  // followKey 추종 상태(저장 안 함) — follower별 마지막 본문 시그니처 + 시드된 rowId 집합.
  const followSigRef = useRef<Record<string, string>>({});
  const seededRowIdsRef = useRef<Set<string>>(new Set());

  // "+ 행 추가" 직후 포커스 조회 범위 — 이 반복 블록 인스턴스의 바깥 컨테이너.
  // 한 작성 화면에 같은 위젯이 여러 벌(중첩 반복·모달) 그려지므로 document 전역 조회는 금물.
  // 세 렌더 경로(표형/민짜/카드형)가 각자 최상위 요소에 같은 ref를 단다 — 한 번에 하나만 산다.
  const blockRef = useRef<HTMLDivElement>(null);

  // 행 스코프 쓰기 누적(makeRowScopedOnChange) = 함수형 갱신의 prev. 매 렌더 후 호스트에서 내려온
  // 행 배열이 우리가 올려보낸 것이면 pending 유지(지연 반영), 외부 변경이면 폐기(reconcileRowScopedPending
  // JSDoc — 비동기 병렬 커밋 유실 차단). deps 없는 effect = 매 렌더 후 실행(의도). 비교 대상은 rows가
  // 아니라 values[widget.over] 원본(normalizeRows는 배열 identity를 보존하고, compute 파생 rows는 매 렌더
  // 새 배열이라 자연히 외부 취급).
  const rowsPending = useRef<RowScopedPending>({ current: null, sent: new WeakSet() }).current;
  useEffect(() => {
    reconcileRowScopedPending(rowsPending, values[widget.over]);
  });
  const followDataDefs = childContext.dataDefs;
  const followResolvedRows = childContext.resolvedRows;
  // 반복 행 안 콤보의 본문(반복 밖) 콤보 선택 추종 — 본문 선택 변화 시 전 행 덮어쓰기, 새 행 시드.
  // 캔버스(interactive)에선 미실행(저작 중 샘플 값 오염 방지 — 실폼 전용).
  useEffect(() => {
    if (interactive) return;
    const followers = collectFollowerComboWidgets(widget.children);
    if (followers.length === 0) return;
    const rows = normalizeRows(values[widget.over]);
    if (rows.length === 0) return;
    const selections = followers.map((f) =>
      resolveFollowKeySelection(f, values, followDataDefs, followResolvedRows),
    );
    const result = computeFollowKeyRows(
      rows,
      selections,
      followSigRef.current,
      seededRowIdsRef.current,
    );
    followSigRef.current = result.nextSig;
    seededRowIdsRef.current = result.nextSeeded;
    if (result.changed) onChange(widget.over, result.rows);
  }, [values, widget, interactive, followDataDefs, followResolvedRows, onChange]);

  const dsDef = (childContext.dataSources ?? []).find((d) => d.key === widget.over);
  // compute 파생 dataDef 폴백 — dataSources에 없으면 dataDefs의 compute 정의를 **표시 전용(readonly)**
  // 소스로 인정한다(예: current-status-joined = join-by-key로 다른 소스를 조인). 편집형 dataSource와
  // 달리 행 추가/편집·seed 되돌리기가 없고, rows는 매 렌더 compute로 산출(작성 중 실시간 반영).
  const ddDef = !dsDef
    ? (childContext.dataDefs ?? []).find((d) => d.key === widget.over && d.compute)
    : undefined;
  if (!dsDef && !ddDef) {
    // over 미해석 — 템플릿 선언(dataSources/dataDefs)에 해당 key가 없음. 크래시 대신 안내(폴백 규약).
    return (
      <p className="text-xs text-gray-400 px-3 py-2 border border-dashed border-gray-300 rounded-card">
        반복 위젯: 데이터 소스 &apos;{widget.over}&apos;를 찾을 수 없습니다(템플릿 dataSources/dataDefs
        선언을 확인하세요).
      </p>
    );
  }

  const rowFields = dsDef ? dataSourceFieldsToRowFields(dsDef.fields) : [];
  // rows: 편집형 dataSource=values[over] / compute 파생 dataDef=values snapshot 우선, 없으면 매 렌더
  // runAssembler(inputs를 values에서 dotted-path resolve)로 산출 — 조인 소스(riskRows-src 등) 입력
  // 변경 시 실시간 재계산. (compute-skip 없이 항상 재계산해 작성 중 실시간 반영을 보장.)
  const rows = ddDef?.compute
    ? normalizeRows(
        runAssembler(
          ddDef.compute,
          Object.fromEntries(
            Object.entries(ddDef.compute.inputs ?? {}).map(([name, path]) => [
              name,
              getByPath(values, path),
            ]),
          ),
        ) ??
          ddDef.defaultRows ??
          [],
      )
    : normalizeRows(values[widget.over]);
  const seed = dsDef ? values[widget.over + ROWS_SEED_SUFFIX] : undefined;
  // 추가/삭제는 별개 축(shared effectiveRowAddRemove — allowAddRemove 상위 스위치 + allowAdd/allowRemove).
  // 선언(dsDef) 부재 = dataDefs compute 표시 전용 폴백이라 둘 다 없음(ROW_ADD_REMOVE_NONE).
  const { add: allowAdd, remove: allowRemove } = dsDef
    ? effectiveRowAddRemove(dsDef)
    : ROW_ADD_REMOVE_NONE;
  // rowLimit 도달 시 추가 버튼 비활성 — 숨기지 않는다(왜 못 넣는지 title이 설명). 삭제는
  // 게이트하지 않는다(DataSourceDef.rowLimit JSDoc — 초과 추가만 금지, 삭제는 자유).
  const addBlocked = isRowAddBlockedByLimit(dsDef?.rowLimit, rows.length);
  const addBlockedTitle = rowAddLimitTitle(dsDef?.rowLimit, rows.length);
  const summaryFields = summaryFieldsForRow(rowFields);
  const isAllCollapsed = isAllRowsCollapsed(rows, collapsedKeys);
  // 하이라이트 대상 행(마지막 클릭 인스턴스) — 미클릭/행 소멸 시 첫 행 폴백(트리 패널에서
  // 템플릿 위젯을 선택해도 캔버스 인스턴스 1곳엔 하이라이트가 보이게).
  const focusKey =
    focusRowKey != null && rows.some((r, i) => rowCollapseKey(r, i) === focusRowKey)
      ? focusRowKey
      : rows.length > 0
        ? rowCollapseKey(rows[0], 0)
        : null;

  /**
   * "+ 행 추가"(표형/민짜/카드형 공용) — 행을 붙이고 **방금 만든 행의 첫 입력 칸으로 커서**.
   *
   * 표 위젯("+ 항목 추가")·행 그리드와 같은 규약이다. 다만 반복 행 안은 칼럼 스키마가 아니라
   * 임의 위젯 트리라 "어느 칸"을 선언에서 고를 수 없어, 새 행 컨테이너를 통째로 훑어 첫 입력
   * 컨트롤을 집는다(focusAddedRowContainer). 새 행은 `createBlankRow`가 uuid rowId를 붙이므로
   * 앵커 값이 유일하고, 그 key는 접힘 Set(기존 행 key만 담긴다)에 없어 **카드형 새 행은 항상
   * 펼침**이다 — 접힌 채라 포커스가 사라지는 경우가 없다.
   *
   * ⚠️ 저작 캔버스(interactive)는 제외한다 — 표 위젯 addRow와 같은 판정(그쪽은 같은 뜻의
   * suggest.authoringCanvas). 캔버스는 렌더가 잦고, 포커스가 캔버스로 튀면 속성 패널 조작이 끊긴다.
   */
  const addRowWithFocus = (): void => {
    const next = addCollectionRow(rows, rowFields);
    onChange(widget.over, next);
    if (interactive) return;
    const addedRowId = next[next.length - 1]?.rowId;
    if (typeof addedRowId !== 'string') return;
    focusAddedRowContainer(blockRef.current, REPEAT_ROW_ID_ATTR, addedRowId);
  };

  // 행 서브트리 1개 렌더(정상/민짜/표형 공용) — 행 스코프(rowValues/rowOnChange) + 저작 행 선택 채널.
  // tableCellsMode=true(표형)면 같은 단일 트리 인스턴스가 최상위 children을 <td>들로 배치해 반환한다
  // (행=한 중첩 렌더 인스턴스 불변 조건 — actionItem→opensWidgetId 모달 채널이 인스턴스 스코프).
  const renderRowBody = (
    row: Record<string, unknown>,
    idx: number,
    tableCellsMode = false,
  ): ReactNode => {
    const collapseKey = rowCollapseKey(row, idx);
    const rowValues = row;
    const rowOnChange = makeRowScopedOnChange(widget.over, rows, idx, rowsPending, onChange);
    return (
      <ComposerWidgetTreeView
        tree={widget.children}
        values={rowValues}
        onChange={rowOnChange}
        tableCellsMode={tableCellsMode}
        interactive={
          interactive
            ? repeatRowScopedInteractive(interactive, collapseKey === focusKey, () =>
                setFocusRowKey(collapseKey),
              )
            : undefined
        }
        rootParentId={widget.id}
        dataSources={childContext.dataSources}
        dataDefs={childContext.dataDefs}
        dataSourceRowsRenderer={childContext.dataSourceRowsRenderer}
        registeredFormWidgets={childContext.registeredFormWidgets}
        registeredWidgetContext={{ ...childContext.registeredWidgetContext, values }}
        view={childContext.view}
        resolvedRows={childContext.resolvedRows}
        onCatalogRowSubmit={childContext.onCatalogRowSubmit}
        onSuggestSearch={childContext.onSuggestSearch}
        onSuggestSelect={childContext.onSuggestSelect}
        onPhotoUpload={childContext.onPhotoUpload}
        signatureSlots={childContext.signatureSlots}
        // 서명 슬롯 주소의 행 스코프 열쇠 — 이 반복의 over가 곧 slotKey 파생의 dataSource 키.
        signatureRowScopeKey={widget.over}
        activeBranchSlots={childContext.activeBranchSlots}
        // 행 인스턴스 스코프 DOM id 접미 — 이 행 조각을 바깥 접미(중첩 repeat)에 누적해 내린다.
        // 없으면 행마다 같은 `wf-{id}`가 반복돼 label htmlFor가 전부 첫 행 input을 가리킨다.
        domIdSuffix={appendRowDomIdSuffix(childContext.domIdSuffix, collapseKey, idx)}
      />
    );
  };

  // 표형(displayVariant:'table') 활성 판정 — chrome:'none'·columns보다 우선(상호배타 — 표가 자체
  // 골격 소유, 결정 2026-08-24-repeat-table-display-variant). 반응형은 단일 렌더 분기(표·카드 이중
  // DOM 동시 렌더 금지 — 모달·포커스 이중화 위험):
  //  - 실폼: 뷰포트 matchMedia(md 미만이면 아래 기존 카드 렌더로 폴백).
  //  - 캔버스(interactive): 뷰포트가 아니라 활성 저작 뷰의 authoringWidthPx로 해소(디바이스 프레임과
  //    뷰포트 반응형이 어긋나는 admin 규약 — resolveGroupColumns 동형). 좁은 뷰=카드 폴백(WYSIWYG).
  const narrowAuthoringView =
    !!interactive &&
    !!childContext.view &&
    childContext.view !== COMPOSER_BASE_VIEW_KEY &&
    (() => {
      const def = COMPOSER_VIEWS.find((v) => v.key === childContext.view);
      return def?.authoringWidthPx != null && def.authoringWidthPx < MD_BREAKPOINT_PX;
    })();
  const tableMode =
    widget.displayVariant === 'table' && (interactive ? !narrowAuthoringView : isMdUpViewport);

  if (tableMode) {
    // 열 도출 — repeatTableColumnChildren 단일 정본(순서·비시각 제외·뷰 숨김 제외).
    // 행 셀(tableCellsMode)도 같은 함수를 쓰므로 th↔td 정합이 구조로 보장된다.
    const columnChildren = repeatTableColumnChildren(widget.children, childContext.view);
    // 액션(×) 열은 remove 축만 본다 — add만 꺼진 소스(RA 항목)는 열이 남고 표 아래 버튼만 없다.
    const totalCols = columnChildren.length + (allowRemove ? 1 : 0);
    // 조건 분기(branch) 열 헤더의 평가 스코프 — 헤더는 행 밖이라 **top-level values**로 평가한다
    // (행 렌더의 branchScope 동형 조합: 본문 값을 깔고 현재 스코프 키 우선). 반복 자체가 모달·중첩
    // 인스턴스 안일 수 있어 registeredWidgetContext.values를 아래에 깐다.
    const headerCondScope = childContext.registeredWidgetContext?.values
      ? { ...childContext.registeredWidgetContext.values, ...values }
      : values;
    return (
      <div ref={blockRef} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* formTable 외곽 질감 동형 — 라운드 8 + 옅은 테두리, 가로 넘침 스크롤. */}
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            overflow: 'hidden',
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            {allowRemove ? (
              <colgroup>
                {columnChildren.map((c) => (
                  <col key={c.id} />
                ))}
                {/* 액션(삭제) 열 고정폭 — formTable hasActionsCol 동형. */}
                <col style={{ width: 36 }} />
              </colgroup>
            ) : null}
            <thead>
              <tr>
                {columnChildren.map((c) => (
                  <th key={c.id} style={REPEAT_TABLE_HEADER_CELL_STYLE} scope="col">
                    {repeatTableColumnHeaderLabel(
                      c,
                      headerCondScope,
                      childContext.activeBranchSlots,
                    )}
                    {/* 필수 표식 — 셀 라벨이 억제된 표형에서 라벨 안 `*`의 자리를 헤더가 대신한다
                        (기존 라벨 * 시각 언어 동형 — REQUIRED_STAR_EL 단일 정본). */}
                    {repeatTableColumnRequired(c) ? REQUIRED_STAR_EL : null}
                  </th>
                ))}
                {allowRemove ? <th style={REPEAT_TABLE_HEADER_CELL_STYLE} scope="col" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={Math.max(1, totalCols)}
                    style={{ ...FORM_TABLE_CELL_STYLE, textAlign: 'center' }}
                    className="text-xs text-gray-400"
                  >
                    행이 없습니다.{allowAdd ? ' 아래 버튼으로 추가하세요.' : ''}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr
                    key={row.rowId ?? idx}
                    // 행 추가 직후 포커스 앵커(레이아웃 중립 data 속성) — addRowWithFocus 참조.
                    // ⚠️ 리터럴은 addedRowFocus의 REPEAT_ROW_ID_ATTR와 같은 이름이어야 한다.
                    data-repeat-row-id={row.rowId}
                    style={{ borderBottom: '1px solid #f3f4f6' }}
                    className="transition-colors hover:bg-gray-50"
                  >
                    {/* 한 행 = 한 중첩 트리 렌더 인스턴스(tableCellsMode) — 인스턴스가 최상위
                        children을 <td>들로 반환한다(모달 채널·행 스코프 어댑터는 카드형과 동일). */}
                    {renderRowBody(row as Record<string, unknown>, idx, true)}
                    {allowRemove ? (
                      <td style={{ ...FORM_TABLE_BODY_CELL_STYLE, textAlign: 'center' }}>
                        {/* 행 삭제 — formTable 반복 행 removeRow 룩 동형(마지막 열 ×). */}
                        <button
                          type="button"
                          className="text-gray-400 hover:text-red-500 px-1"
                          aria-label="행 삭제"
                          onClick={() => onChange(widget.over, removeRowAtIndex(rows, idx))}
                        >
                          ×
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {allowAdd && (
          <button
            type="button"
            className="hover:bg-brand-50"
            style={{
              ...addItemButtonStyle,
              alignSelf: 'flex-start',
              ...(addBlocked ? addItemButtonDisabledStyle : {}),
            }}
            disabled={addBlocked}
            title={addBlockedTitle}
            onClick={addRowWithFocus}
          >
            + 행 추가
          </button>
        )}
      </div>
    );
  }

  // chrome='none'(민짜): 요약 헤더/접기/번호/"모두 접기"/행 카드 장식 없이 행 children만 나열.
  // columns>=2면 행 인스턴스를 grid 다단(실폼 md 미만 1단 degrade — <style> 주입, 본문 RepeatNode 동형).
  // 행 헤더 부재라 행별 삭제 버튼은 없음(remove 축을 렌더할 자리가 없다 — readonly 소스면
  // allowAddRemove=false로 추가도 자연 부재).
  if (widget.chrome === 'none') {
    const columns = Math.max(1, Math.floor(widget.columns ?? 1));
    const gap = widget.gap ?? 0;
    const rowNodes = rows.map((row, idx) => (
      // data-repeat-row-id: 행 추가 직후 포커스 앵커(addedRowFocus REPEAT_ROW_ID_ATTR와 같은 이름).
      <div key={row.rowId ?? idx} data-repeat-row-id={row.rowId}>
        {renderRowBody(row as Record<string, unknown>, idx)}
      </div>
    ));
    let rowsContainer: ReactNode;
    if (columns >= 2 && rows.length > 0) {
      const sel = `[data-repeatgrid="${widget.id}"]`;
      const css =
        `${sel}{display:grid;grid-template-columns:1fr;gap:${gap}px}` +
        `@media(min-width:${MD_BREAKPOINT_PX}px){${sel}{grid-template-columns:repeat(${columns},minmax(0,1fr));align-items:start;column-gap:${gap}px;row-gap:${gap}px}}`;
      rowsContainer = (
        <>
          <style>{css}</style>
          <div data-repeatgrid={widget.id}>{rowNodes}</div>
        </>
      );
    } else {
      rowsContainer = (
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>{rowNodes}</div>
      );
    }
    return (
      <div ref={blockRef} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.length === 0 && (
          <p className="text-xs text-gray-400 px-3 py-3 border border-dashed border-gray-300 rounded-card text-center">
            행이 없습니다.{allowAdd ? ' 아래 버튼으로 추가하세요.' : ''}
          </p>
        )}
        {rows.length > 0 && rowsContainer}
        {allowAdd && (
          <button
            type="button"
            className="hover:bg-brand-50"
            style={{
              ...addItemButtonStyle,
              alignSelf: 'flex-start',
              ...(addBlocked ? addItemButtonDisabledStyle : {}),
            }}
            disabled={addBlocked}
            title={addBlockedTitle}
            onClick={addRowWithFocus}
          >
            + 행 추가
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={blockRef} className="space-y-3">
      {rows.length === 0 && (
        <p className="text-xs text-gray-400 px-3 py-3 border border-dashed border-gray-300 rounded-card text-center">
          행이 없습니다.{allowAdd ? ' 아래 버튼으로 추가하세요.' : ''}
        </p>
      )}

      {rows.length > 0 && (
        <div className="flex items-center justify-end">
          <button
            type="button"
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-control hover:bg-gray-50"
            onClick={() => setCollapsedKeys(collapsedForToggleAll(rows, isAllCollapsed))}
          >
            {isAllCollapsed ? '모두 펴기' : '모두 접기'}
          </button>
        </div>
      )}

      {rows.map((row, idx) => {
        const seedRow = findSeedRow(seed, row.rowId);
        const collapseKey = rowCollapseKey(row, idx);
        const isExpanded = !collapsedKeys.has(collapseKey);
        const modified = isRowModified(row, seedRow, rowFields);
        const rowValues = row as Record<string, unknown>;
        // 행 스코프 쓰기 어댑터 — 해당 행만 갱신·타 행 보존(index 축: rowId 부재 행 혼입에도 안전).
        const rowOnChange = makeRowScopedOnChange(widget.over, rows, idx, rowsPending, onChange);
        return (
          <div
            key={row.rowId ?? idx}
            // 행 추가 직후 포커스 앵커(addedRowFocus REPEAT_ROW_ID_ATTR와 같은 이름). 새 행은
            // collapseKey가 접힘 Set에 없어 항상 펼침이라 본문 컨트롤이 조회 범위 안에 있다.
            data-repeat-row-id={row.rowId}
            className="border border-gray-200 rounded-card"
            style={rowCardStyle}
          >
            {/* 요약 헤더(클릭=토글) — 행 그리드 동형: 번호 배지 + 텍스트 요약 + ⟲/삭제 + chevron. */}
            <div
              className="flex items-center gap-2 px-3 py-2"
              style={{
                ...rowHeaderBaseStyle,
                borderBottom: isExpanded ? '1px solid #e2e8f0' : undefined,
              }}
              onClick={() => setCollapsedKeys((prev) => toggleCollapseKey(prev, collapseKey))}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCollapsedKeys((prev) => toggleCollapseKey(prev, collapseKey));
                }
              }}
              aria-expanded={isExpanded}
            >
              <span className="text-xs font-semibold" style={rowBadgeStyle}>
                {idx + 1}
              </span>
              {summaryFields.length > 0 ? (
                summaryFields.map((field, i) => (
                  <span
                    key={field.field}
                    className={i === 0 ? 'text-sm font-medium' : 'text-sm text-gray-600'}
                    style={i === 0 ? summaryPrimaryStyle : summarySecondaryStyle}
                  >
                    {summaryText(row, field)}
                  </span>
                ))
              ) : (
                <span style={{ flex: '1 1 0%' }} />
              )}
              {modified && seedRow && (
                // 행 단위 seed 되돌리기 — 헤더 클릭(토글)으로 버블 차단 필요(삭제 버튼 동형).
                <span
                  className="flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <RevertButton
                    onClick={() => onChange(widget.over, replaceRowAtIndex(rows, idx, seedRow))}
                  />
                </span>
              )}
              {allowRemove && (
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-500 text-sm px-1 flex-shrink-0"
                  aria-label="행 삭제"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(widget.over, removeRowAtIndex(rows, idx));
                    setCollapsedKeys((prev) => collapsedAfterRemove(prev, collapseKey));
                  }}
                >
                  × 행 삭제
                </button>
              )}
              <span className="text-gray-400 flex-shrink-0" aria-hidden="true">
                {isExpanded ? '▲' : '▼'}
              </span>
            </div>

            {/* 펼침 행 본문 — children 서브트리를 행 스코프(rowValues/rowOnChange)로 렌더.
                중첩 ComposerWidgetTreeView가 자체 모달 열기 채널을 가져 actionItem 매칭이
                행 인스턴스 스코프로 격리된다(모달 격리 동형). 저작 캔버스면 행 스코프 선택
                채널 주입(P4a) — 클릭 인스턴스 행에만 하이라이트, 실폼(interactive 미전달)은
                종전 순수 렌더. */}
            {isExpanded && (
              <div className="p-3">
                <ComposerWidgetTreeView
                  tree={widget.children}
                  values={rowValues}
                  onChange={rowOnChange}
                  interactive={
                    interactive
                      ? repeatRowScopedInteractive(interactive, collapseKey === focusKey, () =>
                          setFocusRowKey(collapseKey),
                        )
                      : undefined
                  }
                  rootParentId={widget.id}
                  dataSources={childContext.dataSources}
                  dataDefs={childContext.dataDefs}
                  dataSourceRowsRenderer={childContext.dataSourceRowsRenderer}
                  registeredFormWidgets={childContext.registeredFormWidgets}
                  // 본문 스코프 values를 합성 채널로 주입 — 행 스코프(rowValues) 안 브리지 위젯이
                  // 기준표('risk-assessment-config' 등)를 본문 채널로 폴백 조회한다(P4d).
                  registeredWidgetContext={{ ...childContext.registeredWidgetContext, values }}
                  view={childContext.view}
                  resolvedRows={childContext.resolvedRows}
                  onCatalogRowSubmit={childContext.onCatalogRowSubmit}
                  onSuggestSearch={childContext.onSuggestSearch}
                  onSuggestSelect={childContext.onSuggestSelect}
                  onPhotoUpload={childContext.onPhotoUpload}
                  signatureSlots={childContext.signatureSlots}
                  // 서명 슬롯 주소의 행 스코프 열쇠 — 표형(renderRowBody)과 같은 채널.
                  signatureRowScopeKey={widget.over}
                  activeBranchSlots={childContext.activeBranchSlots}
                  // 카드형(접힘 헤더) 행도 표형(renderRowBody)과 같은 접미 채널 — 행마다 유일한 DOM id.
                  domIdSuffix={appendRowDomIdSuffix(childContext.domIdSuffix, collapseKey, idx)}
                />
              </div>
            )}
          </div>
        );
      })}

      {allowAdd && (
        <button
          type="button"
          className="hover:bg-brand-50"
          style={{ ...addItemButtonStyle, ...(addBlocked ? addItemButtonDisabledStyle : {}) }}
          disabled={addBlocked}
          title={addBlockedTitle}
          onClick={addRowWithFocus}
        >
          + 행 추가
        </button>
      )}
    </div>
  );
}

/**
 * composerWidgetTree(ComposerWidget[])를 반응형으로 렌더. 저작 트리(그룹/제목/구분선 + 입력)를
 * 작성 화면 입력 폼으로 표현한다. hidden 입력은 렌더 안 함(renderWriteNode가 null).
 * dataSources(RowsCollectionEditor 등) 도메인 위젯 렌더는 web 전용이라 `footer` slot으로 주입받아
 * 트리 뒤에 append한다(코어는 도메인 로직 미포함).
 * ⚠️ AI 제안(aiSuggest)은 트리 모델에서 제외(2026-07-08) — 향후 커스텀(등록형) 위젯으로 재도입.
 *    옛 flat writeFormExtras + AiSelectBlock 경로는 2026-07-25 철거됐다(현재 AI 제안 렌더 경로 없음).
 */
export function ComposerWidgetTreeView({
  tree,
  values,
  onChange,
  footer,
  interactive,
  view,
  registeredFormWidgets,
  registeredWidgetContext,
  dataSourceRowsRenderer,
  dataSources,
  dataDefs,
  resolvedRows,
  onCatalogRowSubmit,
  onSuggestSearch,
  onSuggestSelect,
  onPhotoUpload,
  signatureSlots,
  signatureRowScopeKey,
  initialValuesReady,
  rootParentId,
  activeBranchSlots,
  tableCellsMode,
  domIdSuffix,
}: {
  tree: ComposerWidget[];
  values: Record<string, unknown>;
  onChange: (bindingKey: string, value: unknown) => void;
  footer?: ReactNode;
  /**
   * 편집형 list 데이터 소스 선언 — formTable 반복 행이 sourceKey로 def(행 필드 위젯)를 조회한다.
   * 미주입이면 반복 행 셀은 기본 텍스트 편집기로 degrade(회귀 안전).
   */
  dataSources?: DataSourceDef[];
  /**
   * 내장 데이터셋 선언(template.dataDefs) — widget:'table' 구분 그룹 틀을 기본 행에서 자동
   * 파생(행 전부 삭제에도 그룹 유지). 미주입이면 groupValues/현재 행 파생만(기존 동작).
   */
  dataDefs?: DataDef[];
  /**
   * host가 런타임 resolve한 후보 rows(소스 키별) — sortableSelect rows resolve 최우선(P3, resolveSortableRows).
   * 모달 스코프는 본문 values와 격리라, AI 후보처럼 비동기로 도착하는 데이터를 본문 values 대신 이 채널로
   * 주입해야 모달 안 sortableSelect가 본다(ModalTriggerButton childContext로 전달). 미주입=기존 동작.
   */
  resolvedRows?: Record<string, Record<string, unknown>[]>;
  /**
   * 카탈로그 행 즉시 등록 호스트 콜백(submitAction 모달 — 결정 2026-07-17 "등록 모달 = 즉시 등록").
   * 성공 시 등록/갱신된 행(카탈로그 rows 형식, id 포함)을 반환 — 호스트 구현부는 자기 소유 제안
   * rows(resolvedRows)에도 그 행을 합류시킬 것(제안 목록 즉시 반영). null=실패(모달 유지+오류 문구).
   * 미배선 호스트(external 무인증 등)에선 submitAction 모달을 여는 콤보 액션 항목이 숨겨진다.
   */
  onCatalogRowSubmit?: (
    catalogKey: string,
    values: Record<string, string>,
  ) => Promise<Record<string, unknown> | null>;
  /**
   * 범용 검색 제안 축(suggestionsFrom) 호스트 검색 콜백 — 소비 위젯(text·textarea-list/text-list)
   * 입력 중 서버 검색해 드롭다운 제안(디바운스·↑↓/Enter·기추가 제외는 rc 소유). 미배선 호스트
   * (external 등)는 제안 없이 일반 입력 degrade. 캔버스(interactive)에선 제안 비활성(followKey
   * 캔버스 미실행 규약 동형). 결정: 2026-08-05-composer-suggestion-source-axis.
   */
  onSuggestSearch?: ComposerSuggestSearch;
  /**
   * 제안 행 **선택 확정 시에만** 통지(자유 타이핑은 통지 없음) — 호스트 부수효과용(대책 라이브러리
   * usage 증가 등, fire-and-forget). onSuggestSearch 미배선이면 제안 자체가 없어 무의미.
   */
  onSuggestSelect?: ComposerSuggestSelect;
  /**
   * photo 위젯 업로드 호스트 콜백 — 파일 업로드 후 저장 URL 반환(web 작성 페이지가 files/upload
   * 래퍼를 주입). 미배선 호스트(external 무인증 공유 등)는 안내 placeholder로 degrade, 캔버스
   * (interactive)는 실폼과 같은 버튼 모양 유지 + 동작 inert(suggest authoringCanvas 규약 동형).
   */
  onPhotoUpload?: ComposerPhotoUpload;
  /**
   * 서명 슬롯 host 채널(P2) — 걷은 서명 맵(GET /reports/:id/slot-signatures) + 탭/지우기 콜백.
   * 미주입(캔버스·external·생성 모드 — reportId 부재)이면 표시 전용 빈 칸으로 degrade
   * (모양은 실폼과 동일 — WYSIWYG). onPhotoUpload와 같은 host 주입 결.
   */
  signatureSlots?: ComposerSignatureSlotsContext;
  /**
   * (내부) 이 트리 인스턴스가 반복 행 스코프로 렌더될 때 그 반복의 `over` 키 —
   * RepeatRowsBlock이 행 서브트리에 주입한다. 서명 슬롯 위젯이 slotKey를
   * `resolveSignatureSlotKey(over, field)`로 해소하는 열쇠(formTable 반복 셀의 sourceKey 대응물).
   * 미지정(최상위·모달 스코프)=서명 위젯은 주소 불성립으로 표시 전용.
   */
  signatureRowScopeKey?: string;
  /**
   * 행 그리드(dataSourceRows) 위젯 렌더 주입 — host(web)가 편집형 list def를 bindingKey로 조회해
   * RowsCollectionEditor를 그린다. 미주입(admin 캔버스·미리보기)이면 안내 placeholder(코어 무도메인).
   */
  dataSourceRowsRenderer?: DataSourceRowsRenderer;
  /**
   * 등록형 폼 위젯(widget==='registered') 구현 레지스트리(widgetKey→컴포넌트). host(web)가 주입.
   * 미주입(admin 미리보기 포함) 또는 미등록 키는 안내 placeholder 렌더.
   */
  registeredFormWidgets?: Record<string, ComposerRegisteredFormWidget>;
  /**
   * 등록형 위젯에 전달할 렌더 컨텍스트(무도메인 — id 문자열만). host(web 작성 페이지)가 현재 현장/회사 id를 주입.
   * 미지정(admin 미리보기·기타 경로)이면 위젯은 안내/비활성으로 degrade.
   * values(선택)는 본문 스코프 값 — 반복 행 서브트리 렌더 시 RepeatRowsBlock이 얹어, 행 스코프의
   * 브리지 위젯이 기준표 등 본문 채널을 폴백 조회하게 한다(host는 주입하지 않음, 내부 합성 채널).
   * improvementImage(선택)는 개선 이미지 AI 호스트 콜백 — 조립 렌더러(ComposerWidgetTreeRenderer)가
   * `onImprovementImage` prop을 여기에 접어 넘긴다(등록형 행 위젯 `improvement-image`가 context에서
   * 꺼내 쓴다).
   */
  registeredWidgetContext?: {
    companyId?: string;
    siteId?: string;
    values?: Record<string, unknown>;
    improvementImage?: ComposerImprovementImageHost;
  };
  /**
   * 활성 반응형 뷰 키(저작 캔버스 뷰 스위처). 지정 시 그룹 단 수를 이 뷰로 명시 해소하고
   * 고정 grid로 렌더(md: 자동 degrade 없음). 미지정(web 실폼)=base + 기존 md: 반응형(회귀0).
   */
  view?: string;
  /**
   * 초기값(기존 데이터/prefill 반영) 준비 완료 신호 — collapseWhenFilled 섹션의 "채워져 있으면
   * 접힘" 1회 판정 시점. prefill을 비동기 로드하는 host(web 작성 페이지)는 로드 settle 후 true로
   * 전환할 것(마운트 시 판정하면 빈 값 기준으로 항상 열림). 미지정=true(동기 값 host — admin 캔버스 등).
   */
  initialValuesReady?: boolean;
  /**
   * 이 트리 최상위 형제들의 부모 id — dnd renderWrapper의 ctx.parentId와 말미 renderDropSlot의
   * parentId 인자에 쓰인다. 미지정(=null=기존 동작, 회귀 0). modalTrigger 저작(P2)처럼 이 트리가
   * 별도 `ComposerWidgetTreeView`로 렌더될 때, 최상위 children의 실제 부모(modalTrigger id)를
   * 주입해 드롭 위치가 본문 root로 오배치되는 것을 막는다(결정 2026-07-15-composer-modal-authoring-nonmodal-dnd).
   */
  rootParentId?: string;
  /**
   * 저작 활성 분기 슬롯 override(P3 대비 최소 배선) — branch 위젯 id → 렌더할 슬롯 index.
   * 지정된 위젯은 condition 평가 대신 이 index 슬롯을 렌더(범위 밖이면 clamp). 미지정/미포함 위젯은
   * 라이브(evaluateCondition). P3 저작 UI가 "슬롯 탭 선택"으로 이 맵을 채운다(없어도 컴파일·동작).
   */
  activeBranchSlots?: Record<string, number>;
  /**
   * repeat 표형(displayVariant:'table') 행 인스턴스 전용 — 최상위 children을 세로 스택 대신
   * `<td>` 셀들(Fragment)로 반환한다(호출부 `<tr>` 안 배치 — tr 골격·삭제 열은 RepeatRowsBlock 소유).
   * 단일 트리 인스턴스를 유지한 채 배치만 바꾸는 축이라 모달 채널(modalOpenRequest — 이 인스턴스
   * 스코프)·값 어댑터는 카드형과 동일. 셀 안 위젯은 라벨 억제(suppressRepeatTableCellChrome) +
   * noTextEdit(라벨 인라인 편집 억제 — formTable 셀 동형). 비시각 위젯(hideButton modalTrigger)은
   * 열을 만들지 않고 마지막 셀 안에서 렌더(모달 채널 보존 — tr 직속은 invalid DOM).
   * footer/renderDropSlot은 이 모드에서 미지원(반복 행 스코프는 dnd 미주입 — repeatRowScopedInteractive).
   */
  tableCellsMode?: boolean;
  /**
   * 행 인스턴스 스코프 DOM id 접미 — 이 트리 인스턴스가 **여러 번** 렌더되는 경우(반복 행, 그 안의
   * 모달)에만 호출부가 내린다. 내려오면 이 트리 안 입력의 id/htmlFor/(radio) name이
   * `wf-{id}--{suffix}`로 유일해진다(writeFieldDomId). 미지정(최상위 실폼·캔버스 본문)=`wf-{id}`
   * 그대로라 기존 소비처(missing 포커스 `getElementById('wf-'+id)`)는 회귀 0.
   */
  domIdSuffix?: string;
  // admin 작성 페이지 편집 셸 전용 — 위젯 클릭 선택(하이라이트) + 중앙 직접드래그(Slice B).
  // 미전달 시 순수 렌더(web 회귀0). renderWrapper/renderDropSlot은 admin이 @dnd-kit draggable/droppable을
  // 주입하는 hook(패키지는 dnd 미의존) — 미전달이면 기존 클릭선택 래퍼만.
  interactive?: {
    selectedId?: string;
    /** 다중 선택 집합(하이라이트 표시용). selectedId(anchor)는 이 집합에 포함되는 게 정상. */
    selectedIds?: string[];
    /** 좌측 트리 행 hover 연동 — 캔버스의 해당 위젯에 옅은 하이라이트(선택 표시가 우선). */
    hoveredId?: string;
    /**
     * additive=Ctrl/⌘ 클릭(토글), range=Shift+클릭(앵커~클릭 위젯 사이 형제 범위 — 셀 포커스 중이면
     * 그 표가 앵커로 승격) — host가 다중선택 갱신에 사용. 동시면 host가 range 우선. 미지정=단일 선택.
     */
    onSelect?: (id: string, opts?: { additive?: boolean; range?: boolean }) => void;
    /**
     * group isolation 더블클릭 드릴다운 콜백(admin 작성 캔버스 전용). 텍스트 인라인 편집 대상이
     * 아닌 위젯(디자인/중첩 섹션 본문 등)을 더블클릭하면 host가 그 위젯이 속한 섹션(group)으로
     * 선택 컨텍스트를 진입시킨다. 미전달(web 등) 시 더블클릭 드릴 비활성(선택/편집만, 회귀0).
     * (텍스트 위젯 더블클릭=인라인 편집, formTable 셀 더블클릭=셀 드릴은 각각 우선 처리되어 여기 미도달.)
     */
    onWidgetDrill?: (id: string) => void;
    /**
     * 텍스트 위젯(input 라벨/heading 문구/group 섹션명) 더블클릭 인라인 편집 커밋 콜백.
     * host(admin)가 위젯 kind로 대상 필드를 판정해 갱신(wtApply 히스토리 경유 → undo 1스텝).
     * 미전달이면 더블클릭 인라인 편집 비활성(선택/드래그만).
     */
    onWidgetTextChange?: (id: string, text: string) => void;
    /**
     * 캔버스 위젯 우클릭(컨텍스트 메뉴) 콜백 — host가 그 위젯을 선택하고 메뉴를 연다. 일반 위젯은
     * renderWrapper(ComposerCanvasWidget)가 자체 onContextMenu로 처리하지만, renderWrapper가 없는
     * **폴백 선택 래퍼**(반복 행 children 등 dnd 미주입 경로)는 이 콜백으로 우클릭을 위임한다 —
     * 미전달·미배선이면 우클릭이 조상(반복 컨테이너)으로 버블돼 컨테이너가 선택되는 결함(2026-07-20 제보).
     */
    onWidgetContextMenu?: (id: string, e: React.MouseEvent) => void;
    /**
     * 인라인 편집 자동 진입 요청(위젯 추가 직후 등) — nonce가 바뀔 때 1회 그 위젯의 편집 모드로
     * 진입한다. 선택은 host가 이미 맞춘 상태를 전제(여기선 editingId만 설정 — 승격 부작용 없음).
     * 대상이 인라인 편집 미지원 kind면 렌더에서 매칭되지 않아 자연 no-op. host는 소비 후 곧
     * 해제할 것(잔존 시 리마운트 때 재진입).
     */
    editRequest?: { id: string; nonce: number };
    /**
     * formTable 셀 클릭/더블클릭 → host 속성 패널에서 해당 행 펼침+셀 포커스 연동.
     * additive=Ctrl/⌘ 클릭(host가 같은 표 안 복수 선택 토글에 사용, 미지정=단일).
     * range=Shift+클릭(host가 anchor→클릭 셀 사각 범위 선택에 사용). range·additive 동시면 host가 range 우선.
     * (셀 위젯 라벨 더블클릭은 위젯 래퍼가 stopPropagation으로 우선 처리 — 여기 도달 = 텍스트/빈/입력필드 셀.)
     */
    onFormTableCellFocus?: (
      tableId: string,
      rowId: string,
      columnId: string,
      opts?: { additive?: boolean; range?: boolean },
    ) => void;
    /**
     * formTable 텍스트 셀(text 종류) 더블클릭 인라인 편집 커밋 콜백. host(admin)가 그 셀의
     * text를 갱신(히스토리 경유 → undo 1스텝). 미전달이면 텍스트 셀 더블클릭은 셀 포커스만(기존 동작).
     */
    onFormTableCellTextChange?: (
      tableId: string,
      rowId: string,
      columnId: string,
      text: string,
    ) => void;
    /**
     * 포커스(복수) 중인 표 셀 — 캔버스 표 안에서 해당 td를 하이라이트.
     * 배열의 마지막 = anchor(진한 하이라이트), 나머지는 옅게.
     */
    focusedCells?: { tableId: string; rowId: string; columnId: string }[];
    /**
     * 속성 패널 hover 중인 표 셀(위치 표시) — 포커스와 **별도 채널**(앰버 스타일, 포커스 위에도
     * 우선 표시). 포커스와 상호배타로 묶으면 셀 포커스 중(계산 셀 편집기 등) hover 표시가
     * 전달되지 않는 버그 재발(2026-07-13).
     */
    hoveredCell?: { tableId: string; rowId: string; columnId: string };
    // 각 위젯을 감싸는 hook(draggable + insert-before droppable). 미전달이면 클릭선택 래퍼만.
    // gridColumn: 부모 섹션 채움 스킴 활성 시 이 위젯(=그리드 직속 자식)의 인라인 grid-column 값(없으면 미적용).
    renderWrapper?: (
      ctx: {
        widget: ComposerWidget;
        parentId: string | null;
        index: number;
        gridded: boolean;
        gridColumn?: string;
      },
      children: ReactNode,
    ) => ReactNode;
    // 각 컨테이너(root/그룹) 말미의 append 드롭존. 빈 그룹도 여기로 드롭.
    // layout: 다단 grid 안 전폭 배치용(col-span 클래스/인라인 gridColumn) — 슬롯 루트에 직접
    // 적용할 것(rc가 래퍼로 감싸면 슬롯이 숨어도 래퍼가 grid 행/flex gap을 만들어 실폼과 어긋남).
    renderDropSlot?: (
      parentId: string | null,
      layout?: { className?: string; style?: CSSProperties },
    ) => ReactNode;
    /**
     * 저작 캔버스에서 "모달 편집 열기"로 열린 modalTrigger의 id(admin 전용, P1b). 이 id의
     * modalTrigger는 캔버스에서 모달을 **열린 상태로 유지**하고 콘텐츠를 interactive(선택/인라인
     * 편집)로 렌더한다. 미지정=기존 동작(버튼 클릭 미리보기만). 실폼(web)은 미전달.
     */
    modalAuthoringId?: string;
    /** 저작 열림 모달의 닫기(Esc/취소/오버레이) 요청 — host가 modalAuthoringId를 해제한다. */
    onModalAuthoringClose?: () => void;
  };
}) {
  // 비-base 뷰: 형제를 그 뷰 표시 순서로 정렬해 렌더(index=표시 index — dnd/reorder가 그 뷰 order 갱신).
  // base/미지정(web 실폼)은 base 배열 순서 그대로(회귀0).
  const ordered = (siblings: ComposerWidget[]): ComposerWidget[] =>
    view && view !== COMPOSER_BASE_VIEW_KEY ? orderSiblingsForView(siblings, view) : siblings;

  // 콤보 드롭다운 액션 항목 → modalTrigger 모달 열기 요청 채널(cross-widget, 이 트리 스코프 한정).
  // seq 증가로 같은 모달 재요청을 구분 — ModalTriggerButton이 자기 id 요청의 seq 변화를 감지해 연다.
  // 모달 children은 중첩 ComposerWidgetTreeView가 자체 채널을 가진다(값 스코프 격리와 동형 — 모달
  // 안 콤보는 그 모달 스코프의 modalTrigger만 열 수 있고, 중첩 모달 버튼은 규약상 금지라 실질 무대상).
  // 결정: 2026-07-17-composer-widget-preset-and-registered-widget-path(콤보 드롭다운 액션 항목 축).
  const [modalOpenRequest, setModalOpenRequest] = useState<{
    widgetId: string;
    seq: number;
  } | null>(null);
  const requestModalOpen = (widgetId: string): void =>
    setModalOpenRequest((prev) => ({ widgetId, seq: (prev?.seq ?? 0) + 1 }));

  // 검색 제안 콜백 묶음(suggestionsFrom 실행부) — 캔버스(저작 interactive)에선 비활성(followKey
  // 캔버스 미실행 규약 동형: 저작 중 서버 검색·writes 전파로 샘플 값 오염 방지). onSearch 미배선이면
  // 제안 자체가 성립하지 않으므로 onSelect만으로는 묶음을 만들지 않는다.
  // 캔버스는 검색을 **일부러** 끄므로 "미배선"과 구분되는 표식을 실어 보낸다 — 그래야 선택 전용
  // 칸의 읽기 전용 degrade가 캔버스 모양을 실폼과 갈라놓지 않는다(아래 renderWriteNode suggest doc).
  const suggestHandlers = interactive
    ? { authoringCanvas: true }
    : onSuggestSearch
      ? { onSearch: onSuggestSearch, onSelect: onSuggestSelect }
      : undefined;

  // 캔버스 텍스트 위젯 인라인 편집 대상 id(로컬 state — 영속 불요, 리마운트/트리 변경 시 자연 해제).
  // editingId가 가리키는 위젯이 트리에서 사라지면 walk에서 매칭 안 돼 에디터가 렌더되지 않음(무시).
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  // formTable 텍스트 셀 인라인 편집 좌표(더블클릭 진입, 로컬 state — 커밋/취소 시 해제).
  // 대상 셀이 트리 변경으로 사라지면 렌더 매칭이 안 돼 에디터가 그려지지 않음(무시).
  const [editingFtCell, setEditingFtCell] = useState<{
    tableId: string;
    rowId: string;
    columnId: string;
  } | null>(null);
  // 편집 차단 셀(자동 채움) 더블클릭/F2 시도 시 일시 툴팁 — hover title은 클릭 순간 사라지고
  // 프로그램 표시 API도 없어(브라우저 한계) **hover 툴팁을 흉내**: 같은 문구를 네이티브 위치
  // (커서 아래 — F2 재발행은 host가 셀 안 좌표 주입)·네이티브 룩(흰 배경/회색 테두리)으로
  // body portal + fixed에 표시(표 wrapper overflow·캔버스 zoom transform 클리핑 무관).
  // nonce로 연속 시도마다 타이머 리셋. 자동 소멸(2.4s).
  const [ftBlockedHint, setFtBlockedHint] = useState<{
    x: number; // 커서/앵커 지점(뷰포트)
    y: number;
    nonce: number;
  } | null>(null);
  useEffect(() => {
    if (!ftBlockedHint) return;
    const t = window.setTimeout(() => setFtBlockedHint(null), 2400);
    return () => window.clearTimeout(t);
  }, [ftBlockedHint]);
  // formTable 셀 드래그 범위 선택(양식 탭 엑셀식 드래그 동형, 2026-07-14) — mousedown(드릴다운
  // 문맥의 plain 클릭)에서 시작 셀 단일 포커스+arm, 드래그 중 진입 td마다 range 재계산, mouseup 해제.
  // ref(리렌더 무관 즉시성) + 한 번에 한 표만. 저작 모드 전용(cellFocus 有 경로에서만 arm).
  const ftDragTableRef = useRef<string | null>(null);
  useEffect(() => {
    const up = (): void => {
      ftDragTableRef.current = null;
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);
  const canEditText = !!interactive?.onWidgetTextChange;
  // 인라인 편집 자동 진입 요청(host — 위젯 추가 직후 등). 선택은 host가 맞췄으므로 editingId만.
  const editRequestId = interactive?.editRequest?.id;
  const editRequestNonce = interactive?.editRequest?.nonce;
  useEffect(() => {
    if (editRequestId == null || editRequestNonce == null || !canEditText) return;
    setEditingId(editRequestId);
  }, [editRequestId, editRequestNonce, canEditText]);
  const startTextEdit = (id: string) => {
    // 단일 선택으로 맞춘 뒤 편집 진입(전역 단축키는 편집 input 포커스=isEditing 가드로 보호됨).
    interactive?.onSelect?.(id);
    setEditingId(id);
  };
  const commitTextEdit = (id: string, text: string) => {
    interactive?.onWidgetTextChange?.(id, text);
    setEditingId(undefined);
  };
  const cancelTextEdit = () => setEditingId(undefined);

  // 순수 위젯 렌더(현행 그대로). group 자식은 renderWidget(래핑 경로)로 재귀해 개별 선택 가능하게 함.
  // gridColumn(옵션): 부모 섹션이 마지막 행 채우기 스킴 활성이면 이 자식의 인라인 grid-column 값.
  // web-live(비 interactive)에선 renderInner 출력이 grid 자식이라 여기서 인라인 style로 부여하고,
  // interactive에선 renderWidget 래퍼가 grid 자식이라 그쪽에서 부여(여기선 span 미부여).
  const renderInner = (
    w: ComposerWidget,
    gridded: boolean,
    gridColumn?: string,
    /** formTable 셀 안 렌더 — input 컨트롤이 셀 폰트(td)를 상속(renderWriteNode로 전달). */
    inheritCellFont?: boolean,
  ): ReactNode => {
    // 채움 스킴 활성 자식(gridColumn 지정) — 기존 span 클래스 경로를 끄고 인라인 grid-column로 대체.
    const schemeChild = !!gridColumn;
    // web-live 스킴 자식은 인라인 grid-column을 직접 지님(flex 스택(md 미만)에선 무효, md+ grid에서만 적용).
    const childGridStyle: CSSProperties | undefined =
      schemeChild && !interactive ? { gridColumn } : undefined;
    // design 위젯(heading/separator/spacer)은 web-live 경로에서 자신이 grid 자식이라 여기서 전폭 부여.
    // 입력 위젯의 span은 renderWidget 래퍼(그리드 자식)에서 처리(Fragment엔 클래스 못 얹음).
    // 스킴 활성 시엔 클래스 span 미부여(인라인 grid-column이 대신).
    const spanFull =
      !schemeChild && gridded && resolveWidgetFullWidth(w, view)
        ? view
          ? 'col-span-full'
          : 'md:col-span-full'
        : '';
    const editingThis = canEditText && editingId === w.id;
    switch (w.kind) {
      case 'heading': {
        // 기본 = 현행 유지(14px/600/gray-700). textStyle로 오버라이드.
        const headingStyle = textStyleCss(w.textStyle, COMPOSER_HEADING_DEFAULT_STYLE);
        if (editingThis) {
          return (
            <h4 key={w.id} className={spanFull} style={{ ...headingStyle, ...childGridStyle }}>
              <InlineTextEditor
                initial={w.text ?? ''}
                textStyle={headingStyle}
                onCommit={(v) => commitTextEdit(w.id, v)}
                onCancel={cancelTextEdit}
              />
            </h4>
          );
        }
        // 자동 채움(prefillFrom) 지정 시 카탈로그 resolve 값(values[w.id])을 우선 표시.
        // 값이 해석되지 않거나(undefined/null) 빈 문자열이면 정적 text로 fallback. 시각은 heading 그대로.
        const prefillValue = w.prefillFrom ? values[w.id] : undefined;
        const headingText =
          prefillValue != null && prefillValue !== '' ? String(prefillValue) : w.text;
        return (
          <h4 key={w.id} className={spanFull} style={{ ...headingStyle, ...childGridStyle }}>
            {headingText}
          </h4>
        );
      }
      case 'separator':
        // 저작 모드: 1px 선만으론 클릭(선택) 히트 영역이 없어 캔버스에서 선택 불가(2026-07-13
        // 사용자 제보) — 상하 6px 투명 히트 영역. 단 음수 마진으로 상쇄해 **차지 공간은 실폼과
        // 동일(1px)**하게 유지(2026-07-14 캔버스=실폼 레이아웃 결 원칙 — 히트는 이웃 gap 위로 겹침).
        if (interactive)
          return (
            <div
              key={w.id}
              className={spanFull}
              style={{ ...childGridStyle, padding: '6px 0', margin: '-6px 0' }}
            >
              <hr className="border-gray-200" />
            </div>
          );
        return (
          <hr key={w.id} className={`border-gray-200 ${spanFull}`} style={childGridStyle} />
        );
      case 'spacer': {
        // 순수(web 실폼): 보이지 않는 여백 — 높이는 w.height(px, 미지정 8px 기본).
        // 저작 모드: 형태 없는 위젯도 보이게 점선 힌트 — 단 **실제 지정 높이 그대로**(이전 최소
        // 16px 강제는 캔버스가 실폼보다 커지는 WYSIWYG 결함, 2026-07-14 원칙). 라벨은 들어갈
        // 높이(≥14px)일 때만, 얇은 여백은 점선 스트립만(선택은 클릭/트리로 가능).
        const spacerH = w.height ?? 8;
        if (!interactive)
          return (
            <div
              key={w.id}
              className={spanFull}
              style={{ ...childGridStyle, height: spacerH }}
              aria-hidden="true"
            />
          );
        return (
          <div
            key={w.id}
            className={`flex items-center justify-center overflow-hidden rounded border border-dashed border-gray-200 text-[10px] text-gray-300 select-none ${spanFull}`}
            style={{ ...childGridStyle, height: spacerH }}
          >
            {spacerH >= 14 ? `여백${w.height != null ? ` ${w.height}px` : ''}` : null}
          </div>
        );
      }
      case 'group': {
        // displayVariant='tabs'는 본문을 탭 UI로 대체 — 단 수/채움 스킴 무의미(1단 수직 flow)로 고정해
        // 탭 바+활성 자식만 렌더한다. 그룹 자체 헤더/박스(title/collapsible/boxStyle)는 기존 규약대로.
        const isTabs = w.displayVariant === 'tabs';
        const cols = isTabs ? 1 : resolveGroupColumns(w, view);
        // 이 섹션의 마지막 행 채우기 스킴(fillLastRow 켜짐 + 조건 충족 시만). null=기존 클래스 경로.
        const childOrdered = ordered(w.children);
        // 실폼: visibleWhen 미충족(자리 유지 없음) 자식은 실제로 미렌더(null)라 흐름 계산에서도
        // 제외해야 L 판정이 렌더와 일치 — 포함하면 숨은 위젯이 L===cols를 만들어 채움이 안 걸리고
        // 남은 위젯이 반폭+빈칸이 된다(2026-07-17 결함 수정). 판정은 renderWidget null 반환과 같은
        // 헬퍼(isWidgetFlowRemovedInLiveForm — 단일 진실). 캔버스(interactive)는 억제 위젯이
        // 반투명으로 슬롯을 점유하므로 기존대로 흐름 포함(제외하면 겹침 발생).
        const fillSuppressed = interactive
          ? undefined
          : (child: ComposerWidget): boolean => isWidgetFlowRemovedInLiveForm(child, values);
        // cols 2·3단은 클래스 경로(회귀0), 4~6단은 클래스 맵 부재라 인라인/주입 grid 경로(gridContainer)를
        // 재사용한다. fillLastRow가 조건 미충족(null)이어도 cols>3이면 순수 N단 grid 레이아웃으로 채운다.
        // 비율 배치(span이 숫자로 지정된 자식이 있는 섹션)는 행 컨테이너 경로가 전담한다 — 비율이
        // 임의 소수면 단일 grid의 정수 트랙(LCM)으로 표현할 수 없기 때문. 비율이 없으면 null이라
        // 아래 기존 경로가 그대로 산다(회귀 0).
        const rowLayout: ComposerRatioRowLayout | null = isTabs
          ? null
          : computeGroupRatioRowLayout(childOrdered, cols, view, fillSuppressed, w.fillLastRow);
        const fillLayout: GroupFillLastRowLayout | null =
          isTabs || rowLayout
            ? null
            : ((w.fillLastRow
                ? computeGroupFillLastRowLayout(childOrdered, cols, view, fillSuppressed)
                : null) ?? (cols > 3 ? computePlainGridLayout(cols) : null));
        // 뷰 지정(저작): 명시 해소된 단 수를 고정 grid로. 미지정(web 실폼): 기존 md: 자동 degrade(회귀0).
        // cols>3은 위 fillLayout(인라인/주입 grid)이 항상 담당하므로 gridClass는 미사용(space-y-4 무해).
        const gridClass =
          cols > 1 && cols <= 3
            ? view
              ? GROUP_GRID_FIXED_CLASS[cols]
              : GROUP_GRID_CLASS[cols]
            : 'space-y-4';
        // tabs: 자식을 정상 경로로 1:1 렌더(renderedKids)하되, 활성/비탭만 트리에 삽입(탭 body).
        // 탭 대상 group 자식은 **자체 헤더/접기를 억제**(title/collapsible 제거 사본) — 탭 라벨이 제목
        // 역할이라 중복 표기 방지(구 SMS 탭 동형, 2026-07-21 사용자 정정). 제목 편집은 속성 패널/트리에서
        // (id 보존이라 선택·드릴 채널 불변). 미탭: 기존대로 grid 배치(회귀 0).
        const kids = isTabs ? (
          <ComposerGroupTabsBody
            childOrdered={childOrdered}
            renderedKids={childOrdered.map((c, i) =>
              renderWidget(
                c.kind === 'group'
                  ? ({ ...c, title: undefined, collapsible: undefined } as ComposerWidget)
                  : c,
                false,
                w.id,
                i,
              ),
            )}
            values={values}
          />
        ) : rowLayout ? (
          // 비율 배치: 자식을 행 컨테이너로 묶는다. 폭은 행 트랙(fr)이 소유하므로 자식에겐
          // span 클래스도 인라인 grid-column도 주지 않는다(gridded=false, fillLayout 미전달) —
          // 두 축이 겹치면 칸 폭이 두 번 정해진다. 뷰 지정(캔버스)은 인라인 grid, 실폼은
          // ComposerGroupBlock이 주입하는 <style>이 data-composer-row 선택자로 잡는다.
          rowLayout.rows.map((row, ri) => (
            <div
              key={`cwrow-${w.id}-${ri}`}
              data-composer-row={ri}
              style={
                view
                  ? {
                      display: 'grid',
                      gridTemplateColumns: ratioRowTemplateColumns(row),
                      rowGap: w.gap ?? 16,
                      columnGap: w.gapX ?? w.gap ?? 16,
                    }
                  : undefined
              }
            >
              {row.indexes.map((ci) => renderWidget(childOrdered[ci], false, w.id, ci))}
            </div>
          ))
        ) : (
          childOrdered.map((c, i) => renderWidget(c, cols > 1, w.id, i, fillLayout))
        );
        // 저작 모드에서만 append 드롭존(빈 섹션도 여기로 드롭). 스킴 활성 시 전폭 배치(gridColumn 1/-1).
        // 래퍼 div 없이 layout 인자를 슬롯에 직접 전달 — 래퍼가 항상 흐름에 남으면 슬롯이
        // display:none이어도 grid 행/flex gap을 한 칸 만들어 실폼보다 하단이 벌어짐(2026-07-14 제보).
        // 비율 배치에선 바깥이 세로 flex라 슬롯이 이미 한 줄을 차지한다(배치 지시 불필요).
        const dropSlot = interactive?.renderDropSlot
          ? interactive.renderDropSlot(w.id, {
              className: !fillLayout && !rowLayout && cols > 1 ? 'md:col-span-full' : undefined,
              style: fillLayout ? { gridColumn: '1 / -1' } : undefined,
            })
          : null;
        // 섹션 헤더/접이식은 로컬 접힘 state(useState)가 필요해 컴포넌트로 위임.
        const block = (
          <ComposerGroupBlock
            key={w.id}
            widget={w}
            cols={cols}
            gridClass={gridClass}
            fillLayout={fillLayout}
            rowLayout={rowLayout}
            view={view}
            kids={kids}
            dropSlot={dropSlot}
            interactive={!!interactive}
            editingTitle={editingThis}
            onStartTitleEdit={canEditText ? () => startTextEdit(w.id) : undefined}
            onTitleCommit={(v) => commitTextEdit(w.id, v)}
            onTitleCancel={cancelTextEdit}
            // collapseWhenFilled(채워져 있으면 접힘) — 초기값 준비 신호(initialValuesReady)와
            // 현재 values 기준 채움 판정을 내려 1회 초기 상태 결정(구 SMS 사업장 개요 동형).
            autoCollapse={
              w.collapsible && w.collapseWhenFilled
                ? { ready: initialValuesReady ?? true, filled: sectionInputsFilled(w, values) }
                : undefined
            }
          />
        );
        // 이 그룹이 부모 섹션 채움 스킴의 grid 자식이면(web-live) 자신도 인라인 grid-column을 지녀야 함.
        return childGridStyle ? (
          <div key={w.id} style={childGridStyle}>
            {block}
          </div>
        ) : (
          block
        );
      }
      case 'input': {
        // 등록형 위젯: host 레지스트리에서 widgetKey lookup → 있으면 그 컴포넌트, 없으면 안내 placeholder.
        // 행 그리드(dataSourceRows): host renderer 주입 시 그 렌더(RowsCollectionEditor), 아니면 placeholder.
        const iw = w as InputWidget;
        // 캔버스 라벨 인라인 편집 중이면 라벨 자리에 스왑할 에디터. registered/dataSourceRows도
        // placeholder 라벨 자리에 동일 스왑(자체 라벨이 아니라 캔버스는 항상 placeholder).
        const labelSlot = editingThis ? (
          <InlineTextEditor
            initial={iw.label ?? ''}
            textStyle={textStyleCss(iw.labelStyle, COMPOSER_INPUT_LABEL_DEFAULT_STYLE)}
            onCommit={(v) => commitTextEdit(w.id, v)}
            onCancel={cancelTextEdit}
          />
        ) : undefined;
        // enabledWhen(활성 조건) — visibleWhen과 평가 규약 동일(단일 진실 헬퍼), 표현은 컨트롤
        // disabled(값 유지). 실폼·캔버스 동일 시각(WYSIWYG — interactive 특례 없음, 선택·저작은
        // selectable 래퍼가 담당 + DISABLED_CONTROL_STYLE의 pointerEvents:none이 클릭을 래퍼로 통과).
        const widgetDisabled = isWidgetDisabledByCondition(iw, values);
        // registered/dataSourceRows는 host 소유 렌더라 개별 컨트롤에 disabled를 꽂을 수 없음 —
        // 서브트리 통째 비활성(반투명+포인터 차단, 레이아웃 중립).
        const wrapDisabledSubtree = (n: ReactNode): ReactNode =>
          widgetDisabled ? (
            <div style={DISABLED_SUBTREE_STYLE} aria-disabled="true">
              {n}
            </div>
          ) : (
            n
          );
        // 서명 슬롯(P2) — 행 스코프(signatureRowScopeKey=반복 over)에서만 주소가 성립한다.
        // slotKey·signerNameField는 **선언(dataSources)이 정본**(고정 slotKey 존중) — 위젯에
        // 구운 signerNameField는 선언 조회 실패(선언 리네임 등) 시 폴백(placed copy drift 완충).
        const signatureSlot = (() => {
          if (iw.widget !== 'signature' || !signatureRowScopeKey) return undefined;
          const declField = (dataSources ?? [])
            .find((d) => d.key === signatureRowScopeKey)
            ?.fields.find((f) => f.field === iw.bindingKey);
          return signatureSlotViewProps(
            signatureSlots,
            signatureRowScopeKey,
            declField ?? { field: iw.bindingKey, signerNameField: iw.signerNameField },
            values,
          );
        })();
        // 배지(withRequiredBadge)는 3분기 **공통 합성 지점**(아래 node 합성)에서 원본 iw 기준으로
        // 얹는다 — registered/dataSourceRows(host 소유 렌더)도 커버 + 콤보 actionItem 제거 스프레드
        // (파생 객체)가 표형 셀 억제 등록(WeakSet 참조 축)을 잃어도 판정이 흔들리지 않는다.
        const node = withRequiredBadge(
          iw,
          iw.widget === 'registered'
            ? wrapDisabledSubtree(
                renderRegisteredWidget(
                  iw,
                  values,
                  onChange,
                  registeredFormWidgets,
                  // resolvedRows를 컨텍스트에 합성 — 브리지 위젯(행 위젯→폼 위젯)이 기준표
                  // (resolvedRows["risk-assessment-config"] 등)를 반복 행 스코프에서 읽게 한다.
                  { ...registeredWidgetContext, resolvedRows },
                  labelSlot,
                ),
              )
            : iw.widget === 'dataSourceRows'
              ? // mode:'hidden' 존중 — 일반 입력은 renderWriteNodeCore가 null을 돌려주지만 이
                // 분기는 host 렌더러로 직행해 hidden이 무시되는 비대칭이 있었다(2026-09-07 정합).
                // 용법: 값 채널(prefill 1-c 주입·footer 중복 억제의 "배치됨" 판정)은 유지하면서
                // 그리드 UI만 없는 소스 — 훅이 파생/전파로만 쓰는 tbmPhotos-src류.
                iw.mode === 'hidden'
                ? null
                : wrapDisabledSubtree(
                    dataSourceRowsRenderer
                      ? dataSourceRowsRenderer(iw, values[iw.bindingKey], onChange)
                      : renderDataSourceRowsPlaceholder(iw, labelSlot),
                  )
              : renderWriteNodeCore(
                  // 콤보 액션 항목이 여는 모달이 submitAction(즉시 등록)인데 호스트 콜백 미배선
                  // (external 무인증 등)이면 액션 항목을 숨긴다(콜백 부재 = 액션 비활성 — 결정
                  // 2026-07-17 "등록 모달 = 즉시 등록"). commit 모달 액션 항목은 종전대로.
                  iw.widget === 'combobox' &&
                  iw.actionItem &&
                  !onCatalogRowSubmit &&
                  findModalTriggerById(tree, iw.actionItem.opensWidgetId)?.submitAction
                    ? { ...iw, actionItem: undefined }
                    : iw,
                  // 중첩 인지 read: flat 우선(합성 키 `__selectedId`·점 포함 합성 키 0 보존) +
                  // 점 포함 시 getByPath 폴백(반복 행 객체의 중첩 필드 `riskEstimation.likelihood`).
                  // 본문 readBindingValue 규약 동형 — 점 없는 단일 키는 flat 조회로 회귀 0.
                  readBindingValue(values, w.bindingKey),
                  onChange,
                  labelSlot,
                  inheritCellFont,
                  dataDefs,
                  values,
                  resolvedRows,
                  widgetDisabled,
                  requestModalOpen,
                  suggestHandlers,
                  // 표시 형식(format)의 보조 경로가 본문 키(기준표 등)를 읽게 하는 폴백 채널 —
                  // 반복 행 서브트리에서 RepeatRowsBlock이 registeredWidgetContext.values에 실어 보낸
                  // 본문 값이다(branchScope·등록형 위젯과 같은 채널). 최상위에선 undefined.
                  registeredWidgetContext?.values,
                  onPhotoUpload,
                  // 행 인스턴스 스코프 DOM id 접미(최상위=undefined → `wf-{id}` 그대로).
                  domIdSuffix,
                  // 서명 슬롯 resolved 표시값+상호작용(P2 — 위에서 행 스코프 해소).
                  signatureSlot,
                ),
        );
        // 채움 스킴(web-live): 일반/전폭 무관 모든 input이 인라인 grid-column을 지녀야 하므로 div 래핑.
        // (interactive는 renderWidget 래퍼가 grid 자식이라 여기선 Fragment — 래퍼가 grid-column 부여.)
        if (childGridStyle) {
          return (
            <div key={w.id} style={childGridStyle}>
              {node}
            </div>
          );
        }
        if (schemeChild) return <Fragment key={w.id}>{node}</Fragment>;
        // 입력 위젯 전체폭 — web-live 경로에선 renderInner 출력이 grid 자식이라 여기서 부여
        // (저작은 renderWidget 래퍼가 grid 자식이라 거기서도 부여 — 중첩은 무해).
        const inputSpan =
          gridded && resolveWidgetSpanFull(w, view)
            ? view
              ? 'col-span-full'
              : 'md:col-span-full'
            : '';
        return inputSpan ? (
          <div key={w.id} className={inputSpan}>
            {node}
          </div>
        ) : (
          <Fragment key={w.id}>{node}</Fragment>
        );
      }
      case 'modalTrigger': {
        // 커스텀 모달 버튼 — input과 동일한 grid 배치 규약(전폭/스킴 자식/일반). 값 스코프는 위젯이 소유.
        const node = (
          <ModalTriggerButton
            widget={w}
            bodyValues={values}
            onBodyChange={onChange}
            interactive={!!interactive}
            openRequest={modalOpenRequest}
            // submitAction 성공 시 반환 행을 적용할 콤보(actionItem 역참조) — 본문 트리에서 계산.
            linkedCombobox={w.submitAction ? findActionComboboxFor(tree, w.id) : undefined}
            childContext={{
              dataSources,
              dataDefs,
              dataSourceRowsRenderer,
              registeredFormWidgets,
              registeredWidgetContext,
              view,
              resolvedRows,
              onCatalogRowSubmit,
              onSuggestSearch,
              onSuggestSelect,
              onPhotoUpload,
              signatureSlots,
              interactive,
              authoringOpenId: interactive?.modalAuthoringId,
              onAuthoringClose: interactive?.onModalAuthoringClose,
              activeBranchSlots,
              // 모달 children은 연 스코프의 접미를 물려받는다(행 안 모달 = 그 행 스코프).
              domIdSuffix,
            }}
          />
        );
        if (childGridStyle) {
          return (
            <div key={w.id} style={childGridStyle}>
              {node}
            </div>
          );
        }
        if (schemeChild) return <Fragment key={w.id}>{node}</Fragment>;
        const modalSpan =
          gridded && resolveWidgetSpanFull(w, view)
            ? view
              ? 'col-span-full'
              : 'md:col-span-full'
            : '';
        return modalSpan ? (
          <div key={w.id} className={modalSpan}>
            {node}
          </div>
        ) : (
          <Fragment key={w.id}>{node}</Fragment>
        );
      }
      case 'formTable': {
        // 구모델(columns.label/group 파생 thead) → 신모델(헤더=일반 고정 행) 즉석 정규화.
        // 순수·결정적(헤더 행 id 고정)이라 admin이 트리에 기록한 정규화 결과와 항상 일치 —
        // 셀 포커스 좌표(rowId)가 저장 트리와 어긋나지 않는다. 신형 입력은 참조 그대로(회귀 0).
        const ft = normalizeFormTable(w as FormTableWidget);
        const cols = ft.columns;
        // 행 표시 조건(visibleWhen, 고정 행 전용) — branch 슬롯과 같은 evaluateCondition + 같은 스코프
        // (반복 항목 안에서는 본문 값(registeredWidgetContext.values) 위에 현재 행 필드(values)를 머지 —
        // branchScope 규약 동형). 캔버스(interactive)는 항상 렌더(+조건 배지 — 숨기면 편집 불가).
        // ⚠️ 위젯 visibleWhen(값 존재 판정 키)과 동명이지만 행 축은 조건식(결정 2026-08-17).
        const rowCondScope = registeredWidgetContext?.values
          ? { ...registeredWidgetContext.values, ...values }
          : values;
        const isRowCondHidden = (row: FormTableRow): boolean =>
          row.type === 'fixed' &&
          row.visibleWhen !== undefined &&
          !interactive &&
          !evaluateCondition(row.visibleWhen, rowCondScope);
        // 렌더 대상 행 — 숨은 행은 rowSpan 점유 계산에서도 제외. 정규화가 병합 구간의 visibleWhen을
        // strip하므로 정상 데이터에선 숨은 행이 병합에 얽히지 않지만, 위반 데이터도 격자가 깨지지 않게
        // 점유 walk와 tbody가 같은 목록을 쓴다(방어).
        const renderRows = ft.rows.filter((row) => !isRowCondHidden(row));
        const repeatDef = (
          row: Extract<FormTableRow, { type: 'repeat' }>,
        ): DataSourceDef | undefined => (dataSources ?? []).find((d) => d.key === row.sourceKey);
        // 반복 행 중 하나라도 **삭제** 허용이면 우측에 액션(삭제 버튼) 열을 둔다(전 행 열 정렬 유지).
        // 추가/삭제는 별개 축 — shared effectiveRowAddRemove(def, row): 키마다 행 오버라이드 → def → true.
        const rowAllowsRemove = (row: FormTableRow): boolean =>
          row.type === 'repeat' && effectiveRowAddRemove(repeatDef(row), row).remove;
        const hasActionsCol = ft.rows.some(rowAllowsRemove);

        // 본문 폰트 일괄 — 미지정 시 구 SMS 작성 테이블 기본(14/400/gray-700). 헤더는 정규화가
        // 헤더 셀 style로 박제(표 수준 headerStyle 축 소멸)라 여기선 별도 합성 없음.
        // col.align은 반복 행 셀 정렬 fallback(정규화가 반복 행 참조 열의 align을 보존).
        const cellFont = textStyleCss(ft.cellStyle, COMPOSER_FORM_TABLE_CELL_DEFAULT_STYLE);
        const colStyle = (col: FormTableColumn): CSSProperties => ({
          ...FORM_TABLE_BODY_CELL_STYLE,
          ...cellFont,
          ...(col.align ? { textAlign: col.align } : {}),
        });

        // 고정 행 셀 위젯 — renderWidget 재귀 경로(캔버스 선택/hover/상시편집). 라벨은 헤더가 대신하므로 억제.
        const renderCellWidget = (cell: FormTableFixedCell): ReactNode => {
          if (!cell.widget) return null;
          // visibleWhen(P3): 셀 위젯도 트리 위젯과 동일 규약 — 실폼=미렌더(빈 셀, 표 격자는 유지),
          // 캔버스=반투명 유지(셀 포커스/저작 가능). 셀은 selectable 래퍼를 안 타므로 여기서 처리
          // (판정은 트리 위젯과 같은 단일 진실 헬퍼).
          const cellVisSuppressed = isWidgetVisSuppressed(cell.widget, values);
          if (cellVisSuppressed && !interactive) return null;
          // 라벨 본문·hint는 헤더가 대신하므로 억제 — 단 labelSuffixFrom(라벨 병기)은 spread로
          // 유지되어 참조 값이 있으면 병기만 셀 라벨로 남는다(예: 단위 "(dB)" — shared 규약).
          const suppressed: InputWidget = {
            ...cell.widget,
            label: undefined,
            labelHint: undefined,
          };
          // 셀 = 단일 개념(2026-07-13): 셀 안 위젯은 별도 선택 래퍼 없이 순수 렌더 — 클릭은 td로
          // 버블해 셀 포커스(위젯 속성은 셀 속성 패널에 인라인). 값 편집(상시 편집)은 그대로 동작.
          // inheritCellFont: 셀 폰트(표 cellStyle/셀 style)가 컨트롤 값 표시에도 반영(2026-07-13 제보 fix).
          const rendered = renderInner(suppressed, false, undefined, true);
          return cellVisSuppressed ? <div style={{ opacity: 0.4 }}>{rendered}</div> : rendered;
        };

        // 텍스트 셀 더블클릭 인라인 편집 커밋(저작 전용) — 미전달이면 편집 비활성(셀 포커스만).
        const cellTextChange = interactive?.onFormTableCellTextChange;
        // 고정 셀 내용 — 셀 종류 상호배타(text/compute/widget). 계산 셀은 같은 표 위젯 값(values=writeValues)을
        // scope로 CellCompute 엔진 재사용해 실시간 산출·표시 전용(박제 안 함). cellFont는 td colStyle이 상속.
        const renderFixedCellContent = (cell: FormTableFixedCell, rowId: string): ReactNode => {
          if (cell.text !== undefined) {
            // 더블클릭 인라인 편집 중 — **정적 텍스트 셀 전용**(자동 채움(prefillFrom) 셀은 진입
            // 차단: 양식 탭 "정적/빈 텍스트 셀만 인라인 편집, binding 셀 제외" 규약 동형. 표시가
            // 파생값이라 텍스트 편집기로 수정할 수 없음 — 채움 변경/해제는 속성 패널, 2026-07-14
            // 사용자 확정 "수정해도 카탈로그는 유지"). 정적 셀은 표시=text라 시드=기존 내용.
            // 폰트는 td(셀 폰트 합성)를 InlineTextEditor의 font:inherit이 상속(layout shift 최소).
            if (
              cellTextChange &&
              !cell.prefillFrom &&
              editingFtCell &&
              editingFtCell.tableId === ft.id &&
              editingFtCell.rowId === rowId &&
              editingFtCell.columnId === cell.columnId
            ) {
              return (
                <InlineTextEditor
                  initial={cell.text}
                  // 정렬은 셀(td textAlign — 열/셀 정렬 합성)을 상속 — 가운데 정렬 셀은 편집도
                  // 가운데 정렬 상태로(input은 textAlign 미상속이라 명시 필요, 셀 컨트롤 상속 규약 동형).
                  textStyle={{ textAlign: 'inherit' }}
                  onCommit={(v) => {
                    setEditingFtCell(null);
                    // 무변경 커밋은 스킵 — 불필요한 undo 스텝/dirty 방지.
                    if (v !== cell.text) cellTextChange(ft.id, rowId, cell.columnId, v);
                  }}
                  onCancel={() => setEditingFtCell(null)}
                />
              );
            }
            // 자동 채움(prefillFrom) 지정 시 카탈로그 resolve 값(values[합성키])을 우선 표시.
            // 값 미해석(undefined/null)/빈 문자열이면 정적 text로 fallback(heading 자동 채움과 동형).
            if (cell.prefillFrom) {
              const pv = values[ftTextCellPrefillKey(ft.id, rowId, cell.columnId)];
              if (pv != null && pv !== '') return String(pv);
            }
            return cell.text;
          }
          if (cell.compute) {
            const cv = String(computeCellCompute(cell.compute, values));
            // 값 없을 때도 박스 높이 유지(nbsp) — 구 SMS 계 박스 질감.
            return <span style={FORM_TABLE_COMPUTE_BOX_STYLE}>{cv === '' ? NBSP : cv}</span>;
          }
          return renderCellWidget(cell);
        };

        // 셀 더블클릭 → host 속성 패널 행 펼침+셀 포커스(저작 전용 — 미전달이면 무동작). 항상 단일.
        const cellFocus = interactive?.onFormTableCellFocus;
        // 선택 의도 클릭에서 셀 안 입력 위젯의 포커스 해제 — 위젯 셀은 클릭이 내부 input에
        // 포커스를 남겨 host의 Ctrl+C/V(셀 복붙) 가드가 "입력 편집 중"으로 오인해 무시한다
        // (위젯 셀 블록 복사 무반응 버그, 2026-07-13). 편집 중인 셀 자체의 클릭은 각 핸들러가
        // editing-inside 판정으로 미개입(캐럿 유지) — 여기 도달 = 선택/전환 동작이라 항상 blur.
        const blurInnerFocus = (e: MouseEvent<HTMLTableCellElement>): void => {
          const ae = document.activeElement;
          if (!(ae instanceof HTMLElement)) return;
          if (e.currentTarget.contains(ae)) {
            ae.blur();
            return;
          }
          // 같은 표의 **다른 셀**을 편집 중(컨트롤 포커스)일 수도 있음 — 셀 전환 시 그 편집도
          // 종료(blur=커밋). 안 하면 mousedown preventDefault가 브라우저 기본 blur까지 막아
          // 이전 셀 편집이 잔존(타이핑이 옛 셀로 감).
          const aeCell = ae.closest?.('[data-ftcell]');
          if (aeCell?.getAttribute('data-ftcell')?.startsWith(`${ft.id}::`)) ae.blur();
        };
        // 포커스(복수)/속성 hover 셀 하이라이트 — 인라인(rc-tailwind-purge 규약). 이 표의 포커스 셀만.
        // anchor(배열 마지막)=진한 링+배경, 나머지 선택 셀=옅은 링+옅은 배경.
        const fcs = (interactive?.focusedCells ?? []).filter((c) => c.tableId === ft.id);
        const fcAnchorIdx = fcs.length - 1;
        const hc =
          interactive?.hoveredCell && interactive.hoveredCell.tableId === ft.id
            ? interactive.hoveredCell
            : null;
        const cellFocusStyle = (rowId: string, columnId: string): CSSProperties => {
          // hover(위치 표시, 앰버)가 최우선 — 일시적(mouseleave 즉시 복귀)이라 포커스 위에 겹쳐도 안전.
          // 채움은 반투명만 허용 — 셀 자체 background(사용자 지정 배경색)가 비쳐 보여야 한다
          // (양식 탭 셀 선택 outline 관례 동형: 불투명 채움은 배경색 편집 결과를 가림).
          if (hc && hc.rowId === rowId && hc.columnId === columnId) {
            return { boxShadow: 'inset 0 0 0 2px #f59e0b', background: 'rgba(245, 158, 11, 0.22)' };
          }
          const idx = fcs.findIndex((c) => c.rowId === rowId && c.columnId === columnId);
          if (idx < 0) return {};
          return idx === fcAnchorIdx
            ? { boxShadow: 'inset 0 0 0 2px #818cf8' } // brand-400 (앵커 셀)
            : { boxShadow: 'inset 0 0 0 1px #a5b4fc' }; // brand-300 (범위 내 셀)
        };
        const hasFocusHere = fcs.length > 0;
        // 이 표 위젯 자체가 선택돼 있는지 — 더블클릭 단계별 드릴 사다리의 게이트.
        const tableSelected =
          !!interactive &&
          (interactive.selectedId === ft.id ||
            (interactive.selectedIds?.includes(ft.id) ?? false));
        // 선택 제스처 = 양식 탭 테이블 노드와 동일(2026-07-13 확정 + 2026-07-14 사다리 정합):
        //   클릭=표(isolation은 최상위 그룹) 선택 → 더블클릭=한 단계 드릴(그룹→…→표는 래퍼 버블,
        //   표 선택 후 더블클릭=셀 선택) → 셀 포커스 문맥의 더블클릭=그 셀 **편집 진입**
        //   (텍스트 셀=인라인 에디터/위젯·반복 셀=컨트롤 포커스 — 종류 무관 동일 제스처).
        //   단일 재클릭=편집 진입 규약은 폐지(양식 탭 동형: 편집은 더블클릭/F2로만) — 편집 중
        //   (셀 안 컨트롤 포커스)인 셀의 클릭만 브라우저 기본(캐럿 이동) 유지.
        // Ctrl/⌘+클릭=복수 토글, Shift+클릭=anchor→클릭 셀 사각 범위 — **셀 포커스가 있을 때만**
        // (없으면 수식어 무관 표 선택 단계로 버블 — 위젯 다중 선택이 풀리던 2026-07-14 제보).
        // 셀 안 위젯은 별도 선택 개념 없음. allowAdditive=false(반복 행)면 additive/range 무시(단일만).
        const cellFocusClick = (rowId: string, columnId: string, allowAdditive: boolean) =>
          cellFocus
            ? (e: MouseEvent<HTMLTableCellElement>) => {
                const additive = allowAdditive && (e.ctrlKey || e.metaKey);
                const range = allowAdditive && e.shiftKey;
                // 이 표에 셀 포커스가 없으면(수식어 무관) 표 선택 단계 — td 클릭을 래퍼로 버블.
                if (!hasFocusHere) return;
                e.stopPropagation();
                // 이 셀을 편집 중(컨트롤/에디터 포커스)이면 선택 로직 미개입 — 캐럿 클릭 유지.
                if (e.currentTarget.contains(document.activeElement)) return;
                if (range) e.preventDefault(); // Shift+클릭 텍스트 드래그 선택 방지
                blurInnerFocus(e);
                cellFocus(ft.id, rowId, columnId, { additive, range });
              }
            : undefined;
        // 셀=콘텐츠 일체화(2026-07-13 사용자 제보: "input 클릭 시 셀이 아니라 input만 선택됨") —
        // 선택 의도 클릭에선 셀 안 컨트롤(input/textarea/select)이 mousedown으로 포커스(캐럿)를
        // 채가지 못하게 차단해, input 위 클릭도 셀 여백 클릭과 완전히 같은 동작(표/셀 선택)이 되게
        // 한다. 이 셀을 이미 편집 중(더블클릭/F2로 컨트롤 포커스)일 때만 브라우저 기본(캐럿/드래그)
        // 유지. 저작 모드(cellFocus 有)에서만.
        const cellFocusMouseDown = (rowId: string, columnId: string) =>
          cellFocus
            ? (e: MouseEvent<HTMLTableCellElement>) => {
                // 편집 중인 셀 — 미개입(캐럿 이동/텍스트 선택 등 기본 동작).
                if (e.currentTarget.contains(document.activeElement)) return;
                const tag = (e.target as HTMLElement).tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') e.preventDefault();
                // 드래그 범위 선택 시작(엑셀식, 2026-07-14) — 드릴다운 문맥(이 표에 셀 포커스 有)의
                // plain mousedown에서 시작 셀을 단일 포커스(anchor)하고 arm. 이동 중 진입 td가
                // range를 재계산(cellFocusDragEnter), mouseup(전역)이 해제. 텍스트 드래그 선택
                // 방지를 위해 preventDefault(컨트롤 외 텍스트 셀 포함).
                if (!e.ctrlKey && !e.metaKey && !e.shiftKey && hasFocusHere && e.button === 0) {
                  e.preventDefault();
                  blurInnerFocus(e);
                  cellFocus(ft.id, rowId, columnId, {});
                  ftDragTableRef.current = ft.id;
                }
              }
            : undefined;
        // 드래그 중 셀 진입 → anchor(드래그 시작 셀) 고정 직사각 범위 재계산.
        const cellFocusDragEnter = (rowId: string, columnId: string) =>
          cellFocus
            ? () => {
                if (ftDragTableRef.current !== ft.id) return;
                cellFocus(ft.id, rowId, columnId, { range: true });
              }
            : undefined;
        // 셀 더블클릭 — 단계별 사다리(양식 탭 isolation 드릴 동형, 2026-07-14 사용자 확정):
        //   ① 표 미선택(그룹 선택 단계 등): 미처리 버블 → 래퍼 onWidgetDrill이 한 단계 드릴
        //      (그룹→중첩 그룹→표 순서로 더블클릭마다 한 단계).
        //   ② 표 선택 + 셀 포커스 없음: 셀 선택까지만(편집 아님).
        //   ③ 셀 포커스 문맥: 그 셀 편집 진입 — 정적 텍스트 셀=인라인 에디터, 위젯/반복 셀=컨트롤
        //      포커스(F2 동등). 자동 채움(prefillFrom) 텍스트 셀은 차단(표시가 파생값 — 양식 탭
        //      binding 셀 제외 동형, td title로 사유 안내), 계산/빈 셀은 편집 대상 없음.
        const cellFocusDbl = (rowId: string, columnId: string) =>
          cellFocus
            ? (e: MouseEvent<HTMLTableCellElement>) => {
                if (!tableSelected && !hasFocusHere) return; // ① 래퍼로 버블(드릴)
                e.stopPropagation();
                blurInnerFocus(e);
                // 더블클릭이 만든 단어 선택 해제 — 텍스트 선택이 남으면 Ctrl+C가 텍스트 복사로 분기.
                window.getSelection()?.removeAllRanges();
                const entering = !hasFocusHere; // ② 표 선택 → 셀 선택 진입 단계
                cellFocus(ft.id, rowId, columnId);
                if (entering) return;
                // ③ 편집 진입.
                const row = ft.rows.find((r) => r.id === rowId);
                const cell =
                  row?.type === 'fixed'
                    ? row.cells.find((c) => c.columnId === columnId)
                    : undefined;
                if (cell?.text !== undefined) {
                  if (cellTextChange && !cell.prefillFrom) {
                    setEditingFtCell({ tableId: ft.id, rowId, columnId });
                  } else if (cellTextChange && cell.prefillFrom) {
                    // 차단 사유 능동 안내 — 커서 지점(네이티브 hover 툴팁과 동일 기준).
                    // F2 재발행 dblclick은 host가 셀 안 좌표를 주입, 좌표 없으면(0) 셀 rect fallback.
                    const r = e.currentTarget.getBoundingClientRect();
                    setFtBlockedHint({
                      x: e.clientX > 0 ? e.clientX : r.left + 16,
                      y: e.clientY > 0 ? e.clientY : r.top + r.height / 2,
                      nonce: Date.now(),
                    });
                  }
                  return;
                }
                if (cell?.compute) return; // 계산 셀 — 편집 대상 없음(셀 선택 유지)
                // 위젯 셀/반복 행 셀 — 셀 안 컨트롤 포커스(F2 동등). 없으면(빈 셀) no-op.
                (
                  e.currentTarget.querySelector('input, textarea, select') as HTMLElement | null
                )?.focus();
              }
            : undefined;

        // rowSpan 점유 맵 — 위쪽 고정 행의 rowSpan 셀이 덮는 (행 rowId × 열 인덱스) 위치는 이 행에서
        // td를 만들지 않는다(HTML rowspan 렌더 규약). widget.rows 순서로 훑어 열별 잔여 커버 행수를 이월.
        // repeat 행을 만나면 이월을 비운다(병합은 인접 고정 행 전용이라 실 데이터엔 없지만 방어).
        const occupiedColsByRowId = new Map<string, Set<number>>();
        {
          const colIdxMap = new Map(cols.map((c, i) => [c.id, i]));
          const pending = new Map<number, number>(); // 열 인덱스 → 아래로 남은 커버 행수
          for (const row of renderRows) {
            if (row.type !== 'fixed') {
              pending.clear();
              continue;
            }
            const occ = new Set<number>();
            for (const [ci, n] of pending) if (n > 0) occ.add(ci);
            occupiedColsByRowId.set(row.id, occ);
            // 이 행 소비 → 잔여 -1
            for (const ci of Array.from(pending.keys())) {
              const nn = (pending.get(ci) ?? 0) - 1;
              if (nn <= 0) pending.delete(ci);
              else pending.set(ci, nn);
            }
            // 이 행의 rowSpan 셀이 아래로 덮을 열 구간(colSpan 포함) 등록.
            for (const cell of row.cells) {
              const start = colIdxMap.get(cell.columnId);
              if (start === undefined) continue;
              const rs = Math.max(1, cell.rowSpan ?? 1);
              if (rs <= 1) continue;
              const cs = Math.max(1, cell.colSpan ?? 1);
              for (let k = start; k < start + cs; k++) pending.set(k, rs - 1);
            }
          }
        }

        const renderFixedRow = (row: Extract<FormTableRow, { type: 'fixed' }>): ReactNode => {
          const cellByCol = new Map(row.cells.map((c) => [c.columnId, c]));
          const occupied = occupiedColsByRowId.get(row.id) ?? new Set<number>();
          // 행 표시 조건 배지(캔버스 전용) — 행 첫 td 좌상단 absolute 오버레이(레이아웃 중립·히트 불가).
          // branch 빈 슬롯 힌트와 같은 teal 결(캔버스 어포던스 계열). 실폼은 행 자체가 조건으로
          // 제거되므로 여기 도달 시 interactive뿐이지만 방어적으로 게이트.
          const rowCondBadge =
            interactive && row.visibleWhen !== undefined ? (
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 1,
                  maxWidth: 240,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  padding: '0 4px',
                  fontSize: 10,
                  lineHeight: '14px',
                  borderRadius: '0 0 4px 0',
                  border: '1px solid #99f6e4',
                  background: '#f0fdfa',
                  color: '#0f766e',
                  pointerEvents: 'none',
                }}
              >
                조건: {row.visibleWhen}
              </span>
            ) : null;
          const tds: ReactNode[] = [];
          let ci = 0;
          while (ci < cols.length) {
            if (occupied.has(ci)) {
              ci += 1; // 위 rowSpan이 덮은 위치 — td 미생성
              continue;
            }
            // 배지는 행에서 처음 생성되는 td에만(absolute 기준점으로 그 td를 relative화).
            const hostsBadge = rowCondBadge !== null && tds.length === 0;
            const col = cols[ci];
            const cell = cellByCol.get(col.id);
            if (cell) {
              const span = Math.max(1, cell.colSpan ?? 1);
              const rSpan = Math.max(1, cell.rowSpan ?? 1);
              // 셀 대각선(사선) — 양식 탭 표 셀과 같은 오버레이(cellDiagonalOverlay 공유).
              // 기준 박스(position:relative)는 cellStyleToCss가 같은 조건으로 부여한다.
              // ⚠️ 미지정이면 null이고, 그때는 **td children 형상을 바꾸지 않는다**(아래 조건부 Fragment)
              // — 「대각선 없는 셀은 무영향」이 이 오버레이의 계약이다(RenderTemplate 쪽과 같은 규약).
              const diag = cellDiagonalOverlay(cell.style);
              tds.push(
                <td
                  key={col.id}
                  colSpan={span}
                  rowSpan={rSpan > 1 ? rSpan : undefined}
                  style={{
                    ...colStyle(col),
                    // 셀별 스타일(TableCellStyle) — 지정 속성만 표 cellStyle/기본 위에 덮어씀(폰트/배경/
                    // 테두리 변별/패딩). 양식 탭 셀 렌더와 동일 합성(cellStyleToCss 공유).
                    ...cellStyleToCss(cell.style),
                    // 셀 단위 정렬은 열 align(colStyle)·셀 style보다 우선. cellFocusStyle은 boxShadow/background만 건드림.
                    ...fixedCellAlignStyle(cell),
                    ...cellFocusStyle(row.id, col.id),
                    ...(hostsBadge ? { position: 'relative' as const } : {}),
                  }}
                  // 저작 모드 셀 DOM 식별자 — host 키보드 F2(셀 컨트롤 편집 진입)가 querySelector로 사용.
                  data-ftcell={cellFocus ? `${ft.id}::${row.id}::${col.id}` : undefined}
                  // 저작 모드 hover 툴팁 — 셀 값의 출처를 캔버스에서 바로 알 수 있게(2026-08-05 사용자
                  // 제보: "값이 그냥 나와서 왜 나오는지 모르겠다"). 위젯 셀=바인딩 키(+라벨), 자동 채움
                  // 텍스트 셀=카탈로그 키 + 편집 차단 사유. 네이티브 title이라 레이아웃 중립(실폼 미노출).
                  title={
                    !cellFocus
                      ? undefined
                      : cell.widget
                        ? `바인딩: ${cell.widget.bindingKey}${cell.widget.label ? ` (${cell.widget.label})` : ''}`
                        : cell.text !== undefined && cell.prefillFrom
                          ? `자동 채움: ${cell.prefillFrom.catalogKey}${cell.prefillFrom.select ? `.${cell.prefillFrom.select}` : ''} — 내용은 속성 패널의 "자동 채움"에서 변경합니다`
                          : undefined
                  }
                  onMouseDown={cellFocusMouseDown(row.id, col.id)}
                  onMouseEnter={cellFocusDragEnter(row.id, col.id)}
                  onClick={cellFocusClick(row.id, col.id, true)}
                  onDoubleClick={cellFocusDbl(row.id, col.id)}
                >
                  {hostsBadge ? rowCondBadge : null}
                  {diag === null ? (
                    renderFixedCellContent(cell, row.id)
                  ) : (
                    <>
                      {diag}
                      {renderFixedCellContent(cell, row.id)}
                    </>
                  )}
                </td>,
              );
              ci += span;
            } else {
              tds.push(
                <td
                  key={col.id}
                  style={{
                    ...colStyle(col),
                    ...cellFocusStyle(row.id, col.id),
                    ...(hostsBadge ? { position: 'relative' as const } : {}),
                  }}
                  data-ftcell={cellFocus ? `${ft.id}::${row.id}::${col.id}` : undefined}
                  onMouseDown={cellFocusMouseDown(row.id, col.id)}
                  onMouseEnter={cellFocusDragEnter(row.id, col.id)}
                  onClick={cellFocusClick(row.id, col.id, true)}
                  onDoubleClick={cellFocusDbl(row.id, col.id)}
                >
                  {hostsBadge ? rowCondBadge : null}
                </td>,
              );
              ci += 1;
            }
          }
          return (
            // 행 높이 지정 시 tr height로 반영 — 셀의 FORM_TABLE_BODY_CELL_STYLE height:36은 최소값으로 유지되어
            // 지정값이 36보다 작아도 36 아래로는 줄지 않는다(지정값 우선, 36 최소).
            <tr
              key={row.id}
              className="transition-colors hover:bg-gray-50"
              style={{
                borderBottom: '1px solid #f3f4f6',
                ...(row.height != null ? { height: row.height } : {}),
              }}
            >
              {tds}
              {hasActionsCol ? <td style={FORM_TABLE_BODY_CELL_STYLE} /> : null}
            </tr>
          );
        };

        const renderRepeatRow = (row: Extract<FormTableRow, { type: 'repeat' }>): ReactNode => {
          const def = repeatDef(row);
          const defFieldByKey = new Map((def?.fields ?? []).map((f) => [f.field, f]));
          const fieldByCol = new Map(row.cellFields.map((cf) => [cf.columnId, cf.field]));
          const dataRows = Array.isArray(values[row.sourceKey])
            ? (values[row.sourceKey] as Record<string, unknown>[])
            : [];
          const { add: allowAdd, remove: allowRemove } = effectiveRowAddRemove(def, row);
          // rowLimit 도달 시 추가 버튼 비활성(반복 위젯 경로와 같은 게이트 — 삭제는 자유).
          const addBlocked = isRowAddBlockedByLimit(def?.rowLimit, dataRows.length);
          const addBlockedTitle = rowAddLimitTitle(def?.rowLimit, dataRows.length);
          const updateCell = (ri: number, field: string, v: unknown): void => {
            onChange(
              row.sourceKey,
              dataRows.map((r, i) => (i === ri ? { ...r, [field]: v } : r)),
            );
          };
          /**
           * "+ 행 추가" — 행을 붙이고 **방금 만든 행의 첫 입력 셀로 커서**(반복 위젯·표 위젯 동형).
           * 이쪽 행은 rowId가 없어(값이 평범한 객체 배열) **위치**로 앵커를 만든다:
           * `${row.id}::${새 인덱스}` — 한 표에 반복 블록이 여럿일 수 있어 인덱스만으론 갈린다.
           * 조회 범위는 클릭한 버튼이 속한 그 표(중첩 반복 안에서 같은 표가 여러 벌 그려진다).
           * 캔버스(interactive)는 제외 — 저작 중 포커스가 캔버스로 튀면 속성 패널 조작이 끊긴다.
           */
          const addRow = (ev: MouseEvent<HTMLButtonElement>): void => {
            const blank: Record<string, unknown> = {};
            for (const cf of row.cellFields) blank[cf.field] = '';
            onChange(row.sourceKey, [...dataRows, blank]);
            if (interactive) return;
            focusAddedRowContainer(
              ev.currentTarget.closest('table'),
              FT_REPEAT_ROW_ATTR,
              `${row.id}::${dataRows.length}`,
            );
          };
          const removeRow = (ri: number): void => {
            onChange(
              row.sourceKey,
              dataRows.filter((_, i) => i !== ri),
            );
          };
          const dataTrs = dataRows.map((dr, ri) => (
            <tr
              key={`${row.id}-${ri}`}
              // 행 추가 직후 포커스 앵커(addedRowFocus FT_REPEAT_ROW_ATTR와 같은 이름).
              data-ft-repeat-row={`${row.id}::${ri}`}
              className="transition-colors hover:bg-gray-50"
              style={{ borderBottom: '1px solid #f3f4f6' }}
            >
              {cols.map((col) => {
                const field = fieldByCol.get(col.id);
                return (
                  <td
                    key={col.id}
                    style={{ ...colStyle(col), ...cellFocusStyle(row.id, col.id) }}
                    data-ftcell={cellFocus ? `${ft.id}::${row.id}::${col.id}` : undefined}
                    onMouseDown={cellFocusMouseDown(row.id, col.id)}
                    onClick={cellFocusClick(row.id, col.id, false)}
                    onDoubleClick={cellFocusDbl(row.id, col.id)}
                  >
                    {field
                      ? renderFormTableRepeatCell(
                          dr[field],
                          defFieldByKey.get(field),
                          (v) => updateCell(ri, field, v),
                          dr,
                          // 서명 슬롯(P2) — 이 반복 행의 sourceKey가 slotKey 파생 열쇠.
                          { slots: signatureSlots, sourceKey: row.sourceKey },
                        )
                      : null}
                  </td>
                );
              })}
              {hasActionsCol ? (
                <td style={{ ...FORM_TABLE_BODY_CELL_STYLE, textAlign: 'center' }}>
                  {allowRemove ? (
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-500 px-1"
                      aria-label="행 삭제"
                      onClick={() => removeRow(ri)}
                    >
                      ×
                    </button>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ));
          const addTr = allowAdd ? (
            <tr key={`${row.id}-add`}>
              <td colSpan={cols.length + (hasActionsCol ? 1 : 0)} style={FORM_TABLE_CELL_STYLE}>
                <button
                  type="button"
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                  style={addBlocked ? addItemButtonDisabledStyle : undefined}
                  disabled={addBlocked}
                  title={addBlockedTitle}
                  onClick={addRow}
                >
                  + 행 추가
                </button>
              </td>
            </tr>
          ) : null;
          return (
            <Fragment key={row.id}>
              {dataTrs}
              {addTr}
            </Fragment>
          );
        };

        // 열 너비 해석 — 양식 탭 renderTable colWidths 규약 동형: 숫자=px, 문자열('%'/'auto')=그대로,
        // 'auto'/미지정=width 미지정(균등). widthPct(구)는 정규화가 width로 변환하므로 여기선 width만 읽는다.
        const colWidthCss = (col: FormTableColumn): string | undefined => {
          const w = col.width;
          if (w == null || w === 'auto') return undefined;
          return typeof w === 'number' ? `${w}px` : w;
        };
        const needsColgroup = cols.some((c) => colWidthCss(c) != null) || hasActionsCol;
        // 표 외곽 테두리 — 전체 셀 테두리와 별개(양식 탭 TableNode.outerBorder 동형). border-collapse:collapse
        // 하에서 table 엘리먼트 테두리가 외곽으로 동작. 미지정=없음(기존 동작, 회귀 0).
        const ftTableStyle: CSSProperties = {
          width: '100%',
          borderCollapse: 'collapse',
          ...resolveCellBorder(ft.outerBorder, ft.outerBorderOverrides),
        };
        const table = (
          <table style={ftTableStyle}>
            {needsColgroup ? (
              <colgroup>
                {cols.map((c) => {
                  const w = colWidthCss(c);
                  return <col key={c.id} style={w != null ? { width: w } : undefined} />;
                })}
                {hasActionsCol ? <col style={{ width: 36 }} /> : null}
              </colgroup>
            ) : null}
            <tbody>
              {renderRows.map((row) =>
                row.type === 'fixed' ? renderFixedRow(row) : renderRepeatRow(row),
              )}
            </tbody>
          </table>
        );

        // 표 제목(캡션) — 저작 캔버스에서 더블클릭 인라인 편집(group 섹션명 패턴). 실폼(비interactive)은
        // 설정된 제목만 정적 표시. 제목 미설정이어도 저작(canEditText)이면 옅은 placeholder로 진입점 제공.
        const ftTitleStyleCss = textStyleCss(ft.titleStyle, COMPOSER_SECTION_TITLE_DEFAULT_STYLE);
        const titleEl = editingThis ? (
          <div style={{ marginBottom: 4 }}>
            <InlineTextEditor
              initial={ft.title ?? ''}
              textStyle={ftTitleStyleCss}
              onCommit={(v) => commitTextEdit(ft.id, v)}
              onCancel={cancelTextEdit}
            />
          </div>
        ) : ft.title ? (
          <div
            style={{ marginBottom: 4, ...ftTitleStyleCss }}
            onDoubleClick={
              canEditText
                ? (e) => {
                    e.stopPropagation();
                    startTextEdit(ft.id);
                  }
                : undefined
            }
          >
            {ft.title}
          </div>
        ) : null;
        // 무제목이면 제목 영역 자체를 그리지 않는다(2026-07-13 사용자 확정 — 저작 캔버스의
        // "표 제목" placeholder 제거). 제목 부여 진입점은 표 속성 패널의 캡션 입력, 부여된
        // 제목의 수정은 캔버스 더블클릭 인라인 편집 그대로.
        const content = (
          <>
            {titleEl}
            {/* 구 SMS 표 외곽 — 라운드 8 + 옅은 테두리, 셀 테두리는 없음(무테두리+행 구분선 질감).
                가로 넘침은 스크롤 허용. border-collapse 표를 감싸 외곽만 라운드 클립. */}
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                overflow: 'hidden',
                overflowX: 'auto',
              }}
            >
              {table}
            </div>
          </>
        );
        // 전폭 배치 — web-live 채움 스킴이면 childGridStyle, 아니면 spanFull 클래스(design 위젯과 동일 규약).
        if (childGridStyle) {
          return (
            <div key={w.id} style={childGridStyle}>
              {content}
            </div>
          );
        }
        return (
          <div key={w.id} className={spanFull}>
            {content}
          </div>
        );
      }
      case 'repeat': {
        // 반복(행 스코프 조립) 컨테이너 — 행마다 children 서브트리를 행 스코프 어댑터로 렌더
        // (RepeatRowsBlock — 실행부 B). grid 배치 규약은 formTable(블록 컨테이너)과 동일.
        // interactive(저작 캔버스)는 행 인스턴스 선택 채널로 하강(P4a — 행 스코프 파생은 블록 내부).
        const repeatContent = (
          <RepeatRowsBlock
            widget={w}
            values={values}
            onChange={onChange}
            interactive={interactive}
            childContext={{
              dataSources,
              dataDefs,
              dataSourceRowsRenderer,
              registeredFormWidgets,
              registeredWidgetContext,
              view,
              resolvedRows,
              onCatalogRowSubmit,
              onSuggestSearch,
              onSuggestSelect,
              onPhotoUpload,
              signatureSlots,
              activeBranchSlots,
              // 바깥 접미(중첩 repeat) — RepeatRowsBlock이 행 조각을 여기에 누적한다.
              domIdSuffix,
            }}
          />
        );
        // 래퍼 앵커 — **컨테이너 축**(2026-09-29). `withWriteFieldAnchor`는 `renderWriteNodeCore`에
        // 있고 그것은 `kind:'input'`만 렌더하므로 컨테이너엔 앵커가 닿지 않는다. 그런데 required
        // repeat은 **행 0개일 때 자기 자신이 missing으로 보고된다**(shared walkCheckableInputs —
        // 「최소 1행 필수」, 구 DLR 「작업 행 ≥1」 게이트의 일반화). 그 id로 조회하는 host의
        // `focusMissingRequiredField`가 앵커를 못 찾아 **조용히 반환**하던 것이 이 자리의 결함이었다
        // (2026-09-29 dev 실측: `scrollIntoView` 0건 · 포커스 이동 0 · prod 2자리
        //  `daily-safety-inspection-log`/`rw-work-items-src` · `risk-assessment-report`/`ra-wf-items-repeat`).
        //
        // 하강 대상은 행 0개면 그 표의 **「+ 행 추가」 버튼**이 된다 — 사용자가 실제로 눌러야 하는 것이
        // 그것이다(textarea-list가 「+ 항목 추가」로 닿는 것과 동형). required 여부와 **무관하게 늘** 얹는다
        // (조건부로 두면 「required가 아니었다가 켜진」 표에서 앵커가 없어진다 — withWriteFieldAnchor 동형).
        // ⚠️ 반환 경로가 둘이라 양쪽에 얹는다. 하나만 고치면 채움 스킴(gridColumn) 경로에서만 죽어
        // 재현 조건이 좁아진다 — census 컨테이너 축이 이 누락을 잡는다.
        const repeatAnchorProps = writeFieldAnchorProps(w.id, domIdSuffix);
        if (childGridStyle) {
          return (
            <div key={w.id} style={childGridStyle} {...repeatAnchorProps}>
              {repeatContent}
            </div>
          );
        }
        return (
          <div key={w.id} className={spanFull} {...repeatAnchorProps}>
            {repeatContent}
          </div>
        );
      }
      case 'branch': {
        // 분기(투명 컨테이너) — 조건 매칭 첫 슬롯의 children만 부모와 동일한 값 스코프로 렌더한다
        // (반복과 달리 행 재바인딩 없음 — group cols=1 수직 flow 동형). 본문 RenderTemplate branch
        // 렌더(첫 매칭 슬롯만, 없으면 null) 미러.
        // 저작 override(activeBranchSlots): 지정 위젯은 condition 무시하고 그 index 슬롯을 렌더(clamp).
        const override = activeBranchSlots?.[w.id];
        // 반복 행 스코프에서 branch condition이 본문 키(예: riskAssessmentMethodType)를 읽도록
        // 본문 값(registeredWidgetContext.values)을 아래에 깔고 현재 스코프(values=행) 키를 우선 머지.
        // 최상위(비반복)에선 ctx.values 부재/본문 동일이라 무해(P4d).
        const branchScope = registeredWidgetContext?.values
          ? { ...registeredWidgetContext.values, ...values }
          : values;
        // 활성 슬롯 판정은 표형 헤더(repeatTableColumnHeaderLabel)와 같은 단일 지점을 재사용한다 —
        // 두 경로가 갈라지면 헤더가 실제 렌더 슬롯과 다른 이름을 보이게 된다.
        const activeIndex = resolveActiveBranchSlotIndex(w.branches, branchScope, override);
        const activeSlot = activeIndex >= 0 ? w.branches[activeIndex] : undefined;
        // 매칭 슬롯 없음 = 아무것도 렌더 안 함(null — 본문 규약 동형).
        if (!activeSlot) return null;
        // 저작(interactive)에서만: 활성 슬롯 말미 드롭존(빈 슬롯도 여기로 드롭 — group append 동형,
        // 부모=슬롯 id) + 슬롯이 비면 점선 힌트. 실폼(web)은 !interactive라 children만(빈 슬롯=0높이, 회귀0).
        const branchDropSlot =
          interactive?.renderDropSlot ? interactive.renderDropSlot(activeSlot.id) : null;
        const branchEmptyHint =
          interactive && activeSlot.children.length === 0 ? (
            <div
              style={{
                border: '1px dashed #99f6e4',
                borderRadius: 4,
                padding: '10px 12px',
                fontSize: 12,
                color: '#0f766e',
                background: '#f0fdfa',
              }}
            >
              이 슬롯에 위젯을 추가하세요 — 트리 슬롯 행의 &quot;+&quot; 또는 여기로 드래그
            </div>
          ) : null;
        const branchContent = (
          <div className="space-y-4">
            {/* parentId=activeSlot.id — branch 위젯 id는 insertWidgetAt/dnd의 컨테이너 키가 아니라
                슬롯 id가 부모 키다(composerWidgetTree 규약). w.id로 두면 dnd·드롭존·추가가 슬롯을
                건너뛰어 root로 오배치되므로 활성 슬롯 id를 부모로 전달한다(저작 전용, 실폼 무영향). */}
            {ordered(activeSlot.children).map((c, i) => renderWidget(c, false, activeSlot.id, i))}
            {branchEmptyHint}
            {branchDropSlot}
          </div>
        );
        if (childGridStyle) {
          return (
            <div key={w.id} style={childGridStyle}>
              {branchContent}
            </div>
          );
        }
        return (
          <div key={w.id} className={spanFull}>
            {branchContent}
          </div>
        );
      }
      default:
        return null;
    }
  };

  // interactive면 각 위젯을 클릭 선택 래퍼로 감싼다(선택 시 파란 ring). 자식 클릭은 stopPropagation으로
  // group 버블링 차단 → 개별 선택. interactive 미전달이면 순수 렌더 반환(회귀0).
  const renderWidget = (
    w: ComposerWidget,
    gridded: boolean,
    parentId: string | null,
    index: number,
    fillLayout?: GroupFillLastRowLayout | null,
    /** true=더블클릭 라벨 인라인 편집 억제 — 표 셀 위젯(라벨이 헤더로 대체·억제)용. 더블클릭은 td로 버블해 셀 포커스. */
    noTextEdit = false,
  ): ReactNode => {
    const hidden = resolveWidgetHidden(w, view);
    // visibleWhen(P3, 전 위젯 공통 — group/formTable 컨테이너는 서브트리 통째): 지정 시 그 키의
    // 현재 스코프 값이 비어 있으면 숨김(`!isEmptyConditionValue(values[visibleWhen])`이면 표시 — shared 규약).
    // 판정은 fillLastRow 흐름 계산과 공유하는 단일 진실 헬퍼(isWidgetVisSuppressed).
    // 결정: 2026-07-16-composer-combobox-dependent-value-axes.
    const visSuppressed = isWidgetVisSuppressed(w, values);
    // 자리 유지 옵션(visibleWhenReserveSpace): 표시 조건 미충족이어도 실폼에서 그리드 슬롯을 투명
    // 점유(visibility hidden — 상속 숨김)해 이웃 레이아웃 불변. N단 섹션에서 조건 위젯 등장/퇴장 시
    // 이웃 폭 출렁임 방지(2026-07-17 사용자 제안). display:contents 래퍼라 grid 배치(inner의 span
    // 클래스/gridColumn)는 그대로 유지된다. 상호작용(visibility hidden=이벤트 없음)·required 검증
    // 제외는 미렌더와 동일. responsive hidden(뷰 숨김)은 해당 없음(자리째 제거가 의도).
    const reserveHidden = visSuppressed && !hidden && !!w.visibleWhenReserveSpace && !interactive;
    // 게이트(visibleWhenHint): 실폼에서 표시 조건 미충족 시 위젯 대신 "라벨 + 회색 안내 박스"를 렌더
    // (구 SMS 개선 후 위험도 게이트 동형). 저작(interactive)은 기존 dimmed 채널 유지(게이트 미표시).
    // reserveSpace와 배타 — reserveHidden이 우선(둘 다 지정된 비정상 조합에선 자리 유지).
    const showVisHintGate =
      !interactive && visSuppressed && !hidden && !reserveHidden && w.visibleWhenHint !== undefined;
    // 실폼(web): 뷰 숨김 또는 자리째 미렌더(visibleWhen 미충족·자리 유지 없음)면 null(레이아웃 제거)
    // — 같은 판정을 부모 섹션 fillLastRow 흐름 계산이 재사용한다(렌더↔흐름 정합). 저작(interactive):
    // dimmed로 남겨 선택·해제 가능(responsive hidden의 opacity-40 채널과 동형).
    if (!interactive && (hidden || isWidgetFlowRemovedInLiveForm(w, values))) return null;
    const reserveWrap = (node: ReactNode): ReactNode =>
      reserveHidden ? (
        <div key={w.id} style={{ display: 'contents', visibility: 'hidden' }} aria-hidden="true">
          {node}
        </div>
      ) : (
        node
      );
    // 부모 섹션 채움 스킴 활성이면 이 자식의 인라인 grid-column(마지막 행 균등 분할/전폭/일반).
    const gridColumn = fillLayout ? fillChildGridColumn(w, index, fillLayout, view) : undefined;
    // 게이트 렌더 시 위젯 컨트롤 대신 라벨+안내 박스(위젯은 그리드 슬롯 유지 — gridColumn 그대로).
    const inner = showVisHintGate ? renderVisHintGate(w) : renderInner(w, gridded, gridColumn);
    // 위젯 안쪽 여백(ComposerWidgetBase.padding) — 지정 시에만 padding 래퍼(미지정=기존 그대로, 회귀 0).
    // boxStyle 있는 group은 예외 — padding 래퍼는 테두리 **바깥**이라, 박스 요소(ComposerGroupBlock의
    // boxDecorStyle)가 padding을 직접 소유해 테두리 안쪽 여백이 되게 한다(구 SMS 카드 p-5 동형).
    const groupBoxOwnsPadding = w.kind === 'group' && !!w.boxStyle;
    const padStyle: CSSProperties | undefined =
      w.padding !== undefined && !groupBoxOwnsPadding
        ? { padding: boxSpacingToCss(w.padding) }
        : undefined;
    // 칸/부모 안 나의 수평 위치(Outer 정렬, ComposerWidgetBase.alignH — 양식 NodeLayout.alignH 대응물).
    // 지정 시에만 flex 래퍼로 위치 지정 — 고유 폭 위젯(모달 버튼·텍스트 등)에 효과, 칸을 채우는
    // 위젯(입력 w-full)은 무의미(양식 "채움이면 정렬 무의미" 규약 동형). 미지정=래퍼 없음.
    // align-items는 flex-start 고정 — flex 기본 stretch가 버튼·텍스트를 행 높이만큼 늘어뜨리는
    // 부작용 차단. 수직 축(alignV)은 의도적으로 없음(2026-07-15 결정 — 표 셀 외 사용 상황 없음).
    const alignStyle: CSSProperties | undefined =
      w.alignH === 'end' || w.alignH === 'center'
        ? {
            display: 'flex',
            justifyContent: w.alignH === 'end' ? 'flex-end' : 'center',
            alignItems: 'flex-start',
          }
        : undefined;
    const wrapStyle: CSSProperties | undefined =
      padStyle || alignStyle ? { ...padStyle, ...alignStyle } : undefined;
    if (!interactive) {
      if (!wrapStyle) return reserveWrap(inner);
      // 실폼: inner가 grid 배치(span 클래스 또는 web-live gridColumn)를 지니므로 래퍼가 그 배치를 대신
      // 지녀야 함(inner 자신의 배치는 비-grid-자식이라 무해). 스킴(gridColumn)이면 클래스 대신 인라인만.
      return reserveWrap(
        <div
          key={w.id}
          className={gridColumn ? '' : widgetSpanClass(w, gridded, view)}
          style={{ ...(gridColumn ? { gridColumn } : {}), ...wrapStyle }}
        >
          {inner}
        </div>
      );
    }
    // 스킴 활성 시 클래스 span 대신 인라인 grid-column(래퍼가 grid 직속 자식).
    const spanClass = gridColumn ? '' : widgetSpanClass(w, gridded, view);
    const selected =
      interactive.selectedId === w.id || (interactive.selectedIds?.includes(w.id) ?? false);
    // 선택/트리 hover 하이라이트는 인라인 style — rc 전용 Tailwind 유틸(ring-offset 등)은 앱 CSS에
    // 미생성될 수 있어 화면에서 조용히 사라짐(메모리 rc-tailwind-purge). hover만 클래스(비임계).
    const treeHovered = !selected && interactive.hoveredId === w.id;
    // formTable: 셀 포커스 중엔 표 전체 선택 링 억제 — 표 링+셀 inset 링 이중 파랑이 어색
    // (셀 하이라이트가 선택 표시를 대신, 2026-07-12 사용자 피드백).
    const ringSuppressed =
      selected &&
      w.kind === 'formTable' &&
      (interactive.focusedCells?.some((c) => c.tableId === w.id) ?? false);
    // 링을 위젯 시각 박스로 수축시킬 위젯: modalTrigger(버튼=content-hug 상시) / 정렬된 heading
    // (center·end는 flex 래퍼 안에서 텍스트 폭으로 shrink). 그 외(input·group·기본 heading·separator·
    // spacer·formTable)는 시각 박스=칸이라 링을 칸 래퍼(selectable)에 그대로 둔다(회귀 0). 양식 탭
    // SelectionOverlay가 노드 실제 DOM 박스 크기로 그리는 것과 동형(2026-07-15 사용자 제보).
    const shrinkRingToContent =
      w.kind === 'modalTrigger' ||
      (w.kind === 'heading' && (w.alignH === 'center' || w.alignH === 'end'));
    // 선택/트리 hover 링 그림자(값은 rc-tailwind-purge 규약상 인라인 — ring-offset 유틸은 앱 CSS에
    // 미생성 위험). ringSuppressed(표 셀 포커스)면 미표시. shrink 위젯은 아래 ringBox로, 그 외는
    // selectable(칸 래퍼)로 부여.
    // 2026-07-27에는 이 파랑을 브랜드 토큰화에서 뺐다 — admin이 미리스킨(brand 0/blue 776)이라
    // 여기만 인디고면 파란 에디터 chrome 안에서 되레 튀기 때문이었다. **2026-07-28 admin 리스킨으로
    // 그 이유가 사라져 brand로 옮긴다** — 이제 튀는 쪽은 파랑이다.
    // hex를 인라인으로 쓰는 이유는 색 선택이 아니라 링을 boxShadow로 그리기 때문이다
    // (레이아웃 중립 — border/outline은 흐름을 바꾼다). 값은 preset의 brand 램프와 같아야 한다.
    const ringShadow: string | undefined =
      selected && !ringSuppressed
        ? '0 0 0 1px #ffffff, 0 0 0 3px #6366f1' // ring-offset-1 + ring-2 brand-500 등가
        : treeHovered
          ? '0 0 0 1px #ffffff, 0 0 0 3px #a5b4fc' // 트리 행 hover = 옅은 brand-300
          : undefined;
    // 칸 래퍼(selectable) 스타일: 기본은 링 그림자 + gridColumn. shrink 위젯은 링을 ringBox(시각 박스)로
    // 옮기므로 selectable엔 gridColumn만(칸 전체 하이라이트 방지) — boxShadow 미부여.
    const wrapperStyle: CSSProperties | undefined =
      (!shrinkRingToContent && ringShadow) || gridColumn
        ? {
            ...(!shrinkRingToContent && ringShadow ? { boxShadow: ringShadow } : {}),
            ...(gridColumn ? { gridColumn } : {}),
          }
        : undefined;
    // shrink 위젯의 링 박스 — inner를 fit-content로 감싸 링/hover가 위젯 시각 박스에만 그려지게 한다.
    // *상시* 존재(선택 여부 무관)해 흐름이 토글마다 바뀌지 않음 — margin/padding/border 없이 boxShadow만
    // (레이아웃 무영향). wrapStyle(alignH flex) 안이면 자연 shrink flex item, 밖이면 좌측 fit-content 블록.
    const wrapRingBox = (node: ReactNode): ReactNode => (
      <div
        className={`rounded${selected ? '' : ' hover:ring-1 hover:ring-brand-300'}`}
        style={{ width: 'fit-content', ...(ringShadow ? { boxShadow: ringShadow } : {}) }}
      >
        {node}
      </div>
    );
    // 텍스트 위젯(heading/input) 더블클릭 = 인라인 편집. group은 헤더 영역 더블클릭(ComposerGroupBlock)에서
    // 처리하므로 래퍼에선 제외(래퍼는 자식 전체를 감싸 헤더 밖 본문 더블클릭까지 잡히는 것 방지).
    const editableHere =
      canEditText && !noTextEdit && w.kind !== 'group' && isTextEditableWidget(w);
    const selectable = (
      <div
        // 크로스 내비게이션 앵커(양식↔작성 이동 스크롤·플래시 대상) — interactive 캔버스 래퍼 전용,
        // 레이아웃 중립 속성. 실폼 render는 위 !interactive 조기 반환으로 이 래퍼에 미도달(무접촉).
        data-composer-widget-id={w.id}
        className={`relative cursor-pointer ${shrinkRingToContent ? '' : 'rounded'} ${!shrinkRingToContent && !selected ? 'hover:ring-1 hover:ring-brand-300' : ''} ${spanClass} ${hidden || visSuppressed ? 'opacity-40' : ''}`}
        style={wrapperStyle}
        onMouseDown={(e) => {
          // 우클릭(컨텍스트 메뉴 의도)은 컨트롤에 캐럿을 만들지 않는다 — 우클릭 mousedown이 focus를
          // 부여하면 contextmenu 시점에 "편집 중"으로 오판돼 항상 브라우저 메뉴가 뜸(2026-07-16 제보:
          // 커서가 뜨는 입력란 우클릭 = 시스템 메뉴). 이미 포커스된(진짜 편집 중) 컨트롤만 유지.
          if (e.button === 2) {
            const ctrl = (e.target as HTMLElement).closest?.('input, textarea, select');
            if (ctrl && document.activeElement !== ctrl) e.preventDefault();
            return; // 우클릭은 아래 선택/드릴 mousedown 로직 대상 아님
          }
          if (!interactive.onWidgetDrill) return;
          // isolation(그룹 우선): 선택 의도 클릭(위젯 미선택)에선 컨트롤이 mousedown으로
          // 포커스(캐럿)를 채가지 못하게 차단 — formTable 셀=콘텐츠 일체화와 동형(2026-07-14
          // 제보 "input 컨트롤 바로 접근"). 드릴로 선택된 위젯의 재클릭(편집 의도)만 허용 —
          // 이때 버블을 끊어 미선택 조상(그룹) 래퍼가 다시 차단하지 않게 한다.
          if (selected) {
            e.stopPropagation();
            return;
          }
          const tag = (e.target as HTMLElement).tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') e.preventDefault();
        }}
        onClick={(e) => {
          e.stopPropagation();
          // 캔버스에서 라벨 클릭은 선택/편집 의도 — native label→input 포커스 전달(htmlFor)을
          // 차단해 캐럿이 컨트롤로 새지 않게 한다(2026-07-16 제보: 라벨 더블클릭 시 input에 커서).
          if ((e.target as HTMLElement).closest?.('label')) e.preventDefault();
          interactive.onSelect?.(w.id, {
            additive: e.ctrlKey || e.metaKey,
            range: e.shiftKey,
          });
        }}
        // 우클릭 — 폴백 래퍼 전용(일반 위젯은 renderWrapper가 처리). host 콜백에 이 위젯 id를 위임해
        // 조상(반복 컨테이너)으로 버블돼 컨테이너가 선택되는 결함을 막는다(2026-07-20). 핸들러가
        // 컨트롤 포커스 판정·preventDefault·stopPropagation을 소유하므로 여기선 위임만(미전달 시 네이티브).
        onContextMenu={
          interactive.onWidgetContextMenu
            ? (e) => interactive.onWidgetContextMenu!(w.id, e)
            : undefined
        }
        onDoubleClick={
          editableHere || interactive.onWidgetDrill
            ? (e) => {
                // isolation 더블클릭 규약(2026-07-14 사용자 확정, 보고서 탭 동형):
                //   미선택 위젯 = 드릴(그 위젯 선택 — 그룹 선택 해제) / 이미 선택된 위젯 = 인라인 편집.
                //   (더블클릭의 선행 클릭이 먼저 선택/승격을 갱신하므로, 루트 직계 위젯은 dbl 시점에
                //    이미 selected=true가 되어 편집으로, 그룹 안 위젯은 selected=false(그룹 승격)라
                //    드릴로 자연 분기된다.) onWidgetDrill 미전달(deepest/web)이면 현행 편집만.
                const tEl = e.target as HTMLElement;
                const tag = tEl.tagName;
                // 라벨 더블클릭도 아래 일반 분기(isolation 규약: 미선택=드릴/선택=라벨 편집)를 그대로
                // 따른다(2026-07-16 사용자 정정 — 선택 무관 편집 직행은 규약 위반). 종전 문제("라벨
                // 더블클릭 시 input에 커서")의 근본 원인은 native htmlFor 포커스 전달뿐이었고, 그건
                // onClick preventDefault가 차단한다.
                // readonly 값 표시 박스(aria-readonly div)는 컨트롤과 동일 취급 — 값은 캔버스에서도
                // readonly(편집 없음, 브라우저 기본=텍스트 선택만). 여기서 라벨 편집기를 열면 라벨
                // 없는 위젯에서 빈 편집기가 떠 "기존 내용 없는 수정 모드"로 오인(2026-07-14 제보).
                // 라벨 요소 자체의 더블클릭은 종전대로 라벨 편집(저작 행위 — readonly와 무관).
                if (
                  tag === 'INPUT' ||
                  tag === 'TEXTAREA' ||
                  tag === 'SELECT' ||
                  tEl.closest?.('[aria-readonly="true"]')
                ) {
                  // 입력 컨트롤: 위젯이 이미 선택돼 있으면 브라우저 기본(단어 선택=값 편집) 유지.
                  // 미선택이면 드릴 — 버블을 끊지 않으면 그룹 래퍼가 그룹 id로 받아 클릭 지점 유실.
                  if (interactive.onWidgetDrill && !selected) {
                    e.stopPropagation();
                    interactive.onWidgetDrill(w.id);
                  }
                  return;
                }
                if (interactive.onWidgetDrill && !selected) {
                  e.stopPropagation();
                  interactive.onWidgetDrill(w.id);
                  return;
                }
                if (editableHere) {
                  e.stopPropagation();
                  startTextEdit(w.id);
                  return;
                }
                // 선택된 비편집 위젯(그룹 본문 여백 등) = 드릴(그룹 진입). host가 group 아니면 no-op.
                if (interactive.onWidgetDrill) {
                  e.stopPropagation();
                  interactive.onWidgetDrill(w.id);
                }
              }
            : undefined
        }
      >
        {hidden ? (
          <span className="absolute right-0 top-0 z-10 rounded-bl bg-gray-500 px-1 text-[9px] leading-tight text-white">
            {view} 숨김
          </span>
        ) : null}
        {/* 저작 캔버스에서도 말단 입력 필드를 실제 편집 가능하게 개방(별도 시뮬레이션 모드 없이 상시 편집).
            포커스가 생겨도 전역 단축키(Delete/Ctrl+G)는 target이 INPUT/TEXTAREA면 무시하는 isEditing
            가드로 보호되므로 pointer-events 차단이 불필요. 클릭은 wrapper onClick으로 버블해 위젯 선택 유지. */}
        {/* 위젯 안쪽 여백·Outer 정렬 — 선택 ring 안쪽에 적용(미지정=래퍼 없이 그대로, WYSIWYG).
            shrink 위젯은 링을 칸이 아닌 위젯 시각 박스(ringBox)에 그린다 — wrapStyle(정렬 flex) 안이면
            그 flex item이 ringBox, 밖이면 좌측 fit-content ringBox. */}
        {shrinkRingToContent ? (
          wrapStyle ? (
            <div style={wrapStyle}>{wrapRingBox(inner)}</div>
          ) : (
            wrapRingBox(inner)
          )
        ) : wrapStyle ? (
          <div style={wrapStyle}>{inner}</div>
        ) : (
          inner
        )}
      </div>
    );
    // renderWrapper(admin)로 draggable/droppable 주입. 없으면 클릭선택 래퍼만.
    // key는 래퍼 유무와 무관하게 Fragment로 유지(Fragment는 grid 레이아웃에 투명 — col-span 회귀0).
    return (
      <Fragment key={w.id}>
        {interactive.renderWrapper
          ? interactive.renderWrapper({ widget: w, parentId, index, gridded, gridColumn }, selectable)
          : selectable}
      </Fragment>
    );
  };
  // 편집 차단 툴팁 — 네이티브 hover(title) 툴팁 모사: 커서 아래 좌측 정렬 + 흰 배경/회색 테두리/
  // 검정 텍스트(브라우저 툴팁 룩). body portal + fixed라 표 wrapper overflow·캔버스 transform에
  // 잘리지 않음. 하단 근접 시 커서 위로 플립, 우측은 뷰포트 클램프. 인라인 스타일 전용
  // (rc-tailwind-purge) + pointer-events 없음(클릭 방해 금지).
  const blockedHintPortal =
    ftBlockedHint && typeof document !== 'undefined'
      ? createPortal(
          <div
            style={{
              position: 'fixed',
              left: Math.max(8, Math.min(ftBlockedHint.x, window.innerWidth - 360)),
              ...(ftBlockedHint.y + 46 > window.innerHeight
                ? { top: ftBlockedHint.y - 30 }
                : { top: ftBlockedHint.y + 18 }),
              zIndex: 9999,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              background: '#ffffff',
              color: '#1f2937',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
              padding: '3px 6px',
              border: '1px solid #d1d5db',
              borderRadius: 3,
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
            }}
          >
            {'자동 채움(시스템 조회) 셀 — 내용은 속성 패널의 "자동 채움"에서 변경합니다'}
          </div>,
          document.body,
        )
      : null;
  // 최상위 형제/말미 드롭존의 부모 id. 미지정=null(본문 root, 기존 동작). modalTrigger 저작은
  // rootParentId=widget.id를 받아 모달 children의 부모를 정확히 계산(portal 안 parentId 오배치 해결).
  const baseParentId = rootParentId ?? null;
  // repeat 표형 행 인스턴스(tableCellsMode) — 최상위 children을 <td> 셀로 배치해 반환(단일 인스턴스
  // 유지 — 모달 채널 보존). 열 대상/순서는 RepeatRowsBlock 헤더와 같은 repeatTableColumnChildren
  // 단일 정본이라 th↔td 정합. 비시각 위젯은 마지막 셀 안에서 렌더. 뷰 숨김 위젯은 열째 제외
  // (유령 열 방지 — 실폼 renderWidget이 어차피 null 반환하던 것을 헤더와 함께 구조로 제거).
  if (tableCellsMode) {
    const list = ordered(tree);
    // renderWidget의 index 인자는 **형제(ordered 전체) 기준 위치** — 필터 후에도 원래 위치를
    // 보존해야 하므로 id→index 맵으로 1회 산출(기존 list.indexOf O(n²) 반복 제거).
    const siblingIndex = new Map(list.map((c, i) => [c.id, i] as const));
    const columnChildren = repeatTableColumnChildren(tree, view);
    const hiddenNodes = list.filter(isRepeatTableNonColumnChild).map((c) => (
      <Fragment key={c.id}>
        {renderWidget(c, false, baseParentId, siblingIndex.get(c.id) ?? 0, undefined, true)}
      </Fragment>
    ));
    return (
      <>
        {columnChildren.length === 0 ? (
          // 열이 하나도 없는 방어 케이스(비시각 위젯만) — 빈 셀 하나에 담아 tr 구조 유지.
          <td style={FORM_TABLE_BODY_CELL_STYLE}>{hiddenNodes}</td>
        ) : (
          columnChildren.map((c, i) => (
            <td key={c.id} style={FORM_TABLE_BODY_CELL_STYLE}>
              {renderWidget(
                suppressRepeatTableCellChrome(c),
                false,
                baseParentId,
                siblingIndex.get(c.id) ?? 0,
                undefined,
                true,
              )}
              {i === columnChildren.length - 1 ? hiddenNodes : null}
            </td>
          ))
        )}
        {blockedHintPortal}
      </>
    );
  }
  return (
    <div className="space-y-4">
      {ordered(tree).map((w, i) => renderWidget(w, false, baseParentId, i))}
      {interactive?.renderDropSlot?.(baseParentId)}
      {footer}
      {blockedHintPortal}
    </div>
  );
}
